import React, { useState, useEffect, useContext, useMemo, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft, Download, FileText, Lock, Plus, X, Save, Edit, Trash2,
  Bell, AlertTriangle, Clock, Check, Globe, Calendar, Printer, Table, Eye,
  BarChart3, Package, Server, TrendingUp, TrendingDown, Receipt, ClipboardList,
  DollarSign, Search, ChevronLeft, ChevronRight, Layers, ArrowUpDown, Percent, Fuel, Network, RefreshCw,
  PieChart as PieChartIcon, Building2, Shield, FileSpreadsheet, Truck, ChevronDown
} from "lucide-react";
import { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { Button } from "../components/ui/button";
import MercaderiasPendientesReport from "../components/MercaderiasPendientesReport";
import useDepositos from "../hooks/useDepositos";
import { Input } from "../components/ui/input";
import { AuthContext } from "../App";
import { toast } from "sonner";
import { buildReporteConstructora } from "../lib/constructoraReportes";
import { svgDocumentHeaderLogoHtml } from "../lib/marcaLogoSvg";
import DateInput from "../components/ui/DateInput";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const mesActual = () => new Date().toISOString().slice(0, 7);
const fmtPYG = (n) => `₲ ${Math.round(Number(n || 0)).toLocaleString("es-PY")}`;
const fmtUSD = (n) => `$ ${Number(n || 0).toLocaleString("es-PY", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtBRL = (n) => `R$ ${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const mesLabel = (m) => {
  if (!m) return "";
  const d = new Date(`${m}-02T00:00:00`);
  return d.toLocaleDateString("es-PY", { month: "long", year: "numeric" });
};
const MESES = [
  "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"
];
const addMonths = (periodo, delta) => {
  const [y, m] = periodo.split("-").map(Number);
  const d = new Date(y, m - 1 + delta, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};
const setMonthPart = (periodo, monthIdx) => `${periodo.slice(0, 5)}${String(monthIdx + 1).padStart(2, "0")}`;
const endOfMonthDate = (periodo) => {
  const [y, m] = periodo.split("-").map(Number);
  return new Date(y, m, 0).toISOString().slice(0, 10);
};

// ─── Helpers para el reporte (PDF + Excel) ───────────────────────────────────
const COLORS = {
  inv:   { bg: "#1e3a5f", border: "#152b47", rowAlt: "#f0f4f8", title: "#1e3a5f" },
  cred:  { bg: "#7c3aed", border: "#5b21b6", rowAlt: "#faf5ff", title: "#047857" },
  ctas:  { bg: "#0f766e", border: "#0d5e57", rowAlt: "#f0fdfa", title: "#0f766e" },
  det:   { bg: "#ea580c", border: "#c2410c", rowAlt: "#fff7ed", title: "#ea580c" },
};

// Estilo <th> con color sólido (inline, sin Tailwind)
const thStyle = (c) => ({
  backgroundColor: c.bg,
  color: "#ffffff",
  border: `1px solid ${c.border}`,
  padding: "5px 7px",
  textAlign: "left",
  fontWeight: "bold",
  fontSize: "9px",
  whiteSpace: "nowrap",
});

// Estilo <td> con filas alternadas
const tdStyle = (even, c, extra = {}) => ({
  backgroundColor: even ? "#ffffff" : c.rowAlt,
  border: "1px solid #d1d5db",
  padding: "3px 6px",
  fontSize: "9px",
  verticalAlign: "middle",
  wordBreak: "normal",
  overflowWrap: "break-word",
  ...extra,
});

// Título de sección
const SecTitle = ({ color, children }) => (
  <div style={{
    color,
    fontWeight: "bold",
    fontSize: "13px",
    marginTop: "22px",
    marginBottom: "5px",
    borderBottom: `2px solid ${color}`,
    paddingBottom: "2px",
    textTransform: "uppercase",
    letterSpacing: "0.04em",
  }}>
    {children}
  </div>
);

// Pie que se agrega al final de TODOS los reportes impresos / PDF.
const PIE_REPORTE_HTML = `<div style="margin-top:18px;padding-top:8px;border-top:1px solid #e2e8f0;text-align:center;font-family:Arial,Helvetica,sans-serif;font-size:9px;color:#94a3b8;letter-spacing:.3px">Este documento fue generado por <strong style="color:#64748b">Sistema AranduJAR-ERP</strong></div>`;

// ─── Cargador de xlsx-js-style (con soporte de colores en celdas) ─────────────
const loadXLSX = () =>
  new Promise((resolve, reject) => {
    // Si ya cargamos la versión con estilos, reusar
    if (window._XLSX_STYLED && window.XLSX) { resolve(window.XLSX); return; }
    // Borrar versión base anterior (misma variable global) para forzar recarga
    delete window.XLSX;
    const tryLoad = (urls, i) => {
      if (i >= urls.length) { reject(new Error("No se pudo cargar la librería Excel")); return; }
      const s = document.createElement("script");
      s.src = urls[i];
      s.onload  = () => {
        if (window.XLSX) { window._XLSX_STYLED = true; resolve(window.XLSX); }
        else tryLoad(urls, i + 1);
      };
      s.onerror = () => tryLoad(urls, i + 1);
      document.head.appendChild(s);
    };
    tryLoad([
      "https://unpkg.com/xlsx-js-style@1.2.0/dist/xlsx.bundle.js",
      "https://cdn.jsdelivr.net/npm/xlsx-js-style@1.2.0/dist/xlsx.bundle.js",
    ], 0);
  });

const ChipSearch = ({ value, onChange, chips, onChipsChange, placeholder }) => {
  const addChip = () => {
    const term = value.trim();
    if (!term || chips.some(c => c.toLowerCase() === term.toLowerCase())) return;
    onChipsChange([...chips, term]);
    onChange("");
  };
  const removeChip = (chip) => onChipsChange(chips.filter(c => c !== chip));

  return (
    <div className="flex-1 bg-arandu-dark border border-white/10 rounded-lg px-3 py-2 min-h-[44px] flex flex-wrap items-center gap-2 focus-within:border-arandu-blue/60">
      <Search className="w-4 h-4 text-slate-500 shrink-0" />
      {chips.map(chip => (
        <span key={chip} className="inline-flex items-center gap-1 bg-red-500/15 text-red-200 border border-red-500/25 rounded-full px-2 py-1 text-xs">
          {chip}
          <button type="button" onClick={() => removeChip(chip)} className="text-red-200 hover:text-white">
            <X className="w-3 h-3" />
          </button>
        </span>
      ))}
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") { e.preventDefault(); addChip(); }
          if (e.key === "Backspace" && !value && chips.length) onChipsChange(chips.slice(0, -1));
        }}
        className="bg-transparent text-white placeholder:text-slate-500 outline-none flex-1 min-w-[220px] text-sm"
        placeholder={placeholder}
      />
    </div>
  );
};

const matchesChips = (text, chips, inputValue) => {
  const terms = [...chips, inputValue.trim()].filter(Boolean).map(t => t.toLowerCase());
  if (!terms.length) return true;
  const haystack = text.toLowerCase();
  return terms.every(term => haystack.includes(term));
};

// ─── Selector de producto buscable ───────────────────────────────────────────
const ProductoSearchSelect = ({ value, onChange, products, placeholder = "Buscar producto...", showTodos = true }) => {
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const selected = products.find(p => p.id === value);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const displayText = open ? search : (selected ? `${selected.nombre}${selected.sku ? ` (${selected.sku})` : ""}` : "");
  const filtered = search
    ? products.filter(p => (p.nombre + " " + (p.sku || "")).toLowerCase().includes(search.toLowerCase()))
    : products;

  return (
    <div ref={ref} className="relative">
      <div
        className="flex items-center gap-2 bg-arandu-dark border border-white/10 rounded-lg px-3 py-2 cursor-text focus-within:border-blue-500/40 transition-colors"
        onClick={() => setOpen(true)}
      >
        <Search className="w-3.5 h-3.5 text-slate-500 shrink-0" />
        <input
          value={displayText}
          onChange={e => { setSearch(e.target.value); setOpen(true); }}
          onFocus={() => { setSearch(""); setOpen(true); }}
          placeholder={!value ? placeholder : ""}
          className="bg-transparent text-white text-sm outline-none flex-1 min-w-0 placeholder:text-slate-500"
        />
        {value && (
          <button type="button" onClick={e => { e.stopPropagation(); onChange(""); setSearch(""); setOpen(false); }} className="text-slate-500 hover:text-white">
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
      {open && (
        <div className="absolute z-50 w-full mt-1 bg-arandu-dark border border-white/10 rounded-lg shadow-xl max-h-48 overflow-y-auto">
          {showTodos && (
            <button type="button" onClick={() => { onChange(""); setSearch(""); setOpen(false); }}
              className={`w-full text-left px-3 py-2 text-sm hover:bg-white/5 transition-colors ${!value ? "text-blue-300 font-medium" : "text-slate-400"}`}>
              Todos
            </button>
          )}
          {filtered.length === 0 ? (
            <p className="px-3 py-2 text-xs text-slate-500">Sin resultados</p>
          ) : filtered.map(p => (
            <button key={p.id} type="button" onClick={() => { onChange(p.id); setSearch(""); setOpen(false); }}
              className={`w-full text-left px-3 py-2 text-sm hover:bg-white/5 transition-colors flex items-center gap-2 ${value === p.id ? "text-blue-300 bg-blue-500/10" : "text-white"}`}>
              <span className="flex-1 truncate">{p.nombre}</span>
              {p.sku && <span className="text-xs text-slate-500 font-mono shrink-0">{p.sku}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Componente principal ─────────────────────────────────────────────────────
// Selector múltiple compacto (mismo tamaño que un <select>): botón + lista desplegable con checkboxes.
const MultiSelectDropdown = ({ opciones, seleccion, onChange, placeholder = "Todas" }) => {
  const [abierto, setAbierto] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!abierto) return undefined;
    const fuera = (e) => { if (ref.current && !ref.current.contains(e.target)) setAbierto(false); };
    document.addEventListener("mousedown", fuera);
    return () => document.removeEventListener("mousedown", fuera);
  }, [abierto]);
  const texto = seleccion.length === 0 ? placeholder : seleccion.length === 1 ? seleccion[0] : `${seleccion.length} seleccionadas`;
  return (
    <div ref={ref} className="relative min-w-[150px]">
      <button type="button" onClick={() => setAbierto(v => !v)}
        className="w-full flex items-center justify-between gap-2 bg-arandu-dark border border-white/10 text-slate-300 rounded-lg px-3 py-2.5 text-sm focus:border-blue-500/40 outline-none transition-colors">
        <span className="truncate">{texto}</span>
        <ChevronDown className={`w-4 h-4 flex-shrink-0 text-slate-500 transition-transform ${abierto ? "rotate-180" : ""}`} />
      </button>
      {abierto && (
        <div className="absolute z-30 mt-1 w-64 bg-arandu-dark border border-white/10 rounded-lg shadow-xl p-2">
          <div className="flex items-center justify-between text-[10px] text-slate-500 mb-1 px-1">
            <span>{seleccion.length === 0 ? "Todas" : `${seleccion.length} seleccionada${seleccion.length !== 1 ? "s" : ""}`}</span>
            <span className="flex gap-2">
              <button type="button" onClick={() => onChange([...opciones])} className="text-blue-400 hover:underline">Todas</button>
              <button type="button" onClick={() => onChange([])} className="text-slate-400 hover:underline">Limpiar</button>
            </span>
          </div>
          <div className="max-h-56 overflow-y-auto space-y-0.5">
            {opciones.map(nombre => (
              <label key={nombre} className="flex items-center gap-2 text-xs text-slate-300 cursor-pointer hover:bg-white/5 rounded px-1 py-1">
                <input type="checkbox" checked={seleccion.includes(nombre)}
                  onChange={e => onChange(e.target.checked ? [...seleccion, nombre] : seleccion.filter(x => x !== nombre))} />
                {nombre}
              </label>
            ))}
            {opciones.length === 0 && <div className="text-xs text-slate-500 px-1 py-1">Sin categorías</div>}
          </div>
        </div>
      )}
    </div>
  );
};

const ReportesPage = () => {
  const { token, user, activeEmpresaPropia, hasPermission, hasModule } = useContext(AuthContext);
  const isAdmin = user?.role === "admin";
  const [searchParams] = useSearchParams();
  const empresaFromUrl = searchParams.get("empresa") || "";
  const nuevoFromUrl = searchParams.get("nuevo") || "";
  const tabFromUrl = searchParams.get("tab") || "";

  // Ref para cerrar el multi-select de cuentas al hacer click afuera
  const cuentasPlanRef = useRef(null);

  const [empresas, setEmpresas] = useState([]);
  const [alertas, setAlertas] = useState([]);
  const [alertasProximas, setAlertasProximas] = useState([]);
  const [activos, setActivos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("reportes");
  const [sidebarGruposOpen, setSidebarGruposOpen] = useState({}); // id de grupo -> abierto/cerrado (si no está, se abre solo el que contiene el reporte activo)
  const [reporteCategoria, setReporteCategoria] = useState("tecnico"); // "financiero" | "inventario" | "tecnico"

  const [selectedEmpresas, setSelectedEmpresas] = useState(empresaFromUrl ? [empresaFromUrl] : []);
  const [sortBy, setSortBy] = useState("nombre");

  const [showAlertForm, setShowAlertForm] = useState(false);
  const [editingAlert, setEditingAlert] = useState(null);
  const [alertForm, setAlertForm] = useState({
    empresa_id: empresaFromUrl || "", tipo: "dominio", nombre: "", descripcion: "",
    fecha_vencimiento: "", activo_id: "", notificar_dias: 30, notificar_dias_urgente: 0,
  });
  const [alertSearch, setAlertSearch] = useState("");
  const [alertSearchChips, setAlertSearchChips] = useState([]);
  const [handledNuevo, setHandledNuevo] = useState(false);

  // Estado del preview
  const [showPreview, setShowPreview] = useState(false);
  const [previewData, setPreviewData] = useState(null);       // lista de activos
  const [previewWithCreds, setPreviewWithCreds] = useState(false);
  const [cuentasAsociadas, setCuentasAsociadas] = useState([]); // sección 3
  const [detalleCuentas, setDetalleCuentas] = useState([]);     // sección 4
  const [reportMonth, setReportMonth] = useState(mesActual());
  const [reportYear, setReportYear] = useState(String(new Date().getFullYear()));
  const [reportCurrency, setReportCurrency] = useState("PYG");
  const [reportClienteId, setReportClienteId] = useState("");
  const [clientePickerTipo, setClientePickerTipo] = useState(null); // null = cerrado
  const [clientePickerSearch, setClientePickerSearch] = useState("");
  const [clientePickerTempId, setClientePickerTempId] = useState("");
  const [genericReport, setGenericReport] = useState(null);
  const [genericReportLoading, setGenericReportLoading] = useState(false);
  const [stockHistorialProductoId, setStockHistorialProductoId] = useState("");
  const [stockProductos, setStockProductos] = useState([]);
  const [stockHistorialDesde, setStockHistorialDesde] = useState(() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-01`; });
  const [stockHistorialHasta, setStockHistorialHasta] = useState(() => new Date(Date.now() - new Date().getTimezoneOffset()*60000).toISOString().slice(0,10));
  const [stockDesde, setStockDesde] = useState("");
  const [stockHasta, setStockHasta] = useState("");
  const [stockMoneda, setStockMoneda] = useState(""); // "" = todas | "PYG" | "USD" | "BRL"
  const [stockProductoId, setStockProductoId] = useState("");
  const [stockProveedorId, setStockProveedorId] = useState("");
  const [stockDepositoId, setStockDepositoId] = useState("");
  const depReporte = useDepositos(token, activeEmpresaPropia?.slug);
  const [stockProveedores, setStockProveedores] = useState([]);
  const [stockTodos, setStockTodos] = useState(false); // con rango de fechas: todos los productos con su stock al final del período
  const [stockCategoria, setStockCategoria] = useState("");
  const [stockCategoriasSel, setStockCategoriasSel] = useState([]);
  const [stockSubcategoria, setStockSubcategoria] = useState("");
  const [stockSubcategorias, setStockSubcategorias] = useState([]);
  const [stockCategorias, setStockCategorias] = useState([]);
  const [stockAtributos, setStockAtributos] = useState([]);
  const [stockAtributosFiltro, setStockAtributosFiltro] = useState({});
  const [stockConfigProductos, setStockConfigProductos] = useState({ usar_subcategorias: false, usar_atributos: false });
  const [descuentoEstado, setDescuentoEstado] = useState("todos");
  const [vencProdEstado, setVencProdEstado] = useState("todos");
  const [libroVentasIncluirAnuladas, setLibroVentasIncluirAnuladas] = useState(false);
  const [libroVentasData, setLibroVentasData] = useState(null); // datos crudos para exportar a Excel
  // Kardex state
  const [kardexProductoId, setKardexProductoId] = useState("");
  const [kardexDesde, setKardexDesde] = useState(() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-01`; });
  const [kardexHasta, setKardexHasta] = useState(() => new Date(Date.now() - new Date().getTimezoneOffset()*60000).toISOString().slice(0,10));
  const [kardexTipoMov, setKardexTipoMov] = useState([]); // array: ["entrada","salida","ajuste"]
  const [kardexIncluirEliminados, setKardexIncluirEliminados] = useState(false);
  const [kardexData, setKardexData] = useState(null);
  const [kardexLoading, setKardexLoading] = useState(false);
  // Sidebar nav state
  const [sidebarBombasOpen, setSidebarBombasOpen] = useState(false);
  const [bombasDesde, setBombasDesde] = useState(() => { const d = new Date(); d.setDate(1); return d.toISOString().slice(0, 10); });
  const [bombasHasta, setBombasHasta] = useState(new Date().toISOString().slice(0, 10));
  const [bombasData, setBombasData] = useState(null);
  const [bombasLoading, setBombasLoading] = useState(false);
  const [bombasPlayero, setBombasPlayero] = useState("");
  const [bombasPlayeros, setBombasPlayeros] = useState([]);
  const [reporteSeleccionado, setReporteSeleccionado] = useState(null);
  const [catastroTipo, setCatastroTipo] = useState("todos"); // todos | clientes | proveedores | empleados
  // ── Balancete - Obra ─────────────────────────────────────────────────────
  const [balObraClienteId, setBalObraClienteId] = useState("");
  const [balObraTipoFacturaIds, setBalObraTipoFacturaIds] = useState([]); // [] = todas las obras
  const [balObraDesde, setBalObraDesde] = useState("");
  const [balObraHasta, setBalObraHasta] = useState("");
  const [clientesBalObra, setClientesBalObra] = useState([]);
  const [obrasBalObra, setObrasBalObra] = useState([]);
  const [balanceteObraChart, setBalanceteObraChart] = useState([]); // filas crudas para los gráficos de torta
  // ── Garantías ─────────────────────────────────────────────────────────────
  const [garantiasTipo, setGarantiasTipo] = useState("todas"); // todas | recibidas | concedidas
  const [garantiasEstadoDoc, setGarantiasEstadoDoc] = useState("");
  const [garantiasDesde, setGarantiasDesde] = useState("");
  const [garantiasHasta, setGarantiasHasta] = useState("");
  // ── Loteadora: Precificación por manzana ─────────────────────────────────────
  const [loteamientosLoteadora, setLoteamientosLoteadora] = useState([]);
  const [loteadoraPrecifLoteamientoId, setLoteadoraPrecifLoteamientoId] = useState("");
  const [loteadoraPrecifNCuotas, setLoteadoraPrecifNCuotas] = useState(12);
  // ── Comisiones de Vendedores ──────────────────────────────────────────────
  const [comisionVendedoresOpciones, setComisionVendedoresOpciones] = useState([]);
  const [comisionVendedorId, setComisionVendedorId] = useState("");
  const [comisionDesde, setComisionDesde] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
  });
  const [comisionHasta, setComisionHasta] = useState(() => new Date(Date.now() - new Date().getTimezoneOffset()*60000).toISOString().slice(0,10));
  // ── Gastos por Categoría ──────────────────────────────────────────────────
  const [gastosCategoriaOpciones, setGastosCategoriaOpciones] = useState([]);
  const [gastosCategoriaSel, setGastosCategoriaSel] = useState("");
  const [gastosCategoriaDesde, setGastosCategoriaDesde] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
  });
  const [gastosCategoriaHasta, setGastosCategoriaHasta] = useState(() => new Date(Date.now() - new Date().getTimezoneOffset()*60000).toISOString().slice(0,10));

  const [retencionesDesde, setRetencionesDesde] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
  });
  const [retencionesHasta, setRetencionesHasta] = useState(() => new Date(Date.now() - new Date().getTimezoneOffset()*60000).toISOString().slice(0,10));
  const [retencionesOrigen, setRetencionesOrigen] = useState("todas");

  const [costoHaChacra, setCostoHaChacra] = useState("");
  const [costoHaChacras, setCostoHaChacras] = useState([]);
  const [costoHaDesde, setCostoHaDesde] = useState(() => `${new Date().getFullYear()}-01-01`);
  const [costoHaHasta, setCostoHaHasta] = useState(() => new Date(Date.now() - new Date().getTimezoneOffset()*60000).toISOString().slice(0,10));

  const [transporteClientesOpciones, setTransporteClientesOpciones] = useState([]);
  const [transporteChoferesOpciones, setTransporteChoferesOpciones] = useState([]);
  const [transporteClienteId, setTransporteClienteId] = useState("");
  const [transporteChoferId, setTransporteChoferId] = useState("");
  const [constructoraProyectoId, setConstructoraProyectoId] = useState("");
  const [constructoraProyectos, setConstructoraProyectos] = useState([]);
  const [transporteDesde, setTransporteDesde] = useState(() => {
    const d = new Date(); d.setDate(1);
    return d.toISOString().slice(0, 10);
  });
  const [transporteHasta, setTransporteHasta] = useState(() => new Date(Date.now() - new Date().getTimezoneOffset()*60000).toISOString().slice(0,10));
  const [cumpleanosConfig, setCumpleanosConfig] = useState({ notificar_dias_amarillo: 10, notificar_dias_urgente: 0 });
  const [showCajaPicker, setShowCajaPicker] = useState(false);
  const [cajaCuentasPicker, setCajaCuentasPicker] = useState([]);
  const hoyIso = new Date(Date.now() - new Date().getTimezoneOffset()*60000).toISOString().slice(0,10);
  const [reportCajaDesde, setReportCajaDesde] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
  });
  const [reportCajaHasta, setReportCajaHasta] = useState(hoyIso);

  // ── Movimientos (reporte nuevo, independiente de Caja/Banco) ──
  const [showMovPicker, setShowMovPicker] = useState(false);
  const [movCuentasPicker, setMovCuentasPicker] = useState([]);
  const [reportMovDesde, setReportMovDesde] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
  });
  const [reportMovHasta, setReportMovHasta] = useState(hoyIso);
  const [reportMovMes, setReportMovMes] = useState(() => new Date().toISOString().slice(0, 7));
  const [movFiltro, setMovFiltro] = useState("todos");
  const [movData, setMovData] = useState(null); // datos crudos del último fetch, para recalcular el filtro sin re-consultar

  // Cuentas por cobrar / pagar
  const [cuentasData, setCuentasData] = useState(null);      // { rows, resumen }
  const [cuentasLoading, setCuentasLoading] = useState(false);
  const [cuentasDesde, setCuentasDesde] = useState(() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-01`; });
  const [cuentasHasta, setCuentasHasta] = useState(hoyIso);
  // Flow de Facturaciones (unificado)
  const [movDesde, setMovDesde] = useState(() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-01`; });
  const [movHasta, setMovHasta] = useState(hoyIso);
  const [movTipos, setMovTipos] = useState(["facturas", "boletas", "notas_credito", "compras"]);
  const [movProductoId, setMovProductoId] = useState("");
  const [movVarianteId, setMovVarianteId] = useState("");
  // Por defecto igual al comportamiento histórico del reporte: las anuladas se
  // seguían mostrando (nunca se filtraban) y las inactivas/eliminadas seguían
  // ocultas (requieren permiso de eliminar). Los checkboxes permiten cambiarlo.
  const [movIncluirAnuladas, setMovIncluirAnuladas] = useState(true);
  const [movIncluirInactivas, setMovIncluirInactivas] = useState(false);
  // Flow de Financieros (unificado)
  const [flowFinDesde, setFlowFinDesde] = useState(() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-01`; });
  const [flowFinHasta, setFlowFinHasta] = useState(hoyIso);
  const [flowFinTipos, setFlowFinTipos] = useState(["ingresos", "recibos", "gastos"]);
  // Préstamos
  const [prestReporteDesde, setPrestReporteDesde] = useState("2000-01-01");
  const [prestReporteHasta, setPrestReporteHasta] = useState(hoyIso);
  const [prestReporteTipo, setPrestReporteTipo] = useState("todos");
  const [prestReporteEstado, setPrestReporteEstado] = useState("todos");
  const [prestReporteEntidadId, setPrestReporteEntidadId] = useState("");
  const [prestReporteEntidadNombre, setPrestReporteEntidadNombre] = useState("");
  const [prestReporteEntidadBusq, setPrestReporteEntidadBusq] = useState("");
  const [prestReporteEntidadOpen, setPrestReporteEntidadOpen] = useState(false);
  const [prestReporteBuscar, setPrestReporteBuscar] = useState("");
  const [prestReporteEntidades, setPrestReporteEntidades] = useState([]);
  // Balance unificado
  const [balTipoSel, setBalTipoSel] = useState("mensual"); // "mensual" | "anual" | "detallado"
  // Búsqueda cliente (cuentas_cobrar)
  const [cuentasEmpresaId, setCuentasEmpresaId] = useState("");
  const [cuentasEmpresaNombre, setCuentasEmpresaNombre] = useState("");
  const [cuentasEmpresaBusq, setCuentasEmpresaBusq] = useState("");
  const [cuentasEmpresaOpen, setCuentasEmpresaOpen] = useState(false);
  // Búsqueda proveedor (cuentas_pagar)
  const [cuentasProveedorId, setCuentasProveedorId] = useState("");
  const [cuentasProveedorNombre, setCuentasProveedorNombre] = useState("");
  const [cuentasProveedorBusq, setCuentasProveedorBusq] = useState("");
  const [cuentasProveedorOpen, setCuentasProveedorOpen] = useState(false);
  // Multi-select cuenta contable
  const [cuentasPlanIds, setCuentasPlanIds] = useState([]);    // array de IDs seleccionados
  const [cuentasPlanOpen, setCuentasPlanOpen] = useState(false);
  const [cuentasVencimiento, setCuentasVencimiento] = useState("todos");
  const [cuentasIncluirPagadas, setCuentasIncluirPagadas] = useState(false);
  const [cuentasTipoFacId, setCuentasTipoFacId] = useState("");
  const [cuentasTipoFacNombre, setCuentasTipoFacNombre] = useState("");
  const [tiposFacList, setTiposFacList] = useState([]);
  const [cuentasPlanCuentas, setCuentasPlanCuentas] = useState([]);
  const [cuentasProveedores, setCuentasProveedores] = useState([]);
  const [cuentasProveedoresCobrables, setCuentasProveedoresCobrables] = useState([]);
  const [cuentasExpanded, setCuentasExpanded] = useState({});  // doc_id -> bool

  // Estado de Cuenta
  const EC_TODOS = "__todos__";
  const [ecTipo, setEcTipo] = useState("cobrar");
  const [ecEntidadId, setEcEntidadId] = useState("");
  const [ecEntidadNombre, setEcEntidadNombre] = useState("");
  const [ecEntidadBusq, setEcEntidadBusq] = useState("");
  const [ecEntidadOpen, setEcEntidadOpen] = useState(false);
  const [ecIncluirPagadas, setEcIncluirPagadas] = useState(false);
  const [ecData, setEcData] = useState(null);
  const [ecLoading, setEcLoading] = useState(false);
  const estadoCuentaEntidades = useMemo(() => {
    const opciones = ecTipo === "cobrar"
      ? [
          ...empresas.map(e => ({ ...e, _tipoEstadoCuenta: "Cliente" })),
          ...cuentasProveedoresCobrables.map(p => ({
            ...p,
            _tipoEstadoCuenta: p.tiene_anticipo && p.tiene_prestamo
              ? "Proveedor con anticipo/préstamo"
              : (p.tiene_prestamo ? "Proveedor con préstamo" : "Proveedor con anticipo"),
          })),
        ]
      : cuentasProveedores.map(p => ({ ...p, _tipoEstadoCuenta: "Proveedor" }));
    const vistos = new Set();
    return opciones.filter(e => {
      if (!e?.id || vistos.has(e.id)) return false;
      vistos.add(e.id);
      return true;
    });
  }, [ecTipo, empresas, cuentasProveedores, cuentasProveedoresCobrables]);

  // IPs Libres
  const [ipsLibresEmpresaId, setIpsLibresEmpresaId] = useState("");
  const [ipsLibresEmpresaNombre, setIpsLibresEmpresaNombre] = useState("");
  const [ipsLibresEmpresaBusq, setIpsLibresEmpresaBusq] = useState("");
  const [ipsLibresEmpresaOpen, setIpsLibresEmpresaOpen] = useState(false);
  const [ipsLibresDesde, setIpsLibresDesde] = useState("192.168.0.1");
  const [ipsLibresHasta, setIpsLibresHasta] = useState("192.168.0.254");
  const [ipsLibresTipo, setIpsLibresTipo] = useState("local"); // "local" | "publica"
  const [ipsLibresData, setIpsLibresData] = useState(null);
  const [ipsLibresLoading, setIpsLibresLoading] = useState(false);

  // Orientación de impresión (horizontal = landscape, vertical = portrait)

  // Inyectar CSS de impresión una sola vez
  useEffect(() => {
    const id = "arandu-print-css";
    if (!document.getElementById(id)) {
      const s = document.createElement("style");
      s.id = id;
      s.textContent = `
        @media print {
          @page { size: landscape; margin: 1.2cm 1cm; }
          body {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            background: white !important;
          }
          .no-print { display: none !important; }
          #inventario-print {
            display: block !important;
            padding: 0 !important; margin: 0 !important;
            max-width: 100% !important; box-shadow: none !important;
          }
          #inventario-print table { page-break-inside: auto; width: 100%; }
          #inventario-print tr    { page-break-inside: avoid; page-break-after: auto; }
          #inventario-print thead { display: table-header-group; }
        }
      `;
      document.head.appendChild(s);
    }
    return () => { /* no removemos: puede usarse en otra sesión */ };
  }, []);

  // ── Imprimir en ventana nueva — evita el problema del modal/overflow ──────────
  const handlePrint = () => {
    const content = document.getElementById("inventario-print");
    if (!content) return;

    const printWin = window.open("", "_blank");
    if (!printWin) {
      toast.error("El navegador bloqueó la ventana emergente. Habilitala e intentá de nuevo.");
      return;
    }

    // Clonar el nodo para no afectar el DOM visible, y limpiar estilos inline
    // que impedirían que el contenido use el ancho completo del papel
    const clone = content.cloneNode(true);
    clone.style.maxWidth  = "none";
    clone.style.width     = "100%";
    clone.style.padding   = "0";
    clone.style.margin    = "0";
    clone.style.boxShadow = "none";

    printWin.document.write(`<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>Inventario Técnico — Arandu&JAR</title>
  <style>
    @page { margin: 1.2cm 1cm; }
    *, *::before, *::after { box-sizing: border-box; }
    body {
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
      font-family: Arial, Helvetica, sans-serif;
      font-size: 9px; line-height: 1.3;
      margin: 0; padding: 0;
      background: white; color: #000;
    }
    table { border-collapse: collapse; width: 100%; }
    /* thead como grupo — repite título + columnas en cada página nueva */
    thead { display: table-header-group !important; }
    /* tbody puede cortar libremente entre filas — sin esto el thead NO repite */
    tbody { display: table-row-group !important; }
    /* Solo las FILAS no se parten al medio */
    tbody tr { break-inside: avoid !important; page-break-inside: avoid !important; }
    td { overflow: hidden; }
    /* Cada sección arranca en página nueva */
    .section-page-break { page-break-before: always; break-before: page; }
    @media screen { body { padding: 16px; } }
  </style>
</head>
<body>
  ${clone.outerHTML}${clone.outerHTML.includes("Sistema AranduJAR-ERP") ? "" : PIE_REPORTE_HTML}
  <script>
    window.addEventListener('load', function() {
      setTimeout(function() { window.print(); window.close(); }, 400);
    });
  <\/script>
</body>
</html>`);
    printWin.document.close();
    printWin.focus();
  };

  const printNode = (nodeId, title = "Reporte") => {
    const content = document.getElementById(nodeId);
    if (!content) return;
    const printWin = window.open("", "_blank");
    if (!printWin) {
      toast.error("El navegador bloqueó la ventana emergente.");
      return;
    }
    const clone = content.cloneNode(true);
    printWin.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"><title>${title}</title><link rel="preconnect" href="https://fonts.googleapis.com"><link rel="preconnect" href="https://fonts.gstatic.com" crossorigin><link href="https://fonts.googleapis.com/css2?family=Noto+Sans:wght@400;600;700&display=swap" rel="stylesheet"><style>
      @page { margin: 0.8cm; }
      * { box-sizing: border-box; }
      body { font-family: 'Noto Sans', Arial, Helvetica, sans-serif; margin:0; color:#0f172a; background:white; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      table { border-collapse: separate; border-spacing: 0; width: 100%; }
      .no-print { display:none !important; }
      .modern-report { box-shadow:none !important; }
    </style></head><body>${clone.outerHTML}${clone.outerHTML.includes("Sistema AranduJAR-ERP") ? "" : PIE_REPORTE_HTML}<script>window.onload=function(){setTimeout(function(){window.print();window.close();},300)}<\/script></body></html>`);
    printWin.document.close();
    printWin.focus();
  };

  const imprimirIpsLibres = (data) => {
    if (!data) return;
    const logoHtml = svgDocumentHeaderLogoHtml(activeEmpresaPropia?.slug || "arandujar");
    const hoy = new Date().toLocaleDateString("es-PY", { day: "2-digit", month: "2-digit", year: "numeric" });
    const pct = data.total_rango > 0 ? Math.round((data.total_libres / data.total_rango) * 100) : 0;
    const tipoLabel = data.tipo_ip === "publica" ? "IP Pública" : "IP Privada (local)";

    // Grid de IPs libres — 6 columnas por fila
    const COLS = 6;
    const libresRows = [];
    for (let i = 0; i < data.libres.length; i += COLS) {
      libresRows.push(data.libres.slice(i, i + COLS));
    }
    const libresHtml = libresRows.map((row, ri) => `
      <tr style="background:${ri % 2 === 0 ? "#f0fdf4" : "#ffffff"}">
        ${row.map(ip => `<td style="padding:4px 8px;font-family:monospace;font-size:9px;color:#166534;border:1px solid #d1fae5">${ip}</td>`).join("")}
        ${Array(COLS - row.length).fill(`<td style="border:1px solid #d1fae5;background:#f9fafb"></td>`).join("")}
      </tr>`).join("");

    const usadasHtml = data.usadas.map((u, i) => `
      <tr style="background:${i % 2 === 0 ? "#ffffff" : "#fff7ed"}">
        <td style="padding:4px 8px;font-family:monospace;font-size:9px;border:1px solid #e2e8f0;color:#c2410c;font-weight:600">${u.ip}</td>
        <td style="padding:4px 8px;font-size:9px;border:1px solid #e2e8f0;color:#1e293b">${u.nombre}</td>
        <td style="padding:4px 8px;font-size:9px;border:1px solid #e2e8f0;color:#64748b">${u.categoria || "-"}</td>
        <td style="padding:4px 8px;font-size:9px;border:1px solid #e2e8f0;color:#64748b">${u.subtipo || "-"}</td>
      </tr>`).join("");

    const html = `<!DOCTYPE html><html><head><meta charset="utf-8">
      <title>IPs Libres — ${data.empresa_nombre}</title>
      <style>
        @page { size: A4 landscape; margin: 1cm; }
        * { box-sizing: border-box; }
        body { font-family: Arial, Helvetica, sans-serif; margin:0; color:#1e293b; background:white;
               -webkit-print-color-adjust:exact; print-color-adjust:exact; }
        table { border-collapse: collapse; width:100%; }
        thead { display: table-header-group; }
        tbody { display: table-row-group; }
        tbody tr { break-inside: avoid; page-break-inside: avoid; }
        h2 { font-size:11px; margin:14px 0 4px; color:#0f172a; font-weight:700; }
        @media screen { body { padding:16px; } }
      </style></head><body>
      <!-- Encabezado -->
      <div style="display:flex;justify-content:space-between;align-items:flex-start;border-bottom:2px solid #0f172a;padding-bottom:10px;margin-bottom:14px">
        <div style="display:flex;align-items:center;gap:12px">
          ${logoHtml}
          <div>
            <div style="font-size:16px;font-weight:800;color:#0f172a">Reporte de IPs Libres</div>
            <div style="font-size:10px;color:#475569;margin-top:2px">Cliente: <b>${data.empresa_nombre}</b></div>
            <div style="font-size:10px;color:#475569">Rango: <b style="font-family:monospace">${data.ip_desde} — ${data.ip_hasta}</b> &nbsp;·&nbsp; Tipo: <b>${tipoLabel}</b></div>
          </div>
        </div>
        <div style="text-align:right">
          <div style="font-size:9px;color:#94a3b8">Generado: ${hoy}</div>
        </div>
      </div>
      <!-- Stats -->
      <div style="display:flex;gap:10px;margin-bottom:14px">
        <div style="flex:1;border:1px solid #e2e8f0;border-radius:6px;padding:8px 12px;background:#f8fafc">
          <div style="font-size:9px;color:#64748b;text-transform:uppercase;letter-spacing:0.05em;font-weight:700">Total en rango</div>
          <div style="font-size:22px;font-weight:800;color:#0f172a">${data.total_rango.toLocaleString()}</div>
        </div>
        <div style="flex:1;border:1px solid #fed7aa;border-radius:6px;padding:8px 12px;background:#fff7ed">
          <div style="font-size:9px;color:#c2410c;text-transform:uppercase;letter-spacing:0.05em;font-weight:700">IPs en uso</div>
          <div style="font-size:22px;font-weight:800;color:#c2410c">${data.total_usadas.toLocaleString()}</div>
        </div>
        <div style="flex:1;border:1px solid #bbf7d0;border-radius:6px;padding:8px 12px;background:#f0fdf4">
          <div style="font-size:9px;color:#15803d;text-transform:uppercase;letter-spacing:0.05em;font-weight:700">IPs libres</div>
          <div style="font-size:22px;font-weight:800;color:#15803d">${data.total_libres.toLocaleString()}</div>
        </div>
        <div style="flex:1;border:1px solid #e0e7ff;border-radius:6px;padding:8px 12px;background:#eef2ff">
          <div style="font-size:9px;color:#4338ca;text-transform:uppercase;letter-spacing:0.05em;font-weight:700">Disponibilidad</div>
          <div style="font-size:22px;font-weight:800;color:#4338ca">${pct}%</div>
        </div>
      </div>
      <!-- Tabla IPs Libres -->
      <h2>✅ IPs Libres (${data.total_libres})</h2>
      <table>
        <thead>
          <tr style="background:#166534">
            ${Array(COLS).fill(`<th style="padding:5px 8px;text-align:left;font-size:9px;color:#fff;font-weight:700;border:1px solid #14532d">Dirección IP</th>`).join("")}
          </tr>
        </thead>
        <tbody>${libresHtml || `<tr><td colspan="${COLS}" style="padding:8px;text-align:center;color:#6b7280;font-size:9px">No hay IPs libres en este rango</td></tr>`}</tbody>
      </table>
      ${data.usadas.length > 0 ? `
      <!-- Tabla IPs Usadas -->
      <h2 style="page-break-before:auto">🔒 IPs en Uso (${data.total_usadas})</h2>
      <table>
        <thead>
          <tr style="background:#c2410c">
            <th style="padding:5px 8px;text-align:left;font-size:9px;color:#fff;font-weight:700;border:1px solid #9a3412">IP</th>
            <th style="padding:5px 8px;text-align:left;font-size:9px;color:#fff;font-weight:700;border:1px solid #9a3412">Dispositivo</th>
            <th style="padding:5px 8px;text-align:left;font-size:9px;color:#fff;font-weight:700;border:1px solid #9a3412">Categoría</th>
            <th style="padding:5px 8px;text-align:left;font-size:9px;color:#fff;font-weight:700;border:1px solid #9a3412">Subtipo</th>
          </tr>
        </thead>
        <tbody>${usadasHtml}</tbody>
      </table>` : ""}
      ${PIE_REPORTE_HTML}
      <script>window.onload=function(){setTimeout(function(){window.print();window.close();},400)}<\/script>
      </body></html>`;

    const w = window.open("", "_blank");
    if (!w) { toast.error("El navegador bloqueó la ventana emergente. Habilitala e intentá de nuevo."); return; }
    w.document.write(html);
    w.document.close();
    w.focus();
  };

  const buildReportHtml = (nodeId, title = "Reporte") => {
    const content = document.getElementById(nodeId);
    if (!content) return null;
    const clone = content.cloneNode(true);
    return `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${title}</title><link rel="preconnect" href="https://fonts.googleapis.com"><link rel="preconnect" href="https://fonts.gstatic.com" crossorigin><link href="https://fonts.googleapis.com/css2?family=Noto+Sans:wght@400;600;700&display=swap" rel="stylesheet"><style>
      @page { margin: 0.8cm; }
      * { box-sizing: border-box; }
      body { font-family: 'Noto Sans', Arial, Helvetica, sans-serif; margin:0; color:#0f172a; background:white; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      table { border-collapse: separate; border-spacing: 0; width: 100%; }
      .no-print { display:none !important; }
      .modern-report { box-shadow:none !important; }
    </style></head><body>${clone.outerHTML}${clone.outerHTML.includes("Sistema AranduJAR-ERP") ? "" : PIE_REPORTE_HTML}</body></html>`;
  };

  const [pdfLoading, setPdfLoading] = useState(false);

  const downloadPdf = async (nodeId, title = "Reporte") => {
    const html = buildReportHtml(nodeId, title);
    if (!html) return;
    setPdfLoading(true);
    try {
      const res = await fetch(`${API}/admin/reports/pdf`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ html, title }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Error al generar el PDF");
        return;
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${title}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Error de conexión al generar el PDF");
    } finally {
      setPdfLoading(false);
    }
  };

  // ── Imprimir Cuentas por cobrar / pagar ──────────────────────────────────────
  const handlePrintCuentas = () => {
    if (!cuentasData) return;
    const rows = cuentasData.rows || [];
    const resumenByCur = cuentasData.resumen || {};
    const esCobrar = reporteSeleccionado === "cuentas_cobrar";
    const reportTitle = esCobrar ? "Cuentas por cobrar" : "Cuentas por pagar";
    const empresaNombre = activeEmpresaPropia?.nombre || activeEmpresaPropia?.razon_social || "ERP";

    // Filtros activos para mostrar en el encabezado
    const filtrosActivos = [];
    if (esCobrar && cuentasEmpresaNombre) filtrosActivos.push({ label: "Cliente", valor: cuentasEmpresaNombre });
    if (!esCobrar && cuentasProveedorNombre) filtrosActivos.push({ label: "Proveedor", valor: cuentasProveedorNombre });
    const vencLabels = { vencidos: "Solo vencidos", "1-30": "Por vencer 1–30 días", "31-60": "Por vencer 31–60 días", "+60": "Por vencer +60 días" };
    if (cuentasVencimiento !== "todos") filtrosActivos.push({ label: "Vencimiento", valor: vencLabels[cuentasVencimiento] || cuentasVencimiento });
    if (cuentasIncluirPagadas) filtrosActivos.push({ label: "Incluye", valor: "Documentos pagados" });
    if (esCobrar && cuentasTipoFacId && cuentasTipoFacNombre) filtrosActivos.push({ label: "Tipo de factura", valor: cuentasTipoFacNombre });
    const hoy = new Date();
    const hoyIsoStr = hoy.toISOString().slice(0, 10);
    const hoyFmt = hoy.toLocaleDateString("es-PY", { day: "2-digit", month: "long", year: "numeric" });
    const currencies = [...new Set(rows.map(r => r.moneda || "PYG"))].sort();

    const fmtNum = n => Number(n || 0).toLocaleString("es-PY");
    const fmtMon = (moneda, n) => {
      const v = Number(n || 0);
      if (moneda === "USD") return `$ ${v.toLocaleString("es-PY", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
      if (moneda === "BRL") return `R$ ${v.toLocaleString("es-PY", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
      return `Gs. ${fmtNum(v)}`;
    };

    const estadoBadge = (estado, vencida) => {
      const ef = vencida ? "vencido" : (estado || "pendiente");
      const map = {
        pagado:   { bg: "#d1fae5", color: "#065f46", border: "#6ee7b7", label: "Pagado" },
        pendiente:{ bg: "#fef3c7", color: "#92400e", border: "#fcd34d", label: "Pendiente" },
        parcial:  { bg: "#dbeafe", color: "#1e40af", border: "#93c5fd", label: "Parcial" },
        vencido:  { bg: "#fee2e2", color: "#991b1b", border: "#fca5a5", label: "Vencido" },
        anulado:  { bg: "#f1f5f9", color: "#64748b", border: "#cbd5e1", label: "Anulado" },
      };
      const s = map[ef] || map.pendiente;
      return `<span style="display:inline-block;padding:2px 9px;border-radius:999px;font-size:8px;font-weight:700;letter-spacing:0.04em;background:${s.bg};color:${s.color};border:1px solid ${s.border}">${s.label}</span>`;
    };

    const currencyLabel = m => m === "USD" ? "Dólares (USD)" : m === "BRL" ? "Reales (BRL)" : m === "PYG" ? "Guaraníes (PYG)" : m;

    let sectionsHtml = "";
    currencies.forEach((moneda, idx) => {
      const rowsMon = rows.filter(r => (r.moneda || "PYG") === moneda);
      const res = resumenByCur[moneda] || {};
      const fmt = n => fmtMon(moneda, n);

      const rowsHtml = rowsMon.map((r, i) => {
        const fvenc = r.fecha_vencimiento;
        const vencida = fvenc && fvenc < hoyIsoStr && r.estado !== "pagado";
        const bg = i % 2 === 0 ? "#ffffff" : "#f8fafc";
        const leftBorder = vencida ? "border-left:3px solid #ef4444;" : "";
        return `
          <tr style="background:${bg};${leftBorder}">
            <td style="padding:5px 8px;font-family:monospace;font-weight:700;color:#1d4ed8;font-size:8px;white-space:nowrap">${r.numero || "—"}</td>
            <td style="padding:5px 8px;color:#374151;font-size:8px;white-space:nowrap">${r.fecha_emision || "—"}</td>
            <td style="padding:5px 8px;font-size:8px;white-space:nowrap;${vencida ? "color:#dc2626;font-weight:700" : "color:#374151"}">${fvenc || "—"}</td>
            <td style="padding:5px 8px;color:#1f2937;font-size:8px;max-width:140px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">${r.razon_social || ""}</td>
            <td style="padding:5px 8px;color:#6b7280;font-size:8px;max-width:130px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">${r.descripcion || ""}</td>
            <td style="padding:5px 8px;text-align:right;font-weight:700;color:#111827;font-size:8px;white-space:nowrap">${fmt(r.total_documento)}</td>
            <td style="padding:5px 8px;text-align:right;color:#16a34a;font-size:8px;white-space:nowrap">${fmt(r.monto_pagado)}</td>
            <td style="padding:5px 8px;text-align:right;font-weight:700;font-size:8px;white-space:nowrap;${Number(r.saldo_pendiente) > 0 ? "color:#d97706" : "color:#16a34a"}">${fmt(r.saldo_pendiente)}</td>
            <td style="padding:5px 8px;text-align:center">${estadoBadge(r.estado, vencida)}</td>
          </tr>`;
      }).join("");

      const summaryCards = [
        { label: "Total facturado", val: fmt(res.total_facturado), color: "#1d4ed8", bg: "#eff6ff", border: "#bfdbfe" },
        { label: esCobrar ? "Cobrado" : "Pagado", val: fmt(res.total_cobrado), color: "#16a34a", bg: "#f0fdf4", border: "#86efac" },
        { label: "Pendiente", val: fmt(res.total_pendiente), color: "#d97706", bg: "#fffbeb", border: "#fcd34d" },
        { label: "Vencidos", val: fmt(res.vencidos), color: "#dc2626", bg: "#fef2f2", border: "#fca5a5" },
      ].map(c => `
        <div style="background:${c.bg};border:1px solid ${c.border};border-radius:10px;padding:12px 16px;flex:1;min-width:0">
          <p style="font-size:8px;font-weight:700;text-transform:uppercase;letter-spacing:0.06em;color:#64748b;margin:0 0 5px">${c.label}</p>
          <p style="font-size:15px;font-weight:800;color:${c.color};margin:0;word-break:break-all">${c.val}</p>
        </div>`).join("");

      const vencRows = [
        ["Vencidos (hasta hoy)", res.vencidos, "#dc2626"],
        ["Por vencer (1–30 días)", res.por_vencer_30, "#d97706"],
        ["Por vencer (31–60 días)", res.por_vencer_60, "#2563eb"],
        ["Por vencer (+60 días)", res.mas_60, "#374151"],
      ].map(([label, val, color]) => `
        <tr style="border-bottom:1px solid #e2e8f0">
          <td style="padding:5px 4px;font-size:8px;color:#374151">${label}</td>
          <td style="padding:5px 4px;font-size:9px;font-weight:700;color:${color};text-align:right;white-space:nowrap">${fmt(val || 0)}</td>
        </tr>`).join("");

      const breakStyle = idx > 0 ? 'page-break-before:always;margin-top:20px;' : '';

      sectionsHtml += `
        <div style="${breakStyle}">
          ${currencies.length > 1 ? `
            <div style="margin-bottom:10px">
              <span style="background:${moneda==="USD"?"#d1fae5":moneda==="BRL"?"#fce7f3":"#dbeafe"};color:${moneda==="USD"?"#065f46":moneda==="BRL"?"#9d174d":"#1e40af"};border:1px solid ${moneda==="USD"?"#6ee7b7":moneda==="BRL"?"#f9a8d4":"#93c5fd"};padding:3px 14px;border-radius:999px;font-size:10px;font-weight:700">${currencyLabel(moneda)}</span>
            </div>` : ""}
          <table style="width:100%;border-collapse:collapse;border:1px solid #e2e8f0;font-size:8px;overflow:hidden;border-radius:6px">
            <thead>
              <tr style="background:#1e293b;color:#e2e8f0">
                <th style="padding:7px 8px;text-align:left;font-size:8px;font-weight:700;text-transform:uppercase;letter-spacing:0.05em;white-space:nowrap">Documento</th>
                <th style="padding:7px 8px;text-align:left;font-size:8px;font-weight:700;text-transform:uppercase;white-space:nowrap">Emisión</th>
                <th style="padding:7px 8px;text-align:left;font-size:8px;font-weight:700;text-transform:uppercase;white-space:nowrap">Vencimiento</th>
                <th style="padding:7px 8px;text-align:left;font-size:8px;font-weight:700;text-transform:uppercase">${esCobrar ? "Cliente" : "Proveedor"}</th>
                <th style="padding:7px 8px;text-align:left;font-size:8px;font-weight:700;text-transform:uppercase">Descripción</th>
                <th style="padding:7px 8px;text-align:right;font-size:8px;font-weight:700;text-transform:uppercase;white-space:nowrap">Total doc.</th>
                <th style="padding:7px 8px;text-align:right;font-size:8px;font-weight:700;text-transform:uppercase;white-space:nowrap">${esCobrar ? "Cobrado" : "Pagado"}</th>
                <th style="padding:7px 8px;text-align:right;font-size:8px;font-weight:700;text-transform:uppercase;white-space:nowrap">Pendiente</th>
                <th style="padding:7px 8px;text-align:center;font-size:8px;font-weight:700;text-transform:uppercase">Estado</th>
              </tr>
            </thead>
            <tbody>${rowsHtml}</tbody>
            <tfoot>
              <tr style="background:#f1f5f9;border-top:2px solid #94a3b8">
                <td colspan="5" style="padding:7px 8px;font-size:8px;font-weight:700;text-transform:uppercase;color:#475569">TOTALES ${currencyLabel(moneda)} — ${res.n_documentos || 0} doc.</td>
                <td style="padding:7px 8px;text-align:right;font-weight:800;color:#111827;font-size:9px;white-space:nowrap">${fmt(res.total_facturado)}</td>
                <td style="padding:7px 8px;text-align:right;font-weight:800;color:#16a34a;font-size:9px;white-space:nowrap">${fmt(res.total_cobrado)}</td>
                <td style="padding:7px 8px;text-align:right;font-weight:800;color:#d97706;font-size:9px;white-space:nowrap">${fmt(res.total_pendiente)}</td>
                <td></td>
              </tr>
            </tfoot>
          </table>

          <div style="display:flex;gap:12px;margin-top:14px;align-items:stretch">
            <div style="display:flex;gap:10px;flex:3;align-items:stretch">${summaryCards}</div>
            <div style="flex:1;background:#f8fafc;border:1px solid #e2e8f0;border-radius:10px;padding:12px 14px;min-width:200px">
              <p style="font-size:8px;font-weight:700;text-transform:uppercase;letter-spacing:0.06em;color:#64748b;margin:0 0 8px">Por vencimiento</p>
              <table style="width:100%;border-collapse:collapse">${vencRows}</table>
            </div>
          </div>
        </div>`;
    });

    const html = `<!DOCTYPE html>
<html lang="es">
<head>
  <meta charset="utf-8">
  <title>${reportTitle}</title>
  <style>
    @page { margin: 1cm; size: A4 landscape; }
    * { box-sizing: border-box; }
    body { font-family: Arial, Helvetica, sans-serif; margin: 0; padding: 0; background: white; color: #111827;
           -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    table { border-collapse: collapse; }
    thead { display: table-header-group; }
    tbody tr { break-inside: avoid; }
    @media screen { body { padding: 24px; max-width: 1280px; margin: 0 auto; } }
  </style>
</head>
<body>
  <div style="display:flex;align-items:flex-start;justify-content:space-between;border-bottom:3px solid #0f172a;padding-bottom:12px;margin-bottom:18px">
    <div>
      <div style="font-size:17px;font-weight:800;color:#0f172a;letter-spacing:-0.02em">${empresaNombre}</div>
      <div style="font-size:13px;font-weight:700;color:#1d4ed8;margin-top:3px">${reportTitle}</div>
      <div style="font-size:8px;color:#6b7280;margin-top:3px">Período: ${cuentasDesde} al ${cuentasHasta}</div>
      ${filtrosActivos.length > 0 ? `
      <div style="display:flex;flex-wrap:wrap;gap:6px;margin-top:7px">
        ${filtrosActivos.map(f => `
          <span style="display:inline-flex;align-items:center;gap:4px;background:#f1f5f9;border:1px solid #cbd5e1;border-radius:999px;padding:2px 10px;font-size:8px;color:#374151">
            <span style="font-weight:700;color:#64748b;text-transform:uppercase;letter-spacing:0.05em;font-size:7px">${f.label}:</span>
            <span style="font-weight:600">${f.valor}</span>
          </span>`).join("")}
      </div>` : ""}
    </div>
    <div style="text-align:right">
      <div style="font-size:8px;color:#6b7280">Generado el ${hoyFmt}</div>
      <div style="font-size:8px;color:#6b7280;margin-top:2px">${rows.length} documento${rows.length !== 1 ? "s" : ""} · ${currencies.join(" + ")}</div>
    </div>
  </div>
  ${sectionsHtml}
  ${PIE_REPORTE_HTML}
  <script>window.onload = function(){ setTimeout(function(){ window.print(); window.close(); }, 400); }<\/script>
</body>
</html>`;

    const printWin = window.open("", "_blank");
    if (!printWin) { toast.error("El navegador bloqueó la ventana emergente."); return; }
    printWin.document.write(html);
    printWin.document.close();
    printWin.focus();
  };

  const fetchAll = async () => {
    setLoading(true);
    try {
      const logo = activeEmpresaPropia?.slug || "";
      const buildUrl = (path, entries = {}) => {
        const q = new URLSearchParams();
        if (logo) q.set("logo_tipo", logo);
        Object.entries(entries).forEach(([key, val]) => { if (val) q.set(key, val); });
        const qs = q.toString();
        return `${API}${path}${qs ? `?${qs}` : ""}`;
      };
      const alertFetches = hasPermission?.("alertas.ver") ? [
        fetch(buildUrl("/admin/alertas/sync-cumpleanos"), { method: "POST", headers: { Authorization: `Bearer ${token}` } }),
        fetch(buildUrl("/admin/alertas/cumpleanos/config"), { headers: { Authorization: `Bearer ${token}` } }),
      ] : [];
      const [empRes, alertRes, proxRes, actRes, ...alertExtra] = await Promise.all([
        fetch(buildUrl("/admin/entidades", { es_cliente: "true" }), { headers: { Authorization: `Bearer ${token}` } }),
        fetch(buildUrl("/admin/alertas", { empresa_id: empresaFromUrl }), { headers: { Authorization: `Bearer ${token}` } }),
        fetch(buildUrl("/admin/alertas/proximas"), { headers: { Authorization: `Bearer ${token}` } }),
        fetch(buildUrl("/admin/activos"), { headers: { Authorization: `Bearer ${token}` } }),
        ...alertFetches,
      ]);
      if (empRes.ok) setEmpresas(await empRes.json());
      if (alertRes.ok) setAlertas(await alertRes.json());
      if (proxRes.ok) setAlertasProximas(await proxRes.json());
      if (actRes.ok) setActivos(await actRes.json());
      if (alertExtra[1]?.ok) setCumpleanosConfig(await alertExtra[1].json());
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchAll(); }, [activeEmpresaPropia?.slug]); // eslint-disable-line

  // Cerrar multi-select de cuentas al hacer click fuera del contenedor
  useEffect(() => {
    const handler = (e) => {
      if (cuentasPlanRef.current && !cuentasPlanRef.current.contains(e.target)) {
        setCuentasPlanOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Cargar plan de cuentas y proveedores cuando se selecciona cuentas_cobrar/pagar/estado_cuenta
  // Cargar playeros cuando se selecciona el reporte de volumetría
  useEffect(() => {
    if (reporteSeleccionado !== "reporte_volumetria_bombas") return;
    setBombasPlayero("");
    setBombasPlayeros([]);
    const slug = activeEmpresaPropia?.slug || "";
    const qs = slug ? `?logo_tipo=${slug}` : "";
    fetch(`${API}/admin/lectura-bombas/playeros${qs}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : [])
      .then(d => setBombasPlayeros(Array.isArray(d) ? d : []))
      .catch(() => {});
  }, [reporteSeleccionado, activeEmpresaPropia?.slug]); // eslint-disable-line

  // Cargar clientes y obras (tipos de factura) cuando se selecciona el Balancete - Obra
  useEffect(() => {
    if (reporteSeleccionado !== "balancete_obra") return;
    const slug = activeEmpresaPropia?.slug || "";
    // Reset defensivo: si cambia la empresa activa, cualquier obra/cliente que
    // hubiera quedado seleccionado de la empresa anterior queda descartado —
    // evita mandar al backend un tipo_factura_id que pertenece a otra empresa
    // (eso hacía que el reporte volviera "Obras: 0" en silencio, sin avisar
    // que la obra elegida no es de la empresa activa).
    setBalObraTipoFacturaIds([]);
    setBalObraClienteId("");
    if (!slug) {
      // Sin empresa activa resuelta todavía no listamos obras — listarlas sin
      // logo_tipo mostraría las de TODAS las empresas y permitiría elegir una
      // que no es de la empresa realmente activa al generar el reporte.
      setClientesBalObra([]);
      setObrasBalObra([]);
      return;
    }
    const qs = `?logo_tipo=${slug}`;
    fetch(`${API}/admin/entidades${qs}&es_cliente=true`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : [])
      .then(d => setClientesBalObra(Array.isArray(d) ? d : []))
      .catch(() => {});
    fetch(`${API}/admin/tipos-factura${qs}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : [])
      .then(d => setObrasBalObra(Array.isArray(d) ? d : []))
      .catch(() => {});
  }, [reporteSeleccionado, activeEmpresaPropia?.slug]); // eslint-disable-line

  // Cargar loteamientos cuando se selecciona el reporte de Precificación por manzana
  useEffect(() => {
    if (reporteSeleccionado !== "loteadora_precificacion") return;
    fetch(`${API}/admin/loteadora/loteamientos`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : [])
      .then(d => setLoteamientosLoteadora(Array.isArray(d) ? d : []))
      .catch(() => {});
  }, [reporteSeleccionado]); // eslint-disable-line

  // Cargar vendedores cuando se selecciona el reporte de Comisiones de Vendedores
  useEffect(() => {
    if (reporteSeleccionado !== "comisiones_vendedores") return;
    const qs = activeEmpresaPropia?.slug ? `?logo_tipo=${activeEmpresaPropia.slug}` : "";
    // Usuarios + vendedores del Catastro (rol Vendedor) + quienes ya figuran en ventas
    fetch(`${API}/admin/reportes/comisiones-vendedores/opciones${qs}`, { headers: { Authorization: `Bearer ${token}` }, cache: "no-store" })
      .then(r => r.ok ? r.json() : [])
      .then(d => setComisionVendedoresOpciones(Array.isArray(d) ? d : []))
      .catch(() => setComisionVendedoresOpciones([]));
  }, [reporteSeleccionado, activeEmpresaPropia?.slug]); // eslint-disable-line

  // Cargar categorías cuando se selecciona el reporte de Gastos por Categoría
  useEffect(() => {
    if (reporteSeleccionado !== "gastos_categoria") return;
    const qs = activeEmpresaPropia?.slug ? `?logo_tipo=${activeEmpresaPropia.slug}` : "";
    fetch(`${API}/admin/categorias-gastos${qs}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : [])
      .then(d => setGastosCategoriaOpciones(Array.isArray(d) ? d : []))
      .catch(() => {});
  }, [reporteSeleccionado, activeEmpresaPropia?.slug]); // eslint-disable-line

  // Proyectos de Constructora para los reportes del módulo
  useEffect(() => {
    if (!String(reporteSeleccionado || "").startsWith("constructora_")) return;
    const qs = activeEmpresaPropia?.slug ? `?logo_tipo=${activeEmpresaPropia.slug}` : "";
    fetch(`${API}/admin/constructora/proyectos${qs}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : [])
      .then(d => setConstructoraProyectos(Array.isArray(d) ? d : []))
      .catch(() => {});
  }, [reporteSeleccionado, activeEmpresaPropia?.slug]); // eslint-disable-line

  // Cargar clientes y choferes cuando se selecciona el reporte de Transporte
  useEffect(() => {
    if (reporteSeleccionado !== "transporte_chofer" && reporteSeleccionado !== "transporte_cliente") return;
    const qs = activeEmpresaPropia?.slug ? `?logo_tipo=${activeEmpresaPropia.slug}` : "?";
    fetch(`${API}/admin/entidades${qs}&es_cliente=true`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : [])
      .then(d => setTransporteClientesOpciones(Array.isArray(d) ? d : []))
      .catch(() => {});
    fetch(`${API}/admin/entidades${qs}&es_chofer=true`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : [])
      .then(d => setTransporteChoferesOpciones(Array.isArray(d) ? d : []))
      .catch(() => {});
  }, [reporteSeleccionado, activeEmpresaPropia?.slug]); // eslint-disable-line

  useEffect(() => {
    if (reporteSeleccionado !== "prestamos") return;
    setPrestReporteEntidadId("");
    setPrestReporteEntidadNombre("");
    setPrestReporteEntidadBusq("");
    setPrestReporteEntidades([]);
    const slug = activeEmpresaPropia?.slug || "";
    const params = new URLSearchParams();
    if (slug) params.set("logo_tipo", slug);
    if (prestReporteTipo === "funcionario") params.set("es_empleado", "true");
    if (prestReporteTipo === "proveedor") {
      params.set("es_proveedor", "true");
      params.set("es_entidad_bancaria", "false");
    }
    if (prestReporteTipo === "bancario") {
      params.set("es_proveedor", "true");
      params.set("es_entidad_bancaria", "true");
    }
    fetch(`${API}/admin/entidades?${params.toString()}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : [])
      .then(d => setPrestReporteEntidades(Array.isArray(d) ? d : []))
      .catch(() => {});
  }, [reporteSeleccionado, prestReporteTipo, activeEmpresaPropia?.slug]); // eslint-disable-line

  // y resetear selecciones al cambiar de reporte
  useEffect(() => {
    if (!["cuentas_cobrar", "cuentas_pagar", "estado_cuenta"].includes(reporteSeleccionado)) return;
    // Reset filtros de búsqueda al cambiar de reporte
    setCuentasEmpresaId(""); setCuentasEmpresaNombre(""); setCuentasEmpresaBusq("");
    setCuentasProveedorId(""); setCuentasProveedorNombre(""); setCuentasProveedorBusq("");
    setCuentasPlanIds([]); setCuentasData(null);
    setCuentasTipoFacId(""); setCuentasTipoFacNombre("");
    setEcData(null);
    const slug = activeEmpresaPropia?.slug || "";
    const qs = slug ? `?logo_tipo=${slug}` : "";
    if (reporteSeleccionado !== "estado_cuenta") {
      fetch(`${API}/admin/plan-cuentas${qs}`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => r.ok ? r.json() : [])
        .then(d => setCuentasPlanCuentas(Array.isArray(d) ? d : (d.cuentas || [])))
        .catch(() => {});
    }
    if (reporteSeleccionado === "cuentas_cobrar" && hasModule?.("tipos_factura")) {
      fetch(`${API}/admin/tipos-factura${qs}`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => r.ok ? r.json() : [])
        .then(d => setTiposFacList(Array.isArray(d) ? d : []))
        .catch(() => {});
    } else {
      setTiposFacList([]);
    }
    if (reporteSeleccionado === "cuentas_cobrar" || reporteSeleccionado === "estado_cuenta") {
      fetch(`${API}/admin/reportes/entidades-cobrables${qs}`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => r.ok ? r.json() : { proveedores: [] })
        .then(d => setCuentasProveedoresCobrables(Array.isArray(d?.proveedores) ? d.proveedores : []))
        .catch(() => setCuentasProveedoresCobrables([]));
    } else {
      setCuentasProveedoresCobrables([]);
    }
    if (reporteSeleccionado === "cuentas_pagar" || reporteSeleccionado === "estado_cuenta") {
      const provQs = slug ? `?es_proveedor=true&logo_tipo=${slug}` : `?es_proveedor=true`;
      fetch(`${API}/admin/entidades${provQs}`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => r.ok ? r.json() : [])
        .then(d => setCuentasProveedores(Array.isArray(d) ? d : []))
        .catch(() => {});
    }
  }, [reporteSeleccionado, activeEmpresaPropia?.slug]); // eslint-disable-line

  // Proveedores para el filtro del reporte Productos y stock
  useEffect(() => {
    if (reporteSeleccionado !== "productos_stock") return;
    const slug = activeEmpresaPropia?.slug || "";
    const pq = slug ? `?es_proveedor=true&logo_tipo=${slug}` : `?es_proveedor=true`;
    fetch(`${API}/admin/entidades${pq}`, { headers: { Authorization: `Bearer ${token}` }, cache: "no-store" })
      .then(r => r.ok ? r.json() : [])
      .then(d => setStockProveedores((Array.isArray(d) ? d : []).sort((a, b) => (a.nombre || "").localeCompare(b.nombre || ""))))
      .catch(() => setStockProveedores([]));
  }, [reporteSeleccionado, activeEmpresaPropia?.slug]); // eslint-disable-line

  const buscarCuentas = async () => {
    if (!reporteSeleccionado) return;
    setCuentasLoading(true);
    setCuentasData(null);
    try {
      const endpoint = reporteSeleccionado === "cuentas_cobrar"
        ? "/admin/reportes/cuentas-cobrar"
        : "/admin/reportes/cuentas-pagar";
      const params = {
        desde: cuentasDesde || undefined,
        hasta: cuentasHasta || undefined,
        vencimiento: cuentasVencimiento !== "todos" ? cuentasVencimiento : undefined,
        incluir_pagadas: cuentasIncluirPagadas ? "true" : undefined,
      };
      if (reporteSeleccionado === "cuentas_cobrar" && cuentasEmpresaId) {
        params.cliente_id = cuentasEmpresaId;
        params.empresa_id = cuentasEmpresaId; // compatibilidad con datos/reportes legacy
      }
      if (cuentasProveedorId) params.proveedor_id = cuentasProveedorId;
      if (cuentasPlanIds.length > 0) params.plan_cuenta_ids = cuentasPlanIds.join(",");
      if (reporteSeleccionado === "cuentas_cobrar" && cuentasTipoFacId) params.tipo_factura_id = cuentasTipoFacId;
      const data = await fetchJson(endpoint, params);
      setCuentasData(data);
    } catch (e) { toast.error(e.message || "Error al buscar"); }
    finally { setCuentasLoading(false); }
  };

  const buscarEstadoCuenta = async () => {
    setEcLoading(true);
    setEcData(null);
    try {
      const slug = activeEmpresaPropia?.slug || "";
      const params = {
        tipo: ecTipo,
        entidad_id: (ecEntidadId && ecEntidadId !== EC_TODOS) ? ecEntidadId : undefined,
        incluir_pagadas: ecIncluirPagadas ? "true" : undefined,
        logo_tipo: slug || undefined,
      };
      const data = await fetchJson("/admin/reportes/estado-cuenta", params);
      setEcData(data);
    } catch (e) { toast.error(e.message || "Error al cargar estado de cuenta"); }
    finally { setEcLoading(false); }
  };

  const handlePrintEC = () => {
    if (!ecData) return;
    // Nota: este reporte agrupa por (entidad, cuenta contable) — ver ecData.cuentas,
    // que es la misma estructura que alimenta la tabla en pantalla. Antes esta función
    // leía ecData.rows (una forma vieja que el backend ya no devuelve), así que el
    // PDF salía siempre vacío sin importar los datos — corregido para usar "cuentas".
    const cuentas = ecData.cuentas || [];
    const totales = ecData.totales || {};
    const esCobrar = ecData.tipo === "cobrar";
    const empresaNombre = activeEmpresaPropia?.nombre || activeEmpresaPropia?.razon_social || "ERP";
    const ecNombreImprimir = ecData.entidad_nombre || ecEntidadNombre || "Todos";
    const hoy = new Date();
    const hoyFmt = hoy.toLocaleDateString("es-PY", { day: "2-digit", month: "long", year: "numeric" });
    const currencies = [...new Set(cuentas.map(c => c.moneda || "PYG"))].sort();
    const saldoLabelPrint = esCobrar ? "Saldo Deudor" : "Saldo Acreedor";

    const fmtNum = n => Number(n || 0).toLocaleString("es-PY");
    const fmtMon = (moneda, n) => {
      const v = Number(n || 0);
      if (moneda === "USD") return `$ ${v.toLocaleString("es-PY", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
      if (moneda === "BRL") return `R$ ${v.toLocaleString("es-PY", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
      return `Gs. ${fmtNum(v)}`;
    };

    let sectionsHtml = "";
    currencies.forEach(moneda => {
      const cuentasMon = cuentas.filter(c => (c.moneda || "PYG") === moneda);
      const tot = totales[moneda] || {};
      const fmt = n => fmtMon(moneda, n);
      const rowsHtml = cuentasMon.map((c, i) => {
        const bg = i % 2 === 0 ? "#ffffff" : "#f8fafc";
        const saldoCol = Number(c.saldo || 0);
        return `
          <tr style="background:${bg}">
            <td style="padding:5px 8px;color:#1f2937;font-size:8px;font-weight:600;max-width:200px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">${c.entidad_nombre || "-"}</td>
            <td style="padding:5px 8px;color:#6b7280;font-size:8px;max-width:200px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">${c.cuenta_nombre || ""}</td>
            <td style="padding:5px 8px;text-align:right;font-weight:700;color:#111827;font-size:8px;white-space:nowrap">${fmt(c.total_valor)}</td>
            <td style="padding:5px 8px;text-align:right;font-weight:700;font-size:8px;white-space:nowrap;${saldoCol > 0 ? "color:#d97706" : "color:#16a34a"}">${fmt(saldoCol)}</td>
          </tr>`;
      }).join("");
      sectionsHtml += `
        <table style="width:100%;border-collapse:collapse;border:1px solid #e2e8f0;font-size:8px;border-radius:6px;overflow:hidden;margin-bottom:16px">
          <thead>
            <tr style="background:#f1f5f9">
              <th style="padding:6px 8px;text-align:left;font-size:8px;text-transform:uppercase;letter-spacing:0.06em;color:#64748b;font-weight:700">${esCobrar ? "Cliente" : "Proveedor"}</th>
              <th style="padding:6px 8px;text-align:left;font-size:8px;text-transform:uppercase;letter-spacing:0.06em;color:#64748b;font-weight:700">Cuenta</th>
              <th style="padding:6px 8px;text-align:right;font-size:8px;text-transform:uppercase;letter-spacing:0.06em;color:#64748b;font-weight:700">Valor Total</th>
              <th style="padding:6px 8px;text-align:right;font-size:8px;text-transform:uppercase;letter-spacing:0.06em;color:#64748b;font-weight:700">${saldoLabelPrint}</th>
            </tr>
          </thead>
          <tbody>${rowsHtml}</tbody>
          <tfoot>
            <tr style="background:#f8fafc;border-top:2px solid #e2e8f0">
              <td colspan="2" style="padding:7px 8px;font-size:9px;font-weight:700;text-transform:uppercase;color:#374151;letter-spacing:0.06em">TOTAL GENERAL (${moneda})</td>
              <td style="padding:7px 8px;text-align:right;font-weight:800;color:#111827;font-size:9px">${fmt(tot.total_valor)}</td>
              <td style="padding:7px 8px;text-align:right;font-weight:800;color:#d97706;font-size:9px">${fmt(tot.saldo)}</td>
            </tr>
          </tfoot>
        </table>`;
    });

    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Estado de Cuenta — ${ecNombreImprimir}</title>
      <style>@page{size:landscape;margin:1.2cm 1cm}body{font-family:Arial,Helvetica,sans-serif;color:#1f2937;background:#fff}*{box-sizing:border-box}</style>
      </head><body>
      <div style="display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:18px;padding-bottom:12px;border-bottom:2px solid #e2e8f0">
        <div>
          <h1 style="font-size:16px;font-weight:800;color:#0f172a;margin:0 0 2px">Estado de Cuenta</h1>
          <p style="font-size:11px;color:#64748b;margin:0">${empresaNombre}</p>
        </div>
        <div style="text-align:right">
          <p style="font-size:11px;font-weight:700;color:#1d4ed8;margin:0 0 2px">${ecNombreImprimir}</p>
          <p style="font-size:9px;color:#64748b;margin:0">${esCobrar ? "Cuentas por cobrar" : "Cuentas por pagar"} · Generado: ${hoyFmt}</p>
        </div>
      </div>
      ${sectionsHtml}
      ${PIE_REPORTE_HTML}
      </body></html>`;

    const w = window.open("", "_blank");
    if (!w) { toast.error("El navegador bloqueó la ventana emergente. Habilitala e intentá de nuevo."); return; }
    w.document.write(html);
    w.document.close();
    setTimeout(() => { w.focus(); w.print(); }, 600);
  };

  const toggleEmpresa = (id) => {
    setSelectedEmpresas(prev => prev.includes(id) ? prev.filter(e => e !== id) : [...prev, id]);
  };

  const fetchReportData = async (withCreds) => {
    if (withCreds && !isAdmin) { toast.error("Solo administradores pueden ver credenciales"); return; }
    try {
      const params = new URLSearchParams();
      if (selectedEmpresas.length > 0) params.set("empresa_ids", selectedEmpresas.join(","));
      if (activeEmpresaPropia?.slug) params.set("logo_tipo", activeEmpresaPropia.slug);
      if (withCreds) params.set("incluir_credenciales", "true");
      params.set("ordenar_por", sortBy);
      const res = await fetch(`${API}/admin/reportes/inventario?${params.toString()}`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) {
        const data = await res.json();
        // El backend siempre devuelve { activos, cuentas_asociadas, detalle_cuentas }
        // Las contraseñas están vacías cuando incluir_credenciales=false
        if (Array.isArray(data)) {
          // compatibilidad con versiones viejas que devuelvan lista
          setPreviewData(data);
          setCuentasAsociadas([]);
          setDetalleCuentas([]);
        } else {
          setPreviewData(data.activos || []);
          setCuentasAsociadas(data.cuentas_asociadas || []);
          setDetalleCuentas(data.detalle_cuentas || []);
        }
        setPreviewWithCreds(withCreds);
        setShowPreview(true);
      } else { const e = await res.json(); toast.error(e.detail || "Error"); }
    } catch { toast.error("Error"); }
  };

  useEffect(() => {
    if (reporteSeleccionado !== "costo_hectarea") return;
    const q = activeEmpresaPropia?.slug ? `?logo_tipo=${activeEmpresaPropia.slug}` : "";
    fetch(`${API}/admin/recetario/chacras${q}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => (r.ok ? r.json() : []))
      .then(l => setCostoHaChacras([...new Set((l || []).map(c => (c.nombre || "").trim()).filter(Boolean))].sort((a, b) => a.localeCompare(b, "es"))))
      .catch(() => {});
    // eslint-disable-next-line
  }, [reporteSeleccionado, activeEmpresaPropia?.slug]);

  const fetchJson = async (path, params = {}) => {
    const q = new URLSearchParams();
    if (activeEmpresaPropia?.slug) q.set("logo_tipo", activeEmpresaPropia.slug);
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== "") q.set(k, v);
    });
    const res = await fetch(`${API}${path}${q.toString() ? `?${q.toString()}` : ""}`, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) {
      const e = await res.json().catch(() => ({}));
      throw new Error(e.detail || "No se pudo generar el reporte");
    }
    return res.json();
  };

  const rowsOf = (items, columns) => (items || []).map(item => columns.map(col => {
    const val = typeof col.value === "function" ? col.value(item) : item[col.value];
    return val === undefined || val === null || val === "" ? "-" : val;
  }));

  const montoReportePYG = (item, fields = ["monto", "monto_total", "monto_pagado", "total_pagado"]) => {
    const raw = fields.map(f => item?.[f]).find(v => v !== undefined && v !== null && v !== "");
    const monto = Number(raw || 0);
    if ((item?.moneda || "PYG") === "USD") {
      return Number(item?.monto_gs || item?.monto_pyg || 0) || monto * Number(item?.tipo_cambio || 0);
    }
    return monto;
  };

  const sectionTotal = (items, amountFn) => (items || []).reduce((sum, item) => sum + Number(amountFn(item) || 0), 0);
  const fmtCuentaMonto = (n, cuenta) => (cuenta?.moneda === "USD" ? fmtUSD(n) : fmtPYG(n));
  const fmtMonedaReporte = (n, moneda = "PYG") => {
    if (moneda === "USD") return fmtUSD(n);
    if (moneda === "BRL") return fmtBRL(n);
    return fmtPYG(n);
  };
  const fmtMixto = (pyg = 0, usd = 0) => {
    const parts = [];
    if (Number(pyg || 0) > 0) parts.push(fmtPYG(pyg));
    if (Number(usd || 0) > 0) parts.push(fmtUSD(usd));
    return parts.length ? parts.join(" / ") : "-";
  };
  const montoCuenta = (item, fields) => {
    const raw = fields.map(f => item?.[f]).find(v => v !== undefined && v !== null && v !== "");
    return Number(raw || 0);
  };

  const normalizarMovimientoCuenta = (item, fields = ["monto", "monto_total", "monto_pagado"]) => {
    const raw = montoCuenta(item, fields);
    const tc = Number(item?.tipo_cambio || item?.tipo_cambio_real || 0);
    const monedaDoc = item?.moneda || "PYG";
    const cuentaMoneda = item?.cuenta_moneda || item?.moneda_cuenta || monedaDoc;
    const montoConvertido = monedaDoc === "USD" && cuentaMoneda === "PYG"
      ? Number(item?.monto_gs || item?.monto_pyg || 0) || raw * (tc || 1)
      : raw;
    return {
      ...item,
      moneda: cuentaMoneda,
      cuenta_moneda: cuentaMoneda,
      monto_movimiento: montoConvertido,
    };
  };

  const movimientosDeCompras = (compras = []) => (
    (compras || []).flatMap(compra => {
      if ((compra.tipo_pago || "contado") === "contado") {
        return [normalizarMovimientoCuenta(compra, ["monto_total", "monto"])];
      }
      const pagos = compra.pagos || [];
      return pagos.map(pago => normalizarMovimientoCuenta({
        ...compra,
        ...pago,
        fecha: pago.fecha_pago || pago.fecha || compra.fecha,
        proveedor_nombre: compra.proveedor_nombre,
        factura_numero: compra.factura_numero || compra.numero_factura,
        tipo_pago: "credito",
        estado_pago: compra.estado_pago,
        moneda: pago.moneda || compra.moneda,
        monto: pago.monto_pagado ?? pago.monto,
      }, ["monto", "monto_pagado"]));
    })
  );

  const cuentaTexto = (item) => item?.cuenta_nombre || item?.banco_nombre || item?.cuenta_id || "-";
  const cuentaIdDeItem = (item) => item?.cuenta_id || item?.banco_id || null;
  const monedaDeItem = (item) => item?.cuenta_moneda || item?.moneda_cuenta || item?.moneda || "PYG";
  const cuentasFactura = (fac) => {
    const names = [
      fac?.cuenta_nombre,
      ...(fac?.pagos || []).map(p => p.cuenta_nombre || p.cuenta_id),
    ].filter(Boolean);
    return names.length ? [...new Set(names)].join(", ") : "-";
  };
  const formaFactura = (fac) => {
    const forma = fac?.forma_pago || fac?.tipo_pago;
    if (forma) return forma;
    return fac?.estado === "pendiente" || fac?.estado === "parcial" ? "credito" : "contado";
  };

  const buildCuentaResolver = (cuentas = []) => {
    const byId = new Map(cuentas.map(c => [c.id, c]));
    const defaults = {};
    cuentas.forEach(c => {
      const moneda = c.moneda || "PYG";
      if (!defaults[moneda] || c.es_predeterminada) defaults[moneda] = c;
    });
    return {
      resolve(item = {}) {
        const tagged = cuentaIdDeItem(item);
        const cuenta = tagged ? byId.get(tagged) : null;
        const moneda = monedaDeItem(item);
        if (cuenta && (!moneda || (cuenta.moneda || "PYG") === moneda)) return cuenta;
        return defaults[moneda] || defaults.PYG || cuentas[0] || { id: "sin-cuenta", nombre: "Cuenta predeterminada", moneda };
      },
      include(cuenta) {
        if (reportCurrency === "AMBOS") return true;
        return (cuenta?.moneda || "PYG") === reportCurrency;
      },
    };
  };

  const sectionsPorCuenta = (items, base, resolver, rowBuilder, amountFn) => {
    const grouped = new Map();
    (items || []).forEach(item => {
      const cuenta = resolver.resolve(item);
      if (!resolver.include(cuenta)) return;
      const key = `${cuenta.id || cuenta.nombre || "sin-cuenta"}-${cuenta.moneda || "PYG"}`;
      if (!grouped.has(key)) grouped.set(key, { cuenta, rows: [], items: [] });
      grouped.get(key).rows.push(rowBuilder(item, cuenta));
      grouped.get(key).items.push(item);
    });
    if (grouped.size === 0) {
      return [{
        ...base,
        title: `${base.title} - ${reportCurrency === "USD" ? "Dolares" : reportCurrency === "BRL" ? "Reales" : reportCurrency === "PYG" ? "Guaranies" : "Sin movimientos"}`,
        total: reportCurrency === "USD" ? fmtUSD(0) : reportCurrency === "BRL" ? fmtBRL(0) : fmtPYG(0),
        rows: [],
      }];
    }
    return Array.from(grouped.values()).map(({ cuenta, rows, items }) => ({
      ...base,
      title: `${base.title} - ${cuenta.nombre || cuenta.id || "Cuenta predeterminada"} (${cuenta.moneda || "PYG"})`,
      total: fmtCuentaMonto(sectionTotal(items, amountFn), cuenta),
      rows,
    }));
  };

  const resumenCuentasDesdeItems = (items, resolver, amountFn, title = "Resumen por cuenta") => {
    const grouped = new Map();
    (items || []).forEach(item => {
      const cuenta = resolver.resolve(item);
      if (!resolver.include(cuenta)) return;
      const key = `${cuenta.id || cuenta.nombre || "sin-cuenta"}-${cuenta.moneda || "PYG"}`;
      if (!grouped.has(key)) grouped.set(key, { cuenta, total: 0, count: 0 });
      const current = grouped.get(key);
      current.total += Number(amountFn(item) || 0);
      current.count += 1;
    });
    return {
      title,
      rows: Array.from(grouped.values()).map(({ cuenta, total, count }) => ({
        label: `${cuenta.nombre || cuenta.id || "Cuenta predeterminada"} (${cuenta.moneda || "PYG"})`,
        value: fmtCuentaMonto(total, cuenta),
        note: `${count} mov.`,
      })),
    };
  };

  const resumenSaldosCuentas = (cuentas = [], title = "Saldo actual por cuenta") => ({
    title,
    rows: (cuentas || [])
      .filter(cuenta => reportCurrency === "AMBOS" || (cuenta.moneda || "PYG") === reportCurrency)
      .map(cuenta => ({
        label: `${cuenta.nombre || cuenta.id || "Cuenta"} (${cuenta.moneda || "PYG"})`,
        value: fmtCuentaMonto(cuenta.saldo_actual || 0, cuenta),
        note: cuenta.es_predeterminada ? "Predeterminada" : "",
      })),
  });


  const generarReporte = async (tipo, clienteIdOverride) => {
    setGenericReportLoading(true);
    try {
      let report = null;
      const periodoSub = `Empresa activa: ${activeEmpresaPropia?.nombre || "Todas"} · Generado: ${nowFull}`;
      const cuentasReporte = await fetchJson("/admin/cuentas-bancarias").catch(() => []);
      const cuentaResolver = buildCuentaResolver(cuentasReporte);
      const fetchSaldosCuentas = (hasta) => fetchJson("/admin/cuentas-bancarias/saldos", { hasta }).catch(() => cuentasReporte);
      const currencyLabel = reportCurrency === "USD" ? "Dolares" : reportCurrency === "BRL" ? "Reales" : reportCurrency === "AMBOS" ? "Guaranies, dolares y reales" : "Guaranies";
      const showPYG = reportCurrency === "PYG" || reportCurrency === "AMBOS";
      const showUSD = reportCurrency === "USD" || reportCurrency === "AMBOS";
      const showBRL = reportCurrency === "BRL" || reportCurrency === "AMBOS";
      const selectedCliente = empresas.find(e => e.id === (clienteIdOverride ?? reportClienteId));
      const clienteMatches = (row) => {
        if (!selectedCliente) return false;
        const nombre = (selectedCliente.razon_social || selectedCliente.nombre || "").trim().toLowerCase();
        return row.empresa_id === selectedCliente.id
          || String(row.razon_social || "").trim().toLowerCase() === nombre
          || String(row.empresa_nombre || "").trim().toLowerCase() === nombre
          || (selectedCliente.ruc && String(row.ruc || "").trim() === String(selectedCliente.ruc).trim());
      };
      const cobradoFactura = (f) => {
        const pagos = (f.pagos || []).reduce((s, p) => s + Number(p.monto || p.monto_pagado || 0), 0);
        if (pagos > 0) return pagos;
        if (Number(f.monto_pagado || 0) > 0) return Number(f.monto_pagado || 0);
        return f.estado === "pagada" ? Number(f.monto || 0) : 0;
      };
      if (tipo === "balance_mensual") {
        const [b, saldosCuentas] = await Promise.all([
          fetchJson("/admin/balance", { periodo: reportMonth }),
          fetchSaldosCuentas(endOfMonthDate(reportMonth)),
        ]);
        const summary = [];
        const sections = [];
        if (showPYG) {
          summary.push(
            ["Ingresos Gs", fmtPYG(b.total_ingresos)],
            ["Egresos Gs", fmtPYG(b.total_egresos)],
            ["Balance Gs", fmtPYG(b.balance)],
            ["Saldo acum. Gs", fmtPYG(b.saldo_acumulado)]
          );
          sections.push(
            { group: "Guaranies", title: "Ingresos Gs", columns: ["Concepto", "Cantidad", "Monto"], rows: (b.ingresos_detalle || []).map(v => [v.fuente || "-", v.cantidad || "-", fmtPYG(v.monto_pyg || v.total || 0)]) },
            { group: "Guaranies", title: "Egresos Gs", columns: ["Concepto", "Cantidad", "Monto"], rows: (b.egresos_detalle || []).map(v => [v.fuente || "-", v.cantidad || "-", fmtPYG(v.monto_pyg || v.total || 0)]) }
          );
        }
        if (showUSD) {
          summary.push(
            ["Ingresos USD", fmtUSD(b.total_ingresos_usd)],
            ["Egresos USD", fmtUSD(b.total_egresos_usd)],
            ["Balance USD", fmtUSD(b.balance_usd)],
            ["Saldo acum. USD", fmtUSD(b.saldo_acumulado_usd)]
          );
          sections.push(
            { group: "Dolares", title: "Ingresos USD", columns: ["Concepto", "Cantidad", "Monto"], rows: (b.ingresos_usd_detalle || []).map(v => [v.fuente || "-", v.cantidad || "-", fmtUSD(v.monto_usd || v.total || 0)]) },
            { group: "Dolares", title: "Egresos USD", columns: ["Concepto", "Cantidad", "Monto"], rows: (b.egresos_usd_detalle || []).map(v => [v.fuente || "-", v.cantidad || "-", fmtUSD(v.monto_usd || v.total || 0)]) }
          );
        }
        if (showBRL) {
          summary.push(
            ["Ingresos BRL", fmtBRL(b.total_ingresos_brl)],
            ["Egresos BRL", fmtBRL(b.total_egresos_brl)],
            ["Balance BRL", fmtBRL(b.balance_brl)],
            ["Saldo acum. BRL", fmtBRL(b.saldo_acumulado_brl)]
          );
          sections.push(
            { group: "Reales", title: "Ingresos BRL", columns: ["Concepto", "Cantidad", "Monto"], rows: (b.ingresos_brl_detalle || []).map(v => [v.fuente || "-", v.cantidad || "-", fmtBRL(v.monto_brl || v.total || 0)]) },
            { group: "Reales", title: "Egresos BRL", columns: ["Concepto", "Cantidad", "Monto"], rows: (b.egresos_brl_detalle || []).map(v => [v.fuente || "-", v.cantidad || "-", fmtBRL(v.monto_brl || v.total || 0)]) }
          );
        }
        report = {
          title: `Balance mensual - ${mesLabel(reportMonth)} - ${currencyLabel}`,
          subtitle: periodoSub,
          summary,
          accountSummary: resumenSaldosCuentas(saldosCuentas, `Saldo por banco/cuenta al cierre de ${mesLabel(reportMonth)}`),
          sections,
        };
      } else if (tipo === "balance_anual") {
        const [b, mesesBalance, saldosCuentas] = await Promise.all([
          fetchJson("/admin/balance/anual", { anio: reportYear }),
          (showUSD || showBRL)
            ? Promise.all(Array.from({ length: 12 }, (_, idx) => fetchJson("/admin/balance", { periodo: `${reportYear}-${String(idx + 1).padStart(2, "0")}` }).catch(() => null)))
            : Promise.resolve([]),
          fetchSaldosCuentas(`${reportYear}-12-31`),
        ]);
        const usdRows = (mesesBalance || []).filter(Boolean).map((m, idx) => ({
          periodo: `${reportYear}-${String(idx + 1).padStart(2, "0")}`,
          ingresos: Number(m.total_ingresos_usd || 0),
          egresos: Number(m.total_egresos_usd || 0),
          balance: Number(m.balance_usd || 0),
          acumulado: Number(m.saldo_acumulado_usd || 0),
        }));
        const brlRows = (mesesBalance || []).filter(Boolean).map((m, idx) => ({
          periodo: `${reportYear}-${String(idx + 1).padStart(2, "0")}`,
          ingresos: Number(m.total_ingresos_brl || 0),
          egresos: Number(m.total_egresos_brl || 0),
          balance: Number(m.balance_brl || 0),
          acumulado: Number(m.saldo_acumulado_brl || 0),
        }));
        const summary = [];
        const sections = [];
        if (showPYG) {
          summary.push(["Ingresos Gs", fmtPYG(b.total_anual_ingresos)], ["Egresos Gs", fmtPYG(b.total_anual_egresos)], ["Balance Gs", fmtPYG(b.balance_anual)], ["Saldo inicial Gs", fmtPYG(b.superavit_inicial)]);
          sections.push({ group: "Guaranies", title: "Meses Gs", columns: ["Mes", "Ingresos", "Egresos", "Balance", "Acumulado"], rows: (b.meses || []).map(m => [m.periodo, fmtPYG(m.total_ingresos), fmtPYG(m.total_egresos), fmtPYG(m.balance), fmtPYG(m.acumulado)]) });
        }
        if (showUSD) {
          summary.push(["Ingresos USD", fmtUSD(sectionTotal(usdRows, r => r.ingresos))], ["Egresos USD", fmtUSD(sectionTotal(usdRows, r => r.egresos))], ["Balance USD", fmtUSD(sectionTotal(usdRows, r => r.balance))], ["Saldo final USD", fmtUSD(usdRows.at(-1)?.acumulado || 0)]);
          sections.push({ group: "Dolares", title: "Meses USD", columns: ["Mes", "Ingresos", "Egresos", "Balance", "Acumulado"], rows: usdRows.map(m => [m.periodo, fmtUSD(m.ingresos), fmtUSD(m.egresos), fmtUSD(m.balance), fmtUSD(m.acumulado)]) });
        }
        if (showBRL) {
          summary.push(["Ingresos BRL", fmtBRL(sectionTotal(brlRows, r => r.ingresos))], ["Egresos BRL", fmtBRL(sectionTotal(brlRows, r => r.egresos))], ["Balance BRL", fmtBRL(sectionTotal(brlRows, r => r.balance))], ["Saldo final BRL", fmtBRL(brlRows.at(-1)?.acumulado || 0)]);
          sections.push({ group: "Reales", title: "Meses BRL", columns: ["Mes", "Ingresos", "Egresos", "Balance", "Acumulado"], rows: brlRows.map(m => [m.periodo, fmtBRL(m.ingresos), fmtBRL(m.egresos), fmtBRL(m.balance), fmtBRL(m.acumulado)]) });
        }
        report = {
          title: `Balance anual - ${reportYear} - ${currencyLabel}`,
          subtitle: periodoSub,
          summary,
          accountSummary: resumenSaldosCuentas(saldosCuentas, `Saldo por banco/cuenta al cierre de ${reportYear}`),
          sections,
        };
      } else if (tipo === "balance_detallado") {
        const [b, facturas, ingresosVarios, recibos, notasCredito, compras, pagos, gastos, sueldos, iva, saldosCuentas] = await Promise.all([
          fetchJson("/admin/balance", { periodo: reportMonth }),
          fetchJson("/admin/facturas", { mes: reportMonth }),
          fetchJson("/admin/ingresos-varios", { mes: reportMonth }).catch(() => []),
          fetchJson("/admin/recibos", { mes: reportMonth }).catch(() => []),
          fetchJson("/admin/notas-credito", { mes: reportMonth }).catch(() => []),
          fetchJson("/admin/compras", { mes: reportMonth }),
          fetchJson("/admin/pagos-proveedores", { mes: reportMonth }),
          fetchJson("/admin/costos-fijos-pagos", { mes: reportMonth }).catch(() => []),
          fetchJson("/admin/empleados/sueldos", { periodo: reportMonth }).catch(() => []),
          fetchJson("/admin/balance/iva", { periodo: reportMonth }).catch(() => ({ pagos_iva_detalle: [] })),
          fetchSaldosCuentas(endOfMonthDate(reportMonth)),
        ]);
        const sueldosPagados = (sueldos || []).filter(s => s.sueldo_registrado);
        const montoSueldo = (row) => montoReportePYG(row.sueldo_registrado || row, ["monto_pagado"]);
        const sueldosCuenta = sueldosPagados.map(s => ({
          ...s,
          cuenta_id: s.sueldo_registrado?.cuenta_id || s.cuenta_id,
          cuenta_nombre: s.sueldo_registrado?.cuenta_nombre || s.cuenta_nombre,
          cuenta_moneda: s.sueldo_registrado?.cuenta_moneda || s.sueldo_registrado?.moneda || s.moneda || "PYG",
        }));
        const comprasMov = movimientosDeCompras(compras);
        const comprasContado = comprasMov.filter(c => (c.tipo_pago || "contado") === "contado");
        const comprasCredito = (compras || []).filter(c => c.tipo_pago === "credito");
        const notasVenta = (notasCredito || []).filter(n => !n.tipo || n.tipo === "venta" || n.origen === "venta");
        const notasCompra = (notasCredito || []).filter(n => n.tipo === "compra" || n.origen === "compra");
        const pagosIva = iva.pagos_iva_detalle || [];
        const pagosProveedoresPagados = (pagos || []).filter(p => p.estado === "pagado" || p.fecha_pago);
        const facturasCuenta = (facturas || []).map(f => {
          const p = (f.pagos || [])[0] || {};
          return { ...f, cuenta_id: p.cuenta_id || f.cuenta_id, cuenta_nombre: p.cuenta_nombre || f.cuenta_nombre, cuenta_moneda: p.cuenta_moneda || f.cuenta_moneda || f.moneda };
        });
        report = {
          title: `Balance detallado - ${mesLabel(reportMonth)} - ${currencyLabel}`,
          subtitle: periodoSub,
          summary: [
            ...(showPYG ? [["Ingresos Gs", fmtPYG(b.total_ingresos)], ["Egresos Gs", fmtPYG(b.total_egresos)], ["Balance Gs", fmtPYG(b.balance)], ["Saldo acum. Gs", fmtPYG(b.saldo_acumulado)]] : []),
            ...(showUSD ? [["Ingresos USD", fmtUSD(b.total_ingresos_usd)], ["Egresos USD", fmtUSD(b.total_egresos_usd)], ["Balance USD", fmtUSD(b.balance_usd)], ["Saldo acum. USD", fmtUSD(b.saldo_acumulado_usd)]] : []),
            ...(showBRL ? [["Ingresos BRL", fmtBRL(b.total_ingresos_brl)], ["Egresos BRL", fmtBRL(b.total_egresos_brl)], ["Balance BRL", fmtBRL(b.balance_brl)], ["Saldo acum. BRL", fmtBRL(b.saldo_acumulado_brl)]] : []),
          ],
          accountSummary: resumenSaldosCuentas(saldosCuentas, `Saldo por banco/cuenta al cierre de ${mesLabel(reportMonth)}`),
          sections: [
            ...sectionsPorCuenta(facturasCuenta, { group: "Ingresos", title: "Facturas cobradas / emitidas", totalLabel: "Total facturas", columns: ["Fecha", "Cliente", "Nro", "Tipo", "Estado", "Monto"] }, cuentaResolver, (r, cuenta) => [r.fecha || "-", r.empresa_nombre || r.razon_social || "-", r.numero || "-", formaFactura(r), r.estado || "-", fmtCuentaMonto(montoCuenta(r, ["monto"]), cuenta)], r => montoCuenta(r, ["monto"])),
            ...sectionsPorCuenta(ingresosVarios, { group: "Ingresos", title: "Ingresos varios sin factura", totalLabel: "Total ingresos varios", columns: ["Fecha", "Cliente", "Categoria", "Concepto", "Monto"] }, cuentaResolver, (r, cuenta) => [r.fecha || "-", r.empresa_nombre || r.razon_social || "-", r.categoria || "-", r.descripcion || r.concepto || "-", fmtCuentaMonto(montoCuenta(r, ["monto"]), cuenta)], r => montoCuenta(r, ["monto"])),
            ...sectionsPorCuenta(recibos, { group: "Ingresos", title: "Recibos / cobros registrados", totalLabel: "Total recibos", columns: ["Fecha", "Cliente", "Factura", "Metodo", "Monto"] }, cuentaResolver, (r, cuenta) => [r.fecha_pago || "-", r.empresa_nombre || r.cliente_nombre || "-", r.factura_numero || r.factura_id || "-", r.metodo_pago || "-", fmtCuentaMonto(montoCuenta(r, ["monto"]), cuenta)], r => montoCuenta(r, ["monto"])),
            { group: "Ingresos", title: "Notas de credito ventas", totalLabel: "Total notas venta", total: fmtPYG(sectionTotal(notasVenta, r => montoReportePYG(r, ["monto"]))), columns: ["Fecha", "Cliente", "Factura", "Motivo", "Monto"], rows: rowsOf(notasVenta, [{value:"fecha"}, {value: r => r.empresa_nombre || r.razon_social}, {value: r => r.factura_numero || r.factura_id}, {value:"motivo"}, {value: r => fmtPYG(montoReportePYG(r, ["monto"]))}]) },
            ...sectionsPorCuenta(comprasContado, { group: "Egresos", title: "Compras contado", totalLabel: "Total contado", columns: ["Fecha", "Proveedor", "Factura", "Estado", "Monto"] }, cuentaResolver, (r, cuenta) => [r.fecha || "-", r.proveedor_nombre || "-", r.factura_numero || r.numero_factura || "-", r.estado_pago || "-", fmtCuentaMonto(r.monto_movimiento, cuenta)], r => r.monto_movimiento),
            { group: "Egresos", title: "Compras credito generadas", totalLabel: "Total credito", total: fmtPYG(sectionTotal(comprasCredito, r => montoReportePYG(r, ["monto_total", "monto"]))), columns: ["Fecha", "Proveedor", "Factura", "Estado", "Monto"], rows: rowsOf(comprasCredito, [{value:"fecha"}, {value:"proveedor_nombre"}, {value:"factura_numero"}, {value:"estado_pago"}, {value: r => fmtPYG(montoReportePYG(r, ["monto_total", "monto"]))}]) },
            ...sectionsPorCuenta(pagosProveedoresPagados.map(p => normalizarMovimientoCuenta(p, ["monto"])), { group: "Egresos", title: "Pagos proveedores de compras credito", totalLabel: "Total pagado", columns: ["Fecha pago", "Proveedor", "Concepto", "Estado", "Monto"] }, cuentaResolver, (r, cuenta) => [r.fecha_pago || "-", r.proveedor_nombre || "-", r.concepto || "-", r.estado || "-", fmtCuentaMonto(r.monto_movimiento, cuenta)], r => r.monto_movimiento),
            ...sectionsPorCuenta(gastos, { group: "Egresos", title: "Gastos pagados", totalLabel: "Total gastos", columns: ["Fecha", "Concepto", "Categoria", "Monto"] }, cuentaResolver, (r, cuenta) => [r.fecha_pago || r.fecha || r.periodo || "-", r.costo_nombre || r.nombre || "-", r.categoria || "-", fmtCuentaMonto(montoCuenta(r, ["monto_pagado", "monto"]), cuenta)], r => montoCuenta(r, ["monto_pagado", "monto"])),
            ...sectionsPorCuenta(sueldosCuenta, { group: "Egresos", title: "Sueldos pagados", totalLabel: "Total sueldos", columns: ["Fecha", "Empleado", "Extras", "Adelantos", "Descuentos", "Total pagado"] }, cuentaResolver, (r, cuenta) => [r.sueldo_registrado?.fecha_pago || r.sueldo_registrado?.periodo || r.periodo || "-", r.empleado_nombre || `${r.nombre || ""} ${r.apellido || ""}`, fmtCuentaMonto(r.sueldo_registrado?.total_extras || 0, cuenta), fmtCuentaMonto(r.sueldo_registrado?.total_adelantos || 0, cuenta), fmtCuentaMonto(Number(r.sueldo_registrado?.descuento_ips || 0) + Number(r.sueldo_registrado?.descuentos_adicionales || 0), cuenta), fmtCuentaMonto(montoCuenta(r.sueldo_registrado || r, ["monto_pagado"]), cuenta)], r => montoCuenta(r.sueldo_registrado || r, ["monto_pagado"])),
            ...sectionsPorCuenta(pagosIva, { group: "Egresos", title: "Pago IVA", totalLabel: "Total IVA pagado", columns: ["Fecha", "Periodo IVA", "Descripcion", "Monto"] }, cuentaResolver, (r, cuenta) => [r.fecha_pago || "-", r.periodo_iva || "-", r.descripcion || "-", fmtCuentaMonto(montoCuenta(r, ["monto"]), cuenta)], r => montoCuenta(r, ["monto"])),
            { group: "Egresos", title: "Notas de credito compras", totalLabel: "Total notas compra", total: fmtPYG(sectionTotal(notasCompra, r => montoReportePYG(r, ["monto"]))), columns: ["Fecha", "Proveedor", "Factura compra", "Motivo", "Monto"], rows: rowsOf(notasCompra, [{value:"fecha"}, {value: r => r.proveedor_nombre || r.razon_social}, {value: r => r.compra_numero_factura || r.factura_numero || r.compra_id || r.factura_id}, {value:"motivo"}, {value: r => fmtPYG(montoReportePYG(r, ["monto"]))}]) },
          ],
        };
      } else if (tipo === "cliente_detallado" || tipo === "cliente_estado") {
        if (!selectedCliente) {
          toast.error("Seleccioná un cliente para generar este reporte");
          setGenericReportLoading(false);
          return;
        }
        const paramsCliente = tipo === "cliente_detallado" ? { mes: reportMonth } : {};
        const [facturasAll, recibosAll, notasAll] = await Promise.all([
          fetchJson("/admin/facturas", paramsCliente),
          fetchJson("/admin/recibos", paramsCliente).catch(() => []),
          fetchJson("/admin/notas-credito", { ...paramsCliente, tipo: "venta" }).catch(() => []),
        ]);
        const facturasCliente = (facturasAll || []).filter(clienteMatches).filter(f => f.tipo === "emitida" && f.estado !== "anulada");
        const recibosCliente = (recibosAll || []).filter(clienteMatches);
        const notasCliente = (notasAll || []).filter(clienteMatches);
        const totalFacturado = sectionTotal(facturasCliente, f => montoReportePYG(f, ["monto"]));
        const totalCobrado = sectionTotal(facturasCliente, cobradoFactura);
        const totalNotas = sectionTotal(notasCliente, n => montoReportePYG(n, ["monto"]));
        const saldo = Math.max(0, totalFacturado - totalCobrado - totalNotas);
        const clienteLabel = selectedCliente.razon_social || selectedCliente.nombre || "Cliente";
        report = tipo === "cliente_detallado"
          ? {
              title: `Ventas por cliente - ${clienteLabel}`,
              subtitle: `${mesLabel(reportMonth)} · ${periodoSub}`,
              summary: [["Facturas", facturasCliente.length], ["Facturado", fmtPYG(totalFacturado)], ["Cobrado", fmtPYG(totalCobrado)], ["Notas credito", fmtPYG(totalNotas)], ["Saldo", fmtPYG(saldo)]],
              sections: [
                { title: "Ventas detalladas", totalLabel: "Total facturado", total: fmtPYG(totalFacturado), columns: ["Fecha", "Nro", "Tipo", "Estado", "Concepto", "Monto", "Cobrado", "Saldo"], rows: rowsOf(facturasCliente, [{value:"fecha"}, {value: r => r.numero_boleta || r.numero}, {value: r => r.sin_factura ? "Boleta" : "Factura"}, {value:"estado"}, {value:"concepto"}, {value: r => fmtPYG(montoReportePYG(r, ["monto"]))}, {value: r => fmtPYG(cobradoFactura(r))}, {value: r => fmtPYG(Math.max(0, montoReportePYG(r, ["monto"]) - cobradoFactura(r)))}]) },
                { title: "Cobros / recibos", totalLabel: "Total recibos", total: fmtPYG(sectionTotal(recibosCliente, r => montoReportePYG(r, ["monto"]))), columns: ["Fecha", "Recibo", "Factura", "Metodo", "Monto"], rows: rowsOf(recibosCliente, [{value: r => r.fecha_pago || r.fecha}, {value:"numero"}, {value: r => r.factura_numero || r.factura_id}, {value:"metodo_pago"}, {value: r => fmtPYG(montoReportePYG(r, ["monto"]))}]) },
                { title: "Notas de credito", totalLabel: "Total notas", total: fmtPYG(totalNotas), columns: ["Fecha", "Nro", "Factura", "Motivo", "Monto"], rows: rowsOf(notasCliente, [{value:"fecha"}, {value:"numero"}, {value: r => r.factura_numero || r.factura_id}, {value:"motivo"}, {value: r => fmtPYG(montoReportePYG(r, ["monto"]))}]) },
              ],
            }
          : {
              title: `Estado de cuenta - ${clienteLabel}`,
              subtitle: `Resumen general · ${periodoSub}`,
              summary: [["Facturas abiertas", facturasCliente.filter(f => f.estado !== "pagada").length], ["Total facturado", fmtPYG(totalFacturado)], ["Total cobrado", fmtPYG(totalCobrado)], ["Notas credito", fmtPYG(totalNotas)], ["Saldo cliente", fmtPYG(saldo)]],
              sections: [
                { title: "Estado de cuenta", totalLabel: "Saldo", total: fmtPYG(saldo), columns: ["Cliente", "RUC", "Facturado", "Cobrado", "Notas credito", "Saldo"], rows: [[clienteLabel, selectedCliente.ruc || "-", fmtPYG(totalFacturado), fmtPYG(totalCobrado), fmtPYG(totalNotas), fmtPYG(saldo)]] },
                { title: "Comprobantes con saldo", columns: ["Fecha", "Nro", "Estado", "Total", "Cobrado", "Saldo"], rows: rowsOf(facturasCliente.filter(f => Math.max(0, montoReportePYG(f, ["monto"]) - cobradoFactura(f)) > 0), [{value:"fecha"}, {value: r => r.numero_boleta || r.numero}, {value:"estado"}, {value: r => fmtPYG(montoReportePYG(r, ["monto"]))}, {value: r => fmtPYG(cobradoFactura(r))}, {value: r => fmtPYG(Math.max(0, montoReportePYG(r, ["monto"]) - cobradoFactura(r)))}]) },
              ],
            };
      } else if (tipo === "facturas") {
        const data = await fetchJson("/admin/facturas", { mes: reportMonth });
        const total = sectionTotal(data, x => montoReportePYG(x, ["monto"]));
        const cobrado = sectionTotal(data, x => {
          const pagado = Number(x.monto_pagado ?? 0);
          if (pagado > 0) return montoReportePYG({ ...x, monto: pagado }, ["monto"]);
          return x.estado === "pagada" ? montoReportePYG(x, ["monto"]) : 0;
        });
        const pendiente = Math.max(0, total - cobrado);
        const dataCuenta = (data || []).map(f => {
          const p = (f.pagos || [])[0] || {};
          return { ...f, cuenta_id: p.cuenta_id || f.cuenta_id, cuenta_nombre: p.cuenta_nombre || f.cuenta_nombre, cuenta_moneda: p.cuenta_moneda || f.cuenta_moneda || f.moneda };
        });
        const cobrosCuenta = (data || []).flatMap(f => {
          const pagos = f.pagos || [];
          if (pagos.length) {
            return pagos.map(p => ({ ...p, cuenta_moneda: p.cuenta_moneda || f.moneda || "PYG", moneda: p.moneda || f.moneda || "PYG" }));
          }
          return f.estado === "pagada" ? [f] : [];
        });
        report = {
          title: `Facturas - ${mesLabel(reportMonth)}`,
          subtitle: periodoSub,
          summary: [["Emitidas", data.length], ["Total emitido", fmtPYG(total)], ["Cobrado", fmtPYG(cobrado)], ["Pendiente", fmtPYG(pendiente)]],
          accountSummary: resumenCuentasDesdeItems(cobrosCuenta, cuentaResolver, r => montoCuenta(r, ["monto"]), "Cobros de facturas por banco/cuenta"),
          sections: sectionsPorCuenta(dataCuenta, { title: "Facturas por cuenta", totalLabel: "Total emitido", columns: ["Fecha", "Cliente", "Nro", "Tipo", "Estado", "Emitido", "Cobrado", "Pendiente"] }, cuentaResolver, (r, cuenta) => {
            const emitido = montoCuenta(r, ["monto"]);
            const pagado = Number(r.monto_pagado ?? 0);
            const cobro = pagado > 0 ? pagado : (r.estado === "pagada" ? emitido : 0);
            return [r.fecha || "-", r.empresa_nombre || r.razon_social || "-", r.numero || "-", formaFactura(r), r.estado || "-", fmtCuentaMonto(emitido, cuenta), fmtCuentaMonto(cobro, cuenta), fmtCuentaMonto(Math.max(0, emitido - cobro), cuenta)];
          }, r => montoCuenta(r, ["monto"])),
        };
      } else if (tipo === "ingresos") {
        const data = await fetchJson("/admin/ingresos-varios", { mes: reportMonth });
        const total = data.reduce((s, x) => s + Number(x.monto || 0), 0);
        report = { title: `Ingresos varios - ${mesLabel(reportMonth)}`, subtitle: periodoSub, summary: [["Registros", data.length], ["Total", fmtPYG(total)], ["Categorias", new Set(data.map(x => x.categoria).filter(Boolean)).size], ["Clientes", new Set(data.map(x => x.empresa_id).filter(Boolean)).size]], accountSummary: resumenCuentasDesdeItems(data, cuentaResolver, r => montoCuenta(r, ["monto"]), "Ingresos por banco/cuenta"), sections: sectionsPorCuenta(data, { title: "Ingresos por cuenta", totalLabel: "Total", columns: ["Fecha", "Cliente", "Categoria", "Concepto", "Monto"] }, cuentaResolver, (r, cuenta) => [r.fecha || "-", r.empresa_nombre || r.razon_social || "-", r.categoria || "-", r.descripcion || r.concepto || "-", fmtCuentaMonto(montoCuenta(r, ["monto"]), cuenta)], r => montoCuenta(r, ["monto"])) };
      } else if (tipo === "recibos") {
        const data = await fetchJson("/admin/recibos", { mes: reportMonth });
        const total = data.reduce((s, x) => s + Number(x.monto || 0), 0);
        report = { title: `Recibos - ${mesLabel(reportMonth)}`, subtitle: periodoSub, summary: [["Recibos", data.length], ["Total", fmtPYG(total)], ["Facturas", new Set(data.map(x => x.factura_id).filter(Boolean)).size], ["Clientes", new Set(data.map(x => x.empresa_id).filter(Boolean)).size]], accountSummary: resumenCuentasDesdeItems(data, cuentaResolver, r => montoCuenta(r, ["monto"]), "Recibos por banco/cuenta"), sections: sectionsPorCuenta(data, { title: "Recibos por cuenta", totalLabel: "Total", columns: ["Fecha", "Cliente", "Factura", "Metodo", "Monto"] }, cuentaResolver, (r, cuenta) => [r.fecha_pago || "-", r.empresa_nombre || r.cliente_nombre || "-", r.factura_numero || r.factura_id || "-", r.metodo_pago || "-", fmtCuentaMonto(montoCuenta(r, ["monto"]), cuenta)], r => montoCuenta(r, ["monto"])) };
      } else if (tipo === "notas_credito") {
        const data = await fetchJson("/admin/notas-credito", { mes: reportMonth });
        const total = data.reduce((s, x) => s + montoReportePYG(x, ["monto"]), 0);
        const ventas = data.filter(x => (x.tipo || "venta") === "venta");
        const comprasNc = data.filter(x => x.tipo === "compra");
        report = { title: `Notas de credito - ${mesLabel(reportMonth)}`, subtitle: periodoSub, summary: [["Notas", data.length], ["Total Gs", fmtPYG(total)], ["Ventas", ventas.length], ["Compras", comprasNc.length]], sections: [{ title: "Detalle", columns: ["Fecha", "Tipo", "Cliente/Proveedor", "Factura/Compra", "Motivo", "Monto Gs"], rows: rowsOf(data, [{value:"fecha"}, {value: r => (r.tipo || "venta") === "compra" ? "Compra" : "Venta"}, {value: r => r.empresa_nombre || r.proveedor_nombre || r.razon_social}, {value: r => r.factura_numero || r.compra_numero_factura || r.factura_id || r.compra_id}, {value:"motivo"}, {value: r => fmtPYG(montoReportePYG(r, ["monto"]))}]) }] };
      } else if (tipo === "presupuestos") {
        const data = await fetchJson("/admin/presupuestos");
        report = { title: "Presupuestos", subtitle: periodoSub, summary: [["Total", data.length], ["Aprobados", data.filter(x => x.estado === "aprobado").length], ["Rechazados", data.filter(x => x.estado === "rechazado").length], ["Facturados", data.filter(x => x.facturado).length]], sections: [{ title: "Detalle", columns: ["Fecha", "Cliente", "Estado", "Total", "Facturas"], rows: rowsOf(data, [{value: r => (r.created_at || "").slice(0,10)}, {value:"empresa_nombre"}, {value:"estado"}, {value: r => fmtPYG(r.total)}, {value:"facturas_count"}]) }] };
      } else if (tipo === "flow_facturaciones") {
        // Fetch en paralelo solo los tipos seleccionados con permiso
        const fetches = [];
        const prodQ = movProductoId ? { producto_id: movProductoId, ...(movVarianteId ? { variante_id: movVarianteId } : {}) } : {};
        if (movTipos.includes("facturas"))
          fetches.push(fetchJson("/admin/facturas", { desde: movDesde, hasta: movHasta, incluir_anuladas: movIncluirAnuladas, incluir_eliminadas: movIncluirInactivas, modo_boleta: "con_factura", incluir_conceptos: true, ...prodQ }).then(d => ({ t: "facturas", d: d || [] })));
        if (movTipos.includes("boletas"))
          fetches.push(fetchJson("/admin/facturas", { desde: movDesde, hasta: movHasta, incluir_anuladas: movIncluirAnuladas, incluir_eliminadas: movIncluirInactivas, modo_boleta: "sin_factura", incluir_conceptos: true, ...prodQ }).then(d => ({ t: "boletas", d: d || [] })));
        if (movTipos.includes("notas_credito"))
          fetches.push(fetchJson("/admin/notas-credito", { desde: movDesde, hasta: movHasta, incluir_anuladas: movIncluirAnuladas, incluir_eliminadas: movIncluirInactivas, ...prodQ }).then(d => ({ t: "notas_credito", d: d || [] })));
        if (movTipos.includes("compras"))
          // Compras no tiene estado "anulada" (solo eliminada/inactiva); el checkbox de anuladas no le aplica.
          fetches.push(fetchJson("/admin/compras", { desde: movDesde, hasta: movHasta, incluir_eliminadas: movIncluirInactivas, ...prodQ }).then(d => ({ t: "compras", d: d || [] })));

        const results = await Promise.all(fetches);
        const byT = Object.fromEntries(results.map(r => [r.t, r.d]));
        const factData  = byT.facturas      || [];
        const boletaData = byT.boletas      || [];
        const notasData = byT.notas_credito || [];
        const compData  = byT.compras       || [];

        const periodoLabel = `${movDesde} al ${movHasta}`;
        const prodSel = movProductoId ? stockProductos.find(p => p.id === movProductoId) : null;
        const varSel = prodSel && movVarianteId ? (prodSel.variantes || []).find(v => v.id === movVarianteId) : null;
        const filtroProdTxt = prodSel ? ` · Producto: ${prodSel.nombre}${varSel ? ` (${varSel.atributos_texto || varSel.sku || ""})` : " (todas las variantes)"}` : "";

        // Totales facturas
        const totalFact = sectionTotal(factData, x => montoReportePYG(x, ["monto"]));
        const cobradoFact = sectionTotal(factData, x => {
          const pagado = Number(x.monto_pagado ?? 0);
          if (pagado > 0) return montoReportePYG({ ...x, monto: pagado }, ["monto"]);
          return x.estado === "pagada" ? montoReportePYG(x, ["monto"]) : 0;
        });
        const pendienteFact = Math.max(0, totalFact - cobradoFact);

        // Totales notas
        const totalNotas = notasData.reduce((s, x) => s + montoReportePYG(x, ["monto"]), 0);

        // Totales compras
        const movsComp = movimientosDeCompras(compData);
        const totalCompPYG = sectionTotal(movsComp.filter(x => (x.cuenta_moneda || x.moneda) !== "USD"), x => x.monto_movimiento);
        const totalCompUSD = sectionTotal(movsComp.filter(x => (x.cuenta_moneda || x.moneda) === "USD"), x => x.monto_movimiento);
        const pendComp = compData.filter(x => x.tipo_pago === "credito" && x.estado_pago !== "pagado");
        const pendCompPYG = sectionTotal(pendComp.filter(x => (x.moneda || "PYG") !== "USD"), x => montoCuenta(x, ["saldo_pendiente", "monto_total", "monto"]));

        // Summary dinámico
        const summaryItems = [["Período", periodoLabel]];
        if (prodSel) summaryItems.push(["Producto", prodSel.nombre + (varSel ? ` — ${varSel.atributos_texto || ""}` : "")]);
        if (factData.length) summaryItems.push(["Facturas", factData.length], ["Emitido", fmtPYG(totalFact)], ["Cobrado", fmtPYG(cobradoFact)], ["Pendiente", fmtPYG(pendienteFact)]);
        const totalBol = sectionTotal(boletaData, x => montoReportePYG(x, ["monto"]));
        const cobradoBol = sectionTotal(boletaData, x => {
          const pagado = Number(x.monto_pagado ?? 0);
          if (pagado > 0) return montoReportePYG({ ...x, monto: pagado }, ["monto"]);
          return x.estado === "pagada" ? montoReportePYG(x, ["monto"]) : 0;
        });
        if (boletaData.length) summaryItems.push(["Boletas", boletaData.length], ["Total boletas", fmtPYG(totalBol)], ["Cobrado boletas", fmtPYG(cobradoBol)]);
        if (notasData.length) summaryItems.push(["Notas de crédito", notasData.length], ["Total NC", fmtPYG(totalNotas)]);
        if (compData.length) summaryItems.push(["Compras", compData.length], ["Pagado Gs", fmtPYG(totalCompPYG)], ["Pendiente Gs", fmtPYG(pendCompPYG)]);

        // Secciones por tipo
        const sections = [];
        if (factData.length) {
          const dataCuentaFact = factData.map(f => { const p = (f.pagos || [])[0] || {}; return { ...f, cuenta_id: p.cuenta_id || f.cuenta_id, cuenta_nombre: p.cuenta_nombre || f.cuenta_nombre, cuenta_moneda: p.cuenta_moneda || f.cuenta_moneda || f.moneda }; });
          sections.push(...sectionsPorCuenta(dataCuentaFact,
            { title: "Facturas", totalLabel: "Total emitido", columns: ["Fecha", "Cliente", "Nro", "Tipo", "Estado", "Emitido", "Cobrado", "Pendiente"] },
            cuentaResolver,
            (r, cuenta) => {
              const emitido = montoCuenta(r, ["monto"]);
              const pagado  = Number(r.monto_pagado ?? 0);
              const cobro   = pagado > 0 ? pagado : (r.estado === "pagada" ? emitido : 0);
              return [r.fecha || "-", r.empresa_nombre || r.razon_social || "-", r.numero || "-", formaFactura(r), r.estado || "-", fmtCuentaMonto(emitido, cuenta), fmtCuentaMonto(cobro, cuenta), fmtCuentaMonto(Math.max(0, emitido - cobro), cuenta)];
            },
            r => montoCuenta(r, ["monto"])
          ));
        }
        if (boletaData.length) {
          const dataCuentaBol = boletaData.map(f => { const p = (f.pagos || [])[0] || {}; return { ...f, cuenta_id: p.cuenta_id || f.cuenta_id, cuenta_nombre: p.cuenta_nombre || f.cuenta_nombre, cuenta_moneda: p.cuenta_moneda || f.cuenta_moneda || f.moneda }; });
          sections.push(...sectionsPorCuenta(dataCuentaBol,
            { title: "Boletas", totalLabel: "Total emitido", columns: ["Fecha", "Cliente", "Nro", "Tipo", "Estado", "Emitido", "Cobrado", "Pendiente"] },
            cuentaResolver,
            (r, cuenta) => {
              const emitido = montoCuenta(r, ["monto"]);
              const pagado  = Number(r.monto_pagado ?? 0);
              const cobro   = pagado > 0 ? pagado : (r.estado === "pagada" ? emitido : 0);
              return [r.fecha || "-", r.empresa_nombre || r.razon_social || "-", r.numero || "-", formaFactura(r), r.estado || "-", fmtCuentaMonto(emitido, cuenta), fmtCuentaMonto(cobro, cuenta), fmtCuentaMonto(Math.max(0, emitido - cobro), cuenta)];
            },
            r => montoCuenta(r, ["monto"])
          ));
        }
        if (notasData.length) {
          sections.push({ title: "Notas de crédito", columns: ["Fecha", "Tipo", "Cliente/Proveedor", "Factura/Compra", "Motivo", "Monto Gs"], rows: rowsOf(notasData, [{value:"fecha"}, {value: r => (r.tipo || "venta") === "compra" ? "Compra" : "Venta"}, {value: r => r.empresa_nombre || r.proveedor_nombre || r.razon_social}, {value: r => r.factura_numero || r.compra_numero_factura || r.factura_id || r.compra_id}, {value:"motivo"}, {value: r => fmtPYG(montoReportePYG(r, ["monto"]))}]) });
        }
        if (compData.length) {
          sections.push(
            ...sectionsPorCuenta(movsComp,
              { title: "Compras", totalLabel: "Total", columns: ["Fecha", "Proveedor", "Tipo", "Factura", "Estado", "Monto"] },
              cuentaResolver,
              (r, cuenta) => [r.fecha || "-", r.proveedor_nombre || "-", r.tipo_pago || "-", r.factura_numero || r.numero_factura || "-", r.estado_pago || "-", fmtCuentaMonto(r.monto_movimiento, cuenta)],
              r => r.monto_movimiento
            ),
          );
          if (pendComp.length) {
            sections.push({ title: "Compras crédito pendientes", totalLabel: "Pendiente", total: fmtPYG(pendCompPYG), columns: ["Fecha", "Proveedor", "Factura", "Moneda", "Saldo pendiente"], rows: rowsOf(pendComp, [{value:"fecha"}, {value:"proveedor_nombre"}, {value: r => r.factura_numero || r.numero_factura}, {value:"moneda"}, {value: r => r.moneda === "USD" ? fmtUSD(r.saldo_pendiente || r.monto_total || 0) : fmtPYG(r.saldo_pendiente || r.monto_total || 0)}]) });
          }
        }

        // Detalle por producto: cantidad × precio de venta y cantidad × costo
        const verCostoFlow = isAdmin || hasPermission?.("inventario_productos.ver_costo_compra");
        const lineasProd = [];
        [...factData, ...boletaData].forEach(f => {
          if (f.estado === "anulada") return;
          // Factor por descuento global que no quedó reflejado en los subtotales de los renglones:
          // el total real del documento (monto) manda sobre la suma de renglones.
          const sumaRenglones = (f.conceptos || []).reduce((a, c) => {
            const q = Number(c.cantidad || 0), p0 = Number(c.precio_unitario || c.precio || 0);
            const d0 = Math.max(0, Math.min(100, Number(c.descuento_pct || 0)));
            return a + (c.subtotal != null ? Number(c.subtotal) : p0 * q * (1 - d0 / 100));
          }, 0);
          const montoDoc = Number(f.monto || 0);
          const factorDesc = (montoDoc > 0 && sumaRenglones > 0 && montoDoc < sumaRenglones - 1) ? montoDoc / sumaRenglones : 1;
          (f.conceptos || []).forEach(c => {
            if (!c.producto_id) return;
            if (movProductoId && c.producto_id !== movProductoId) return;
            if (movVarianteId && c.variante_id !== movVarianteId) return;
            const prod = stockProductos.find(p => p.id === c.producto_id);
            const vari = (prod?.variantes || []).find(v => v.id === c.variante_id);
            const cant = Number(c.cantidad || 0);
            const pv = Number(c.precio_unitario || c.precio || 0);
            const desc = Math.max(0, Math.min(100, Number(c.descuento_pct || 0)));
            // Venta real facturada: subtotal ya incluye descuento por ítem y descuento global
            const ventaReal = (c.subtotal != null ? Number(c.subtotal) : pv * cant * (1 - desc / 100)) * factorDesc;
            const costoU = Number(c.precio_costo || 0) > 0 ? Number(c.precio_costo) : Number(vari?.precio_costo || prod?.precio_costo || 0);
            const mon = f.moneda === "USD" ? "USD" : "PYG";
            lineasProd.push({ fecha: f.fecha, numero: f.numero, nombre: (prod?.nombre || c.descripcion || "-") + (vari?.atributos_texto ? ` (${vari.atributos_texto})` : ""), cant, pv: cant ? ventaReal / cant : pv, venta: ventaReal, costo: costoU * cant, mon });
          });
        });
        if (lineasProd.length) {
          const fm = (v, mon) => mon === "USD" ? fmtUSD(v) : fmtPYG(v);
          const totCant = lineasProd.reduce((a, l) => a + l.cant, 0);
          const monedas = Array.from(new Set(lineasProd.map(l => l.mon)));
          const sumaMon = (fn) => monedas.map(m => fm(lineasProd.filter(l => l.mon === m).reduce((a, l) => a + fn(l), 0), m)).join(" · ");
          const cols = ["Fecha", "Nro", "Producto", "Cant.", "Precio venta", "Cant. × Venta"];
          if (verCostoFlow) cols.push("Costo unit.", "Cant. × Costo");
          const rows = lineasProd.map(l => {
            const r = [l.fecha || "-", l.numero || "-", l.nombre, l.cant, fm(l.pv, l.mon), fm(l.venta, l.mon)];
            if (verCostoFlow) r.push(fm(l.cant ? l.costo / l.cant : 0, l.mon), fm(l.costo, l.mon));
            return r;
          });
          const totales = ["", "", "TOTAL", totCant, "", sumaMon(l => l.venta)];
          if (verCostoFlow) totales.push("", sumaMon(l => l.costo));
          rows.push(totales);
          // Total de ventas con descuentos: suma del total real de cada documento (lo que se cobró/facturó).
          // Con un filtro por producto se usan las líneas de ese producto.
          const docsVenta = [...factData, ...boletaData].filter(f => f.estado !== "anulada");
          const totalVentasMon = (mon) => (movProductoId || movVarianteId)
            ? lineasProd.filter(l => l.mon === mon).reduce((a, l) => a + l.venta, 0)
            : docsVenta.filter(f => (f.moneda === "USD" ? "USD" : "PYG") === mon).reduce((a, f) => a + Number(f.monto || 0), 0);
          const totVentas = monedas.map(m => fm(totalVentasMon(m), m)).join(" · ");
          const difMon = (m) => totalVentasMon(m) - lineasProd.filter(l => l.mon === m).reduce((a, l) => a + l.costo, 0);
          summaryItems.push(["Total ventas (con descuentos)", totVentas]);
          if (verCostoFlow) summaryItems.push(["Total Cant. × Costo", sumaMon(l => l.costo)], ["Diferencia: ventas - costo", monedas.map(m => fm(difMon(m), m)).join(" · ")]);
          sections.push({ title: "Detalle por producto (cantidad × precio / costo)", columns: cols, rows });
        }

        report = { title: `Flow de Facturaciones — ${periodoLabel}`, subtitle: periodoLabel + filtroProdTxt, summary: summaryItems, sections };

      } else if (tipo === "flow_financieros") {
        const fetches2 = [];
        if (flowFinTipos.includes("ingresos"))
          fetches2.push(fetchJson("/admin/ingresos-varios", { desde: flowFinDesde, hasta: flowFinHasta }).then(d => ({ t: "ingresos", d: d || [] })));
        if (flowFinTipos.includes("recibos"))
          fetches2.push(fetchJson("/admin/recibos", { desde: flowFinDesde, hasta: flowFinHasta }).then(d => ({ t: "recibos", d: d || [] })));
        if (flowFinTipos.includes("gastos")) {
          // Generar lista de meses en el rango desde–hasta
          const gastosMonths = [];
          {
            const [dy, dm] = flowFinDesde.split("-").map(Number);
            const [hy, hm] = flowFinHasta.split("-").map(Number);
            let gy = dy, gm = dm;
            while (gy < hy || (gy === hy && gm <= hm)) {
              gastosMonths.push(`${gy}-${String(gm).padStart(2, "0")}`);
              gm++; if (gm > 12) { gm = 1; gy++; }
            }
          }
          fetches2.push(Promise.all(
            gastosMonths.map(periodo => fetchJson("/admin/costos-fijos/vencimientos", { periodo }).catch(() => []))
          ).then(results => ({ t: "gastos", d: results.flat() })));
        }

        const results2 = await Promise.all(fetches2);
        const byT2 = Object.fromEntries(results2.map(r => [r.t, r.d]));
        const ingData  = byT2.ingresos || [];
        const recData  = byT2.recibos  || [];
        const gasData  = byT2.gastos   || [];

        const periodoLabel2 = `${flowFinDesde} al ${flowFinHasta}`;
        const totalIng = ingData.reduce((s, x) => s + Number(x.monto || 0), 0);
        const totalRec = recData.reduce((s, x) => s + Number(x.monto || 0), 0);
        const totalGas = gasData.reduce((s, x) => s + Number(x.monto || 0), 0);
        const totalGasPagado = gasData.reduce((s, x) => s + Number(x.total_pagado || 0), 0);

        const summaryItems2 = [["Período", periodoLabel2]];
        if (ingData.length) summaryItems2.push(["Ingresos", ingData.length], ["Total ingresos", fmtPYG(totalIng)]);
        if (recData.length) summaryItems2.push(["Recibos", recData.length], ["Total cobrado", fmtPYG(totalRec)]);
        if (gasData.length) summaryItems2.push(["Gastos", gasData.length], ["Total gastos", fmtPYG(totalGas)], ["Pagado", fmtPYG(totalGasPagado)]);

        const sections2 = [];
        if (ingData.length) {
          sections2.push(...sectionsPorCuenta(ingData,
            { title: "Ingresos varios", totalLabel: "Total", columns: ["Fecha", "Cliente", "Categoría", "Concepto", "Monto"] },
            cuentaResolver,
            (r, cuenta) => [r.fecha || "-", r.empresa_nombre || r.razon_social || "-", r.categoria || "-", r.descripcion || r.concepto || "-", fmtCuentaMonto(montoCuenta(r, ["monto"]), cuenta)],
            r => montoCuenta(r, ["monto"])
          ));
        }
        if (recData.length) {
          sections2.push(...sectionsPorCuenta(recData,
            { title: "Recibos", totalLabel: "Total cobrado", columns: ["Fecha", "Cliente", "Factura", "Método", "Monto"] },
            cuentaResolver,
            (r, cuenta) => [r.fecha_pago || "-", r.empresa_nombre || r.cliente_nombre || "-", r.factura_numero || r.factura_id || "-", r.metodo_pago || "-", fmtCuentaMonto(montoCuenta(r, ["monto"]), cuenta)],
            r => montoCuenta(r, ["monto"])
          ));
        }
        if (gasData.length) {
          const estadoLabelFin = (e) => e === "pagado" ? "Pagado" : e === "pago_parcial" ? "Parcial" : e === "vencido" ? "Vencido" : "Pendiente";
          sections2.push(...sectionsPorCuenta(gasData,
            { title: "Gastos fijos", totalLabel: "Total", columns: ["Período", "Gasto", "Categoría", "Estado", "Monto"] },
            cuentaResolver,
            (r, cuenta) => [r.periodo || "-", r.nombre || "-", r.categoria || "-", estadoLabelFin(r.estado), fmtCuentaMonto(Number(r.monto || 0), cuenta)],
            r => Number(r.monto || 0)
          ));
        }

        report = { title: `Flow de Financieros — ${periodoLabel2}`, subtitle: periodoLabel2, summary: summaryItems2, sections: sections2 };

      } else if (tipo === "prestamos") {
        const data = await fetchJson("/admin/prestamos/reporte-detallado", {
          desde: prestReporteDesde,
          hasta: prestReporteHasta,
          tipo: prestReporteTipo !== "todos" ? prestReporteTipo : undefined,
          estado: prestReporteEstado !== "todos" ? prestReporteEstado : undefined,
          entidad_id: prestReporteEntidadId || undefined,
          buscar: prestReporteBuscar.trim() || undefined,
        });
        const allRows = data.rows || [];
        const rows = reportCurrency === "AMBOS" ? allRows : allRows.filter(r => (r.moneda || "PYG") === reportCurrency);
        const resumenRows = {};
        rows.forEach(r => {
          const moneda = r.moneda || "PYG";
          if (!resumenRows[moneda]) resumenRows[moneda] = { cantidad: 0, original: 0, total: 0, pagado: 0, saldo: 0, vencidas: 0 };
          resumenRows[moneda].cantidad += 1;
          resumenRows[moneda].original += Number(r.monto_original || 0);
          resumenRows[moneda].total += Number(r.total_a_cobrar || 0);
          resumenRows[moneda].pagado += Number(r.monto_pagado || 0);
          resumenRows[moneda].saldo += Number(r.saldo_pendiente || 0);
          resumenRows[moneda].vencidas += Number(r.cuotas_vencidas || 0);
        });
        const resumenCards = Object.entries(resumenRows).flatMap(([moneda, r]) => [
          [`Préstamos ${moneda}`, r.cantidad],
          [`Original ${moneda}`, fmtMonedaReporte(r.original, moneda)],
          [`Pagado ${moneda}`, fmtMonedaReporte(r.pagado, moneda)],
          [`Saldo ${moneda}`, fmtMonedaReporte(r.saldo, moneda)],
        ]);
        const porTipo = rows.reduce((acc, r) => {
          const key = r.tipo || "sin_tipo";
          if (!acc[key]) acc[key] = { cantidad: 0, saldo: 0, moneda: r.moneda || "PYG" };
          acc[key].cantidad += 1;
          acc[key].saldo += Number(r.saldo_pendiente || 0);
          return acc;
        }, {});
        report = {
          title: `Reporte de préstamos - ${prestReporteDesde || "inicio"} al ${prestReporteHasta || "hoy"} - ${currencyLabel}`,
          subtitle: `${periodoSub}${prestReporteTipo !== "todos" ? ` · Tipo: ${prestReporteTipo}` : ""}${prestReporteEntidadNombre ? ` · Entidad: ${prestReporteEntidadNombre}` : ""}${prestReporteBuscar.trim() ? ` · Búsqueda: ${prestReporteBuscar.trim()}` : ""}`,
          summary: resumenCards.length ? resumenCards : [["Préstamos", 0], ["Saldo", fmtPYG(0)]],
          accountSummary: {
            title: "Resumen por tipo",
            rows: Object.entries(porTipo).map(([tipoKey, r]) => ({
              label: tipoKey === "bancario" ? "Préstamos bancarios" : tipoKey === "proveedor" ? "Préstamos a proveedores" : "Préstamos a empleados",
              value: fmtMonedaReporte(r.saldo, r.moneda),
              note: `${r.cantidad} préstamo(s)`,
            })),
          },
          sections: [{
            title: "Detalle de préstamos",
            totalLabel: "Saldo pendiente",
            total: Object.entries(resumenRows).map(([moneda, r]) => fmtMonedaReporte(r.saldo, moneda)).join(" / ") || fmtPYG(0),
            columns: ["Tipo", "Préstamo", "Referencia", "Destino", "Cuenta", "Inicio", "Próximo venc.", "Cuotas", "Original", "Pagado", "Saldo"],
            rows: rows.map(r => [
              r.tipo === "bancario" ? "Bancario" : r.tipo === "proveedor" ? "Proveedor" : "Empleado",
              r.alias || r.entidad_nombre || "-",
              r.referencia || "-",
              r.destino || "-",
              r.plan_cuenta_nombre || r.cuenta_nombre || "-",
              r.fecha_inicio || "-",
              r.fecha_vencimiento_proxima || "-",
              `${r.cuotas_pagadas || 0}/${r.n_cuotas || 0}${r.cuotas_vencidas ? ` · ${r.cuotas_vencidas} venc.` : ""}`,
              fmtMonedaReporte(r.monto_original || 0, r.moneda),
              fmtMonedaReporte(r.monto_pagado || 0, r.moneda),
              fmtMonedaReporte(r.saldo_pendiente || 0, r.moneda),
            ]),
          }, {
            title: "Cuotas y vencimientos",
            totalLabel: "Saldo pendiente",
            total: Object.entries(resumenRows).map(([moneda, r]) => fmtMonedaReporte(r.saldo, moneda)).join(" / ") || fmtPYG(0),
            columns: ["Préstamo", "Cuota", "Vencimiento", "Capital", "Interés", "Total cuota", "Estado", "Fecha pago"],
            rows: rows.flatMap(r => (r.cuotas || []).map(c => [
              r.alias || r.entidad_nombre || "-",
              `${c.numero}/${r.n_cuotas || (r.cuotas || []).length}`,
              c.fecha_vencimiento || "-",
              fmtMonedaReporte(c.monto_capital || 0, r.moneda),
              fmtMonedaReporte(c.monto_interes || 0, r.moneda),
              fmtMonedaReporte(c.monto_total || 0, r.moneda),
              c.estado === "pagada" ? "Pagada" : c.estado === "vencida" ? "VENCIDA" : "Pendiente",
              c.fecha_pago || "-",
            ])),
          }],
        };
      } else if (tipo === "compras") {
        const data = await fetchJson("/admin/compras", { mes: reportMonth });
        const movimientos = movimientosDeCompras(data);
        const totalMovPYG = sectionTotal(movimientos.filter(x => (x.cuenta_moneda || x.moneda) !== "USD"), x => x.monto_movimiento);
        const totalMovUSD = sectionTotal(movimientos.filter(x => (x.cuenta_moneda || x.moneda) === "USD"), x => x.monto_movimiento);
        const totalUSDOriginal = sectionTotal(data.filter(x => x.moneda === "USD"), x => montoCuenta(x, ["monto_total", "monto"]));
        const pendientesCredito = data.filter(x => x.tipo_pago === "credito" && x.estado_pago !== "pagado");
        const pendientesPYG = sectionTotal(pendientesCredito.filter(x => (x.moneda || "PYG") !== "USD"), x => montoCuenta(x, ["saldo_pendiente", "monto_total", "monto"]));
        const pendientesUSD = sectionTotal(pendientesCredito.filter(x => x.moneda === "USD"), x => montoCuenta(x, ["saldo_pendiente", "monto_total", "monto"]));
        report = {
          title: `Compras - ${mesLabel(reportMonth)}`,
          subtitle: periodoSub,
          summary: [
            ["Compras", data.length],
            ["Pagado Gs", fmtPYG(totalMovPYG)],
            ["Pagado USD", fmtUSD(totalMovUSD)],
            ["Compras USD", fmtUSD(totalUSDOriginal)],
            ["Pendiente Gs", fmtPYG(pendientesPYG)],
            ["Pendiente USD", fmtUSD(pendientesUSD)],
          ],
          accountSummary: resumenCuentasDesdeItems(movimientos, cuentaResolver, r => r.monto_movimiento, "Pagos/compras por banco/cuenta"),
          sections: [
            ...sectionsPorCuenta(movimientos, { title: "Compras pagadas por cuenta", totalLabel: "Total", columns: ["Fecha", "Proveedor", "Tipo", "Factura", "Estado", "Monto"] }, cuentaResolver, (r, cuenta) => [r.fecha || "-", r.proveedor_nombre || "-", r.tipo_pago || "-", r.factura_numero || r.numero_factura || "-", r.estado_pago || "-", fmtCuentaMonto(r.monto_movimiento, cuenta)], r => r.monto_movimiento),
            { title: "Compras credito pendientes", totalLabel: "Pendiente", total: `${fmtPYG(pendientesPYG)} / ${fmtUSD(pendientesUSD)}`, columns: ["Fecha", "Proveedor", "Factura", "Moneda", "Saldo pendiente"], rows: rowsOf(pendientesCredito, [{value:"fecha"}, {value:"proveedor_nombre"}, {value: r => r.factura_numero || r.numero_factura}, {value:"moneda"}, {value: r => r.moneda === "USD" ? fmtUSD(r.saldo_pendiente || r.monto_total || 0) : fmtPYG(r.saldo_pendiente || r.monto_total || 0)}]) },
          ],
        };
      } else if (tipo === "gastos") {
        const data = await fetchJson("/admin/costos-fijos/vencimientos", { periodo: reportMonth });
        const totalGeneral = data.reduce((s, x) => s + Number(x.monto || 0), 0);
        const totalPagado = data.reduce((s, x) => s + Number(x.total_pagado || 0), 0);
        const pendientes = data.filter(x => x.estado !== "pagado").length;
        const estadoLabel = (r) => {
          if (r.estado === "pagado") return "Pagado";
          if (r.estado === "pago_parcial") return "Pago parcial";
          if (r.estado === "vencido") return "Vencido";
          return "Pendiente";
        };
        const itemsConCuenta = data.map(r => ({ ...r, cuenta_id: r.pago?.cuenta_id || null }));
        report = {
          title: `Gastos - ${mesLabel(reportMonth)}`,
          subtitle: periodoSub,
          summary: [
            ["Total gastos", data.length],
            ["Total", fmtPYG(totalGeneral)],
            ["Pagado", fmtPYG(totalPagado)],
            ["Pendientes", pendientes],
          ],
          accountSummary: resumenCuentasDesdeItems(
            itemsConCuenta.filter(r => r.cuenta_id),
            cuentaResolver,
            r => Number(r.total_pagado || 0),
            "Gastos pagados por banco/cuenta"
          ),
          sections: [
            {
              title: "Detalle de gastos",
              totalLabel: "Total",
              total: fmtPYG(totalGeneral),
              columns: ["Periodo", "Gasto", "Categoria", "Estado", "Fecha pago", "Monto"],
              rows: data.map(r => [
                r.periodo || "-",
                r.nombre || "-",
                r.categoria || "-",
                estadoLabel(r),
                r.pago?.fecha_pago || "-",
                fmtPYG(r.monto || 0),
              ]),
            },
          ],
        };
      } else if (tipo === "proveedores") {
        const [proveedores, resumenProveedores] = await Promise.all([
          fetchJson("/admin/entidades", { es_proveedor: true }),
          fetchJson("/admin/compras/resumen/por-proveedor", { anio: reportYear }),
        ]);
        const resumenMap = {};
        (resumenProveedores || []).forEach(r => {
          if (r.proveedor_id) resumenMap[r.proveedor_id] = r;
          if (r.proveedor_nombre) resumenMap[r.proveedor_nombre] = r;
        });
        const filasProveedor = (proveedores || []).map(p => {
          const cr = resumenMap[p.id] || resumenMap[p.nombre] || {};
          const totalPYG = Number(cr.total_comprado || 0);
          const totalUSD = Number(cr.total_comprado_usd || 0);
          const deudaPYG = Number(cr.deuda_actual || 0);
          const deudaUSD = Number(cr.deuda_actual_usd || 0);
          return {
            proveedor: p,
            compras: Number(cr.cantidad_compras || 0),
            totalPYG,
            totalUSD,
            deudaPYG,
            deudaUSD,
            pagadoPYG: Math.max(0, totalPYG - deudaPYG),
            pagadoUSD: Math.max(0, totalUSD - deudaUSD),
            ultima: cr.ultima_compra || "-",
          };
        }).sort((a, b) => (
          (b.deudaPYG + b.deudaUSD) - (a.deudaPYG + a.deudaUSD)
          || b.compras - a.compras
          || (a.proveedor.nombre || "").localeCompare(b.proveedor.nombre || "")
        ));
        const totalCompras = sectionTotal(filasProveedor, r => r.compras);
        const totalCompradoPYG = sectionTotal(filasProveedor, r => r.totalPYG);
        const totalCompradoUSD = sectionTotal(filasProveedor, r => r.totalUSD);
        const deudaPYG = sectionTotal(filasProveedor, r => r.deudaPYG);
        const deudaUSD = sectionTotal(filasProveedor, r => r.deudaUSD);
        const pagadoPYG = sectionTotal(filasProveedor, r => r.pagadoPYG);
        const pagadoUSD = sectionTotal(filasProveedor, r => r.pagadoUSD);
        report = {
          title: `Proveedores y deudas - ${reportYear}`,
          subtitle: periodoSub,
          summary: [["Proveedores", proveedores.length], ["Compras", totalCompras], ["Comprado Gs", fmtPYG(totalCompradoPYG)], ["Comprado USD", fmtUSD(totalCompradoUSD)], ["Deuda Gs", fmtPYG(deudaPYG)], ["Deuda USD", fmtUSD(deudaUSD)], ["Pagado Gs", fmtPYG(pagadoPYG)], ["Pagado USD", fmtUSD(pagadoUSD)]],
          sections: [
            {
              title: "Resumen por proveedor",
              totalLabel: "Deuda total",
              total: fmtMixto(deudaPYG, deudaUSD),
              columns: ["Proveedor", "Categoria", "Contacto", "Compras", "Total comprado", "Deuda actual", "Total pagado", "Ultima compra"],
              rows: filasProveedor.map(({ proveedor, compras, totalPYG, totalUSD, deudaPYG, deudaUSD, pagadoPYG, pagadoUSD, ultima }) => [
                proveedor.ruc ? `${proveedor.nombre}\nRUC: ${proveedor.ruc}` : proveedor.nombre,
                proveedor.categoria || "-",
                [proveedor.contacto, proveedor.telefono, proveedor.email].filter(Boolean).join(" / ") || "-",
                compras || "-",
                fmtMixto(totalPYG, totalUSD),
                deudaPYG > 0 || deudaUSD > 0 ? fmtMixto(deudaPYG, deudaUSD) : "Al dia",
                fmtMixto(pagadoPYG, pagadoUSD),
                ultima,
              ]),
            },
          ],
        };
      } else if (tipo === "catastros") {
        const filtroTipo = {};
        if (catastroTipo === "clientes") filtroTipo.es_cliente = true;
        else if (catastroTipo === "proveedores") filtroTipo.es_proveedor = true;
        else if (catastroTipo === "empleados") filtroTipo.es_empleado = true;
        // Se piden activos e inactivos por separado: el endpoint /admin/entidades
        // solo devuelve activos por defecto, y el reporte necesita mostrar el
        // estado real de cada registro (columna "Estado").
        const [activosCatastro, inactivosCatastro] = await Promise.all([
          fetchJson("/admin/entidades", { ...filtroTipo, activo: true }),
          fetchJson("/admin/entidades", { ...filtroTipo, activo: false }),
        ]);
        const entidadesCatastro = [...(activosCatastro || []), ...(inactivosCatastro || [])];
        const tipoLabelDe = (e) => {
          const roles = [];
          if (e.es_cliente) roles.push("Cliente");
          if (e.es_proveedor) roles.push("Proveedor");
          if (e.es_empleado) roles.push("Empleado");
          return roles.join(" / ") || "-";
        };
        const filasCatastro = (entidadesCatastro || []).slice().sort((a, b) => (a.nombre || "").localeCompare(b.nombre || ""));
        const totalActivos = filasCatastro.filter(e => e.activo !== false).length;
        const totalInactivos = filasCatastro.length - totalActivos;
        const tipoTitulo = { clientes: "Clientes", proveedores: "Proveedores", empleados: "Empleados" }[catastroTipo];
        report = {
          title: `Catastro Unificado${tipoTitulo ? ` - ${tipoTitulo}` : ""}`,
          subtitle: periodoSub,
          summary: [
            ["Registros", filasCatastro.length],
            ["Activos", totalActivos],
            ["Inactivos", totalInactivos],
            ["Clientes", filasCatastro.filter(e => e.es_cliente).length],
            ["Proveedores", filasCatastro.filter(e => e.es_proveedor).length],
            ["Empleados", filasCatastro.filter(e => e.es_empleado).length],
          ],
          sections: [
            {
              title: "Catastro de entidades",
              totalLabel: "Total",
              total: String(filasCatastro.length),
              columns: ["ID", "Nombre / Razón Social", "Documento", "Teléfono", "Correo", "Tipo", "Estado"],
              rows: filasCatastro.map(e => [
                e.id || "-",
                [e.nombre, e.apellido].filter(Boolean).join(" ") || "-",
                e.ruc || "-",
                e.telefono || "-",
                e.email || "-",
                tipoLabelDe(e),
                e.activo === false ? "Inactivo" : "Activo",
              ]),
            },
          ],
        };
      } else if (tipo === "balancete_obra") {
        const data = await fetchJson("/admin/reportes/balancete-obra", {
          cliente_id: balObraClienteId || undefined,
          tipo_factura_ids: balObraTipoFacturaIds.length > 0 ? balObraTipoFacturaIds.join(",") : undefined,
          desde: balObraDesde || undefined,
          hasta: balObraHasta || undefined,
        });
        const obras = data.obras || [];
        setBalanceteObraChart(obras);
        const porCliente = new Map();
        obras.forEach(o => {
          const key = o.cliente_nombre || "Sin cliente asignado";
          if (!porCliente.has(key)) porCliente.set(key, []);
          porCliente.get(key).push(o);
        });
        const totalCobrado = obras.reduce((s, o) => s + (o.total_cobrado || 0), 0);
        const totalMateriales = obras.reduce((s, o) => s + (o.costo_materiales || 0), 0);
        const totalPersonal = obras.reduce((s, o) => s + (o.costo_personal || 0), 0);
        const totalGanancia = obras.reduce((s, o) => s + (o.ganancia_neta || 0), 0);
        const margenPromedio = totalCobrado > 0 ? (totalGanancia / totalCobrado) * 100 : 0;
        const rangoLabel = (balObraDesde || balObraHasta) ? `${balObraDesde || "…"} al ${balObraHasta || "…"}` : "Todo el historial";
        // Si se eligieron obras puntuales pero ninguna aparece en el resultado,
        // lo más probable es que esa(s) obra(s) ya no pertenezcan a la empresa
        // activa (el selector se recarga por empresa, pero un cambio de empresa
        // justo antes de generar podía dejar seleccionado un id de otra
        // empresa) — antes esto se veía como un reporte "todo en ₲0" sin
        // explicación; ahora se avisa explícitamente.
        const avisoObraNoEncontrada = balObraTipoFacturaIds.length > 0 && obras.length === 0
          ? [["Aviso", `Ninguna de las obras seleccionadas pertenece a "${activeEmpresaPropia?.nombre || activeEmpresaPropia?.slug || "la empresa activa"}" (o fue eliminada). Volvé a elegir la obra en el filtro de abajo.`]]
          : [];
        report = {
          title: "Balancete - Obra",
          subtitle: `${periodoSub} · Rango: ${rangoLabel}`,
          summary: [
            ...avisoObraNoEncontrada,
            ["Obras", obras.length],
            ["Total facturado", fmtPYG(totalCobrado)],
            ["Costo materiales", fmtPYG(totalMateriales)],
            ["Costo personal", fmtPYG(totalPersonal)],
            ["Ganancia/Pérdida neta", fmtPYG(totalGanancia)],
            ["Margen promedio", `${margenPromedio.toFixed(1)}%`],
          ],
          sections: Array.from(porCliente.entries()).map(([clienteNombre, obrasCliente]) => {
            const gananciaCliente = obrasCliente.reduce((s, o) => s + (o.ganancia_neta || 0), 0);
            return {
              title: `Cliente: ${clienteNombre}`,
              totalLabel: "Ganancia/Pérdida neta",
              total: fmtPYG(gananciaCliente),
              columns: ["Obra", "Facturado (esté o no cobrado)", "Costo materiales", "Costo personal", "Ganancia/Pérdida neta", "Margen %"],
              rows: obrasCliente.map(o => [
                o.obra_nombre || "-",
                fmtPYG(o.total_cobrado),
                fmtPYG(o.costo_materiales),
                fmtPYG(o.costo_personal),
                fmtPYG(o.ganancia_neta),
                `${(o.margen_utilidad_pct ?? 0).toFixed(1)}%`,
              ]),
            };
          }),
        };
      } else if (tipo === "garantias") {
        const tipoParam = garantiasTipo !== "todas" ? garantiasTipo : undefined;
        const data = await fetchJson("/admin/garantias", {
          tipo: tipoParam,
          estado_documento: garantiasEstadoDoc || undefined,
          fecha_desde: garantiasDesde || undefined,
          fecha_hasta: garantiasHasta || undefined,
        });
        const lista = data.garantias || [];
        const kpis = data.kpis || {};
        const recibidas = lista.filter(g => g.tipo_garantia === "recibida");
        const concedidas = lista.filter(g => g.tipo_garantia === "concedida");
        const rangoLabel = (garantiasDesde || garantiasHasta) ? `${garantiasDesde || "…"} al ${garantiasHasta || "…"}` : "Todo el historial";
        const filaGarantia = (g) => [
          g.razon_social || "-",
          `${g.doc_numero || "-"} (cuota ${g.numero})`,
          g.fecha_vencimiento || "-",
          g.moneda === "USD" ? fmtUSD(g.monto) : fmtPYG(g.monto),
          { pendiente: "Pendiente", pagado: "Pagado", vencido: "Vencido", cancelado: "Cancelado" }[g.estado] || g.estado,
          { vigente: "Vigente", cobrado: "Cobrado", cancelado: "Cancelado", anulado: "Anulado", destruido: "Destruido" }[g.estado_documento] || g.estado_documento,
          g.numero_pagare || "-",
          g.tipo_documento_firmante || "-",
        ];
        const sections = [];
        if (garantiasTipo !== "concedidas") {
          sections.push({
            title: "Garantías Recibidas (clientes)",
            totalLabel: "Cantidad",
            total: String(recibidas.length),
            columns: ["Cliente", "Documento", "Vence", "Monto", "Estado pago", "Estado documento", "N° Pagaré", "Tipo doc."],
            rows: recibidas.map(filaGarantia),
          });
        }
        if (garantiasTipo !== "recibidas") {
          sections.push({
            title: "Garantías Concedidas (proveedores)",
            totalLabel: "Cantidad",
            total: String(concedidas.length),
            columns: ["Proveedor", "Documento", "Vence", "Monto", "Estado pago", "Estado documento", "N° Pagaré", "Tipo doc."],
            rows: concedidas.map(filaGarantia),
          });
        }
        report = {
          title: "Garantías",
          subtitle: `${periodoSub} · Rango: ${rangoLabel}`,
          summary: [
            ["Recibidas vigentes", fmtPYG(kpis.recibidas?.monto_vigente_pyg || 0)],
            ["Recibidas vencidas", `${kpis.recibidas?.count_vencidas || 0} (${fmtPYG(kpis.recibidas?.monto_vencidas_pyg || 0)})`],
            ["Concedidas vigentes", fmtPYG(kpis.concedidas?.monto_vigente_pyg || 0)],
            ["Concedidas vencidas", `${kpis.concedidas?.count_vencidas || 0} (${fmtPYG(kpis.concedidas?.monto_vencidas_pyg || 0)})`],
          ],
          sections,
        };
      } else if (tipo === "iva") {
        const data = await fetchJson("/admin/balance/iva", { periodo: reportMonth });
        report = {
          title: `IVA fiscal - ${mesLabel(reportMonth)}`,
          subtitle: periodoSub,
          summary: [
            ["Debito", fmtPYG(data.iva_debito), [
              ["IVA 5%",  fmtPYG(data.debito_iva_5  || 0)],
              ["IVA 10%", fmtPYG(data.debito_iva_10 || 0)],
              ["Exenta",  fmtPYG(data.debito_excenta || 0)],
            ]],
            ["Credito", fmtPYG(data.iva_credito), [
              ["IVA 5%",  fmtPYG(data.credito_iva_5  || 0)],
              ["IVA 10%", fmtPYG(data.credito_iva_10 || 0)],
              ["Exenta",  fmtPYG(data.credito_excenta || 0)],
            ]],
            ["Pagado", fmtPYG(data.pagos_iva_mes)],
            ["Saldo actual", fmtPYG(data.saldo_pendiente_acumulado)],
          ],
          accountSummary: resumenCuentasDesdeItems(data.pagos_iva_detalle || [], cuentaResolver, r => montoCuenta(r, ["monto"]), "Pagos IVA por banco/cuenta"),
          sections: [
            { title: "Debito fiscal", columns: ["Factura", "Cliente", "Monto factura", "IVA"], rows: (data.detalle_debito || []).map(r => {
                const bk = r.iva_breakdown || [];
                return {
                  cells: [r.numero || "-", r.razon_social || "-", fmtPYG(r.monto_factura), fmtPYG(r.iva_pyg)],
                  subRows: bk.map(b => ["", b.tasa, fmtPYG(b.base), b.iva > 0 ? fmtPYG(b.iva) : "—"]),
                };
              })},
            { title: "Credito fiscal", columns: ["Factura", "Proveedor", "Monto compra", "IVA"], rows: (data.detalle_credito || []).map(r => {
                const bk = r.iva_breakdown || [];
                return {
                  cells: [r.numero_factura || r.factura_numero || "-", r.proveedor_nombre || "-", fmtPYG(r.monto_compra), fmtPYG(r.iva_pyg)],
                  subRows: bk.map(b => ["", b.tasa, fmtPYG(b.base), b.iva > 0 ? fmtPYG(b.iva) : "—"]),
                };
              })},
            ...sectionsPorCuenta(data.pagos_iva_detalle || [], { title: "Pagos IVA por cuenta", totalLabel: "Total pagado", columns: ["Fecha", "Periodo IVA", "Descripcion", "Monto"] }, cuentaResolver, (r, cuenta) => [r.fecha_pago || r.fecha || "-", r.periodo_iva || "-", r.descripcion || "-", fmtCuentaMonto(montoCuenta(r, ["monto"]), cuenta)], r => montoCuenta(r, ["monto"])),
            { title: "Historial anual", columns: ["Periodo", "Debito", "Credito", "Neto", "Pagado"], rows: rowsOf(data.historial_meses || [], [{value:"periodo"}, {value: r => fmtPYG(r.debito)}, {value: r => fmtPYG(r.credito)}, {value: r => fmtPYG(r.neto)}, {value: r => fmtPYG(r.pagos_iva)}]) },
          ],
        };
      } else if (tipo === "balance_gerencial") {
        const data = await fetchJson("/admin/reportes/balance-gerencial", { periodo: reportMonth });
        const act = data.activo || {};
        const pas = data.pasivo || {};
        const pat = data.patrimonio || {};
        const res = data.resultados || {};
        const cuadre = data.cuadre || {};
        const kpis = data.kpis || {};
        const obl = pas.obligaciones_detalle || {};
        const semaforoLabel = { verde: "🟢 Verde", amarillo: "🟡 Amarillo", rojo: "🔴 Rojo" };
        const kpiRow = (nombre, k) => k ? [nombre, `${k.valor}%`, semaforoLabel[k.semaforo] || k.semaforo || "-", k.descripcion || "-"] : [nombre, "-", "-", "-"];
        report = {
          title: `Balance Gerencial - ${mesLabel(reportMonth)}`,
          subtitle: `${periodoSub} · No fiscal — método del devengado`,
          summary: [
            ["Activo total", fmtPYG(act.total)],
            ["Pasivo total", fmtPYG(pas.total)],
            ["Patrimonio neto", fmtPYG(pat.total)],
            ["Diferencia de cuadre", fmtPYG(cuadre.diferencia)],
            ["Resultado del ejercicio", fmtPYG(res.resultado_neto)],
            ["Morosidad (CxC vencido)", `${kpis.morosidad?.valor ?? 0}% ${semaforoLabel[kpis.morosidad?.semaforo] || ""}`, [
              ["Monto vencido", fmtPYG(act.cuentas_por_cobrar_vencidas)],
              ["Total CxC", fmtPYG(act.cuentas_por_cobrar)],
            ]],
          ],
          sections: [
            {
              title: "Activo",
              totalLabel: "Total Activo",
              total: fmtPYG(act.total),
              columns: ["Concepto", "Monto (PYG)"],
              rows: [
                ["Disponibilidades (bancos/caja)", fmtPYG(act.disponibilidades)],
                ["Cuentas por Cobrar", fmtPYG(act.cuentas_por_cobrar)],
                ["  de las cuales, Vencidas", fmtPYG(act.cuentas_por_cobrar_vencidas)],
                ["Inventarios (a CPP vigente)", fmtPYG(act.inventarios)],
                ["Bienes de Uso", fmtPYG(act.bienes_de_uso)],
              ],
            },
            {
              title: "Pasivo",
              totalLabel: "Total Pasivo",
              total: fmtPYG(pas.total),
              columns: ["Concepto", "Monto (PYG)"],
              rows: [
                ["Proveedores a pagar", fmtPYG(pas.proveedores)],
                ["Obligaciones pendientes", fmtPYG(pas.obligaciones_pendientes)],
                ["  Costos fijos impagos", fmtPYG(obl.costos_fijos_impagos)],
                ["  Sueldos impagos", fmtPYG(obl.sueldos_impagos)],
                ["  Aporte patronal IPS (provisión)", fmtPYG(obl.aporte_patronal_ips)],
                ["  Provisión de aguinaldo", fmtPYG(obl.provision_aguinaldo)],
              ],
            },
            {
              title: "Patrimonio",
              totalLabel: "Total Patrimonio",
              total: fmtPYG(pat.total),
              columns: ["Concepto", "Monto (PYG)"],
              rows: [
                ["Capital social", fmtPYG(pat.capital_social)],
                ["Resultado del ejercicio", fmtPYG(pat.resultado_del_ejercicio)],
              ],
            },
            {
              title: "Resultado del ejercicio (P&L del período)",
              totalLabel: "Resultado neto",
              total: fmtPYG(res.resultado_neto),
              columns: ["Concepto", "Monto (PYG)"],
              rows: [
                ["Ventas totales", fmtPYG(res.ventas_totales)],
                ["Costo de Mercadería Vendida (CMV)", fmtPYG(res.cmv)],
                ["Utilidad bruta", fmtPYG(res.utilidad_bruta)],
                ["Gastos operativos", fmtPYG(res.gastos_operativos)],
                ["Costo de personal (+ IPS + aguinaldo)", fmtPYG(res.costo_personal)],
              ],
            },
            {
              title: "Cuadro de mando — KPIs",
              totalLabel: "Cuadra (Activo = Pasivo + Patrimonio)",
              total: cuadre.cuadra ? "Sí" : `No (dif. ${fmtPYG(cuadre.diferencia)})`,
              columns: ["Indicador", "Valor", "Semáforo", "Descripción"],
              rows: [
                kpiRow("Independencia financiera", kpis.independencia_financiera),
                kpiRow("Endeudamiento", kpis.endeudamiento),
                kpiRow("Margen bruto", kpis.margen_bruto),
                kpiRow("Margen neto", kpis.margen_neto),
                kpiRow("Liquidez inmediata", kpis.liquidez_inmediata),
                kpiRow("Morosidad", kpis.morosidad),
              ],
            },
          ],
        };
      } else if (tipo === "stock_historial") {
        const stockParams = {};
        if (stockHistorialProductoId) stockParams.producto_id = stockHistorialProductoId;
        if (stockHistorialDesde) stockParams.desde = stockHistorialDesde;
        if (stockHistorialHasta) stockParams.hasta = stockHistorialHasta;
        const data = await fetchJson("/admin/stock-movimientos", stockParams);
        // Un "ajuste" no es un tipo de movimiento propio (a nivel de stock siempre
        // es entrada o salida) — se identifica por el MOTIVO, que es el que elige
        // el usuario en el modal "Ajuste de stock manual" de Productos (ajuste de
        // inventario, donación, rotura/merma, vencimiento, uso interno, corrección
        // de inventario físico, otro). Antes esto se buscaba con x.tipo === "ajuste",
        // que nunca ocurre para estos movimientos, así que siempre daba 0.
        const MOTIVOS_AJUSTE_MANUAL_STOCK = new Set(["ajuste_manual", "donacion", "rotura_merma", "vencimiento", "uso_interno", "correccion_inventario", "otro"]);
        const esAjusteManualMov = (x) => x.tipo === "ajuste" || MOTIVOS_AJUSTE_MANUAL_STOCK.has(x.motivo);
        report = { title: "Historial de stock", subtitle: periodoSub, summary: [["Movimientos", data.length], ["Entradas", data.filter(x => x.tipo === "entrada" && !esAjusteManualMov(x)).length], ["Salidas", data.filter(x => x.tipo === "salida" && !esAjusteManualMov(x)).length], ["Ajustes", data.filter(esAjusteManualMov).length]], sections: [{ title: "Movimientos", columns: ["Fecha", "Producto", "Variante", "SKU", "Tipo", "Cantidad", "Stock", "Motivo", "Usuario"], rows: rowsOf(data, [{value:"fecha"}, {value:"producto_nombre"}, {value: r => r.variante_texto || "-"}, {value:"sku"}, {value:"tipo"}, {value:"cantidad"}, {value: r => `${r.stock_anterior ?? "-"} -> ${r.stock_nuevo ?? "-"}`}, {value:"motivo"}, {value:"usuario_nombre"}]) }] };
      } else if (tipo === "productos_stock") {
        const stockParams = {};
        if (stockDesde && stockHasta && stockDesde > stockHasta) { toast.error("La fecha inicial no puede ser posterior a la final"); setGenericReportLoading(false); return; }
        if (stockDesde) stockParams.desde = stockDesde;
        if (stockHasta) stockParams.hasta = stockHasta;
        if (stockMoneda) stockParams.moneda = stockMoneda;
        if (stockDesde && stockTodos) stockParams.todos = "true";
        if (stockCategoriasSel.length > 0) stockParams.categorias = JSON.stringify(stockCategoriasSel);
        if (stockSubcategoria) stockParams.subcategoria = stockSubcategoria;
        if (stockProductoId) stockParams.producto_id = stockProductoId;
        if (stockProveedorId) stockParams.proveedor_id = stockProveedorId;
        if (stockDepositoId && !stockHasta) stockParams.deposito_id = stockDepositoId;
        const filtrosAttr = Object.fromEntries(Object.entries(stockAtributosFiltro || {}).filter(([, v]) => v));
        if (Object.keys(filtrosAttr).length > 0) stockParams.atributos = JSON.stringify(filtrosAttr);
        const data = await fetchJson("/admin/reportes/productos-stock", stockParams);

        // Separar por moneda — soporta PYG, USD y BRL (cualquier producto con
        // moneda distinta de PYG/USD/BRL cae en PYG por defecto, igual que
        // hacía antes de este fix, pero ya no se formatea con el símbolo ₲
        // si en realidad es USD o BRL).
        const prodPYG = data.filter(p => !p.moneda || p.moneda === "PYG");
        const prodUSD = data.filter(p => p.moneda === "USD");
        const prodBRL = data.filter(p => p.moneda === "BRL");

        const conPeriodo = !!stockDesde;
        const cols = conPeriodo
          ? ["Producto", "SKU", "Categoría", "Atributos", "Depósito", "Stock inicial", "Entradas", "Salidas", "Stock final", "Mínimo", "Costo", "Precio venta"]
          : ["Producto", "SKU", "Categoría", "Atributos", "Depósito", "Stock", "Mínimo", "Costo", "Precio venta"];
        const buildRows = (arr) => rowsOf(arr, [
          {value:"nombre"}, {value:"sku"}, {value:"categoria"}, {value: r => r.atributos_texto || "-"}, {value: r => r.deposito || "-"},
          ...(conPeriodo ? [{value: r => r.stock_inicial ?? 0}, {value: r => r.entradas_periodo ?? 0}, {value: r => r.salidas_periodo ?? 0}] : []),
          {value:"stock_actual"}, {value:"stock_minimo"},
          {value: r => fmtMonedaReporte(r.precio_costo, r.moneda)},
          {value: r => fmtMonedaReporte(r.precio_venta, r.moneda)},
        ]);

        const valorPYG = prodPYG.reduce((s, p) => s + Number(p.stock_actual || 0) * Number(p.precio_costo || 0), 0);
        const valorUSD = prodUSD.reduce((s, p) => s + Number(p.stock_actual || 0) * Number(p.precio_costo || 0), 0);
        const valorBRL = prodBRL.reduce((s, p) => s + Number(p.stock_actual || 0) * Number(p.precio_costo || 0), 0);
        const ventaDe = (arr) => arr.reduce((s, p) => s + Number(p.stock_actual || 0) * Number(p.precio_venta || 0), 0);
        const ventaPYG = ventaDe(prodPYG), ventaUSD = ventaDe(prodUSD), ventaBRL = ventaDe(prodBRL);
        // Totales del período (solo cuando hay "Desde"): cantidades y valores de lo que se movió.
        const sumQ = (arr, k) => arr.reduce((a, p) => a + Number(p[k] || 0), 0);
        const costoMov = (arr, k) => arr.reduce((a, p) => a + Number(p[k] || 0) * Number(p.precio_costo || 0), 0);
        const ventaMov = (arr, k) => arr.reduce((a, p) => a + Number(p[k] || 0) * Number(p.precio_venta || 0), 0);
        const fmtNum0 = (n) => Number(n || 0).toLocaleString("es-PY", { maximumFractionDigits: 2 });
        const movProds = data.filter(p => Number(p.entradas_periodo || 0) > 0 || Number(p.salidas_periodo || 0) > 0);
        const hoyTxt = new Date().toLocaleDateString("es-PY", { day: "2-digit", month: "2-digit", year: "numeric" });
        const periodoTxt = stockDesde ? `del ${stockDesde} al ${stockHasta || hoyTxt}` : (stockHasta ? `al ${stockHasta}` : "");
        const tituloSub = periodoTxt || "Stock actual";

        // Con período y sin "todos": dos grupos — creados en el rango y, aparte, los que
        // tuvieron movimiento en el rango pero se crearon fuera de él.
        const agrupar = conPeriodo && !stockTodos;
        const grupos = agrupar
          ? [
              { label: "Productos creados en el período", arr: data.filter(p => p.seccion === "creados") },
              { label: "Con movimiento en el período (creados fuera del rango)", arr: data.filter(p => p.seccion === "movimiento_otros") },
            ]
          : [{ label: "", arr: data }];
        const seccionesDe = (arr, label) => {
          const pyg = arr.filter(p => !p.moneda || p.moneda === "PYG");
          const usd = arr.filter(p => p.moneda === "USD");
          const brl = arr.filter(p => p.moneda === "BRL");
          const varias = [pyg.length > 0, usd.length > 0, brl.length > 0].filter(Boolean).length > 1;
          const pre = label ? `${label} — ` : "";
          if (!varias) return [{ title: `${pre || "Inventario actual — "}${arr.length} prod.`.replace(/ — $/, ""), columns: cols, rows: buildRows(arr) }];
          return [
            ...(pyg.length > 0 ? [{ title: `${pre}Guaraníes (${pyg.length} prod.)`, columns: cols, rows: buildRows(pyg) }] : []),
            ...(usd.length > 0 ? [{ title: `${pre}Dólares (${usd.length} prod.)`, columns: cols, rows: buildRows(usd) }] : []),
            ...(brl.length > 0 ? [{ title: `${pre}Reales (${brl.length} prod.)`, columns: cols, rows: buildRows(brl) }] : []),
          ];
        };
        let sections = grupos.flatMap(g => (g.arr.length > 0 || !agrupar) ? seccionesDe(g.arr, g.label) : []);
        if (agrupar && sections.length === 0) {
          sections = [{ title: "Sin productos creados ni movimientos en el período", columns: cols, rows: [] }];
        }

        const hoy = new Date().toLocaleDateString("es-PY", { day: "2-digit", month: "2-digit", year: "numeric" });
        report = {
          title: "Productos y stock",
          subtitle: tituloSub,
          periodLabel: periodoTxt || `al ${hoy}`,
          summary: conPeriodo ? [
            // Con período: SOLO totales del período (lo que se movió entre Desde y Hasta).
            // El valor general del stock no se muestra acá (eso es el reporte sin fechas).
            ...(agrupar ? [["Productos creados en el período", data.filter(p => p.seccion === "creados").length]] : []),
            ["Productos con movimiento", movProds.length],
            ["Unidades entradas", fmtNum0(sumQ(data, "entradas_periodo"))],
            ["Unidades salidas", fmtNum0(sumQ(data, "salidas_periodo"))],
            ["Costo entradas (₲)", fmtPYG(costoMov(prodPYG, "entradas_periodo"))],
            ["Venta entradas (₲)", fmtPYG(ventaMov(prodPYG, "entradas_periodo"))],
            ["Costo salidas (₲)", fmtPYG(costoMov(prodPYG, "salidas_periodo"))],
            ["Venta salidas (₲)", fmtPYG(ventaMov(prodPYG, "salidas_periodo"))],
            ...(prodUSD.length > 0 ? [
              ["Costo entradas ($)", fmtUSD(costoMov(prodUSD, "entradas_periodo"))],
              ["Venta entradas ($)", fmtUSD(ventaMov(prodUSD, "entradas_periodo"))],
              ["Costo salidas ($)", fmtUSD(costoMov(prodUSD, "salidas_periodo"))],
              ["Venta salidas ($)", fmtUSD(ventaMov(prodUSD, "salidas_periodo"))],
            ] : []),
            ...(prodBRL.length > 0 ? [
              ["Costo entradas (R$)", fmtBRL(costoMov(prodBRL, "entradas_periodo"))],
              ["Venta entradas (R$)", fmtBRL(ventaMov(prodBRL, "entradas_periodo"))],
              ["Costo salidas (R$)", fmtBRL(costoMov(prodBRL, "salidas_periodo"))],
              ["Venta salidas (R$)", fmtBRL(ventaMov(prodBRL, "salidas_periodo"))],
            ] : []),
          ] : [
            ["Productos", data.length],
            ["Stock bajo", data.filter(x => Number(x.stock_actual || 0) <= Number(x.stock_minimo || 0) && Number(x.stock_minimo || 0) > 0).length],
            ["Valor costo (₲)", fmtPYG(valorPYG)],
            ...(prodUSD.length > 0 ? [["Valor costo ($)", fmtUSD(valorUSD)]] : []),
            ...(prodBRL.length > 0 ? [["Valor costo (R$)", fmtBRL(valorBRL)]] : []),
            ["Valor venta (₲)", fmtPYG(ventaPYG)],
            ...(prodUSD.length > 0 ? [["Valor venta ($)", fmtUSD(ventaUSD)]] : []),
            ...(prodBRL.length > 0 ? [["Valor venta (R$)", fmtBRL(ventaBRL)]] : []),
          ],
          sections,
        };
      } else if (tipo === "descuentos_productos") {
        const params = {};
        if (descuentoEstado && descuentoEstado !== "todos") params.estado = descuentoEstado;
        const data = await fetchJson("/admin/reportes/descuentos-productos", params);
        const hoy2 = new Date().toISOString().slice(0, 10);
        const vigentes = data.filter(p => p.descuento_vence ? p.descuento_vence >= hoy2 : true);
        const vencidos = data.filter(p => p.descuento_vence && p.descuento_vence < hoy2);
        report = {
          title: "Descuentos en productos",
          subtitle: descuentoEstado === "todos" ? "Todos" : descuentoEstado === "vigente" ? "Vigentes" : "Vencidos",
          periodLabel: new Date().toLocaleDateString("es-PY", { day: "2-digit", month: "2-digit", year: "numeric" }),
          summary: [
            ["Total", data.length],
            ["Vigentes", vigentes.length],
            ["Vencidos", vencidos.length],
          ],
          sections: [{
            title: "Productos con descuento habilitado",
            columns: ["Producto", "SKU", "Categoría", "Desc. máx. %", "Vence", "Estado"],
            rows: rowsOf(data, [
              { value: "nombre" },
              { value: r => r.sku || "-" },
              { value: r => r.categoria || "-" },
              { value: r => r.descuento_maximo != null ? `${r.descuento_maximo}%` : "-" },
              { value: r => r.descuento_vence || "-" },
              { value: r => {
                  if (!r.descuento_vence) return "Sin vencimiento";
                  return r.descuento_vence >= hoy2 ? "Vigente" : "Vencido";
                }
              },
            ]),
          }],
        };
      } else if (tipo === "vencimiento_productos") {
        const params = {};
        if (vencProdEstado && vencProdEstado !== "todos") params.estado = vencProdEstado;
        const data = await fetchJson("/admin/reportes/vencimiento-productos", params);
        const vencidos = data.filter(p => p.estado === "vencido");
        const porVencer = data.filter(p => p.estado === "por_vencer");
        const okItems = data.filter(p => p.estado === "ok");
        const estadoLabel = { vencido: "Vencido", por_vencer: "Por vencer", ok: "OK" };
        report = {
          title: "Vencimiento de productos",
          subtitle: vencProdEstado === "todos" ? "Todos" : estadoLabel[vencProdEstado] || vencProdEstado,
          periodLabel: new Date().toLocaleDateString("es-PY", { day: "2-digit", month: "2-digit", year: "numeric" }),
          summary: [
            ["Total", data.length],
            ["Vencidos", vencidos.length],
            ["Por vencer (≤30 días)", porVencer.length],
            ["OK", okItems.length],
          ],
          sections: [{
            title: "Productos con fecha de vencimiento",
            columns: ["SKU", "Producto", "Cód. Fabricante", "Lote", "Vencimiento", "Días p/ vencer", "Stock", "Estado"],
            rows: rowsOf(data, [
              { value: r => r.sku || "-" },
              { value: "nombre" },
              { value: r => r.codigo_fabricante || "-" },
              { value: r => r.lote || "-" },
              { value: r => r.fecha_vencimiento || "-" },
              { value: r => r.dias_para_vencer != null ? r.dias_para_vencer : "-" },
              { value: r => r.stock_actual != null ? r.stock_actual : "-" },
              { value: r => estadoLabel[r.estado] || r.estado },
            ]),
          }],
        };
      } else if (tipo === "libro_ventas") {
        const params = { mes: reportMonth };
        if (libroVentasIncluirAnuladas) params.incluir_anuladas = true;
        const data = await fetchJson("/admin/reportes/libro-ventas", params);
        setLibroVentasData(data);
        const totalVenta = data.reduce((acc, r) => acc + (r.total || 0), 0);
        const totalIva = data.reduce((acc, r) => acc + (r.iva || 0), 0);
        const contado = data.filter(r => r.forma_pago === "contado").length;
        const credito = data.filter(r => r.forma_pago === "credito").length;
        report = {
          title: "Libro Ventas",
          subtitle: `${mesLabel(reportMonth)} · ${periodoSub}${libroVentasIncluirAnuladas ? " · Incluye anuladas" : ""}`,
          periodLabel: mesLabel(reportMonth),
          summary: [
            ["Facturas", data.length],
            ["Total ventas", fmtPYG(totalVenta)],
            ["IVA", fmtPYG(totalIva)],
            ["Contado / Crédito", `${contado} / ${credito}`],
          ],
          sections: [{
            title: "Facturas emitidas",
            columns: ["RUC", "Cliente", "Fecha", "N° Factura", "Total", "Cond. pago", "IVA", "Notas", "Estado"],
            rows: rowsOf(data, [
              { value: r => r.ruc || "-" },
              { value: r => r.cliente || "-" },
              { value: r => r.fecha || "-" },
              { value: r => r.numero || "-" },
              { value: r => fmtMonedaReporte(r.total, r.moneda) },
              { value: r => r.forma_pago === "credito" ? "Crédito" : "Contado" },
              { value: r => fmtMonedaReporte(r.iva, r.moneda) },
              { value: r => r.notas || "-" },
              { value: r => r.anulada ? "Anulado" : "Activo" },
            ]),
          }],
        };
      } else if (tipo === "comisiones_vendedores") {
        // Formato "Gs. X.XXX.XXX" — específico de este reporte (el resto del
        // sistema usa el símbolo ₲ vía fmtPYG, pero este reporte replica el
        // mockup provisto por el cliente que usa el prefijo "Gs.").
        const fmtGs = (n) => `Gs. ${Math.round(Math.abs(Number(n || 0))).toLocaleString("es-PY")}`;
        const fmtGsFirmado = (n) => (Number(n || 0) > 0 ? `- ${fmtGs(n)}` : fmtGs(n));
        const params = { desde: comisionDesde, hasta: comisionHasta };
        if (comisionVendedorId) params.vendedor_id = comisionVendedorId;
        const data = await fetchJson("/admin/reportes/comisiones-vendedores", params);
        const filas = data?.filas || [];
        const resumen = data?.resumen || {};
        report = {
          title: "Comisiones de Vendedores",
          subtitle: `Del ${comisionDesde} al ${comisionHasta} · ${periodoSub}`,
          periodLabel: `${comisionDesde} — ${comisionHasta}`,
          summary: [
            ["Total Ventas Brutas", fmtGs(resumen.total_ventas)],
            ["Total Notas Crédito", fmtGsFirmado(resumen.total_notas_credito)],
            ["Base Neto Comisionable", fmtGs(resumen.base_neto_comisionable)],
            ["Total Comisiones Netas", fmtGs(resumen.total_comisiones_netas)],
          ],
          sections: [{
            title: "Movimientos del período asignado",
            columns: ["Vendedor", "Total Ventas (FAC)", "Notas de Crédito (NCR)", "Base Comisionable", "% Com.", "Liquidación Neta"],
            rows: rowsOf(filas, [
              { value: r => r.vendedor_nombre || "-" },
              { value: r => fmtGs(r.total_ventas) },
              { value: r => fmtGsFirmado(r.notas_credito) },
              { value: r => fmtGs(r.base_comisionable) },
              { value: r => `${Number(r.pct_comision || 0).toFixed(1)}%` },
              { value: r => fmtGs(r.liquidacion_neta) },
            ]),
          }],
        };
      } else if (tipo === "costo_hectarea") {
        const data = await fetchJson("/admin/reportes/costo-por-hectarea", { desde: costoHaDesde, hasta: costoHaHasta, chacra: costoHaChacra });
        const monedas = data?.monedas || [];
        const nf = (n, d = 2) => Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: d, maximumFractionDigits: d });
        const sym = (m) => (m === "USD" ? "USD" : m === "BRL" ? "R$" : "₲");
        report = {
          title: "Reporte de Costo por Hectárea",
          subtitle: `Resumen de costos de insumos aplicados por chacra · Del ${costoHaDesde} al ${costoHaHasta} · ${periodoSub}`,
          periodLabel: `${costoHaDesde} — ${costoHaHasta}`,
          summary: [
            ["Recetas consideradas", data?.recetas || 0],
            ...monedas.flatMap(m => [
              [`Superficie total (${m.moneda})`, `${nf(m.superficie_ha)} ha`],
              [`Costo total (${m.moneda})`, `${sym(m.moneda)} ${nf(m.total_costo)}`],
              [`Costo por ha (${m.moneda})`, m.costo_por_ha == null ? "-" : `${sym(m.moneda)} ${nf(m.costo_por_ha)}`],
            ]),
            ...((data?.sin_costo || []).length ? [["Insumos sin costo cargado", (data.sin_costo || []).join(", ")]] : []),
          ],
          sections: monedas.length ? monedas.map(m => ({
            title: `Resumen de Costos de Insumos Aplicados por Chacra — ${m.moneda}`,
            columns: ["Chacra (Área)", "Superficie (ha)", "Insumo Aplicado", "Cantidad Total", `Costo Total (${m.moneda})`, `Costo por Ha (${m.moneda}/ha)`],
            rows: [
              ...rowsOf(m.filas, [
                { value: r => r.chacra },
                { value: r => nf(r.superficie_ha) },
                { value: r => r.insumo },
                { value: r => `${nf(r.cantidad)} ${r.unidad || ""}`.trim() },
                { value: r => nf(r.costo_total) },
                { value: r => r.costo_por_ha == null ? "-" : nf(r.costo_por_ha) },
              ]),
              ["TOTAL GENERAL", nf(m.superficie_ha), "--", "--", nf(m.total_costo), m.costo_por_ha == null ? "-" : nf(m.costo_por_ha)],
            ],
          })) : [{ title: "Sin recetas con insumos en el período seleccionado", columns: ["Chacra (Área)", "Superficie (ha)", "Insumo Aplicado", "Cantidad Total", "Costo Total", "Costo por Ha"], rows: [] }],
        };
      } else if (tipo === "gastos_categoria") {
        const params = { desde: gastosCategoriaDesde, hasta: gastosCategoriaHasta };
        if (gastosCategoriaSel) params.categoria = gastosCategoriaSel;
        const data = await fetchJson("/admin/reportes/gastos-por-categoria", params);
        const categorias = data?.categorias || [];
        const totalGeneral = data?.total_general || 0;
        report = {
          title: "Gastos por Categoría",
          subtitle: `Del ${gastosCategoriaDesde} al ${gastosCategoriaHasta} · ${periodoSub}`,
          periodLabel: `${gastosCategoriaDesde} — ${gastosCategoriaHasta}`,
          summary: [
            ["Total general", fmtPYG(totalGeneral)],
            ["Categorías con gastos", categorias.length],
            ...categorias.slice(0, 4).map(c => [c.categoria, fmtPYG(c.total)]),
          ],
          sections: categorias.length ? categorias.map(c => ({
            title: `${c.categoria} — ${fmtPYG(c.total)} (${c.cantidad} pago${c.cantidad === 1 ? "" : "s"})`,
            columns: ["Gasto", "Fecha de pago", "Monto pagado", "Factura", "Notas"],
            rows: rowsOf(c.gastos, [
              { value: r => r.costo_nombre || "-" },
              { value: r => r.fecha_pago || "-" },
              { value: r => fmtMonedaReporte(r.monto_pagado, r.moneda) },
              { value: r => r.tiene_factura ? (r.nro_factura || "Sí") : "No" },
              { value: r => r.notas || "-" },
            ]),
          })) : [{
            title: "Sin gastos en el período seleccionado",
            columns: ["Gasto", "Fecha de pago", "Monto pagado", "Factura", "Notas"],
            rows: [],
          }],
        };
      } else if (tipo === "retenciones") {
        const params = { desde: retencionesDesde, hasta: retencionesHasta };
        const [dc, dv] = await Promise.all([
          retencionesOrigen !== "ventas" ? fetchJson("/admin/retenciones/practicadas", params) : Promise.resolve(null),
          retencionesOrigen !== "compras" ? fetchJson("/admin/retenciones/sufridas", params) : Promise.resolve(null),
        ]);
        const tiposComp = dc?.tipos_comprobante || {};
        const filasC = dc?.filas || [];
        const filasV = dv?.filas || [];
        const totTxt = (t) => Object.entries(t || {}).map(([m, v]) => fmtMonedaReporte(v, m)).join(" / ") || "-";
        const sections = [];
        if (dc) sections.push({
          title: `Retenciones practicadas a proveedores (compras) — ${totTxt(dc.totales)}`,
          columns: ["Fecha", "Proveedor", "RUC", "Documento", "Comprobante", "Base s/IVA", "IVA", "%", "Retención", "Pagado en efectivo"],
          rows: rowsOf(filasC, [
            { value: r => r.fecha || "-" },
            { value: r => r.proveedor || "-" },
            { value: r => r.ruc || "-" },
            { value: r => r.documento || "-" },
            { value: r => tiposComp[r.tipo_comprobante] || r.tipo_comprobante || "-" },
            { value: r => r.base == null ? "-" : fmtMonedaReporte(r.base, r.moneda) },
            { value: r => r.iva == null ? "-" : fmtMonedaReporte(r.iva, r.moneda) },
            { value: r => r.pct ?? "-" },
            { value: r => fmtMonedaReporte(r.retencion, r.moneda) },
            { value: r => fmtMonedaReporte(r.efectivo, r.moneda) },
          ]),
        });
        if (dv) sections.push({
          title: `Retenciones sufridas de clientes (ventas) — ${totTxt(dv.totales)}`,
          columns: ["Fecha", "Cliente", "RUC", "Factura", "Pago aplicado", "%", "Retención", "Cobrado en efectivo"],
          rows: rowsOf(filasV, [
            { value: r => r.fecha || "-" },
            { value: r => r.cliente || "-" },
            { value: r => r.ruc || "-" },
            { value: r => r.documento || "-" },
            { value: r => fmtMonedaReporte(r.pago, r.moneda) },
            { value: r => r.pct ?? "-" },
            { value: r => fmtMonedaReporte(r.retencion, r.moneda) },
            { value: r => fmtMonedaReporte(r.efectivo, r.moneda) },
          ]),
        });
        report = {
          title: "Retenciones de IVA",
          subtitle: `Del ${retencionesDesde} al ${retencionesHasta} · ${periodoSub}`,
          periodLabel: `${retencionesDesde} — ${retencionesHasta}`,
          summary: [
            ...(dc ? [["Practicadas a proveedores", totTxt(dc.totales)], ["Pagos con retención (compras)", filasC.length]] : []),
            ...(dv ? [["Sufridas de clientes", totTxt(dv.totales)], ["Cobros con retención (ventas)", filasV.length]] : []),
          ],
          sections,
        };
      } else if (tipo.startsWith("constructora_")) {
        if (!constructoraProyectoId) { toast.error("Elegí un proyecto"); return; }
        const sub = tipo.replace("constructora_", "");
        const data = await fetchJson(`/admin/constructora/reportes/${sub}`, { proyecto_id: constructoraProyectoId });
        const rc = buildReporteConstructora(sub, data);
        report = { ...rc, periodLabel: data?.proyecto?.numero || "" };
      } else if (tipo === "transporte_chofer" || tipo === "transporte_cliente") {
        const params ={ desde: transporteDesde, hasta: transporteHasta };
        if (transporteClienteId && tipo === "transporte_cliente") params.cliente_id = transporteClienteId;
        if (transporteChoferId && tipo === "transporte_chofer") params.chofer_id = transporteChoferId;
        const data = await fetchJson("/admin/transporte/reporte", params);
        const filas = data?.filas || [];
        const totales = data?.totales || {};
        const kg = (v) => (v == null || v === "") ? "-" : Number(v).toLocaleString("es-PY", { maximumFractionDigits: 3 });
        if (tipo === "transporte_chofer") {
          report = {
            title: "Transporte — Choferes",
            subtitle: `Del ${transporteDesde} al ${transporteHasta} · ${periodoSub}`,
            periodLabel: `${transporteDesde} — ${transporteHasta}`,
            summary: [
              ["Cantidad de órdenes", totales.cantidad_ordenes || 0],
              ["Diferencia de peso total (kg)", kg(totales.total_diferencia_peso)],
              ["Faltante a cobrar (Gs)", fmtPYG(totales.total_faltante_gs)],
              ["Comisión de choferes", fmtPYG(totales.total_comision_chofer)],
              ["Gastos de la carga", fmtPYG(totales.total_gastos_carga)],
              ["Neto a choferes (comisión − faltante)", fmtPYG(totales.total_neto_chofer)],
              ["Total liquidado a choferes", fmtPYG(totales.total_liquidado_chofer)],
            ],
            sections: [{
              title: "Órdenes de carga — datos del chofer",
              columns: ["N°", "Fecha", "Chofer", "Origen → Destino", "Remisión", "Peso salida (kg)", "Peso llegada (kg)", "Diferencia (kg)", "Faltante (Gs)", "Comisión", "Gastos carga", "Neto", "Liquidación"],
              rows: rowsOf(filas, [
                { value: r => r.numero || "-" },
                { value: r => r.fecha_carga || "-" },
                { value: r => r.chofer_nombre || "-" },
                { value: r => `${r.origen || "-"} → ${r.destino || "-"}` },
                { value: r => r.nro_remision || "-" },
                { value: r => kg(r.peso_salida) },
                { value: r => kg(r.peso_llegada) },
                { value: r => kg(r.diferencia_peso) },
                { value: r => fmtPYG(r.faltante_gs || 0) },
                { value: r => fmtMonedaReporte(r.chofer_comision, r.moneda) },
                { value: r => fmtMonedaReporte(r.gastos_chofer, r.moneda) },
                { value: r => fmtMonedaReporte(r.chofer_neto, r.moneda) },
                { value: r => r.chofer_liquidado ? fmtMonedaReporte(r.chofer_monto_liquidado, r.moneda) : "Pendiente" },
              ]),
            }],
          };
        } else {
          report = {
            title: "Transporte — Clientes",
            subtitle: `Del ${transporteDesde} al ${transporteHasta} · ${periodoSub}`,
            periodLabel: `${transporteDesde} — ${transporteHasta}`,
            summary: [
              ["Cantidad de órdenes", totales.cantidad_ordenes || 0],
              ["Diferencia de peso total (kg)", kg(totales.total_diferencia_peso)],
              ["Total fletes", fmtPYG(totales.total_flete)],
            ],
            sections: [{
              title: "Órdenes de carga — datos del cliente",
              columns: ["N°", "Fecha", "Cliente", "Origen → Destino", "Remisión", "Peso salida (kg)", "Peso llegada (kg)", "Diferencia (kg)", "Flete", "Factura"],
              rows: rowsOf(filas, [
                { value: r => r.numero || "-" },
                { value: r => r.fecha_carga || "-" },
                { value: r => r.cliente_nombre || "-" },
                { value: r => `${r.origen || "-"} → ${r.destino || "-"}` },
                { value: r => r.nro_remision || "-" },
                { value: r => kg(r.peso_salida) },
                { value: r => kg(r.peso_llegada) },
                { value: r => kg(r.diferencia_peso) },
                { value: r => fmtMonedaReporte(r.monto_flete, r.moneda) },
                { value: r => r.factura_numero || (r.factura_id ? "Sí" : "Sin facturar") },
              ]),
            }],
          };
        }
      } else if (tipo === "loteadora_precificacion") {
        if (!loteadoraPrecifLoteamientoId) {
          toast.error("Seleccioná un loteamiento");
          setGenericReportLoading(false);
          return;
        }
        const nCuotas = Math.max(1, parseInt(loteadoraPrecifNCuotas, 10) || 12);
        const data = await fetchJson("/admin/loteadora/reportes/precificacion", {
          loteamiento_id: loteadoraPrecifLoteamientoId,
          n_cuotas: nCuotas,
        });
        const manzanas = data.manzanas || [];
        const cols = [
          "Lote N°", "Área (m²)",
          "Precio contado (Gs.)", "Precio financiado (Gs.)", "Entrega mínima (Gs.)", "Valor cuota (Gs.)",
          "Precio contado (USD)", "Precio financiado (USD)", "Entrega mínima (USD)", "Valor cuota (USD)",
        ];
        report = {
          title: `Precificación por Manzana — ${data.loteamiento_nombre || ""}`,
          subtitle: `${periodoSub} · N° de cuotas: ${data.n_cuotas || nCuotas}`,
          summary: [
            ["Total de lotes", String(data.total_lotes || 0)],
            ["Área total", `${(data.total_area_m2 || 0).toLocaleString("es-PY")} m²`],
            ["Manzanas", String(manzanas.length)],
          ],
          sections: manzanas.map(mz => ({
            title: `Manzana ${mz.manzana}`,
            totalLabel: "Lotes / Área",
            total: `${mz.cantidad_lotes} lotes · ${(mz.area_m2 || 0).toLocaleString("es-PY")} m²`,
            columns: cols,
            rows: (mz.lotes || []).map(l => [
              l.numero || "-",
              l.superficie_m2 ? l.superficie_m2.toLocaleString("es-PY") : "-",
              fmtPYG(l.precio_contado_gs),
              l.precio_financiado_gs ? fmtPYG(l.precio_financiado_gs) : "-",
              fmtPYG(l.entrega_minima_gs),
              l.valor_cuota_gs ? fmtPYG(l.valor_cuota_gs) : "-",
              l.precio_contado_usd ? fmtUSD(l.precio_contado_usd) : "-",
              l.precio_financiado_usd ? fmtUSD(l.precio_financiado_usd) : "-",
              l.entrega_minima_usd ? fmtUSD(l.entrega_minima_usd) : "-",
              l.valor_cuota_usd ? fmtUSD(l.valor_cuota_usd) : "-",
            ]),
          })),
        };
      }
      if (report && !["descuentos_productos", "productos_stock", "loteadora_precificacion", "vencimiento_productos"].includes(tipo)) {
        report.periodLabel = tipo === "prestamos" ? `${prestReporteDesde} al ${prestReporteHasta}` : ["balance_anual", "proveedores"].includes(tipo) ? reportYear : mesLabel(reportMonth);
      }
      setGenericReport(report);
    } catch (e) {
      toast.error(e.message || "Error al generar reporte");
    } finally {
      setGenericReportLoading(false);
    }
  };

  // ── Kardex ───────────────────────────────────────────────────────────────────
  const buscarKardex = async () => {
    if (!kardexProductoId) { toast.error("Seleccione un producto"); return; }
    setKardexLoading(true);
    try {
      const q = new URLSearchParams({ producto_id: kardexProductoId });
      if (kardexDesde) q.set("desde", kardexDesde);
      if (kardexHasta) q.set("hasta", kardexHasta);
      if (kardexTipoMov.length > 0) q.set("tipo_movimiento", kardexTipoMov.join(","));
      if (kardexIncluirEliminados) q.set("incluir_eliminados", "true");
      if (activeEmpresaPropia?.slug) q.set("logo_tipo", activeEmpresaPropia.slug);
      const res = await fetch(`${API}/admin/reportes/kardex?${q}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) { const e = await res.json().catch(() => ({})); toast.error(e.detail || "Error al cargar Kardex"); return; }
      const data = await res.json();
      setKardexData(data);
    } catch { toast.error("Error de red"); }
    setKardexLoading(false);
  };

  const TIPO_CONFIG = {
    compra:              { bg: "#16a34a", label: "Compra" },
    venta:               { bg: "#dc2626", label: "Venta" },
    devolucion_cliente:  { bg: "#2563eb", label: "Devolución Cliente" },
    devolucion_proveedor:{ bg: "#d97706", label: "Devolución a Prov." },
    ajuste_positivo:     { bg: "#0891b2", label: "Ajuste (+)" },
    ajuste_negativo:     { bg: "#7c3aed", label: "Ajuste (-)" },
    inventario_inicial:  { bg: "#1e3a5f", label: "Inventario Inicial" },
    transferencia:       { bg: "#4f46e5", label: "Transferencia" },
    entrada:             { bg: "#16a34a", label: "Entrada" },
    salida:              { bg: "#dc2626", label: "Salida" },
    otro:                { bg: "#64748b", label: "Movimiento" },
  };

  const handlePrintKardex = () => {
    if (!kardexData) return;
    const p = kardexData.producto;
    const movs = kardexData.movimientos || [];
    const res = kardexData.resumen || {};
    const fmt = n => Number(n || 0).toLocaleString("es-PY");
    const unidad = p.unidad || "unidad";
    const periodoLabel = `${kardexDesde || "..."} al ${kardexHasta || "..."}`;

    const canVerCostoKardex = isAdmin || hasPermission?.("inventario_productos.ver_costo_compra");
    const rows = movs.map((m, i) => {
      const cfg = TIPO_CONFIG[m.tipo_clase] || TIPO_CONFIG.otro;
      const bg = i % 2 === 0 ? "#ffffff" : "#f8fafc";
      const entradaStr = m.entrada > 0 ? `<span style="color:#16a34a;font-weight:600">${fmt(m.entrada)}</span>` : `<span style="color:#94a3b8">0</span>`;
      const salidaStr  = m.salida  > 0 ? `<span style="color:#dc2626;font-weight:600">${fmt(m.salida)}</span>`  : `<span style="color:#94a3b8">0</span>`;
      const costoStr   = canVerCostoKardex ? `<td style="border:1px solid #e2e8f0;padding:5px 7px;font-size:10px;text-align:right;color:#c2410c">${m.costo_compra ? `₲ ${fmt(m.costo_compra)}` : "—"}</td>` : "";
      const margenColor = m.margen != null ? (m.margen >= 0 ? "#16a34a" : "#dc2626") : "#94a3b8";
      const margenStr  = canVerCostoKardex ? `<td style="border:1px solid #e2e8f0;padding:5px 7px;font-size:10px;text-align:right;color:${margenColor};font-weight:${m.margen != null ? 600 : 400}">${m.margen != null ? `₲ ${fmt(m.margen)}` : "—"}</td>` : "";
      return `<tr style="background:${bg}">
        <td style="border:1px solid #e2e8f0;padding:5px 7px;font-size:10px;color:#334155">${m.fecha || ""}</td>
        <td style="border:1px solid #e2e8f0;padding:5px 7px;font-size:10px">
          <span style="display:inline-block;background:${cfg.bg};color:#fff;padding:1px 7px;border-radius:9px;font-size:9px;font-weight:600">${m.tipo_label || cfg.label}</span>
        </td>
        <td style="border:1px solid #e2e8f0;padding:5px 7px;font-size:10px;color:#1e293b;font-weight:600">${m.documento || "-"}</td>
        <td style="border:1px solid #e2e8f0;padding:5px 7px;font-size:10px;color:#475569;max-width:180px;overflow:hidden;white-space:nowrap;text-overflow:ellipsis">${(m.notas || m.usuario_nombre || "")}</td>
        <td style="border:1px solid #e2e8f0;padding:5px 7px;font-size:10px;text-align:right">${entradaStr}</td>
        <td style="border:1px solid #e2e8f0;padding:5px 7px;font-size:10px;text-align:right">${salidaStr}</td>
        <td style="border:1px solid #e2e8f0;padding:5px 7px;font-size:10px;text-align:right;color:#1e293b;font-weight:700">${fmt(m.saldo)}</td>
        <td style="border:1px solid #e2e8f0;padding:5px 7px;font-size:10px;text-align:right;color:#b45309">${m.precio_unitario ? `₲ ${fmt(m.precio_unitario)}` : "—"}</td>
        ${costoStr}${margenStr}
        <td style="border:1px solid #e2e8f0;padding:5px 7px;font-size:10px;color:#64748b">${m.usuario_nombre || ""}</td>
      </tr>`;
    }).join("");

    const html = `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>Kardex - ${p.nombre}</title>
    <style>@media print{@page{margin:12mm 10mm}body{margin:0}}</style></head>
    <body style="font-family:Arial,sans-serif;font-size:11px;color:#1e293b;background:#fff;padding:16px">
      <div style="background:#1e3a5f;color:#fff;border-radius:8px;padding:14px 18px;margin-bottom:14px">
        <div style="display:flex;justify-content:space-between;align-items:flex-start">
          <div>
            <div style="font-size:18px;font-weight:700;letter-spacing:0.5px">FICHA DE MERCADERÍA (KARDEX)</div>
            <div style="font-size:12px;margin-top:4px;color:#93c5fd">Período: ${periodoLabel}</div>
          </div>
          <div style="text-align:right;font-size:11px;color:#bfdbfe">
            <div style="font-weight:600;font-size:16px">${p.sku || ""}</div>
            <div>Generado: ${new Date().toLocaleDateString("es-PY")}</div>
          </div>
        </div>
      </div>

      <div style="display:flex;gap:16px;margin-bottom:14px">
        <div style="flex:1;background:#f8fafc;border:1px solid #e2e8f0;border-radius:8px;padding:12px">
          <div style="font-size:15px;font-weight:700;color:#1e293b;margin-bottom:6px">${p.nombre || ""}</div>
          <table style="font-size:10px;width:100%"><tbody>
            <tr><td style="color:#64748b;padding:1px 0;width:120px">Código:</td><td style="font-weight:600;color:#1e3a5f">${p.sku || "-"}</td></tr>
            <tr><td style="color:#64748b;padding:1px 0">Categoría:</td><td>${p.categoria || "-"}</td></tr>
            <tr><td style="color:#64748b;padding:1px 0">Marca:</td><td>${p.marca || "-"}</td></tr>
            <tr><td style="color:#64748b;padding:1px 0">Unidad de Medida:</td><td>${unidad}</td></tr>
            <tr><td style="color:#64748b;padding:1px 0">Depósito:</td><td>${p.deposito || "-"}</td></tr>
          </tbody></table>
        </div>
        <div style="background:#1e3a5f;color:#fff;border-radius:8px;padding:14px 20px;text-align:center;min-width:140px;display:flex;flex-direction:column;justify-content:center">
          <div style="font-size:11px;font-weight:600;text-transform:uppercase;letter-spacing:0.5px;margin-bottom:4px">STOCK ACTUAL</div>
          <div style="font-size:36px;font-weight:700;color:#60a5fa;line-height:1">${fmt(res.stock_actual)}</div>
          <div style="font-size:11px;margin-top:4px;color:#93c5fd">${unidad}</div>
        </div>
      </div>

      <div style="font-size:12px;font-weight:700;color:#1e3a5f;text-transform:uppercase;letter-spacing:0.5px;border-bottom:2px solid #1e3a5f;padding-bottom:4px;margin-bottom:8px">
        HISTORIAL DE MOVIMIENTOS
      </div>
      <table style="width:100%;border-collapse:collapse;font-size:10px">
        <thead>
          <tr style="background:#1e3a5f">
            <th style="border:1px solid #152b47;color:#fff;padding:6px 8px;text-align:left;font-size:9px;white-space:nowrap">Fecha</th>
            <th style="border:1px solid #152b47;color:#fff;padding:6px 8px;text-align:left;font-size:9px">Tipo Movimiento</th>
            <th style="border:1px solid #152b47;color:#fff;padding:6px 8px;text-align:left;font-size:9px">Documento</th>
            <th style="border:1px solid #152b47;color:#fff;padding:6px 8px;text-align:left;font-size:9px">Descripción / Obs.</th>
            <th style="border:1px solid #152b47;color:#fff;padding:6px 8px;text-align:right;font-size:9px;white-space:nowrap">Entrada</th>
            <th style="border:1px solid #152b47;color:#fff;padding:6px 8px;text-align:right;font-size:9px;white-space:nowrap">Salida</th>
            <th style="border:1px solid #152b47;color:#fff;padding:6px 8px;text-align:right;font-size:9px;white-space:nowrap">Saldo</th>
            <th style="border:1px solid #152b47;color:#fff;padding:6px 8px;text-align:right;font-size:9px;white-space:nowrap">Precio Unit.</th>
            ${canVerCostoKardex ? '<th style="border:1px solid #152b47;color:#fff;padding:6px 8px;text-align:right;font-size:9px;white-space:nowrap">Costo Compra</th>' : ""}
            ${canVerCostoKardex ? '<th style="border:1px solid #152b47;color:#fff;padding:6px 8px;text-align:right;font-size:9px;white-space:nowrap">Margen</th>' : ""}
            <th style="border:1px solid #152b47;color:#fff;padding:6px 8px;text-align:left;font-size:9px">Usuario</th>
          </tr>
        </thead>
        <tbody>${rows || '<tr><td colspan="10" style="text-align:center;padding:16px;color:#64748b">Sin movimientos en el período</td></tr>'}</tbody>
      </table>

      <div style="display:flex;gap:12px;margin-top:14px">
        <div style="flex:1;background:#f8fafc;border:1px solid #e2e8f0;border-radius:8px;padding:12px">
          <div style="font-size:10px;font-weight:700;color:#1e3a5f;text-transform:uppercase;margin-bottom:8px">RESUMEN DEL PERÍODO</div>
          <div style="display:flex;gap:16px;flex-wrap:wrap">
            <div><div style="font-size:9px;color:#64748b">Total Entradas</div><div style="font-size:18px;font-weight:700;color:#16a34a">${fmt(res.total_entradas)} <span style="font-size:10px">${unidad}</span></div></div>
            <div><div style="font-size:9px;color:#64748b">Total Salidas</div><div style="font-size:18px;font-weight:700;color:#dc2626">${fmt(res.total_salidas)} <span style="font-size:10px">${unidad}</span></div></div>
            <div><div style="font-size:9px;color:#64748b">Diferencia</div><div style="font-size:18px;font-weight:700;color:#1e293b">${fmt(res.diferencia)} <span style="font-size:10px">${unidad}</span></div></div>
            <div><div style="font-size:9px;color:#64748b">Stock Actual</div><div style="font-size:18px;font-weight:700;color:#1e3a5f">${fmt(res.stock_actual)} <span style="font-size:10px">${unidad}</span></div></div>
          </div>
        </div>
      </div>
      <div style="margin-top:10px;font-size:9px;color:#64748b;background:#f8fafc;border-radius:6px;padding:6px 10px">
        ⓘ El saldo se calcula después de cada movimiento y representa la cantidad disponible en stock al momento de la operación.
      </div>
      ${PIE_REPORTE_HTML}
    </body></html>`;

    const win = window.open("", "_blank");
    if (!win) { toast.error("Permita las ventanas emergentes para imprimir"); return; }
    win.document.write(html);
    win.document.close();
    win.focus();
    setTimeout(() => { win.print(); }, 400);
  };

  const handleExportKardexExcel = () => {
    if (!kardexData) return;
    const p = kardexData.producto;
    const movs = kardexData.movimientos || [];
    const res = kardexData.resumen || {};
    const unidad = p.unidad || "unidad";
    const canVerCostoCSV = isAdmin || hasPermission?.("inventario_productos.ver_costo_compra");
    const csvHeader = ["Fecha", "Tipo Movimiento", "Documento", "Descripción", "Entrada", "Salida", "Saldo", "Precio Unit."];
    if (canVerCostoCSV) { csvHeader.push("Costo Compra", "Margen"); }
    csvHeader.push("Usuario");
    const rows = [
      ["FICHA DE MERCADERÍA (KARDEX)"],
      [`Producto: ${p.nombre}`, `SKU: ${p.sku || ""}`, `Categoría: ${p.categoria || ""}`, `Unidad: ${unidad}`],
      [`Período: ${kardexDesde || ""} al ${kardexHasta || ""}`],
      [],
      csvHeader,
      ...movs.map(m => {
        const row = [
          m.fecha || "",
          m.tipo_label || "",
          m.documento || "",
          m.notas || "",
          m.entrada || 0,
          m.salida || 0,
          m.saldo || 0,
          m.precio_unitario || "",
        ];
        if (canVerCostoCSV) { row.push(m.costo_compra || "", m.margen != null ? m.margen : ""); }
        row.push(m.usuario_nombre || "");
        return row;
      }),
      [],
      ["RESUMEN DEL PERÍODO"],
      ["Total Entradas", res.total_entradas || 0, unidad],
      ["Total Salidas", res.total_salidas || 0, unidad],
      ["Diferencia (Ent - Sal)", res.diferencia || 0, unidad],
      ["Stock Actual", res.stock_actual || 0, unidad],
    ];
    const csv = rows.map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url;
    a.download = `kardex_${p.sku || p.nombre || "producto"}_${kardexDesde}_${kardexHasta}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  // ── CSV ──────────────────────────────────────────────────────────────────────
  const exportCSV = () => {
    if (!previewData) return;
    let csv = "Empresa,Categoria,Subtipo,Nombre,Descripcion,Ubicacion,IP Local,IPs Locales,IP Publica,IPs Publicas,Dominio,Puerto Local,Puerto Externo,Version,Estado,Observaciones,Marca,Modelo,Chasis,Nº Factura,Fecha Adquisicion,Monto Adquisicion,Moneda Adquisicion,Anio Fabricacion,Estado del Bien";
    if (previewWithCreds) csv += ",Tipo Acceso,Usuario,Password,URL Acceso,Obs.";
    csv += "\n";
    previewData.forEach(row => {
      const base = `"${row.empresa}","${row.categoria}","${row.subtipo}","${row.nombre}","${row.descripcion}","${row.ubicacion}","${row.ip_local}","${(row.ips_locales||[]).join('; ')}","${row.ip_publica}","${(row.ips_publicas||[]).join('; ')}","${row.dominio}","${row.puerto_local}","${row.puerto_externo}","${row.version}","${row.estado}","${row.observaciones}","${row.marca||""}","${row.modelo||""}","${row.chasis||""}","${row.numero_factura||""}","${row.fecha_adquisicion||""}","${row.monto_adquisicion ?? ""}","${row.monto_adquisicion != null ? (row.moneda_adquisicion || "PYG") : ""}","${row.anio_fabricacion ?? ""}","${row.estado_bien === "bajado" ? "Bajado" : "Normal"}"`;
      if (previewWithCreds && row.credenciales?.length > 0) {
        row.credenciales.forEach(c => { csv += `${base},"${c.tipo_acceso}","${c.usuario}","${c.password}","${c.url_acceso}","${c.observaciones}"\n`; });
      } else { csv += base + (previewWithCreds ? `,"","","","",""` : "") + "\n"; }
    });
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `inventario_reporte.csv`; a.click();
    toast.success("CSV exportado");
  };

  // ── Excel — 4 hojas reales .xlsx con celdas estiladas (xlsx-js-style) ────────
  const exportExcel = async () => {
    if (!previewData) return;
    toast.info("Preparando Excel...", { id: "excel-loading" });
    let XLSX;
    try {
      XLSX = await loadXLSX();
      if (!XLSX?.utils) throw new Error("API inválida");
    } catch (err) {
      toast.dismiss("excel-loading");
      toast.error("No se pudo cargar la librería. Verificá tu conexión a internet.");
      console.error("SheetJS load error:", err);
      return;
    }
    toast.dismiss("excel-loading");

    // ── Helpers de estilo para xlsx-js-style ──────────────────────────────────
    const rgb = (hex) => hex.replace("#", "").toUpperCase();

    // Celda de cabecera: fondo sólido con el color de sección, texto blanco negrita
    const hCell = (v, bgHex) => ({
      v, t: "s",
      s: {
        fill: { patternType: "solid", fgColor: { rgb: rgb(bgHex) } },
        font: { bold: true, color: { rgb: "FFFFFF" }, sz: 9, name: "Arial" },
        border: {
          top:    { style: "thin", color: { rgb: "CCCCCC" } },
          bottom: { style: "thin", color: { rgb: "CCCCCC" } },
          left:   { style: "thin", color: { rgb: "CCCCCC" } },
          right:  { style: "thin", color: { rgb: "CCCCCC" } },
        },
        alignment: { horizontal: "left", vertical: "top", wrapText: false },
      }
    });

    // Celda de datos: filas alternadas (blanco / color suave de sección)
    const dCell = (v, even, altHex) => ({
      v: v ?? "", t: typeof (v ?? "") === "number" ? "n" : "s",
      s: {
        fill: even
          ? { patternType: "solid", fgColor: { rgb: "FFFFFF" } }
          : { patternType: "solid", fgColor: { rgb: rgb(altHex) } },
        font: { sz: 9, name: "Arial" },
        border: {
          top:    { style: "thin", color: { rgb: "D1D5DB" } },
          bottom: { style: "thin", color: { rgb: "D1D5DB" } },
          left:   { style: "thin", color: { rgb: "D1D5DB" } },
          right:  { style: "thin", color: { rgb: "D1D5DB" } },
        },
        alignment: { horizontal: "left", vertical: "top", wrapText: true },
      }
    });

    // Construye la hoja a partir de cabeceras + filas de datos ya estiladas
    const makeSheet = (headers, bgHex, dataRows, colWidths, altHex) => {
      const aoa = [
        headers.map(h => hCell(h, bgHex)),
        ...dataRows.map((row, ri) => row.map(v => dCell(v, ri % 2 === 0, altHex)))
      ];
      const ws = XLSX.utils.aoa_to_sheet(aoa);
      ws["!cols"] = colWidths.map(w => ({ wch: w }));
      ws["!tabColor"] = { rgb: rgb(bgHex) };
      ws["!views"] = [{ state: "frozen", ySplit: 1 }];
      return ws;
    };

    const wb = XLSX.utils.book_new();
    const fecha = new Date().toLocaleDateString("es-PY");

    // ── Hoja 1 · Inventario (sin Cuentas de Acceso — evita duplicados) ──────────
    XLSX.utils.book_append_sheet(wb,
      makeSheet(
        ["Nombre","Empresa","Categoría","Subtipo","Estado","IP Privada","IP Pública","Dominio","Ubicación","Marca","Modelo","Chasis","Nº Factura","Fecha Adq.","Monto Adq.","Moneda Adq.","Año Fab.","Estado del Bien","Responsable","Observaciones"],
        COLORS.inv.bg,
        inventarioActivos.map(r => [
          r.nombre, r.empresa, r.categoria, r.subtipo, r.estado,
          [r.ip_local, ...(r.ips_locales||[])].filter(Boolean).join(", "),
          [r.ip_publica, ...(r.ips_publicas||[])].filter(Boolean).join(", "),
          r.dominio, r.ubicacion,
          r.marca || "", r.modelo || (r.marca ? "" : r.version) || "", r.chasis || "",
          r.numero_factura || "", r.fecha_adquisicion || "",
          r.monto_adquisicion != null ? r.monto_adquisicion : "",
          r.monto_adquisicion != null ? (r.moneda_adquisicion || "PYG") : "",
          r.anio_fabricacion != null ? r.anio_fabricacion : "",
          r.estado_bien === "bajado" ? "Bajado" : "Normal",
          r.responsable||"", r.observaciones||""
        ]),
        [24,18,14,14,10,20,20,20,14,12,12,16,16,12,14,10,10,12,14,26],
        COLORS.inv.rowAlt
      ),
      "Inventario"
    );

    // ── Hoja 2 · Credenciales (solo si se exportó con credenciales) ─────────────
    if (previewWithCreds) {
      const credRows = [];
      previewData.forEach(r => {
        (r.credenciales || []).forEach(c => {
          credRows.push([r.nombre, c.servicio||c.tipo_acceso||"", c.usuario||"", c.password||"", c.sensibilidad||"normal", c.observaciones||""]);
        });
      });
      if (credRows.length > 0) {
        XLSX.utils.book_append_sheet(wb,
          makeSheet(
            ["Activo","Servicio","Usuario","Contraseña","Sensibilidad","Notas"],
            COLORS.cred.bg,
            credRows,
            [26,18,18,18,12,30],
            COLORS.cred.rowAlt
          ),
          "Credenciales"
        );
      }
    }

    // ── Hoja 3 · Cuentas Asociadas a Dispositivos ────────────────────────────────
    if (cuentasAsociadas.length > 0) {
      XLSX.utils.book_append_sheet(wb,
        makeSheet(
          ["Dispositivo","Tipo Cuenta","Nombre","ID / Usuario","Contraseña","Detalles"],
          COLORS.ctas.bg,
          cuentasAsociadas.map(r => [r.dispositivo, r.tipo_cuenta, r.nombre, r.usuario||"", r.password||"", r.detalles||""]),
          [24,16,24,18,18,30],
          COLORS.ctas.rowAlt
        ),
        "Cuentas Asociadas"
      );
    }

    // ── Hoja 4 · Detalle de Cuentas de Acceso ────────────────────────────────────
    if (detalleCuentas.length > 0) {
      XLSX.utils.book_append_sheet(wb,
        makeSheet(
          ["Nombre","Subtipo","ID / Usuario","Correo / Servidor","Contraseña","Detalles","Dispositivos"],
          COLORS.det.bg,
          detalleCuentas.map(r => [r.nombre, r.subtipo, r.usuario||"", r.correo_servidor||"", r.password||"", r.detalles||"", `${r.dispositivos_count} disp.`]),
          [26,16,18,24,18,28,13],
          COLORS.det.rowAlt
        ),
        "Detalle Cuentas"
      );
    }

    try {
      XLSX.writeFile(wb, `inventario_${fecha.replace(/\//g,"-")}.xlsx`);
      toast.success("Excel exportado correctamente");
    } catch (e) {
      console.error("writeFile error:", e);
      toast.error("Error al generar el archivo Excel.");
    }
  };

  // ── Excel genérico — cualquier reporte armado en `genericReport` (Prod. y stock, Flow de facturaciones, etc.) ──
  const exportGenericExcel = async () => {
    const rep = genericReport;
    if (!rep) return;
    toast.info("Preparando Excel...", { id: "excel-loading-gen" });
    let XLSX;
    try {
      XLSX = await loadXLSX();
      if (!XLSX?.utils) throw new Error("API inválida");
    } catch (err) {
      toast.dismiss("excel-loading-gen");
      toast.error("No se pudo cargar la librería. Verificá tu conexión a internet.");
      return;
    }
    toast.dismiss("excel-loading-gen");

    // "₲ 1.234.567" / "US$ 12,50" / "R$ 1.000,00" → número real; el resto queda como texto/número.
    const toCell = (v) => {
      if (v === null || v === undefined) return "";
      if (typeof v === "number") return v;
      if (typeof v !== "string") return "";
      const m = v.trim().match(/^(?:₲|US\$|R\$|\$)\s*(-?[\d.]+(?:,\d+)?)$/);
      if (m) {
        const n = Number(m[1].replace(/\./g, "").replace(",", "."));
        if (!Number.isNaN(n)) return n;
      }
      return v;
    };
    const border = { top: { style: "thin", color: { rgb: "D1D5DB" } }, bottom: { style: "thin", color: { rgb: "D1D5DB" } }, left: { style: "thin", color: { rgb: "D1D5DB" } }, right: { style: "thin", color: { rgb: "D1D5DB" } } };
    const hCell = (v) => ({ v, t: "s", s: { fill: { patternType: "solid", fgColor: { rgb: "1D4ED8" } }, font: { bold: true, color: { rgb: "FFFFFF" }, sz: 9, name: "Arial" }, border, alignment: { horizontal: "left", vertical: "top" } } });
    const dCell = (v, even) => {
      const c = toCell(v);
      return { v: c, t: typeof c === "number" ? "n" : "s", z: typeof c === "number" ? "#,##0.##" : undefined, s: { fill: { patternType: "solid", fgColor: { rgb: even ? "FFFFFF" : "EFF6FF" } }, font: { sz: 9, name: "Arial" }, border, alignment: { horizontal: typeof c === "number" ? "right" : "left", vertical: "top", wrapText: true } } };
    };
    const tCell = (v, bold = true, sz = 10) => ({ v, t: "s", s: { font: { bold, sz, name: "Arial" } } });

    const wb = XLSX.utils.book_new();
    const usados = new Set();
    const nombreHoja = (t) => {
      let base = String(t || "Hoja").replace(/[\\/?*\[\]:]/g, " ").slice(0, 28).trim() || "Hoja";
      let n = base, i = 2;
      while (usados.has(n.toLowerCase())) { n = `${base.slice(0, 25)} ${i++}`; }
      usados.add(n.toLowerCase());
      return n;
    };

    // Hoja de resumen
    if ((rep.summary || []).length > 0) {
      const aoa = [[tCell(rep.title, true, 13)], [tCell(rep.subtitle || "", false)], [tCell(rep.periodLabel || "", false)], [], [hCell("Concepto"), hCell("Valor")]];
      rep.summary.forEach(([k, v], i) => aoa.push([dCell(k, i % 2 === 0), dCell(typeof v === "object" ? "" : v, i % 2 === 0)]));
      const ws = XLSX.utils.aoa_to_sheet(aoa);
      ws["!cols"] = [{ wch: 34 }, { wch: 24 }];
      XLSX.utils.book_append_sheet(wb, ws, nombreHoja("Resumen"));
    }
    // Una hoja por sección
    (rep.sections || []).forEach((sec) => {
      const cols = sec.columns || [];
      const aoa = [cols.map(h => hCell(h))];
      let k = 0;
      (sec.rows || []).forEach((row) => {
        if (Array.isArray(row)) { aoa.push(row.map(v => dCell(v, k % 2 === 0))); k++; }
        else if (row && Array.isArray(row.cells)) {
          aoa.push(row.cells.map(v => dCell(v, k % 2 === 0)));
          (row.subRows || []).forEach(sub => aoa.push(sub.map(v => dCell(v, k % 2 === 0))));
          k++;
        }
      });
      if (sec.total) aoa.push([tCell(`${sec.totalLabel || "Total"}: ${sec.total}`)]);
      const ws = XLSX.utils.aoa_to_sheet(aoa);
      ws["!cols"] = cols.map((_, ci) => {
        const maxLen = Math.max(String(cols[ci] || "").length, ...(sec.rows || []).slice(0, 200).map(r => String((Array.isArray(r) ? r[ci] : r?.cells?.[ci]) ?? "").length));
        return { wch: Math.min(Math.max(maxLen + 2, 10), 45) };
      });
      ws["!views"] = [{ state: "frozen", ySplit: 1 }];
      XLSX.utils.book_append_sheet(wb, ws, nombreHoja(sec.title));
    });

    if (wb.SheetNames.length === 0) { toast.error("El reporte no tiene datos para exportar"); return; }
    const slug = String(rep.title || "reporte").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "");
    try {
      XLSX.writeFile(wb, `${slug}_${new Date().toISOString().slice(0, 10)}.xlsx`);
      toast.success("Excel exportado correctamente");
    } catch (e) {
      console.error("writeFile error:", e);
      toast.error("Error al generar el archivo Excel.");
    }
  };

  // ── Excel — Libro Ventas (.xlsx real, celdas estiladas vía xlsx-js-style) ────
  const exportLibroVentasExcel = async () => {
    if (!libroVentasData || libroVentasData.length === 0) return;
    toast.info("Preparando Excel...", { id: "excel-loading-lv" });
    let XLSX;
    try {
      XLSX = await loadXLSX();
      if (!XLSX?.utils) throw new Error("API inválida");
    } catch (err) {
      toast.dismiss("excel-loading-lv");
      toast.error("No se pudo cargar la librería. Verificá tu conexión a internet.");
      console.error("SheetJS load error:", err);
      return;
    }
    toast.dismiss("excel-loading-lv");

    const rgb = (hex) => hex.replace("#", "").toUpperCase();
    const bgHex = "#059669";   // emerald-600 — mismo tono que el resto de reportes financieros
    const altHex = "#D1FAE5";  // emerald-100

    const hCell = (v) => ({
      v, t: "s",
      s: {
        fill: { patternType: "solid", fgColor: { rgb: rgb(bgHex) } },
        font: { bold: true, color: { rgb: "FFFFFF" }, sz: 9, name: "Arial" },
        border: {
          top: { style: "thin", color: { rgb: "CCCCCC" } }, bottom: { style: "thin", color: { rgb: "CCCCCC" } },
          left: { style: "thin", color: { rgb: "CCCCCC" } }, right: { style: "thin", color: { rgb: "CCCCCC" } },
        },
        alignment: { horizontal: "left", vertical: "top", wrapText: false },
      }
    });
    const dCell = (v, even) => ({
      v: v ?? "", t: typeof (v ?? "") === "number" ? "n" : "s",
      s: {
        fill: even
          ? { patternType: "solid", fgColor: { rgb: "FFFFFF" } }
          : { patternType: "solid", fgColor: { rgb: rgb(altHex) } },
        font: { sz: 9, name: "Arial" },
        border: {
          top: { style: "thin", color: { rgb: "D1D5DB" } }, bottom: { style: "thin", color: { rgb: "D1D5DB" } },
          left: { style: "thin", color: { rgb: "D1D5DB" } }, right: { style: "thin", color: { rgb: "D1D5DB" } },
        },
        alignment: { horizontal: "left", vertical: "top", wrapText: true },
      }
    });

    const headers = ["RUC", "Nombre del Cliente", "Fecha de Venta", "Número Factura", "Total Venta", "Contado / Crédito", "IVA", "Notas (Observación)", "Estado"];
    const dataRows = libroVentasData.map(r => [
      r.ruc || "-",
      r.cliente || "-",
      r.fecha || "-",
      r.numero || "-",
      Math.round(Number(r.total || 0)),
      r.forma_pago === "credito" ? "Crédito" : "Contado",
      Math.round(Number(r.iva || 0)),
      r.notas || "",
      r.anulada ? "Anulado" : "Activo",
    ]);
    const aoa = [
      headers.map(h => hCell(h)),
      ...dataRows.map((row, ri) => row.map(v => dCell(v, ri % 2 === 0))),
    ];
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    ws["!cols"] = [16, 26, 14, 18, 16, 16, 14, 30, 10].map(w => ({ wch: w }));
    ws["!tabColor"] = { rgb: rgb(bgHex) };
    ws["!views"] = [{ state: "frozen", ySplit: 1 }];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Libro Ventas");
    const fecha = new Date().toLocaleDateString("es-PY");
    try {
      XLSX.writeFile(wb, `libro_ventas_${fecha.replace(/\//g, "-")}.xlsx`);
      toast.success("Excel exportado correctamente");
    } catch (e) {
      console.error("writeFile error:", e);
      toast.error("Error al generar el archivo Excel.");
    }
  };

  // ── Alert CRUD ───────────────────────────────────────────────────────────────
  const openNewAlert = () => {
    setEditingAlert(null);
    setAlertForm({ empresa_id: empresaFromUrl || "", tipo: "dominio", nombre: "", descripcion: "", fecha_vencimiento: "", activo_id: "", notificar_dias: 30, notificar_dias_urgente: 0 });
    setShowAlertForm(true);
  };
  const openEditAlert = (alerta) => {
    setEditingAlert(alerta);
    setAlertForm({ empresa_id: alerta.empresa_id, tipo: alerta.tipo, nombre: alerta.nombre, descripcion: alerta.descripcion || "", fecha_vencimiento: alerta.fecha_vencimiento?.split("T")[0] || "", activo_id: alerta.activo_id || "", notificar_dias: alerta.notificar_dias || 30, notificar_dias_urgente: alerta.notificar_dias_urgente ?? 0 });
    setShowAlertForm(true);
  };
  const saveAlert = async () => {
    if (!alertForm.empresa_id || !alertForm.nombre || !alertForm.fecha_vencimiento) { toast.error("Campos requeridos"); return; }
    try {
      const url = editingAlert ? `${API}/admin/alertas/${editingAlert.id}` : `${API}/admin/alertas`;
      const res = await fetch(url, { method: editingAlert ? "PUT" : "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` }, body: JSON.stringify(alertForm) });
      if (res.ok) { toast.success(editingAlert ? "Actualizada" : "Creada"); setShowAlertForm(false); fetchAll(); }
    } catch { toast.error("Error"); }
  };
  const toggleAlertEstado = async (alerta) => {
    try { await fetch(`${API}/admin/alertas/${alerta.id}/estado`, { method: "PUT", headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` }, body: JSON.stringify({ estado: alerta.estado === "activa" ? "resuelta" : "activa" }) }); fetchAll(); } catch {}
  };
  const deleteAlert = async (id) => {
    if (!window.confirm("¿Eliminar?")) return;
    try { await fetch(`${API}/admin/alertas/${id}`, { method: "DELETE", headers: { Authorization: `Bearer ${token}` } }); fetchAll(); } catch {}
  };

  useEffect(() => {
    if (tabFromUrl === "alertas" && hasPermission?.("alertas.ver")) {
      setTab("alertas");
    }
  }, [tabFromUrl, activeEmpresaPropia?.id]); // eslint-disable-line

  useEffect(() => {
    if (handledNuevo || nuevoFromUrl !== "alerta") return;
    setTab("alertas");
    openNewAlert();
    setHandledNuevo(true);
  }, [nuevoFromUrl, handledNuevo]); // eslint-disable-line

  const empresaIdsVisibles = useMemo(() => new Set(empresas.map(e => e.id)), [empresas]);
  const perteneceAEmpresaActiva = (empresaId) => !activeEmpresaPropia?.slug || empresaIdsVisibles.has(empresaId);
  const alertasDeEmpresaActiva = useMemo(() => (
    alertas.filter(a => perteneceAEmpresaActiva(a.empresa_id))
  ), [alertas, empresaIdsVisibles, activeEmpresaPropia?.slug]); // eslint-disable-line
  const alertasProximasVisibles = useMemo(() => (
    alertasProximas.filter(a => perteneceAEmpresaActiva(a.empresa_id))
  ), [alertasProximas, empresaIdsVisibles, activeEmpresaPropia?.slug]); // eslint-disable-line
  const activosDeEmpresaActiva = useMemo(() => (
    activos.filter(a => perteneceAEmpresaActiva(a.empresa_id))
  ), [activos, empresaIdsVisibles, activeEmpresaPropia?.slug]); // eslint-disable-line
  const filteredAlertas = useMemo(() => alertasDeEmpresaActiva.filter(a => {
    const texto = [
      a.nombre, a.descripcion, a.tipo, a.estado, a.empresa_nombre, a.activo_nombre,
      a.fecha_vencimiento, String(a.notificar_dias || "")
    ].filter(Boolean).join(" ");
    return matchesChips(texto, alertSearchChips, alertSearch);
  }), [alertasDeEmpresaActiva, alertSearchChips, alertSearch]);

  const getDaysColor = (dias, colorAlerta) => {
    if (colorAlerta === "rojo") return "text-red-400 bg-red-500/10 border-red-500/20";
    if (colorAlerta === "amarillo") return "text-amber-400 bg-amber-500/10 border-amber-500/20";
    if (colorAlerta === "verde") return "text-emerald-400 bg-emerald-500/10 border-emerald-500/20";
    if (dias < 0) return "text-red-400 bg-red-500/10 border-red-500/20";
    if (dias <= 7) return "text-orange-400 bg-orange-500/10 border-orange-500/20";
    if (dias <= 30) return "text-amber-400 bg-amber-500/10 border-amber-500/20";
    return "text-emerald-400 bg-emerald-500/10 border-emerald-500/20";
  };

  const saveCumpleanosConfig = async () => {
    try {
      const res = await fetch(`${API}/admin/alertas/cumpleanos/config`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(cumpleanosConfig),
      });
      if (res.ok) {
        toast.success("Configuración de cumpleaños guardada");
        await fetch(`${API}/admin/alertas/sync-cumpleanos${activeEmpresaPropia?.slug ? `?logo_tipo=${activeEmpresaPropia.slug}` : ""}`, { method: "POST", headers: { Authorization: `Bearer ${token}` } });
        fetchAll();
      }
    } catch {
      toast.error("No se pudo guardar la configuración");
    }
  };

  const ejecutarReporteCajaBanco = async (cuentaId) => {
    setGenericReportLoading(true);
    setShowCajaPicker(false);
    try {
      const desde = reportCajaDesde;
      const hasta = reportCajaHasta;
      if (!desde || !hasta) {
        toast.error("Indicá fecha desde y hasta");
        setGenericReportLoading(false);
        return;
      }
      if (desde > hasta) {
        toast.error("La fecha desde no puede ser posterior a hasta");
        setGenericReportLoading(false);
        return;
      }
      const q = new URLSearchParams({ cuenta_id: cuentaId, desde, hasta });
      if (activeEmpresaPropia?.slug) q.set("logo_tipo", activeEmpresaPropia.slug);
      const res = await fetch(`${API}/admin/reportes/caja-banco?${q}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || "Error al generar reporte");
      const data = await res.json();
      const cuenta = data.cuenta || {};
      const movs = data.movimientos || [];
      const movsSF = data.movimientos_saldo_favor || [];
      const totalSF = data.total_saldo_favor_aplicado || 0;

      // ── Formatter según moneda de la cuenta ──────────────────
      const cuentaMoneda = (cuenta.moneda || "PYG").toUpperCase();
      const fmtCuenta = (n) => {
        const num = Number(n || 0);
        if (cuentaMoneda === "BRL") return `R$ ${num.toLocaleString("es-PY", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
        if (cuentaMoneda === "USD") return `$ ${num.toLocaleString("es-PY", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
        return `₲ ${Math.round(num).toLocaleString("es-PY")}`;
      };

      // ── Calcular saldo corriente fila por fila ───────────────
      // Solo ingresos/egresos reales afectan el saldo bancario
      let saldoAcum = data.saldo_inicial || 0;
      const rowsConSaldo = movs.map(m => {
        const esIngreso = m.tipo === "ingreso";
        saldoAcum = esIngreso ? saldoAcum + m.monto : saldoAcum - m.monto;
        const conceptoBase = `${m.categoria ? `[${m.categoria}] ` : ""}${m.concepto || "-"}`;
        return {
          fecha: m.fecha || "-",
          concepto: conceptoBase,
          ingreso: esIngreso ? m.monto : null,
          egreso: !esIngreso ? m.monto : null,
          saldo: saldoAcum,
        };
      });

      // ── Resumen saldo a favor ─────────────────────────────────
      const summaryExtra = [
        ...(totalSF > 0 ? [["Cobrado via saldo a favor ⓘ", `${fmtCuenta(totalSF)} (sin mov. bancario)`]] : []),
      ];

      // ── Sección saldo a favor (informativa) ──────────────────
      const seccionesSF = movsSF.length > 0 ? [{
        title: `Saldos a Favor aplicados — ${movsSF.length} aplicación(es) · SIN movimiento bancario`,
        columns: ["Fecha", "Concepto", "Monto aplicado"],
        rows: movsSF.map(m => [
          m.fecha || "-",
          m.concepto || "-",
          <span style={{ color: "#d97706", fontWeight: 700 }}>{fmtCuenta(m.monto)}</span>,
        ]),
      }] : [];

      setGenericReport({
        title: `Caja / Banco — ${cuenta.nombre || cuentaId}`,
        subtitle: `${data.desde} → ${data.hasta} · ${activeEmpresaPropia?.nombre || "Todas"}`,
        summary: [
          ["Saldo inicial", fmtCuenta(data.saldo_inicial)],
          ["Total ingresos (banco)", fmtCuenta(data.total_ingresos)],
          ["Total egresos (banco)", fmtCuenta(data.total_egresos)],
          ["Saldo final (banco)", fmtCuenta(data.saldo_final)],
          ...summaryExtra,
        ],
        sections: [
          {
            title: `Movimientos del periodo (${movs.length} registros)`,
            columns: ["Fecha", "Concepto", "Ingresos", "Egresos", "Saldo"],
            rows: rowsConSaldo.map(r => [
              r.fecha,
              r.concepto,
              r.ingreso != null
                ? <span style={{ color: "#16a34a", fontWeight: 700 }}>{fmtCuenta(r.ingreso)}</span>
                : <span style={{ color: "#cbd5e1" }}>—</span>,
              r.egreso != null
                ? <span style={{ color: "#dc2626", fontWeight: 700 }}>{fmtCuenta(r.egreso)}</span>
                : <span style={{ color: "#cbd5e1" }}>—</span>,
              <span style={{ fontWeight: 700, color: r.saldo >= 0 ? "#0f172a" : "#dc2626" }}>
                {fmtCuenta(r.saldo)}
              </span>,
            ]),
          },
          ...seccionesSF,
        ],
        periodLabel: `${desde} → ${hasta}`,
      });
    } catch (e) {
      toast.error(e.message || "Error en reporte caja/banco");
    } finally {
      setGenericReportLoading(false);
    }
  };

  const abrirReporteCajaBanco = async () => {
    setGenericReportLoading(true);
    try {
      const q = activeEmpresaPropia?.slug ? `?logo_tipo=${activeEmpresaPropia.slug}` : "";
      const res = await fetch(`${API}/admin/reportes/caja-banco/cuentas${q}`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.status === 404) {
        toast.error("El reporte de caja/banco no está disponible. Reiniciá el servidor backend o contactá soporte.");
        return;
      }
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Sin acceso al reporte");
      }
      const cuentas = await res.json();
      if (!cuentas.length) {
        // No llamamos /admin/cuentas-bancarias aquí porque eso expondría todos los bancos
        // a usuarios que sólo deberían ver los que les fueron asignados.
        toast.error(
          "No tenés cuentas bancarias habilitadas para este reporte. " +
          "Pedí al administrador que te asigne cuentas en Bancos → ícono de usuarios."
        );
        return;
      }
      setCajaCuentasPicker(cuentas);
      setShowCajaPicker(true);
    } catch (e) {
      toast.error(e.message || "No se pudo cargar cuentas");
    } finally {
      setGenericReportLoading(false);
    }
  };

  // ══════════════════ Reporte MOVIMIENTOS (nuevo, independiente) ══════════════════
  // Consume /admin/reportes/movimientos (gateado por el permiso reportes.movimientos),
  // que internamente reutiliza la misma lógica probada de armado de movimientos que
  // Caja/Banco, pero se muestra como extracto: filtros por tipo, columna de
  // comprobante y saldo corriente fila por fila.
  const buildMovimientosReport = (data, filtro, cuentaId) => {
    const cuenta = data.cuenta || {};
    const movs = data.movimientos || [];
    const movsSF = data.movimientos_saldo_favor || [];
    const totalSF = data.total_saldo_favor_aplicado || 0;

    const cuentaMoneda = (cuenta.moneda || "PYG").toUpperCase();
    const fmtCuenta = (n) => {
      const num = Number(n || 0);
      if (cuentaMoneda === "BRL") return `R$ ${num.toLocaleString("es-PY", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
      if (cuentaMoneda === "USD") return `$ ${num.toLocaleString("es-PY", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
      return `₲ ${Math.round(num).toLocaleString("es-PY")}`;
    };

    // El saldo corriente por fila SIEMPRE se calcula sobre TODOS los movimientos
    // del período (nunca cambia con el filtro) — el filtro solo oculta filas de
    // la vista, igual que un extracto bancario real.
    let saldoAcum = data.saldo_inicial || 0;
    const todasConSaldo = movs.map(m => {
      const esIngreso = m.tipo === "ingreso";
      saldoAcum = esIngreso ? saldoAcum + m.monto : saldoAcum - m.monto;
      const esTransferencia = m.categoria === "Transferencia entre cuentas";
      const conceptoBase = `${m.categoria ? `[${m.categoria}] ` : ""}${m.concepto || "-"}`;
      return {
        fecha: m.fecha || "-",
        comprobante: m.referencia || "-",
        concepto: conceptoBase,
        tipoMov: esIngreso ? "credito" : "debito",
        esTransfRecibida: esTransferencia && esIngreso,
        esTransfRealizada: esTransferencia && !esIngreso,
        ingreso: esIngreso ? m.monto : null,
        egreso: !esIngreso ? m.monto : null,
        saldo: saldoAcum,
      };
    });

    const pasaFiltro = (r) => {
      if (filtro === "credito") return r.tipoMov === "credito";
      if (filtro === "debito") return r.tipoMov === "debito";
      if (filtro === "transf_recibida") return r.esTransfRecibida;
      if (filtro === "transf_realizada") return r.esTransfRealizada;
      return true;
    };
    const rowsConSaldo = todasConSaldo.filter(pasaFiltro);

    const summaryExtra = [
      ...(totalSF > 0 ? [["Cobrado via saldo a favor ⓘ", `${fmtCuenta(totalSF)} (sin mov. bancario)`]] : []),
    ];

    const seccionesSF = movsSF.length > 0 ? [{
      title: `Saldos a Favor aplicados — ${movsSF.length} aplicación(es) · SIN movimiento bancario`,
      columns: ["Fecha", "Concepto", "Monto aplicado"],
      rows: movsSF.map(m => [
        m.fecha || "-",
        m.concepto || "-",
        <span style={{ color: "#d97706", fontWeight: 700 }}>{fmtCuenta(m.monto)}</span>,
      ]),
    }] : [];

    return {
      isMovimientos: true,
      cuentaId: cuentaId,
      title: `Movimientos — ${cuenta.nombre || cuentaId}`,
      subtitle: `${data.desde} → ${data.hasta} · ${activeEmpresaPropia?.nombre || "Todas"}`,
      summary: [
        ["Saldo inicial", fmtCuenta(data.saldo_inicial)],
        ["Total ingresos (banco)", fmtCuenta(data.total_ingresos)],
        ["Total egresos (banco)", fmtCuenta(data.total_egresos)],
        ["Saldo final (banco)", fmtCuenta(data.saldo_final)],
        ...summaryExtra,
      ],
      sections: [
        {
          title: `Movimientos del periodo (${rowsConSaldo.length} de ${todasConSaldo.length} registros)`,
          columns: ["Fecha", "Nro. Comprobante", "Concepto", "Ingresos (Crédito)", "Egresos (Débito)", "Saldo"],
          rows: rowsConSaldo.map(r => [
            r.fecha,
            r.comprobante,
            r.concepto,
            r.ingreso != null
              ? <span style={{ color: "#16a34a", fontWeight: 700 }}>{fmtCuenta(r.ingreso)}</span>
              : <span style={{ color: "#cbd5e1" }}>—</span>,
            r.egreso != null
              ? <span style={{ color: "#dc2626", fontWeight: 700 }}>{fmtCuenta(r.egreso)}</span>
              : <span style={{ color: "#cbd5e1" }}>—</span>,
            <span style={{ fontWeight: 700, color: r.saldo >= 0 ? "#0f172a" : "#dc2626" }}>
              {fmtCuenta(r.saldo)}
            </span>,
          ]),
        },
        ...seccionesSF,
      ],
      periodLabel: `${data.desde} → ${data.hasta}`,
    };
  };

  const handleMovimientosFiltro = (nuevoFiltro) => {
    setMovFiltro(nuevoFiltro);
    if (movData) {
      setGenericReport(buildMovimientosReport(movData, nuevoFiltro, movData.cuenta?.id));
    }
  };

  const ejecutarReporteMovimientos = async (cuentaId) => {
    setGenericReportLoading(true);
    setShowMovPicker(false);
    try {
      const desde = reportMovDesde;
      const hasta = reportMovHasta;
      if (!desde || !hasta) {
        toast.error("Indicá fecha desde y hasta");
        setGenericReportLoading(false);
        return;
      }
      if (desde > hasta) {
        toast.error("La fecha desde no puede ser posterior a hasta");
        setGenericReportLoading(false);
        return;
      }
      const q = new URLSearchParams({ cuenta_id: cuentaId, desde, hasta });
      if (activeEmpresaPropia?.slug) q.set("logo_tipo", activeEmpresaPropia.slug);
      const res = await fetch(`${API}/admin/reportes/movimientos?${q}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || "Error al generar reporte");
      const data = await res.json();
      setMovData(data);
      setMovFiltro("todos");
      setGenericReport(buildMovimientosReport(data, "todos", cuentaId));
    } catch (e) {
      toast.error(e.message || "Error en reporte de movimientos");
    } finally {
      setGenericReportLoading(false);
    }
  };

  const abrirReporteMovimientos = async () => {
    setGenericReportLoading(true);
    try {
      const q = activeEmpresaPropia?.slug ? `?logo_tipo=${activeEmpresaPropia.slug}` : "";
      const res = await fetch(`${API}/admin/reportes/movimientos/cuentas${q}`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.status === 404) {
        toast.error("El reporte de movimientos no está disponible. Reiniciá el servidor backend o contactá soporte.");
        return;
      }
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Sin acceso al reporte");
      }
      const cuentas = await res.json();
      if (!cuentas.length) {
        toast.error(
          "No tenés cuentas bancarias habilitadas para este reporte. " +
          "Pedí al administrador que te asigne cuentas en Bancos → ícono de usuarios."
        );
        return;
      }
      setMovCuentasPicker(cuentas);
      setShowMovPicker(true);
    } catch (e) {
      toast.error(e.message || "No se pudo cargar cuentas");
    } finally {
      setGenericReportLoading(false);
    }
  };

  const empresaName = empresaFromUrl ? empresas.find(e => e.id === empresaFromUrl)?.nombre : null;
  const now = new Date().toLocaleDateString("es-PY", { day: "2-digit", month: "2-digit", year: "numeric" });
  const nowFull = new Date().toLocaleString("es-PY");
  const currentYear = new Date().getFullYear();
  const reportYears = Array.from({ length: 9 }, (_, i) => String(currentYear + 2 - i));
  const reportMonthIndex = Math.max(0, Math.min(11, Number(reportMonth.slice(5, 7)) - 1));
  const updateReportMonth = (next) => {
    setReportMonth(next);
    setReportYear(next.slice(0, 4));
  };

  useEffect(() => {
    if (tab === "alertas" && !hasPermission?.("alertas.ver")) {
      setTab("reportes");
    }
  }, [tab, activeEmpresaPropia?.id, user?.permisos]); // eslint-disable-line

  useEffect(() => {
    const financieroVisible = hasPermission?.("reportes.balance") || hasPermission?.("reportes.flow_facturaciones") || hasPermission?.("reportes.cliente_detallado") || hasPermission?.("reportes.flow_financieros") || hasPermission?.("reportes.presupuestos") || hasPermission?.("reportes.proveedores") || hasPermission?.("reportes.prestamos") || hasPermission?.("reportes.iva") || hasPermission?.("reportes.caja_banco") || hasPermission?.("reportes.movimientos");
    const visibles = [
      financieroVisible && "financiero",
      (hasModule?.("productos_stock") && (hasPermission?.("reportes.productos_stock") || hasPermission?.("reportes.stock_historial") || hasPermission?.("reportes.kardex"))) && "inventario",
      (hasModule?.("inventario_tecnico") && hasPermission?.("reportes.inventario_tecnico")) && "tecnico",
    ].filter(Boolean);
    if (visibles.length && !visibles.includes(reporteCategoria)) {
      setReporteCategoria(visibles[0]);
    }
  }, [reporteCategoria, activeEmpresaPropia?.id, user?.permisos]); // eslint-disable-line

  useEffect(() => {
    if ((reporteCategoria === "inventario" || reporteSeleccionado === "flow_facturaciones") && (stockProductos.length === 0 || stockCategorias.length === 0 || stockAtributos.length === 0)) {
      const q = new URLSearchParams();
      if (activeEmpresaPropia?.slug) q.set("logo_tipo", activeEmpresaPropia.slug);
      fetch(`${API}/admin/productos?${q}`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => r.ok ? r.json() : [])
        .then(data => setStockProductos((data || []).filter(p => p.activo !== false).sort((a, b) => (a.nombre || "").localeCompare(b.nombre || ""))))
        .catch(() => {});
      fetch(`${API}/admin/categorias-productos?${q}`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => r.ok ? r.json() : [])
        .then(data => setStockCategorias(data || []))
        .catch(() => {});
      fetch(`${API}/admin/atributos-productos?${q}`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => r.ok ? r.json() : [])
        .then(data => setStockAtributos((data || []).filter(a => a.activo !== false)))
        .catch(() => {});
      fetch(`${API}/admin/config-productos?${q}`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => r.ok ? r.json() : { usar_subcategorias: false, usar_atributos: false })
        .then(data => setStockConfigProductos({ usar_subcategorias: data.usar_subcategorias !== false, usar_atributos: data.usar_atributos !== false }))
        .catch(() => setStockConfigProductos({ usar_subcategorias: false, usar_atributos: false }));
    }
  }, [reporteCategoria, reporteSeleccionado, activeEmpresaPropia?.slug]); // eslint-disable-line

  if (loading) return <div className="flex items-center justify-center h-screen"><div className="text-arandu-blue animate-pulse">Cargando...</div></div>;

  const selectedEmpresasNombres = selectedEmpresas.map(id => empresas.find(e => e.id === id)?.nombre).filter(Boolean).join(", ");
  // Excluimos "Cuentas de Acceso" de la sección INVENTARIO para evitar duplicados
  // (esas cuentas ya aparecen en las secciones 3 y 4)
  const inventarioActivos = previewData ? previewData.filter(r => r.categoria !== "Cuentas de Acceso") : [];
  const activosConCreds = inventarioActivos.filter(r => r.credenciales?.length > 0);
  // Bienes con datos de adquisición cargados (marca, modelo, chasis, factura, etc.)
  const bienesConDatos = inventarioActivos.filter(r =>
    r.marca || r.modelo || r.chasis || r.numero_factura || r.fecha_adquisicion || r.monto_adquisicion || r.anio_fabricacion
  );

  // Agrupación NVR/DVR para los modos "por_dispositivo" e "ip_agrupado"
  const inventarioGrouped = sortBy === "ip_agrupado" && inventarioActivos.length > 0
    ? (() => {
        // Cámaras con NVR/DVR asignado
        const camerasWithNvr = inventarioActivos.filter(a => a.nvr_dvr_id);
        const nvrIdsSet = new Set(camerasWithNvr.map(c => c.nvr_dvr_id));
        // NVR/DVR que tienen al menos una cámara asignada
        const nvrEntries = inventarioActivos.filter(a => nvrIdsSet.has(a.activo_id));
        const nvrActivoIds = new Set(nvrEntries.map(n => n.activo_id));
        // Activos sin agrupamiento: ni cámara con NVR, ni NVR con cámaras
        const standaloneItems = inventarioActivos.filter(a =>
          !a.nvr_dvr_id && !nvrActivoIds.has(a.activo_id)
        );
        const groups = nvrEntries.map(nvr => ({
          nvr,
          cameras: camerasWithNvr.filter(c => c.nvr_dvr_id === nvr.activo_id)
        }));
        return { groups, standaloneItems };
      })()
    : null;

  // Inline style for the section-title row inside <thead> — repeats automatically on every print page break
  const secTitleInThead = (color) => ({
    color, fontWeight: "bold", fontSize: "12px",
    paddingTop: "16px", paddingBottom: "3px", paddingLeft: "0", paddingRight: "0",
    borderTop: "none", borderLeft: "none", borderRight: "none",
    borderBottom: `2px solid ${color}`,
    textTransform: "uppercase", letterSpacing: "0.04em",
    backgroundColor: "#ffffff",
  });
  const reportLogoAlt = String(activeEmpresaPropia?.nombre || "Logo").replace(/"/g, "&quot;");
  const reportLogoHtml = activeEmpresaPropia?.logo_url
    ? `<div style="display:flex;align-items:center;gap:12px"><img src="${activeEmpresaPropia.logo_url}" alt="${reportLogoAlt}" style="height:54px;width:auto;max-width:190px;object-fit:contain"/></div>`
    : svgDocumentHeaderLogoHtml(activeEmpresaPropia?.slug || "arandujar");

  return (
    <div className="p-4 md:p-8">

      {genericReport && (
        <div className="fixed inset-0 bg-black/85 z-50 overflow-y-auto">
          <div className="no-print sticky top-0 z-10 bg-arandu-dark/95 backdrop-blur-sm border-b border-white/10 px-4 py-3">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-white font-heading font-bold">{genericReport.title}</h3>
                <p className="text-slate-400 text-xs">{genericReport.subtitle}</p>
              </div>
              <div className="flex gap-2 items-center flex-wrap justify-end">
                <Button onClick={() => downloadPdf("generic-report-print", genericReport.title)} disabled={pdfLoading} className="bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-60">
                  <Download className="w-4 h-4 mr-2" /> {pdfLoading ? "Generando..." : "Descargar PDF"}
                </Button>
                {!genericReport.isMovimientos && (
                  <Button onClick={exportGenericExcel} className="bg-emerald-700 hover:bg-emerald-800 text-white">
                    <Download className="w-4 h-4 mr-2" /> Excel
                  </Button>
                )}
                <Button onClick={() => printNode("generic-report-print", genericReport.title)} className="bg-blue-600 hover:bg-blue-700 text-white">
                  <Printer className="w-4 h-4 mr-2" /> Imprimir
                </Button>
                <Button onClick={() => setGenericReport(null)} className="bg-red-600 hover:bg-red-700 text-white">
                  <X className="w-4 h-4 mr-2" /> Cerrar
                </Button>
              </div>
            </div>
            {genericReport.isMovimientos && (
              <div className="flex flex-wrap items-center gap-2 mt-3 pt-3 border-t border-white/10">
                <span className="text-slate-500 text-xs font-medium mr-1">Filtrar movimientos:</span>
                {[
                  ["todos", "Todos"],
                  ["credito", "Crédito"],
                  ["debito", "Débito"],
                  ["transf_recibida", "Transferencia recibida"],
                  ["transf_realizada", "Transferencia realizada"],
                ].map(([id, label]) => (
                  <button key={id} onClick={() => handleMovimientosFiltro(id)}
                    className={`px-3 py-1.5 rounded-full border text-xs font-medium transition-colors ${movFiltro === id ? "bg-emerald-500 text-white border-emerald-400" : "bg-white/5 text-slate-300 border-white/10 hover:bg-white/10"}`}>
                    {label}
                  </button>
                ))}
              </div>
            )}
          </div>
          <div
            id="generic-report-print"
            className="modern-report"
            style={{
              maxWidth: "1220px",
              margin: "16px auto",
              background: "#fff",
              color: "#0f172a",
              padding: 0,
              fontFamily: "Arial, Helvetica, sans-serif",
              borderRadius: "18px",
              overflow: "hidden",
              boxShadow: "0 24px 70px rgba(15,23,42,0.35)",
            }}
          >
            <style>{`
              #generic-report-print table { border-collapse: separate; border-spacing: 0; width: 100%; overflow: hidden; border-radius: 10px; }
              #generic-report-print th { background: #0f172a; color: #fff; font-size: 10px; padding: 9px 10px; text-align: left; border: 0; letter-spacing: .03em; text-transform: uppercase; }
              #generic-report-print td { font-size: 10.5px; padding: 8px 10px; border-bottom: 1px solid #e2e8f0; vertical-align: top; color: #334155; white-space: pre-line; }
              #generic-report-print tbody tr:nth-child(odd) td { background: #f8fafc; }
              #generic-report-print tbody tr:nth-child(even) td { background: #fff; }
              #generic-report-print tbody tr:last-child td { border-bottom: 0; }
              #generic-report-print .summary-card:nth-child(4n+1) { border-color: #14b8a6; }
              #generic-report-print .summary-card:nth-child(4n+2) { border-color: #38bdf8; }
              #generic-report-print .summary-card:nth-child(4n+3) { border-color: #f59e0b; }
              #generic-report-print .summary-card:nth-child(4n+4) { border-color: #a78bfa; }
              @media print {
                #generic-report-print { border-radius: 0 !important; box-shadow: none !important; }
              }
            `}</style>
            <div style={{
              background: "linear-gradient(135deg, #0f172a 0%, #12364a 48%, #0f766e 100%)",
              color: "#fff",
              padding: "22px 26px",
              display: "flex",
              justifyContent: "space-between",
              gap: "20px",
              alignItems: "flex-start",
            }}>
              <div style={{ display: "flex", gap: 16, alignItems: "center" }}>
                <div style={{ background: "#fff", borderRadius: 14, padding: "10px 14px", minWidth: 210 }} dangerouslySetInnerHTML={{ __html: reportLogoHtml }} />
                <div>
                  <div style={{ fontSize: 11, color: "#99f6e4", textTransform: "uppercase", letterSpacing: 2, marginBottom: 5 }}>Reporte administrativo</div>
                  <h1 style={{ margin: 0, fontSize: 25, lineHeight: 1.1, letterSpacing: 0 }}>{genericReport.title}</h1>
                  <p style={{ margin: "7px 0 0", color: "#cbd5e1", fontSize: 11 }}>{genericReport.subtitle}</p>
                </div>
              </div>
              <div style={{ textAlign: "right", fontSize: 10, color: "#dbeafe", minWidth: 150 }}>
                <div style={{ color: "#99f6e4", textTransform: "uppercase", letterSpacing: 1.4 }}>Periodo</div>
                <strong style={{ display: "block", fontSize: 15, marginTop: 4 }}>{genericReport.periodLabel || mesLabel(reportMonth) || reportYear}</strong>
                <div style={{ marginTop: 10 }}>Generado: {nowFull}</div>
              </div>
            </div>
            <div style={{ padding: "24px 26px 26px" }}>
              {(genericReport.sections || []).map((section, sectionIdx) => {
                const showGroup = section.group && section.group !== genericReport.sections?.[sectionIdx - 1]?.group;
                const groupColor = section.group === "Ingresos" ? "#10b981" : "#ef4444";
                return (
                  <React.Fragment key={section.title}>
                    {showGroup && (
                      <div style={{ margin: sectionIdx === 0 ? "2px 0 12px" : "26px 0 12px", padding: "10px 14px", borderRadius: 12, background: section.group === "Ingresos" ? "#ecfdf5" : "#fef2f2", color: groupColor, border: `1px solid ${section.group === "Ingresos" ? "#a7f3d0" : "#fecaca"}`, fontSize: 13, fontWeight: 800, textTransform: "uppercase", letterSpacing: 1.4 }}>
                        {section.group}
                      </div>
                    )}
                    <section className="report-section" style={{ marginTop: sectionIdx === 0 || showGroup ? 0 : 22 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "0 0 8px", breakAfter: "avoid", pageBreakAfter: "avoid" }}>
                        <span style={{ display: "inline-block", width: 8, height: 22, borderRadius: 99, background: section.group === "Egresos" ? "#ef4444" : sectionIdx % 2 ? "#38bdf8" : "#14b8a6" }} />
                        <h2 style={{ margin: 0, fontSize: 15, color: "#0f172a", letterSpacing: 0 }}>{section.title}</h2>
                        <span style={{ color: "#94a3b8", fontSize: 10 }}>({(section.rows || []).length} registros)</span>
                        {section.total && (
                          <span style={{ marginLeft: "auto", background: section.group === "Egresos" ? "#fef2f2" : "#ecfdf5", color: section.group === "Egresos" ? "#b91c1c" : "#047857", border: `1px solid ${section.group === "Egresos" ? "#fecaca" : "#a7f3d0"}`, borderRadius: 999, padding: "5px 10px", fontSize: 11, fontWeight: 700 }}>
                            {section.totalLabel || "Total"}: {section.total}
                          </span>
                        )}
                      </div>
                      <table>
                        <thead>
                          <tr>{section.columns.map(c => <th key={c}>{c}</th>)}</tr>
                        </thead>
                        <tbody>
                          {(section.rows || []).length === 0 ? (
                            <tr><td colSpan={section.columns.length} style={{ color: "#64748b", textAlign: "center", padding: 20 }}>Sin datos para este reporte</td></tr>
                          ) : section.rows.map((row, idx) => {
                            // Flat array row (legacy format)
                            if (Array.isArray(row)) {
                              return <tr key={idx}>{row.map((cell, i) => <td key={i}>{cell}</td>)}</tr>;
                            }
                            // Block row: { cells, subRows }
                            const { cells, subRows = [] } = row;
                            const baseBg = idx % 2 === 0 ? "#fff" : "#f8fafc";
                            const subBg  = idx % 2 === 0 ? "#f5f8fc" : "#eef2f7";
                            return (
                              <React.Fragment key={idx}>
                                <tr style={{ background: baseBg }}>
                                  {cells.map((cell, i) => (
                                    <td key={i} style={{ fontWeight: 600, borderBottom: subRows.length ? "none" : undefined }}>{cell}</td>
                                  ))}
                                </tr>
                                {subRows.map((sub, si) => (
                                  <tr key={`${idx}-s${si}`} style={{ background: subBg }}>
                                    {sub.map((cell, i) => (
                                      <td key={i} style={{ fontSize: 11, color: "#64748b", paddingTop: 2, paddingBottom: 2, paddingLeft: i === 1 ? 20 : undefined, borderBottom: si === subRows.length - 1 ? "2px solid #e2e8f0" : "1px solid #f0f4f8" }}>{cell}</td>
                                    ))}
                                  </tr>
                                ))}
                              </React.Fragment>
                            );
                          })}
                        </tbody>
                      </table>
                    </section>
                  </React.Fragment>
                );
              })}
              {reporteSeleccionado === "balancete_obra" && balanceteObraChart.length > 0 && (
                <div style={{ marginTop: 26 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "0 0 12px" }}>
                    <span style={{ display: "inline-block", width: 8, height: 22, borderRadius: 99, background: "#a78bfa" }} />
                    <h2 style={{ margin: 0, fontSize: 15, color: "#0f172a" }}>Distribución financiera por obra</h2>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: 16 }}>
                    {balanceteObraChart.map(o => {
                      const materiales = Math.max(o.costo_materiales || 0, 0);
                      const personal = Math.max(o.costo_personal || 0, 0);
                      const gananciaPos = Math.max(o.ganancia_neta || 0, 0);
                      const pieData = [
                        { name: "Materiales", value: materiales, color: "#f59e0b" },
                        { name: "Mano de Obra", value: personal, color: "#38bdf8" },
                        { name: "Ganancia Real", value: gananciaPos, color: "#10b981" },
                      ];
                      const sinDatos = materiales + personal + gananciaPos <= 0;
                      return (
                        <div key={o.tipo_factura_id} style={{ border: "1px solid #e2e8f0", borderRadius: 12, padding: 12 }}>
                          <p style={{ margin: 0, fontSize: 12, fontWeight: 700, color: "#0f172a" }}>{o.obra_nombre}</p>
                          <p style={{ margin: "2px 0 6px", fontSize: 10, color: "#64748b" }}>{o.cliente_nombre}</p>
                          {sinDatos ? (
                            <p style={{ fontSize: 11, color: "#94a3b8", textAlign: "center", padding: "24px 0" }}>Sin movimientos para graficar</p>
                          ) : (
                            <div style={{ width: "100%", height: 190 }}>
                              <ResponsiveContainer>
                                <PieChart>
                                  <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={65}
                                    label={({ percent }) => `${(percent * 100).toFixed(0)}%`}>
                                    {pieData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                                  </Pie>
                                  <Tooltip formatter={(value) => fmtPYG(value)} />
                                  <Legend wrapperStyle={{ fontSize: 10 }} />
                                </PieChart>
                              </ResponsiveContainer>
                            </div>
                          )}
                          {o.ganancia_neta < 0 && (
                            <p style={{ fontSize: 10, color: "#b91c1c", marginTop: 4, textAlign: "center" }}>
                              Pérdida: {fmtPYG(o.ganancia_neta)} (los costos superan lo cobrado — no representable en el gráfico)
                            </p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              <div style={{ marginTop: 26, paddingTop: 20, borderTop: "2px solid #e2e8f0", breakBefore: "page", pageBreakBefore: "always" }}>
                <div style={{ fontSize: 10, color: "#64748b", textTransform: "uppercase", letterSpacing: 1.4, fontWeight: 700, marginBottom: 12 }}>Resumen del período</div>
                <div className="summary" style={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: 12, marginBottom: 14 }}>
                  {(genericReport.summary || []).map(([label, value, subItems], idx) => (
                    <div key={label} className="summary-card" style={{ border: "1px solid #14b8a6", borderLeftWidth: 5, borderRadius: 12, padding: "12px 13px", background: idx % 2 ? "#f8fafc" : "#ffffff" }}>
                      <p style={{ margin: 0, color: "#64748b", fontSize: 10, textTransform: "uppercase", letterSpacing: 1 }}>{label}</p>
                      <strong style={{ display: "block", marginTop: 5, fontSize: 19, color: "#0f172a", lineHeight: 1.15 }}>{value}</strong>
                      {subItems && subItems.length > 0 && (
                        <div style={{ marginTop: 7, paddingTop: 6, borderTop: "1px solid #e2e8f0" }}>
                          {subItems.map(([sl, sv]) => (
                            <div key={sl} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 3 }}>
                              <span style={{ fontSize: 9, color: "#64748b" }}>{sl}</span>
                              <span style={{ fontSize: 9, fontWeight: 700, color: "#0f172a" }}>{sv}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                {genericReport.accountSummary?.rows?.length > 0 && (
                  <div style={{ marginBottom: 14, border: "1px solid #dbeafe", borderRadius: 14, overflow: "hidden", background: "#f8fafc" }}>
                    <div style={{ padding: "10px 13px", background: "#eff6ff", borderBottom: "1px solid #dbeafe", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <h2 style={{ margin: 0, color: "#1e3a8a", fontSize: 13, letterSpacing: 0 }}>{genericReport.accountSummary.title || "Resumen por cuenta"}</h2>
                      <span style={{ color: "#64748b", fontSize: 10 }}>{genericReport.accountSummary.rows.length} cuenta(s)</span>
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 8, padding: 12 }}>
                      {genericReport.accountSummary.rows.map((row, idx) => (
                        <div key={`${row.label}-${idx}`} style={{ background: "#ffffff", border: "1px solid #e2e8f0", borderRadius: 11, padding: "9px 10px" }}>
                          <p style={{ margin: 0, color: "#475569", fontSize: 10, lineHeight: 1.25 }}>{row.label}</p>
                          <strong style={{ display: "block", marginTop: 4, color: "#0f172a", fontSize: 16, lineHeight: 1.15 }}>{row.value}</strong>
                          {row.note && <span style={{ display: "block", marginTop: 3, color: "#94a3b8", fontSize: 9 }}>{row.note}</span>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <div style={{ marginTop: 10, paddingTop: 10, borderTop: "1px solid #e2e8f0", display: "flex", justifyContent: "space-between", color: "#64748b", fontSize: 9 }}>
                <span>{activeEmpresaPropia?.nombre || "Arandu&JAR"}</span>
                <span>Este documento fue generado por <strong style={{ color: "#475569" }}>Sistema AranduJAR-ERP</strong></span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════ MODAL SELECTOR DE CLIENTE ══════════════════ */}
      {clientePickerTipo && (
        <div
          className="fixed inset-0 bg-black/75 z-50 flex items-center justify-center p-4"
        >
          <div className="bg-arandu-dark-light border border-white/10 rounded-xl w-full max-w-md p-5">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-white font-heading text-base font-semibold">Seleccionar cliente</h2>
              <button onClick={() => setClientePickerTipo(null)} className="text-slate-400 hover:text-white p-1">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Buscador */}
            <div className="relative mb-3">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none" />
              <input
                autoFocus
                type="text"
                placeholder="Buscar cliente por nombre o RUC..."
                value={clientePickerSearch}
                onChange={e => setClientePickerSearch(e.target.value)}
                className="w-full bg-arandu-dark border border-white/10 text-white text-sm rounded-lg pl-9 pr-3 py-2 outline-none focus:border-sky-500/50"
              />
            </div>

            {/* Lista */}
            <div className="max-h-64 overflow-y-auto space-y-1 mb-4">
              {empresas
                .filter(emp => {
                  const q = clientePickerSearch.toLowerCase();
                  if (!q) return true;
                  return (emp.nombre || emp.razon_social || "").toLowerCase().includes(q)
                    || (emp.ruc || "").toLowerCase().includes(q);
                })
                .map(emp => (
                  <button
                    key={emp.id}
                    onClick={() => setClientePickerTempId(emp.id)}
                    className={`w-full text-left px-3 py-2.5 rounded-lg text-sm transition-all ${
                      clientePickerTempId === emp.id
                        ? "bg-sky-500/20 text-sky-300 border border-sky-500/30"
                        : "text-slate-300 hover:bg-white/5 border border-transparent"
                    }`}
                  >
                    <div className="font-medium">{emp.nombre || emp.razon_social || emp.id}</div>
                    {emp.ruc && <div className="text-xs text-slate-500 mt-0.5">RUC: {emp.ruc}</div>}
                  </button>
                ))}
              {empresas.filter(emp => {
                const q = clientePickerSearch.toLowerCase();
                return !q || (emp.nombre || emp.razon_social || "").toLowerCase().includes(q) || (emp.ruc || "").toLowerCase().includes(q);
              }).length === 0 && (
                <p className="text-slate-500 text-sm text-center py-4">Sin resultados</p>
              )}
            </div>

            {/* Acciones */}
            <div className="flex gap-2 pt-2 border-t border-white/5">
              <button
                onClick={() => setClientePickerTipo(null)}
                className="flex-1 px-4 py-2 text-sm text-slate-400 border border-white/10 rounded-lg hover:bg-white/5 transition-colors"
              >
                Cancelar
              </button>
              <button
                disabled={!clientePickerTempId || genericReportLoading}
                onClick={() => {
                  const tipoAGenerar = clientePickerTipo;
                  const idCliente = clientePickerTempId;
                  setReportClienteId(idCliente);
                  setClientePickerTipo(null);
                  generarReporte(tipoAGenerar, idCliente);
                }}
                className="flex-1 px-4 py-2 text-sm font-medium bg-sky-500/20 text-sky-300 border border-sky-500/30 rounded-lg hover:bg-sky-500/30 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {genericReportLoading ? "Generando..." : "Generar reporte"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════ PRINT PREVIEW ══════════════════ */}
      {showPreview && previewData && (
        <div id="report-modal-overlay" className="fixed inset-0 bg-black/90 z-50 overflow-y-auto">

          {/* Barra de acciones — se oculta al imprimir */}
          <div className="no-print sticky top-0 z-10 bg-arandu-dark/95 backdrop-blur-sm border-b border-white/10 px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <h3 className="text-white font-heading font-bold">Vista Previa del Reporte</h3>
              <span className="text-slate-400 text-sm">{previewData.length} activos</span>
              {previewWithCreds && <span className="text-amber-400 text-xs bg-amber-500/10 px-2 py-0.5 rounded border border-amber-500/20">Con Credenciales</span>}
            </div>
            <div className="flex gap-2 flex-wrap justify-end items-center">
              <Button onClick={handlePrint} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="print-report-btn">
                <Printer className="w-4 h-4 mr-2" /> Imprimir / Guardar PDF
              </Button>
              <Button onClick={exportCSV} className="bg-cyan-600 hover:bg-cyan-700 text-white">
                <Download className="w-4 h-4 mr-2" /> CSV
              </Button>
              <Button onClick={exportExcel} className="bg-emerald-600 hover:bg-emerald-700 text-white">
                <Table className="w-4 h-4 mr-2" /> Excel
              </Button>
              <Button onClick={() => setShowPreview(false)} className="bg-red-600 hover:bg-red-700 text-white">
                <X className="w-4 h-4 mr-2" /> Cerrar
              </Button>
            </div>
          </div>

          {/* ── Contenido del reporte (todo inline styles para que imprima bien) ── */}
          <div
            id="inventario-print"
            style={{
              maxWidth: "1200px", margin: "16px auto",
              backgroundColor: "#fff", color: "#000",
              padding: "24px",
              fontFamily: "Arial, Helvetica, sans-serif",
              fontSize: "10px", lineHeight: "1.4",
            }}
          >

            {/* ─ Encabezado ─ */}
            <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", borderBottom: "2px solid #1e3a5f", paddingBottom: "10px", marginBottom: "14px" }}>
              <div
                style={{ minWidth: "190px", maxWidth: "260px" }}
                dangerouslySetInnerHTML={{ __html: reportLogoHtml }}
              />
              <div style={{ textAlign: "center", flex: 1 }}>
                <div style={{ fontSize: "17px", fontWeight: "bold", color: "#111" }}>Informe de Inventario Tecnico</div>
              </div>
              <div style={{ textAlign: "right", fontSize: "9px", color: "#555", minWidth: "200px" }}>
                <div>Fecha de emisión: {now} | Total: {inventarioActivos.length} activos</div>
                {selectedEmpresasNombres && <div style={{ marginTop: "2px" }}>Filtros — <b>Cliente:</b> {selectedEmpresasNombres}</div>}
                {sortBy === "ip_agrupado" && <div style={{ marginTop: "2px", color: "#60a5fa" }}>Modo: NVR/DVR agrupado por IP</div>}
              </div>
            </div>

            {/* ─ 1. INVENTARIO ─ */}
            {(() => {
              const camColors = { bg: "#f0f9ff", border: "#bae6fd", rowAlt: "#e0f2fe" };
              const tblStyle = { width: "100%", borderCollapse: "collapse", marginBottom: "4px", tableLayout: "fixed" };
              const colgroup = (
                <colgroup>
                  <col style={{ width: "16%" }} /><col style={{ width: "9%" }} />
                  <col style={{ width: "10%" }} /><col style={{ width: "11%" }} />
                  <col style={{ width: "6%" }} /><col style={{ width: "12%" }} />
                  <col style={{ width: "9%" }} /><col style={{ width: "7%" }} />
                  <col style={{ width: "7%" }} /><col style={{ width: "9%" }} />
                  <col style={{ width: "4%" }} />
                </colgroup>
              );
              const colHeaders = (
                <tr>
                  {["Nombre","Empresa","Categoría","Subtipo","Estado","IP Privada","IP Pública","Dominio","Ubicación","Marca/Modelo","Resp."].map(h => (
                    <th key={h} style={thStyle(COLORS.inv)}>{h}</th>
                  ))}
                </tr>
              );
              const invRow = (row, idx, colors = COLORS.inv, firstExtra = {}) => {
                const e = idx % 2 === 0;
                return (
                  <tr key={idx}>
                    <td style={tdStyle(e, colors, { fontWeight: "500", ...firstExtra })}>{row.nombre}</td>
                    <td style={tdStyle(e, colors)}>{row.empresa}</td>
                    <td style={tdStyle(e, colors)}>{row.categoria}</td>
                    <td style={tdStyle(e, colors)}>{row.subtipo}</td>
                    <td style={tdStyle(e, colors)}>{row.estado}</td>
                    <td style={tdStyle(e, colors, { fontFamily: "monospace", whiteSpace: "nowrap", overflow: "hidden" })}>{[row.ip_local, ...(row.ips_locales||[])].filter(Boolean).join(", ")}</td>
                    <td style={tdStyle(e, colors, { fontFamily: "monospace", whiteSpace: "nowrap", overflow: "hidden" })}>{[row.ip_publica, ...(row.ips_publicas||[])].filter(Boolean).join(", ")}</td>
                    <td style={tdStyle(e, colors)}>{row.dominio}</td>
                    <td style={tdStyle(e, colors)}>{row.ubicacion}</td>
                    <td style={tdStyle(e, colors)}>{row.version}</td>
                    <td style={tdStyle(e, colors)}>{row.responsable || ""}</td>
                  </tr>
                );
              };

              if (inventarioGrouped) {
                return (
                  <>
                    {/* ── Tabla 1: NVR/DVR y Cámaras — thead con título repite en cada página ── */}
                    {inventarioGrouped.groups.length > 0 && (
                      <table style={tblStyle}>
                        {colgroup}
                        <thead>
                          <tr>
                            <td colSpan={11} style={secTitleInThead(COLORS.inv.title)}>
                              Inventario de Activos — NVR/DVR y Cámaras Asociadas
                            </td>
                          </tr>
                          {colHeaders}
                        </thead>
                        <tbody>
                          {inventarioGrouped.groups.map(({ nvr, cameras }, gi) => (
                            <React.Fragment key={nvr.activo_id || nvr.nombre}>
                              {gi > 0 && <tr><td colSpan={11} style={{ backgroundColor: "#e2e8f0", padding: "2px", border: "none" }} /></tr>}
                              {/* Fila NVR destacada */}
                              <tr>
                                <td colSpan={11} style={{ backgroundColor: "#1e3a5f", color: "#ffffff", fontWeight: "bold", fontSize: "9px", padding: "5px 7px", border: "1px solid #152b47" }}>
                                  ▶ {nvr.nombre}
                                  <span style={{ fontWeight: "normal", marginLeft: "8px", color: "#93c5fd" }}>{nvr.subtipo}</span>
                                  {[nvr.ip_local, ...(nvr.ips_locales||[])].filter(Boolean).length > 0 && (
                                    <span style={{ fontFamily: "monospace", marginLeft: "8px", color: "#67e8f9" }}>{[nvr.ip_local, ...(nvr.ips_locales||[])].filter(Boolean).join(", ")}</span>
                                  )}
                                  <span style={{ marginLeft: "8px", color: "#94a3b8", fontWeight: "normal" }}>• {nvr.empresa}</span>
                                  <span style={{ marginLeft: "8px", color: "#86efac", fontWeight: "normal" }}>{nvr.estado}</span>
                                  <span style={{ marginLeft: "8px", color: "#fde68a" }}>{cameras.length} cám.</span>
                                </td>
                              </tr>
                              {/* Fila de datos del NVR */}
                              {invRow(nvr, 0, COLORS.inv, { fontWeight: "600" })}
                              {/* Cámaras asociadas */}
                              {cameras.map((cam, ci) => {
                                const e = ci % 2 !== 0;
                                return (
                                  <tr key={`cam-${ci}`}>
                                    <td style={tdStyle(e, camColors, { fontWeight: "500", paddingLeft: "18px" })}>
                                      <span style={{ color: "#0369a1", marginRight: "4px" }}>↳</span>{cam.nombre}
                                    </td>
                                    <td style={tdStyle(e, camColors)}>{cam.empresa}</td>
                                    <td style={tdStyle(e, camColors)}>{cam.categoria}</td>
                                    <td style={tdStyle(e, camColors)}>{cam.subtipo}</td>
                                    <td style={tdStyle(e, camColors)}>{cam.estado}</td>
                                    <td style={tdStyle(e, camColors, { fontFamily: "monospace", whiteSpace: "nowrap", overflow: "hidden" })}>{[cam.ip_local, ...(cam.ips_locales||[])].filter(Boolean).join(", ")}</td>
                                    <td style={tdStyle(e, camColors, { fontFamily: "monospace", whiteSpace: "nowrap", overflow: "hidden" })}>{[cam.ip_publica, ...(cam.ips_publicas||[])].filter(Boolean).join(", ")}</td>
                                    <td style={tdStyle(e, camColors)}>{cam.dominio}</td>
                                    <td style={tdStyle(e, camColors)}>{cam.ubicacion}</td>
                                    <td style={tdStyle(e, camColors)}>{cam.version}</td>
                                    <td style={tdStyle(e, camColors)}>{cam.responsable || ""}</td>
                                  </tr>
                                );
                              })}
                            </React.Fragment>
                          ))}
                        </tbody>
                      </table>
                    )}

                    {/* ── Tabla 2: Otros Activos — thead con título repite en cada página ── */}
                    {inventarioGrouped.standaloneItems.length > 0 && (
                      <table style={{ ...tblStyle, marginTop: "10px" }}>
                        {colgroup}
                        <thead>
                          <tr>
                            <td colSpan={11} style={secTitleInThead(COLORS.inv.title)}>
                              Inventario de Activos — Otros Activos
                            </td>
                          </tr>
                          {colHeaders}
                        </thead>
                        <tbody>
                          {inventarioGrouped.standaloneItems.map((row, idx) => invRow(row, idx))}
                        </tbody>
                      </table>
                    )}
                  </>
                );
              }

              // Modo plano (sin agrupación)
              return (
                <table style={tblStyle}>
                  {colgroup}
                  <thead>
                    <tr>
                      <td colSpan={11} style={secTitleInThead(COLORS.inv.title)}>Inventario de Activos</td>
                    </tr>
                    {colHeaders}
                  </thead>
                  <tbody>
                    {inventarioActivos.map((row, idx) => invRow(row, idx))}
                  </tbody>
                </table>
              );
            })()}

            {/* ─ 1b. DATOS DE ADQUISICIÓN DE BIENES ─ */}
            {bienesConDatos.length > 0 && (
              <div className="section-page-break">
                <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: "4px", tableLayout: "fixed" }}>
                  <colgroup>
                    <col style={{ width: "14%" }} /><col style={{ width: "11%" }} />
                    <col style={{ width: "9%" }} /><col style={{ width: "9%" }} />
                    <col style={{ width: "11%" }} /><col style={{ width: "10%" }} />
                    <col style={{ width: "10%" }} /><col style={{ width: "11%" }} />
                    <col style={{ width: "7%" }} /><col style={{ width: "8%" }} />
                  </colgroup>
                  <thead>
                    <tr>
                      <td colSpan={10} style={secTitleInThead(COLORS.inv.title)}>Datos de Adquisición de Bienes</td>
                    </tr>
                    <tr>
                      {["Nombre","Empresa","Marca","Modelo","Chasis","Nº Factura","Fecha Adq.","Monto Adq.","Año Fab.","Estado del Bien"].map(h => (
                        <th key={h} style={thStyle(COLORS.inv)}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {bienesConDatos.map((row, idx) => {
                      const e = idx % 2 === 0;
                      return (
                        <tr key={idx}>
                          <td style={tdStyle(e, COLORS.inv, { fontWeight: "500" })}>{row.nombre}</td>
                          <td style={tdStyle(e, COLORS.inv)}>{row.empresa}</td>
                          <td style={tdStyle(e, COLORS.inv)}>{row.marca}</td>
                          <td style={tdStyle(e, COLORS.inv)}>{row.modelo}</td>
                          <td style={tdStyle(e, COLORS.inv, { fontFamily: "monospace" })}>{row.chasis}</td>
                          <td style={tdStyle(e, COLORS.inv)}>{row.numero_factura}</td>
                          <td style={tdStyle(e, COLORS.inv)}>{row.fecha_adquisicion}</td>
                          <td style={tdStyle(e, COLORS.inv)}>{row.monto_adquisicion != null ? fmtMonedaReporte(row.monto_adquisicion, row.moneda_adquisicion) : ""}</td>
                          <td style={tdStyle(e, COLORS.inv)}>{row.anio_fabricacion ?? ""}</td>
                          <td style={tdStyle(e, COLORS.inv, row.estado_bien === "bajado" ? { color: "#dc2626", fontWeight: "600" } : {})}>
                            {row.estado_bien === "bajado" ? "Bajado" : "Normal"}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan={10} style={{ padding: "4px 7px", fontSize: "9px", color: "#64748b", border: "none" }}>
                        Monto total de adquisición: {["PYG", "USD", "BRL"].map(mon => {
                          const total = bienesConDatos.filter(r => (r.moneda_adquisicion || "PYG") === mon).reduce((s, r) => s + Number(r.monto_adquisicion || 0), 0);
                          return total > 0 ? fmtMonedaReporte(total, mon) : null;
                        }).filter(Boolean).join(" · ") || fmtPYG(0)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}

            {/* ─ 2. CREDENCIALES ─ */}
            {previewWithCreds && activosConCreds.length > 0 && (
              <div className="section-page-break">
                <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: "4px", tableLayout: "fixed" }}>
                  <colgroup>
                    <col style={{ width: "18%" }} /><col style={{ width: "15%" }} />
                    <col style={{ width: "16%" }} /><col style={{ width: "16%" }} />
                    <col style={{ width: "10%" }} /><col style={{ width: "25%" }} />
                  </colgroup>
                  <thead>
                    <tr>
                      <td colSpan={6} style={secTitleInThead(COLORS.cred.title)}>Credenciales</td>
                    </tr>
                    <tr>
                      {["Activo","Servicio","Usuario","Contraseña","Sensibilidad","Notas"].map(h => (
                        <th key={h} style={thStyle(COLORS.cred)}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {activosConCreds.map(row =>
                      (row.credenciales || []).map((c, ci) => {
                        const e = ci % 2 === 0;
                        return (
                          <tr key={`${row.nombre}-${ci}`}>
                            <td style={tdStyle(e, COLORS.cred, { fontWeight: "500" })}>{row.nombre}</td>
                            <td style={tdStyle(e, COLORS.cred)}>{c.servicio || c.tipo_acceso || ""}</td>
                            <td style={tdStyle(e, COLORS.cred, { fontFamily: "monospace", whiteSpace: "nowrap", overflow: "hidden" })}>{c.usuario}</td>
                            <td style={tdStyle(e, COLORS.cred, { fontFamily: "monospace", whiteSpace: "nowrap", overflow: "hidden" })}>{c.password}</td>
                            <td style={tdStyle(e, COLORS.cred)}>{c.sensibilidad || "normal"}</td>
                            <td style={tdStyle(e, COLORS.cred)}>{c.observaciones}</td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            )}

            {/* ─ 3. CUENTAS ASOCIADAS A DISPOSITIVOS ─ */}
            {cuentasAsociadas.length > 0 && (
              <div className="section-page-break">
                <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: "4px", tableLayout: "fixed" }}>
                  <colgroup>
                    <col style={{ width: "18%" }} /><col style={{ width: "12%" }} />
                    <col style={{ width: "20%" }} /><col style={{ width: "14%" }} />
                    <col style={{ width: "14%" }} /><col style={{ width: "22%" }} />
                  </colgroup>
                  <thead>
                    <tr>
                      <td colSpan={6} style={secTitleInThead(COLORS.ctas.title)}>Cuentas Asociadas a Dispositivos</td>
                    </tr>
                    <tr>
                      {["Dispositivo","Tipo Cuenta","Nombre","ID / Usuario","Contraseña","Detalles"].map(h => (
                        <th key={h} style={thStyle(COLORS.ctas)}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {cuentasAsociadas.map((row, idx) => {
                      const e = idx % 2 === 0;
                      return (
                        <tr key={idx}>
                          <td style={tdStyle(e, COLORS.ctas, { fontWeight: "500" })}>{row.dispositivo}</td>
                          <td style={tdStyle(e, COLORS.ctas)}>{row.tipo_cuenta}</td>
                          <td style={tdStyle(e, COLORS.ctas)}>{row.nombre}</td>
                          <td style={tdStyle(e, COLORS.ctas, { fontFamily: "monospace", whiteSpace: "nowrap", overflow: "hidden" })}>{row.usuario}</td>
                          <td style={tdStyle(e, COLORS.ctas, { fontFamily: "monospace", whiteSpace: "nowrap", overflow: "hidden" })}>{row.password}</td>
                          <td style={tdStyle(e, COLORS.ctas)}>{row.detalles}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* ─ 4. DETALLE DE CUENTAS DE ACCESO ─ */}
            {detalleCuentas.length > 0 && (
              <div className="section-page-break">
                <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: "4px", tableLayout: "fixed" }}>
                  <colgroup>
                    <col style={{ width: "22%" }} /><col style={{ width: "12%" }} />
                    <col style={{ width: "14%" }} /><col style={{ width: "22%" }} />
                    <col style={{ width: "14%" }} /><col style={{ width: "16%" }} />
                  </colgroup>
                  <thead>
                    <tr>
                      <td colSpan={6} style={secTitleInThead(COLORS.det.title)}>Detalle de Cuentas de Acceso</td>
                    </tr>
                    <tr>
                      {["Nombre","Subtipo","ID / Usuario","Correo / Servidor","Contraseña","Detalles"].map(h => (
                        <th key={h} style={thStyle(COLORS.det)}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {detalleCuentas.map((row, idx) => {
                      const e = idx % 2 === 0;
                      return (
                        <tr key={idx}>
                          <td style={tdStyle(e, COLORS.det, { fontWeight: "500" })}>{row.nombre}</td>
                          <td style={tdStyle(e, COLORS.det)}>{row.subtipo}</td>
                          <td style={tdStyle(e, COLORS.det, { fontFamily: "monospace", whiteSpace: "nowrap", overflow: "hidden" })}>{row.usuario}</td>
                          <td style={tdStyle(e, COLORS.det)}>{row.correo_servidor}</td>
                          <td style={tdStyle(e, COLORS.det, { fontFamily: "monospace", whiteSpace: "nowrap", overflow: "hidden" })}>{row.password}</td>
                          <td style={{ ...tdStyle(e, COLORS.det), fontWeight: "bold" }}>{row.dispositivos_count} disp.</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* ─ Pie de página ─ */}
            <div style={{ borderTop: "2px solid #1e3a5f", marginTop: "20px", paddingTop: "8px", display: "flex", justifyContent: "space-between", fontSize: "8px", color: "#6b7280" }}>
              <span>Arandu&JAR Informatica - Inventario Tecnico{previewWithCreds ? " - CONFIDENCIAL" : ""}</span>
              <span>Generado: {nowFull}</span>
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════ ENCABEZADO DE PÁGINA ══════════════════ */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <Link to={empresaFromUrl ? "/sistema/empresas" : "/sistema"} className="text-slate-400 hover:text-arandu-blue flex items-center gap-2 mb-2">
            <ArrowLeft className="w-4 h-4" /> {empresaFromUrl ? "Volver a Empresas" : "Volver al Dashboard"}
          </Link>
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-white flex items-center gap-3" data-testid="reportes-title">
            <FileText className="w-8 h-8 text-amber-400" /> Reportes & Alertas
            {empresaName && <span className="text-arandu-blue text-lg font-normal">- {empresaName}</span>}
          </h1>
        </div>
        <button
          onClick={fetchAll}
          disabled={loading}
          className="w-10 h-10 rounded-xl bg-arandu-dark-lighter hover:bg-white/10 border border-white/10 text-slate-300 hover:text-white disabled:opacity-50 flex items-center justify-center transition-colors"
          title="Actualizar"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
        </button>
      </div>

      {/* ══════════════════ BANNER ALERTAS PRÓXIMAS ══════════════════ */}
      {alertasProximasVisibles.length > 0 && (
        <div className="mb-6 bg-red-500/5 border border-red-500/20 rounded-xl p-4">
          <h3 className="text-red-400 font-semibold text-sm flex items-center gap-2 mb-3"><AlertTriangle className="w-4 h-4" /> Alertas Proximas ({alertasProximasVisibles.length})</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
            {alertasProximasVisibles.map(a => (
              <div key={a.id} className={`flex items-center justify-between p-2 rounded-lg border ${getDaysColor(a.dias_restantes, a.color_alerta)}`}>
                <div><p className="text-white text-sm font-medium">{a.nombre}</p><p className="text-slate-400 text-xs">{a.empresa_nombre}</p></div>
                <span className="text-xs font-bold">{a.dias_restantes < 0 ? `Vencido ${Math.abs(a.dias_restantes)}d` : `${a.dias_restantes}d`}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ══════════════════ TABS ══════════════════ */}
      <div className="flex gap-2 mb-6">
        <button onClick={() => setTab("reportes")} className={`px-4 py-2 rounded-lg font-medium text-sm flex items-center gap-2 transition-all ${tab === "reportes" ? "bg-amber-500/20 text-amber-400 border border-amber-500/30" : "bg-arandu-dark-lighter text-slate-400 border border-white/5"}`} data-testid="tab-reportes"><Download className="w-4 h-4" /> Reportes</button>
        {hasPermission?.("alertas.ver") && (
          <button onClick={() => setTab("alertas")} className={`px-4 py-2 rounded-lg font-medium text-sm flex items-center gap-2 transition-all ${tab === "alertas" ? "bg-red-500/20 text-red-400 border border-red-500/30" : "bg-arandu-dark-lighter text-slate-400 border border-white/5"}`} data-testid="tab-alertas">
            <Bell className="w-4 h-4" /> Alertas {alertasProximasVisibles.length > 0 && <span className="bg-red-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">{alertasProximasVisibles.length}</span>}
          </button>
        )}
      </div>

      {/* ══════════════════ TAB: Reportes ══════════════════ */}
      {tab === "reportes" && (() => {
        const inventarioVisible = hasModule?.("productos_stock") && (hasPermission?.("reportes.productos_stock") || hasPermission?.("reportes.stock_historial") || hasPermission?.("reportes.kardex") || hasPermission?.("reportes.descuentos") || hasPermission?.("reportes.vencimiento_productos") || hasPermission?.("reportes.mercaderias_pendientes"));
        const tecnicoVisible = hasModule?.("inventario_tecnico") && hasPermission?.("reportes.inventario_tecnico");

        const INVENTARIO_NAV = [
          { id: "productos_stock", perm: "reportes.productos_stock", label: "Prod. y stock", icon: Package },
          { id: "historial_stock", perm: "reportes.stock_historial", label: "Historial de stock", icon: Table },
          { id: "kardex", perm: "reportes.kardex", label: "Ficha de Mercadería", sub: "Kardex", icon: ClipboardList },
          { id: "descuentos_productos", perm: "reportes.descuentos", label: "Desc. en productos", icon: Percent },
          { id: "vencimiento_productos", perm: "reportes.vencimiento_productos", label: "Vencimiento de productos", icon: Clock },
          { id: "mercaderias_pendientes", perm: "reportes.mercaderias_pendientes", label: "Mercaderías pendientes", icon: Package },
        ];
        const FINANCIERO_NAV = [
          { id: "balance", perm: "reportes.balance", label: "Balance", icon: BarChart3 },
          { id: "flow_facturaciones", perm: "reportes.flow_facturaciones", label: "Flow de Facturaciones", icon: Layers },
          { id: "cliente_detallado", perm: "reportes.cliente_detallado", label: "Ventas por cliente", icon: FileText },
          { id: "libro_ventas", perm: "reportes.libro_ventas", label: "Libro Ventas", icon: Receipt },
          { id: "comisiones_vendedores", perm: "reportes.comisiones_vendedores", label: "Comisiones de Vendedores", icon: Percent },
          { id: "gastos_categoria", perm: "reportes.gastos_categoria", label: "Gastos por Categoría", icon: Calendar },
          { id: "retenciones", perm: "reportes.retenciones", label: "Retenciones de IVA", icon: Percent },
          ...(hasModule?.("recetario") ? [{ id: "costo_hectarea", perm: "reportes.costo_hectarea", label: "Costo por Hectárea", icon: Layers }] : []),
          { id: "transporte_chofer", perm: "reportes.transporte_chofer", label: "Transporte — Choferes", icon: Truck },
          { id: "transporte_cliente", perm: "reportes.transporte", label: "Transporte — Clientes", icon: Truck },
          { id: "constructora_general", perm: "constructora_reportes.ver", label: "Constructora — General", icon: Building2 },
          { id: "constructora_costos", perm: "constructora_reportes.ver", label: "Constructora — Costos por grupo", icon: Building2 },
          { id: "constructora_cronograma", perm: "constructora_reportes.ver", label: "Constructora — Cronograma", icon: Building2 },
          { id: "constructora_equipos", perm: "constructora_reportes.ver", label: "Constructora — Equipos", icon: Building2 },
          { id: "flow_financieros", perm: "reportes.flow_financieros", label: "Flow de Financieros", icon: ArrowUpDown },
          { id: "presupuestos", perm: "reportes.presupuestos", label: "Presupuestos", icon: ClipboardList },
          { id: "proveedores", perm: "reportes.proveedores", label: "Proveedores", icon: DollarSign },
          { id: "catastros", perm: "reportes.catastros", label: "Catastro Unificado", icon: ClipboardList },
          { id: "balancete_obra", perm: "reportes.balancete_obra", label: "Balancete - Obra", icon: Building2 },
          { id: "garantias", perm: "reportes.garantias", label: "Garantías", icon: Shield },
          { id: "loteadora_precificacion", perm: "reportes.loteadora_precificacion", label: "Precificación Loteadora", icon: Building2 },
          { id: "balance_gerencial", perm: "reportes.balance_gerencial", label: "Balance Gerencial", icon: PieChartIcon },
          { id: "prestamos", perm: "reportes.prestamos", label: "Préstamos", icon: DollarSign },
          { id: "iva", perm: "reportes.iva", label: "IVA fiscal", icon: Table },
          { id: "caja_banco", perm: "reportes.caja_banco", label: "Caja / Banco", icon: DollarSign },
          { id: "movimientos", perm: "reportes.movimientos", label: "Movimientos", icon: ArrowUpDown },
          { id: "cuentas_cobrar", perm: "reportes.cuentas_cobrar", label: "Cuentas por cobrar", icon: TrendingUp },
          { id: "cuentas_pagar", perm: "reportes.cuentas_pagar", label: "Cuentas por pagar", icon: TrendingDown },
          { id: "estado_cuenta", perm: "reportes.estado_cuenta", label: "Estado de Cuenta", icon: FileText },
        ];

        const REPORT_META = {
          productos_stock:   { title: "Productos y stock",      desc: "Estado actual del inventario y valorización por producto",          icon: Package,      cat: "inventario", color: "blue" },
          historial_stock:   { title: "Historial de stock",     desc: "Entradas, salidas, ajustes, stock anterior, nuevo y usuario",       icon: Table,        cat: "inventario", color: "blue" },
          kardex:            { title: "Ficha de Mercadería",     desc: "Control de entradas/salidas con saldo acumulado por producto",      icon: ClipboardList,cat: "inventario", color: "blue" },
          balance:           { title: "Balance",                 desc: "Balance mensual, anual o detallado — elegí el tipo y período",    icon: BarChart3,    cat: "financiero", color: "emerald" },
          balance_mensual:   { title: "Balance mensual",        desc: "Resumen mensual de ingresos, egresos y saldo neto",                 icon: TrendingUp,   cat: "financiero", color: "emerald" },
          balance_anual:     { title: "Balance anual",          desc: "Resumen por meses del año seleccionado",                            icon: BarChart3,    cat: "financiero", color: "emerald" },
          balance_detallado: { title: "Balance detallado",      desc: "Facturas, compras, pagos proveedores, gastos y sueldos del mes",    icon: FileText,     cat: "financiero", color: "emerald" },
          flow_facturaciones:{ title: "Flow de Facturaciones",  desc: "Facturas, notas de crédito y compras del período seleccionado",    icon: Layers,       cat: "financiero", color: "emerald" },
          flow_financieros:  { title: "Flow de Financieros",    desc: "Ingresos varios, recibos y gastos fijos del período seleccionado",  icon: ArrowUpDown,  cat: "financiero", color: "emerald" },
          facturas:          { title: "Facturas",               desc: "Facturas emitidas, cobradas y pendientes del período",              icon: Receipt,      cat: "financiero", color: "emerald" },
          cliente_detallado: { title: "Ventas por cliente",     desc: "Facturas, boletas, cobros y notas del cliente elegido",             icon: FileText,     cat: "financiero", color: "emerald" },
          libro_ventas:      { title: "Libro Ventas",           desc: "RUC, cliente, fecha, número de factura, total, contado/crédito, IVA y notas — exportable a Excel", icon: Receipt, cat: "financiero", color: "emerald" },
          comisiones_vendedores: { title: "Comisiones de Vendedores", desc: "Ventas, notas de crédito, base comisionable y liquidación neta por vendedor en el período", icon: Percent, cat: "financiero", color: "emerald" },
          retenciones:       { title: "Retenciones de IVA", desc: "Retenciones practicadas a proveedores (compras) y sufridas de clientes (ventas), calculadas por cada pago", icon: Percent, cat: "financiero", color: "emerald" },
          gastos_categoria:  { title: "Gastos por Categoría",  desc: "Detalle de gastos pagados agrupados por categoría, con filtro por categoría y fecha", icon: Calendar, cat: "financiero", color: "emerald" },
          costo_hectarea:    { title: "Costo por Hectárea",   desc: "Costo de insumos aplicados por chacra (según recetas) dividido por su superficie — costo aproximado por ha", icon: Layers, cat: "financiero", color: "emerald" },
          transporte_chofer: { title: "Transporte — Choferes",  desc: "Peso salida/llegada, diferencia, faltante en Gs, comisión, gastos de la carga y liquidación por chofer", icon: Truck, cat: "financiero", color: "emerald" },
          transporte_cliente: { title: "Transporte — Clientes", desc: "Peso salida/llegada, diferencia, flete y factura por cliente", icon: Truck, cat: "financiero", color: "emerald" },
          constructora_general: { title: "Constructora — General", desc: "Cobros, cuotas, anticipos, costos y margen del proyecto", icon: Building2, cat: "financiero", color: "emerald" },
          constructora_costos: { title: "Constructora — Costos por grupo", desc: "Compras, gastos y personal agrupados por grupo (vidrios, albañilería…)", icon: Building2, cat: "financiero", color: "emerald" },
          constructora_cronograma: { title: "Constructora — Cronograma", desc: "Cumplimiento, días de atraso, reprogramaciones y días de lluvia", icon: Building2, cat: "financiero", color: "emerald" },
          constructora_equipos: { title: "Constructora — Equipos", desc: "Uso de maquinarias por proyecto y uso compartido", icon: Building2, cat: "financiero", color: "emerald" },
          ingresos:          { title: "Ingresos varios",       desc: "Ingresos directos del período, categorías y clientes",              icon: TrendingUp,   cat: "financiero", color: "emerald" },
          recibos:           { title: "Recibos",                desc: "Cobros registrados contra facturas en el período",                  icon: Receipt,      cat: "financiero", color: "emerald" },
          notas_credito:     { title: "Notas de crédito",       desc: "Notas de ventas y compras con motivo y monto",                     icon: AlertTriangle,cat: "financiero", color: "emerald" },
          presupuestos:      { title: "Presupuestos",           desc: "Presupuestos con estados, montos y facturación",                    icon: ClipboardList,cat: "financiero", color: "emerald" },
          compras:           { title: "Compras",                desc: "Compras contado/crédito, facturas y estado de pago del mes",        icon: TrendingDown, cat: "financiero", color: "emerald" },
          gastos:            { title: "Gastos",                 desc: "Pagos de gastos fijos del período",                                 icon: Calendar,     cat: "financiero", color: "emerald" },
          proveedores:       { title: "Proveedores y deudas",   desc: "Proveedores, pagos y pendientes del año seleccionado",              icon: DollarSign,   cat: "financiero", color: "emerald" },
          catastros:         { title: "Catastro Unificado",     desc: "Clientes, proveedores y empleados: ID, documento, teléfono, correo y estado", icon: ClipboardList, cat: "financiero", color: "emerald" },
          balancete_obra:    { title: "Balancete - Obra",        desc: "Ganancia o pérdida por obra: facturado (esté o no cobrado), costo de materiales, personal y compras/insumos", icon: Building2, cat: "financiero", color: "emerald" },
          garantias:         { title: "Garantías",              desc: "Pagarés recibidos de clientes y concedidos a proveedores: vigentes, cobrados y vencidos", icon: Shield, cat: "financiero", color: "emerald" },
          balance_gerencial: { title: "Balance Gerencial",      desc: "Balance analítico gerencial: activo, pasivo, patrimonio, resultado del período y KPIs con semáforo", icon: PieChartIcon, cat: "financiero", color: "emerald" },
          prestamos:         { title: "Préstamos",              desc: "Detalle de préstamos por alias, referencia, cuenta, vencimientos y saldo", icon: DollarSign, cat: "financiero", color: "emerald" },
          iva:               { title: "IVA fiscal",             desc: "Débito, crédito, pagos y saldo acumulado",                          icon: Table,        cat: "financiero", color: "emerald" },
          caja_banco:        { title: "Caja / Banco",           desc: "Movimientos por cuenta, filtrable por banco o caja",                icon: DollarSign,   cat: "financiero", color: "emerald" },
          movimientos:       { title: "Movimientos",            desc: "Extracto de una cuenta tipo banco: saldo corriente por fila, filtros por tipo y comprobante", icon: ArrowUpDown, cat: "financiero", color: "emerald" },
          cuentas_cobrar:    { title: "Cuentas por cobrar",     desc: "Facturas emitidas pendientes de cobro por cliente y período",        icon: TrendingUp,   cat: "financiero", color: "emerald" },
          cuentas_pagar:     { title: "Cuentas por pagar",      desc: "Compras pendientes de pago por proveedor y período",                icon: TrendingDown, cat: "financiero", color: "emerald" },
          estado_cuenta:     { title: "Estado de Cuenta",       desc: "Todas las cuentas de un cliente o proveedor con vencimientos y saldos deudor/acreedor", icon: FileText, cat: "financiero", color: "emerald" },
          inventario_tecnico:{ title: "Inventario técnico",     desc: "Activos técnicos, credenciales y accesos por cliente",              icon: Server,       cat: "tecnico",    color: "amber" },
          ips_libres:        { title: "IPs Libres",             desc: "IPs no registradas en el sistema dentro de un rango configurable", icon: Network,      cat: "tecnico",    color: "amber" },
          descuentos_productos:{ title: "Descuentos en productos", desc: "Productos con descuento habilitado, % máximo, vencimiento y estado (vigente/vencido)", icon: Percent, cat: "inventario", color: "blue" },
          mercaderias_pendientes:{ title: "Mercaderías pendientes", desc: "Mercadería facturada pendiente de entrega o comprada pendiente de recibir (entrega posterior), con saldo por documento", icon: Package, cat: "inventario", color: "blue" },
          vencimiento_productos:{ title: "Vencimiento de productos", desc: "Productos con fecha de vencimiento cargada (SKU, código de fabricante, lote), ordenados por vencimiento", icon: Clock, cat: "inventario", color: "blue" },
          reporte_volumetria_bombas: { title: "Volumetría de Bombas", desc: "Litros expedidos y facturación por bomba y pico en el período seleccionado", icon: Fuel, cat: "bombas", color: "orange" },
        };

        const meta = REPORT_META[reporteSeleccionado] || null;
        const isFinanciero = meta?.cat === "financiero";
        const isInventario = meta?.cat === "inventario";
        const isBombas = meta?.cat === "bombas";

        const SidebarItem = ({ item, activeColor, dotColor }) => {
          const IIcon = item.icon;
          const active = reporteSeleccionado === item.id;
          return (
            <button
              onClick={() => { setReporteSeleccionado(item.id); setReporteCategoria(REPORT_META[item.id]?.cat || "inventario"); }}
              className={`w-full flex items-center gap-2.5 pl-9 pr-3 py-2 text-xs transition-colors text-left ${active ? `${activeColor} font-medium` : "text-slate-400 hover:text-white hover:bg-white/5"}`}>
              <IIcon className="w-3.5 h-3.5 shrink-0 opacity-70" />
              <span className="flex-1 truncate">{item.label}</span>
              {item.sub && !active && <span className="text-[10px] text-slate-600 font-mono shrink-0">({item.sub})</span>}
              {active && <div className={`w-1.5 h-1.5 rounded-full shrink-0 ${dotColor}`} />}
            </button>
          );
        };

        // Menú de reportes reorganizado por área (Contabilidad, Entidades, Financiero,
        // Facturación, Inventario técnico, Inventario, Transporte, Constructora).
        const NAV_ALL = [...INVENTARIO_NAV, ...FINANCIERO_NAV,
          { id: "inventario_tecnico", perm: "reportes.inventario_tecnico", label: "Activos técnicos", icon: Server },
          { id: "ips_libres", perm: "reportes.inventario_tecnico", label: "IPs Libres", icon: Network },
        ];
        const navItem = (id, label) => { const it = NAV_ALL.find(x => x.id === id); return it ? { ...it, ...(label ? { label } : {}) } : null; };
        const GRUPOS = [
          { id: "contabilidad", label: "Contabilidad", icon: BarChart3, color: "violet", items: [navItem("balance"), navItem("libro_ventas"), navItem("balancete_obra", "Balance – Obra"), navItem("balance_gerencial"), navItem("iva", "IVA Fiscal")] },
          { id: "entidades", label: "Entidades", icon: ClipboardList, color: "sky", items: [navItem("catastros", "Catastros Unificados")] },
          { id: "financiero", label: "Financiero", icon: DollarSign, color: "emerald", items: [navItem("gastos_categoria"), navItem("retenciones"), navItem("flow_financieros"), navItem("garantias"), navItem("loteadora_precificacion", "Precificación de Loteadora"), navItem("prestamos"), navItem("caja_banco"), navItem("movimientos"), navItem("cuentas_cobrar"), navItem("cuentas_pagar"), navItem("estado_cuenta"), navItem("proveedores", "Proveedores y Deudas"), navItem("costo_hectarea")] },
          { id: "facturacion", label: "Facturación", icon: Receipt, color: "blue", items: [navItem("flow_facturaciones", "Flow de Facturación"), navItem("cliente_detallado"), navItem("comisiones_vendedores", "Comisión de Vendedores"), navItem("presupuestos")] },
          { id: "tecnico", label: "Inventario técnico", icon: Server, color: "amber", visible: tecnicoVisible, items: [navItem("inventario_tecnico"), navItem("ips_libres")] },
          { id: "inventario", label: "Inventario", icon: Package, color: "cyan", visible: inventarioVisible, items: INVENTARIO_NAV },
          { id: "transporte", label: "Transporte", icon: Truck, color: "teal", items: [navItem("transporte_chofer", "Transporte Choferes"), navItem("transporte_cliente", "Transporte Cliente")] },
          { id: "constructora", label: "Constructora", icon: Building2, color: "rose", items: [navItem("constructora_general"), navItem("constructora_costos"), navItem("constructora_cronograma"), navItem("constructora_equipos")] },
        ];
        const GRUPO_COLORES = {
          violet: { text: "text-violet-400", bg: "bg-violet-500/20", item: "text-violet-300 bg-violet-500/10", dot: "bg-violet-400" },
          sky: { text: "text-sky-400", bg: "bg-sky-500/20", item: "text-sky-300 bg-sky-500/10", dot: "bg-sky-400" },
          emerald: { text: "text-emerald-400", bg: "bg-emerald-500/20", item: "text-emerald-300 bg-emerald-500/10", dot: "bg-emerald-400" },
          blue: { text: "text-blue-400", bg: "bg-blue-500/20", item: "text-blue-300 bg-blue-500/10", dot: "bg-blue-400" },
          amber: { text: "text-amber-400", bg: "bg-amber-500/20", item: "text-amber-300 bg-amber-500/10", dot: "bg-amber-400" },
          cyan: { text: "text-cyan-400", bg: "bg-cyan-500/20", item: "text-cyan-300 bg-cyan-500/10", dot: "bg-cyan-400" },
          teal: { text: "text-teal-400", bg: "bg-teal-500/20", item: "text-teal-300 bg-teal-500/10", dot: "bg-teal-400" },
          rose: { text: "text-rose-400", bg: "bg-rose-500/20", item: "text-rose-300 bg-rose-500/10", dot: "bg-rose-400" },
        };
        const gruposVisibles = GRUPOS
          .filter(g => g.visible !== false)
          .map(g => ({ ...g, items: g.items.filter(it => it && (it.permAny ? it.permAny.some(pp => hasPermission?.(pp)) : hasPermission?.(it.perm))) }))
          .filter(g => g.items.length > 0);

        const handleGenerar = () => {
          if (reporteSeleccionado === "cliente_detallado") {
            setClientePickerTempId(reportClienteId || ""); setClientePickerSearch(""); setClientePickerTipo("cliente_detallado");
          } else if (reporteSeleccionado === "caja_banco") {
            abrirReporteCajaBanco();
          } else if (reporteSeleccionado === "movimientos") {
            abrirReporteMovimientos();
          } else if (["cuentas_cobrar", "cuentas_pagar"].includes(reporteSeleccionado)) {
            buscarCuentas();
          } else if (reporteSeleccionado === "estado_cuenta") {
            buscarEstadoCuenta();
          } else if (reporteSeleccionado) {
            generarReporte(reporteSeleccionado);
          }
        };

        return (
          <div className="flex gap-4 items-start">

            {/* ── Sidebar ──────────────────────────────────────────────── */}
            <nav className="w-56 shrink-0 bg-arandu-dark-light border border-white/5 rounded-xl flex flex-col sticky top-4 max-h-[calc(100vh-6rem)] overflow-y-auto">
              <p className="text-[10px] font-bold tracking-[0.14em] uppercase text-slate-500 px-4 pt-3 pb-1.5">Reportes</p>

              {gruposVisibles.map((g, gi) => {
                const col = GRUPO_COLORES[g.color];
                const GIcon = g.icon;
                const tieneActivo = g.items.some(it => it.id === reporteSeleccionado);
                const abierto = sidebarGruposOpen[g.id] ?? tieneActivo;
                return (
                  <div key={g.id} className={gi > 0 ? "border-t border-white/5" : ""}>
                    <button onClick={() => setSidebarGruposOpen(o => ({ ...o, [g.id]: !abierto }))}
                      className={`w-full flex items-center gap-2 px-3 py-2.5 text-sm font-semibold transition-colors ${tieneActivo ? col.text : "text-slate-300 hover:text-white"}`}>
                      <div className={`w-6 h-6 rounded-md ${col.bg} flex items-center justify-center shrink-0`}>
                        <GIcon className={`w-3.5 h-3.5 ${col.text}`} />
                      </div>
                      <span className="flex-1 text-left text-sm">{g.label}</span>
                      <ChevronRight className={`w-3.5 h-3.5 text-slate-500 transition-transform ${abierto ? "rotate-90" : ""}`} />
                    </button>
                    {abierto && (
                      <div className="pb-1">
                        {g.items.map(item => (
                          <SidebarItem key={item.id} item={item} activeColor={col.item} dotColor={col.dot} />
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}

              {/* Lectura de Bombas */}
              {hasModule?.("lectura_bombas") && hasPermission?.("lectura_bombas.reportes") && (
                <div className="border-t border-white/5">
                  <button onClick={() => setSidebarBombasOpen(v => !v)}
                    className={`w-full flex items-center gap-2 px-3 py-2.5 text-sm font-semibold transition-colors ${reporteCategoria === "bombas" ? "text-orange-400" : "text-slate-300 hover:text-white"}`}>
                    <div className="w-6 h-6 rounded-md bg-orange-500/20 flex items-center justify-center shrink-0">
                      <Fuel className="w-3.5 h-3.5 text-orange-400" />
                    </div>
                    <span className="flex-1 text-left text-sm">Bombas</span>
                    <ChevronRight className={`w-3.5 h-3.5 text-slate-500 transition-transform ${sidebarBombasOpen ? "rotate-90" : ""}`} />
                  </button>
                  {sidebarBombasOpen && (
                    <div className="pb-1">
                      <SidebarItem item={{ id: "reporte_volumetria_bombas", label: "Volumetría", icon: Fuel }} activeColor="text-orange-300 bg-orange-500/10" dotColor="bg-orange-400" />
                    </div>
                  )}
                </div>
              )}

            </nav>

            {/* ── Main content ─────────────────────────────────────── */}
            <div className="flex-1 min-w-0 space-y-4">

              {!reporteSeleccionado ? (
                <div className="flex flex-col items-center justify-center py-24 text-center">
                  <div className="w-14 h-14 rounded-2xl bg-white/5 flex items-center justify-center mb-4">
                    <FileText className="w-7 h-7 text-slate-600" />
                  </div>
                  <p className="text-slate-400 font-medium mb-1">Seleccioná un reporte</p>
                  <p className="text-slate-600 text-sm">Elegí una opción del menú lateral para ver sus filtros y generarlo</p>
                </div>
              ) : (<>

                {/* Breadcrumb */}
                <div className="flex items-center gap-1.5 text-xs text-slate-500">
                  <FileText className="w-3.5 h-3.5" />
                  <span>Reportes</span>
                  <ChevronRight className="w-3.5 h-3.5" />
                  <span className={meta?.cat === "inventario" ? "text-blue-400" : meta?.cat === "tecnico" ? "text-amber-400" : meta?.cat === "bombas" ? "text-orange-400" : "text-emerald-400"}>
                    {meta?.cat === "inventario" ? "Inventario" : meta?.cat === "tecnico" ? "Inv. técnico" : meta?.cat === "bombas" ? "Bombas" : "Financiero"}
                  </span>
                  <ChevronRight className="w-3.5 h-3.5" />
                  <span className="text-slate-300">{meta?.title}</span>
                </div>

                {/* Report header */}
                {meta && (() => {
                  const HIcon = meta.icon;
                  const colorMap = { blue: "bg-blue-500/15 text-blue-400", emerald: "bg-emerald-500/15 text-emerald-400", amber: "bg-amber-500/15 text-amber-400", orange: "bg-orange-500/15 text-orange-400" };
                  return (
                    <div className="flex items-start gap-4">
                      <div className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${colorMap[meta.color]}`}>
                        <HIcon className="w-5 h-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h2 className="text-white font-semibold text-lg leading-tight">{meta.title}</h2>
                        <p className="text-slate-500 text-sm mt-0.5">{meta.desc}</p>
                      </div>
                    </div>
                  );
                })()}

                {/* ── Filtros de búsqueda ──────────────────────────────────── */}
                <div className="bg-arandu-dark-light border border-white/5 rounded-xl p-4">
                  <h3 className="text-slate-300 font-medium text-sm mb-4 flex items-center gap-2">
                    <Search className="w-4 h-4 text-slate-500" /> Filtros de búsqueda
                  </h3>

                  {/* Balance unificado */}
                  {reporteSeleccionado === "balance" && (
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
                      <div className="flex flex-col gap-1">
                        <label className="text-slate-500 text-xs font-medium">Tipo de balance</label>
                        <select value={balTipoSel} onChange={e => setBalTipoSel(e.target.value)}
                          className="bg-arandu-dark border border-white/10 text-white rounded-lg px-3 py-2.5 text-sm focus:border-emerald-500/40 outline-none transition-colors">
                          <option value="mensual">Balance mensual</option>
                          <option value="anual">Balance anual</option>
                          <option value="detallado">Balance detallado</option>
                        </select>
                      </div>
                      {balTipoSel === "anual" ? (
                        <div className="flex flex-col gap-1">
                          <label className="text-slate-500 text-xs font-medium">Año</label>
                          <select value={reportYear} onChange={e => { setReportYear(e.target.value); setReportMonth(`${e.target.value}-${reportMonth.slice(5,7)}`); }}
                            className="bg-arandu-dark border border-white/10 text-white rounded-lg px-3 py-2.5 text-sm focus:border-emerald-500/40 outline-none transition-colors">
                            {reportYears.map(y => <option key={y} value={y}>{y}</option>)}
                          </select>
                        </div>
                      ) : (
                        <div className="flex flex-col gap-1">
                          <label className="text-slate-500 text-xs font-medium">Período</label>
                          <div className="flex items-center gap-1 bg-arandu-dark border border-white/10 rounded-lg px-2 py-1.5">
                            <button onClick={() => updateReportMonth(addMonths(reportMonth, -1))} className="w-7 h-7 rounded-md bg-white/5 hover:bg-white/10 text-slate-300 flex items-center justify-center"><ChevronLeft className="w-4 h-4" /></button>
                            <select value={reportMonthIndex} onChange={e => updateReportMonth(setMonthPart(reportMonth, Number(e.target.value)))} className="bg-transparent text-white text-sm outline-none px-1">
                              {MESES.map((m, idx) => <option key={m} value={idx}>{m}</option>)}
                            </select>
                            <select value={reportYear} onChange={e => { setReportYear(e.target.value); setReportMonth(`${e.target.value}-${reportMonth.slice(5,7)}`); }} className="bg-transparent text-white text-sm outline-none px-1">
                              {reportYears.map(y => <option key={y} value={y}>{y}</option>)}
                            </select>
                            <button onClick={() => updateReportMonth(addMonths(reportMonth, 1))} className="w-7 h-7 rounded-md bg-white/5 hover:bg-white/10 text-slate-300 flex items-center justify-center"><ChevronRight className="w-4 h-4" /></button>
                          </div>
                        </div>
                      )}
                      <button onClick={() => generarReporte(balTipoSel === "anual" ? "balance_anual" : balTipoSel === "detallado" ? "balance_detallado" : "balance_mensual")} disabled={genericReportLoading}
                        className="flex items-center justify-center gap-2 px-5 py-2.5 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 text-white rounded-lg text-sm font-semibold transition-colors shadow-lg shadow-emerald-500/20">
                        <Printer className="w-4 h-4" /> Generar reporte
                      </button>
                    </div>
                  )}

                  {/* Flow de Facturaciones */}
                  {reporteSeleccionado === "flow_facturaciones" && (
                    <div className="space-y-3">
                      <div className="flex flex-wrap gap-3">
                        <label className="flex items-center gap-1.5 cursor-pointer">
                          <div onClick={() => setMovIncluirAnuladas(v => !v)}
                            className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 cursor-pointer transition-all ${movIncluirAnuladas ? "bg-amber-500/80 border-amber-500" : "border-white/20 hover:border-amber-400/50"}`}>
                            {movIncluirAnuladas && <Check className="w-2.5 h-2.5 text-white" />}
                          </div>
                          <span className="text-slate-300 text-xs">Facturas Anuladas</span>
                        </label>
                        <label className="flex items-center gap-1.5 cursor-pointer">
                          <div onClick={() => setMovIncluirInactivas(v => !v)}
                            className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 cursor-pointer transition-all ${movIncluirInactivas ? "bg-amber-500/80 border-amber-500" : "border-white/20 hover:border-amber-400/50"}`}>
                            {movIncluirInactivas && <Check className="w-2.5 h-2.5 text-white" />}
                          </div>
                          <span className="text-slate-300 text-xs">Facturas Inactivas</span>
                        </label>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 items-end">
                        <div className="flex flex-col gap-1">
                          <label className="text-slate-500 text-xs font-medium">Desde</label>
                          <DateInput value={movDesde} onChange={e => setMovDesde(e.target.value)}
                            className="bg-arandu-dark border border-white/10 text-white rounded-lg px-3 py-2.5 text-sm focus:border-emerald-500/40 outline-none transition-colors" />
                        </div>
                        <div className="flex flex-col gap-1">
                          <label className="text-slate-500 text-xs font-medium">Hasta</label>
                          <DateInput value={movHasta} onChange={e => setMovHasta(e.target.value)}
                            className="bg-arandu-dark border border-white/10 text-white rounded-lg px-3 py-2.5 text-sm focus:border-emerald-500/40 outline-none transition-colors" />
                        </div>
                        <div className="flex flex-col gap-1">
                          <label className="text-slate-500 text-xs font-medium">Tipos de movimiento</label>
                          <div className="flex flex-wrap gap-3 bg-arandu-dark border border-white/10 rounded-lg px-3 py-2.5">
                            {[
                              { value: "facturas",      label: "Facturas" },
                              { value: "boletas",       label: "Boletas" },
                              { value: "notas_credito", label: "Notas de crédito" },
                              { value: "compras",       label: "Compras" },
                            ].map(({ value, label }) => (
                              <label key={value} className="flex items-center gap-1.5 cursor-pointer">
                                <div onClick={() => setMovTipos(prev => prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value])}
                                  className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 cursor-pointer transition-all ${movTipos.includes(value) ? "bg-emerald-500/80 border-emerald-500" : "border-white/20 hover:border-emerald-400/50"}`}>
                                  {movTipos.includes(value) && <Check className="w-2.5 h-2.5 text-white" />}
                                </div>
                                <span className="text-slate-300 text-xs">{label}</span>
                              </label>
                            ))}
                          </div>
                        </div>
                        <div className="flex flex-col gap-1 sm:col-span-2">
                          <label className="text-slate-500 text-xs font-medium">Producto (opcional)</label>
                          <ProductoSearchSelect value={movProductoId} onChange={id => { setMovProductoId(id); setMovVarianteId(""); }} products={stockProductos} placeholder="Todos los productos" showTodos={true} />
                        </div>
                        {(() => {
                          const prod = stockProductos.find(p => p.id === movProductoId);
                          const vars = (prod?.variantes || []).filter(v => v.activo !== false);
                          if (!prod || vars.length === 0) return null;
                          return (
                            <div className="flex flex-col gap-1 sm:col-span-2">
                              <label className="text-slate-500 text-xs font-medium">Variante (opcional)</label>
                              <select value={movVarianteId} onChange={e => setMovVarianteId(e.target.value)}
                                className="bg-arandu-dark border border-white/10 text-white rounded-lg px-3 py-2.5 text-sm focus:border-emerald-500/40 outline-none transition-colors">
                                <option value="">Todas las variantes</option>
                                {vars.map(v => <option key={v.id} value={v.id}>{v.atributos_texto || v.sku || v.id}</option>)}
                              </select>
                            </div>
                          );
                        })()}
                        <button onClick={() => generarReporte("flow_facturaciones")} disabled={genericReportLoading || !movDesde || !movHasta || movTipos.length === 0}
                          className="flex items-center justify-center gap-2 px-5 py-2.5 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg text-sm font-semibold transition-colors shadow-lg shadow-emerald-500/20">
                          <Printer className="w-4 h-4" /> Generar reporte
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Flow de Financieros */}
                  {reporteSeleccionado === "flow_financieros" && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 items-end">
                      <div className="flex flex-col gap-1">
                        <label className="text-slate-500 text-xs font-medium">Desde</label>
                        <DateInput value={flowFinDesde} onChange={e => setFlowFinDesde(e.target.value)}
                          className="bg-arandu-dark border border-white/10 text-white rounded-lg px-3 py-2.5 text-sm focus:border-emerald-500/40 outline-none transition-colors" />
                      </div>
                      <div className="flex flex-col gap-1">
                        <label className="text-slate-500 text-xs font-medium">Hasta</label>
                        <DateInput value={flowFinHasta} onChange={e => setFlowFinHasta(e.target.value)}
                          className="bg-arandu-dark border border-white/10 text-white rounded-lg px-3 py-2.5 text-sm focus:border-emerald-500/40 outline-none transition-colors" />
                      </div>
                      <div className="flex flex-col gap-1">
                        <label className="text-slate-500 text-xs font-medium">Tipos de movimiento</label>
                        <div className="flex flex-wrap gap-3 bg-arandu-dark border border-white/10 rounded-lg px-3 py-2.5">
                          {[
                            { value: "ingresos", label: "Ingresos varios" },
                            { value: "recibos",  label: "Recibos" },
                            { value: "gastos",   label: "Gastos" },
                          ].map(({ value, label }) => (
                            <label key={value} className="flex items-center gap-1.5 cursor-pointer">
                              <div onClick={() => setFlowFinTipos(prev => prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value])}
                                className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 cursor-pointer transition-all ${flowFinTipos.includes(value) ? "bg-emerald-500/80 border-emerald-500" : "border-white/20 hover:border-emerald-400/50"}`}>
                                {flowFinTipos.includes(value) && <Check className="w-2.5 h-2.5 text-white" />}
                              </div>
                              <span className="text-slate-300 text-xs">{label}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                      <button onClick={() => generarReporte("flow_financieros")} disabled={genericReportLoading || !flowFinDesde || !flowFinHasta || flowFinTipos.length === 0}
                        className="flex items-center justify-center gap-2 px-5 py-2.5 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg text-sm font-semibold transition-colors shadow-lg shadow-emerald-500/20">
                        <Printer className="w-4 h-4" /> Generar reporte
                      </button>
                    </div>
                  )}

                  {/* Historial de stock */}
                  {reporteSeleccionado === "historial_stock" && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 items-end">
                      <div className="flex flex-col gap-1">
                        <label className="text-slate-500 text-xs font-medium">Desde</label>
                        <DateInput value={stockHistorialDesde} onChange={e => setStockHistorialDesde(e.target.value)}
                          className="bg-arandu-dark border border-white/10 text-white rounded-lg px-3 py-2.5 text-sm focus:border-blue-500/40 outline-none transition-colors" />
                      </div>
                      <div className="flex flex-col gap-1">
                        <label className="text-slate-500 text-xs font-medium">Hasta</label>
                        <DateInput value={stockHistorialHasta} onChange={e => setStockHistorialHasta(e.target.value)}
                          className="bg-arandu-dark border border-white/10 text-white rounded-lg px-3 py-2.5 text-sm focus:border-blue-500/40 outline-none transition-colors" />
                      </div>
                      <div className="flex flex-col gap-1">
                        <label className="text-slate-500 text-xs font-medium">Producto</label>
                        <ProductoSearchSelect value={stockHistorialProductoId} onChange={setStockHistorialProductoId} products={stockProductos} placeholder="Todos los productos" showTodos={true} />
                      </div>
                      <button onClick={() => generarReporte("stock_historial")} disabled={genericReportLoading || !stockHistorialDesde || !stockHistorialHasta}
                        className="flex items-center justify-center gap-2 px-5 py-2.5 bg-blue-500 hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg text-sm font-semibold transition-colors shadow-lg shadow-blue-500/20">
                        <Printer className="w-4 h-4" /> Generar reporte
                      </button>
                    </div>
                  )}

                  {/* Ficha de Mercadería (Kardex) */}
                  {reporteSeleccionado === "kardex" && (
                    <div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 items-end mb-3">
                        <div className="flex flex-col gap-1">
                          <label className="text-slate-500 text-xs font-medium">Producto <span className="text-red-400">*</span></label>
                          <ProductoSearchSelect value={kardexProductoId} onChange={id => { setKardexProductoId(id); setKardexData(null); }} products={stockProductos} placeholder="Buscar producto..." showTodos={false} />
                        </div>
                        <div className="flex flex-col gap-1">
                          <label className="text-slate-500 text-xs font-medium">Desde</label>
                          <DateInput value={kardexDesde} onChange={e => setKardexDesde(e.target.value)}
                            className="bg-arandu-dark border border-white/10 text-white rounded-lg px-3 py-2.5 text-sm focus:border-blue-500/40 outline-none transition-colors" />
                        </div>
                        <div className="flex flex-col gap-1">
                          <label className="text-slate-500 text-xs font-medium">Hasta</label>
                          <DateInput value={kardexHasta} onChange={e => setKardexHasta(e.target.value)}
                            className="bg-arandu-dark border border-white/10 text-white rounded-lg px-3 py-2.5 text-sm focus:border-blue-500/40 outline-none transition-colors" />
                        </div>
                        <div className="flex flex-col gap-1">
                          <label className="text-slate-500 text-xs font-medium">Tipo movimiento</label>
                          <div className="flex gap-3 bg-arandu-dark border border-white/10 rounded-lg px-3 py-2.5">
                            {[{ value: "entrada", label: "Entradas" }, { value: "salida", label: "Salidas" }, { value: "ajuste", label: "Ajustes" }, { value: "nota_credito", label: "NC / Dev." }].map(({ value, label }) => (
                              <label key={value} className="flex items-center gap-1.5 cursor-pointer">
                                <div onClick={() => setKardexTipoMov(prev => prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value])}
                                  className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 cursor-pointer transition-all ${kardexTipoMov.includes(value) ? "bg-blue-500/80 border-blue-500" : "border-white/20 hover:border-blue-400/50"}`}>
                                  {kardexTipoMov.includes(value) && <Check className="w-2.5 h-2.5 text-white" />}
                                </div>
                                <span className="text-slate-300 text-xs">{label}</span>
                              </label>
                            ))}
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-2 items-center mb-1">
                        <label className="flex items-center gap-2 cursor-pointer select-none">
                          <div onClick={() => setKardexIncluirEliminados(prev => !prev)}
                            className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 cursor-pointer transition-all ${kardexIncluirEliminados ? "bg-amber-500/80 border-amber-500" : "border-white/20 hover:border-amber-400/50"}`}>
                            {kardexIncluirEliminados && <Check className="w-2.5 h-2.5 text-white" />}
                          </div>
                          <span className="text-slate-300 text-xs">Movimientos Eliminados / anulados</span>
                        </label>
                      </div>
                      <div className="flex gap-2">
                        <button onClick={buscarKardex} disabled={kardexLoading || !kardexProductoId}
                          className="flex items-center gap-2 px-5 py-2.5 bg-blue-500 hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg text-sm font-semibold transition-colors shadow-lg shadow-blue-500/20">
                          <Search className="w-4 h-4" />{kardexLoading ? "Buscando..." : "Buscar"}
                        </button>
                        {kardexData && (<>
                          <button onClick={handlePrintKardex}
                            className="flex items-center gap-2 px-4 py-2.5 bg-slate-700/50 hover:bg-slate-700 border border-white/10 text-slate-300 rounded-lg text-sm font-medium transition-colors">
                            <Printer className="w-4 h-4" /> Imprimir
                          </button>
                          <button onClick={handleExportKardexExcel}
                            className="flex items-center gap-2 px-4 py-2.5 bg-emerald-500/10 hover:bg-emerald-500/20 border border-emerald-500/30 text-emerald-400 rounded-lg text-sm font-medium transition-colors">
                            <Download className="w-4 h-4" /> CSV/Excel
                          </button>
                        </>)}
                      </div>
                    </div>
                  )}

                  {/* Productos y stock */}
                  {reporteSeleccionado === "productos_stock" && (
                    <div className="flex flex-wrap items-end gap-3">
                      <div className="flex flex-col gap-1">
                        <label className="text-slate-500 text-xs font-medium">Desde <span className="text-slate-600 font-normal">(opcional)</span></label>
                        <DateInput value={stockDesde} max={stockHasta || undefined} onChange={e => setStockDesde(e.target.value)}
                          className="bg-arandu-dark border border-white/10 text-white rounded-lg px-3 py-2.5 text-sm focus:border-blue-500/40 outline-none transition-colors" />
                      </div>
                      <div className="flex flex-col gap-1">
                        <label className="text-slate-500 text-xs font-medium">Hasta <span className="text-slate-600 font-normal">(opcional)</span></label>
                        <DateInput value={stockHasta} min={stockDesde || undefined} onChange={e => setStockHasta(e.target.value)}
                          className="bg-arandu-dark border border-white/10 text-white rounded-lg px-3 py-2.5 text-sm focus:border-blue-500/40 outline-none transition-colors" />
                      </div>
                      {stockDesde && (
                        <label className="flex items-center gap-2 cursor-pointer select-none pb-2.5" title="Con rango de fechas: incluye todos los productos con su stock al final del período, no solo los creados o movidos">
                          <input type="checkbox" checked={stockTodos} onChange={e => setStockTodos(e.target.checked)} className="accent-blue-500" />
                          <span className="text-slate-300 text-xs">Todos los productos (stock al final del período)</span>
                        </label>
                      )}
                      <div className="flex flex-col gap-1">
                        <label className="text-slate-500 text-xs font-medium">Moneda <span className="text-slate-600 font-normal">(opcional)</span></label>
                        <select value={stockMoneda} onChange={e => setStockMoneda(e.target.value)}
                          style={{ colorScheme: "dark" }}
                          className="bg-arandu-dark border border-white/10 text-slate-300 rounded-lg px-3 py-2.5 text-sm focus:border-blue-500/40 outline-none transition-colors min-w-[150px]">
                          <option value="">Todas</option>
                          <option value="PYG">Guaraníes (₲)</option>
                          <option value="USD">Dólares ($)</option>
                          <option value="BRL">Reales (R$)</option>
                        </select>
                      </div>
                      <div className="flex flex-col gap-1">
                        <label className="text-slate-500 text-xs font-medium">Categorías <span className="text-slate-600 font-normal">(opcional)</span></label>
                        {(() => {
                          const lista = (stockCategorias.length > 0 ? stockCategorias : Array.from(new Set(stockProductos.map(p => p.categoria).filter(Boolean))).map(nombre => ({ id: nombre, nombre }))).map(c => c.nombre);
                          const cambiar = async (arr) => {
                            setStockCategoriasSel(arr);
                            const cat = arr.length === 1 ? arr[0] : "";
                            setStockCategoria(cat);
                            setStockSubcategoria("");
                            setStockAtributosFiltro({});
                            if (cat) {
                              const q = new URLSearchParams({ categoria: cat });
                              if (activeEmpresaPropia?.slug) q.set("logo_tipo", activeEmpresaPropia.slug);
                              const res = await fetch(`${API}/admin/subcategorias-productos?${q}`, { headers: { Authorization: `Bearer ${token}` } });
                              if (res.ok) setStockSubcategorias((await res.json()).filter(s => s.activo !== false));
                              else setStockSubcategorias([]);
                            } else {
                              setStockSubcategorias([]);
                            }
                          };
                          return <MultiSelectDropdown opciones={lista} seleccion={stockCategoriasSel} onChange={cambiar} />;
                        })()}
                      </div>
                      {stockConfigProductos.usar_subcategorias !== false && stockCategoria && stockSubcategorias.length > 0 && (
                        <div className="flex flex-col gap-1">
                          <label className="text-slate-500 text-xs font-medium">Subcategoría <span className="text-slate-600 font-normal">(opcional)</span></label>
                          <select value={stockSubcategoria} onChange={e => setStockSubcategoria(e.target.value)}
                            style={{ colorScheme: "dark" }}
                            className="bg-arandu-dark border border-white/10 text-slate-300 rounded-lg px-3 py-2.5 text-sm focus:border-blue-500/40 outline-none transition-colors min-w-[150px]">
                            <option value="">Todas</option>
                            {stockSubcategorias.map(s => (
                              <option key={s.id} value={s.nombre}>{s.nombre}</option>
                            ))}
                          </select>
                        </div>
                      )}
                      {(() => {
                        if (stockConfigProductos.usar_atributos === false) return null;
                        const cat = stockCategorias.find(c => c.nombre === stockCategoria);
                        const enabled = stockCategoria
                          ? (cat?.atributos_habilitados || [])
                          : stockAtributos.map(a => a.id);
                        const attrs = stockAtributos.filter(a => enabled.includes(a.id));
                        if (attrs.length === 0) return null;
                        return attrs.map(attr => (
                          <div key={attr.id} className="flex flex-col gap-1">
                            <label className="text-slate-500 text-xs font-medium">{attr.nombre} <span className="text-slate-600 font-normal">(opcional)</span></label>
                            <select
                              value={stockAtributosFiltro[attr.id] || ""}
                              onChange={e => setStockAtributosFiltro(f => ({ ...f, [attr.id]: e.target.value }))}
                              style={{ colorScheme: "dark" }}
                              className="bg-arandu-dark border border-white/10 text-slate-300 rounded-lg px-3 py-2.5 text-sm focus:border-blue-500/40 outline-none transition-colors min-w-[150px]"
                            >
                              <option value="">Todos</option>
                              {(attr.opciones || []).filter(o => o.activo !== false).map(opt => (
                                <option key={opt.id} value={opt.id}>{opt.nombre}</option>
                              ))}
                            </select>
                          </div>
                        ));
                      })()}
                      <div className="flex flex-col gap-1 min-w-[220px]">
                        <label className="text-slate-500 text-xs font-medium">Producto <span className="text-slate-600 font-normal">(opcional)</span></label>
                        <ProductoSearchSelect value={stockProductoId} onChange={setStockProductoId} products={stockProductos} placeholder="Todos los productos" showTodos={true} />
                      </div>
                      <div className="flex flex-col gap-1">
                        <label className="text-slate-500 text-xs font-medium">Proveedor <span className="text-slate-600 font-normal">(opcional)</span></label>
                        <select value={stockProveedorId} onChange={e => setStockProveedorId(e.target.value)}
                          style={{ colorScheme: "dark" }}
                          className="bg-arandu-dark border border-white/10 text-slate-300 rounded-lg px-3 py-2.5 text-sm focus:border-blue-500/40 outline-none transition-colors min-w-[180px]">
                          <option value="">Todos</option>
                          {stockProveedores.map(pv => <option key={pv.id} value={pv.id}>{pv.nombre}</option>)}
                        </select>
                        {stockProveedores.length === 0 && (
                          <p className="text-amber-400/80 text-[11px] mt-1 max-w-[260px]">No hay proveedores en el Catastro de esta empresa (o falta el permiso Proveedores · ver).</p>
                        )}
                      </div>
                      {depReporte.activo && (
                        <div className="flex flex-col gap-1">
                          <label className="text-slate-500 text-xs font-medium">Depósito <span className="text-slate-600 font-normal">(stock actual)</span></label>
                          <select value={stockDepositoId} onChange={e => setStockDepositoId(e.target.value)}
                            style={{ colorScheme: "dark" }}
                            className="bg-arandu-dark border border-white/10 text-slate-300 rounded-lg px-3 py-2.5 text-sm focus:border-blue-500/40 outline-none transition-colors min-w-[180px]">
                            <option value="">Todos los depósitos</option>
                            {depReporte.depositos.map(d => <option key={d.id} value={d.id}>{d.codigo} · {d.nombre}</option>)}
                          </select>
                        </div>
                      )}
                      <button onClick={() => generarReporte("productos_stock")} disabled={genericReportLoading}
                        className="flex items-center gap-2 px-5 py-2.5 bg-blue-500 hover:bg-blue-600 disabled:opacity-50 text-white rounded-lg text-sm font-semibold transition-colors shadow-lg shadow-blue-500/20">
                        <Printer className="w-4 h-4" /> Generar reporte
                      </button>
                    </div>
                  )}

                  {/* Descuentos en productos */}
                  {reporteSeleccionado === "descuentos_productos" && (
                    <div className="flex flex-wrap items-end gap-3">
                      <div className="flex flex-col gap-1">
                        <label className="text-slate-500 text-xs font-medium">Estado</label>
                        <div className="flex gap-1">
                          {[["todos", "Todos"], ["vigente", "Vigentes"], ["vencido", "Vencidos"]].map(([val, lbl]) => (
                            <button key={val} onClick={() => setDescuentoEstado(val)}
                              className={`px-3 py-2 rounded-lg text-xs font-medium border transition-colors ${descuentoEstado === val ? "bg-blue-500/20 border-blue-500/40 text-blue-300" : "bg-transparent border-white/10 text-slate-400 hover:text-white"}`}>
                              {lbl}
                            </button>
                          ))}
                        </div>
                      </div>
                      <button onClick={() => generarReporte("descuentos_productos")} disabled={genericReportLoading}
                        className="flex items-center gap-2 px-5 py-2.5 bg-blue-500 hover:bg-blue-600 disabled:opacity-50 text-white rounded-lg text-sm font-semibold transition-colors shadow-lg shadow-blue-500/20">
                        <Percent className="w-4 h-4" /> Generar reporte
                      </button>
                    </div>
                  )}

                  {/* Mercaderías pendientes (por despachar / por recibir) */}
                  {reporteSeleccionado === "mercaderias_pendientes" && (
                    <MercaderiasPendientesReport token={token} logoTipo={activeEmpresaPropia?.slug} />
                  )}

                  {/* Libro Ventas */}
                  {reporteSeleccionado === "libro_ventas" && (
                    <div className="flex flex-wrap items-end gap-3">
                      <div className="flex flex-col gap-1">
                        <label className="text-slate-500 text-xs font-medium">Período</label>
                        <div className="flex items-center gap-1 bg-arandu-dark border border-white/10 rounded-lg px-2 py-1.5">
                          <button onClick={() => updateReportMonth(addMonths(reportMonth, -1))} className="w-7 h-7 rounded-md bg-white/5 hover:bg-white/10 text-slate-300 flex items-center justify-center"><ChevronLeft className="w-4 h-4" /></button>
                          <select value={reportMonthIndex} onChange={e => updateReportMonth(setMonthPart(reportMonth, Number(e.target.value)))} className="bg-transparent text-white text-sm outline-none px-1">
                            {MESES.map((m, idx) => <option key={m} value={idx}>{m}</option>)}
                          </select>
                          <select value={reportYear} onChange={e => { setReportYear(e.target.value); setReportMonth(`${e.target.value}-${reportMonth.slice(5,7)}`); }} className="bg-transparent text-white text-sm outline-none px-1">
                            {reportYears.map(y => <option key={y} value={y}>{y}</option>)}
                          </select>
                          <button onClick={() => updateReportMonth(addMonths(reportMonth, 1))} className="w-7 h-7 rounded-md bg-white/5 hover:bg-white/10 text-slate-300 flex items-center justify-center"><ChevronRight className="w-4 h-4" /></button>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 mb-1">
                        <label className="flex items-center gap-2 cursor-pointer select-none">
                          <div onClick={() => setLibroVentasIncluirAnuladas(prev => !prev)}
                            className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 cursor-pointer transition-all ${libroVentasIncluirAnuladas ? "bg-amber-500/80 border-amber-500" : "border-white/20 hover:border-amber-400/50"}`}>
                            {libroVentasIncluirAnuladas && <Check className="w-2.5 h-2.5 text-white" />}
                          </div>
                          <span className="text-slate-300 text-xs">Mostrar inactivos o anulados</span>
                        </label>
                      </div>
                      <button onClick={() => generarReporte("libro_ventas")} disabled={genericReportLoading}
                        className="flex items-center justify-center gap-2 px-5 py-2.5 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 text-white rounded-lg text-sm font-semibold transition-colors shadow-lg shadow-emerald-500/20">
                        <Printer className="w-4 h-4" /> Generar reporte
                      </button>
                      {libroVentasData && libroVentasData.length > 0 && (
                        <button onClick={exportLibroVentasExcel}
                          className="flex items-center justify-center gap-2 px-5 py-2.5 bg-white/10 hover:bg-white/20 text-white rounded-lg text-sm font-semibold transition-colors border border-white/10">
                          <FileSpreadsheet className="w-4 h-4" /> Exportar Excel
                        </button>
                      )}
                    </div>
                  )}

                  {/* Comisiones de Vendedores */}
                  {reporteSeleccionado === "comisiones_vendedores" && (
                    <div className="flex flex-wrap items-end gap-3">
                      <div className="flex flex-col gap-1">
                        <label className="text-slate-500 text-xs font-medium">Desde</label>
                        <DateInput value={comisionDesde} onChange={e => setComisionDesde(e.target.value)}
                          className="bg-arandu-dark border border-white/10 rounded-lg px-3 py-2 text-white text-sm outline-none" />
                      </div>
                      <div className="flex flex-col gap-1">
                        <label className="text-slate-500 text-xs font-medium">Hasta</label>
                        <DateInput value={comisionHasta} onChange={e => setComisionHasta(e.target.value)}
                          className="bg-arandu-dark border border-white/10 rounded-lg px-3 py-2 text-white text-sm outline-none" />
                      </div>
                      <div className="flex flex-col gap-1">
                        <label className="text-slate-500 text-xs font-medium">Vendedor (opcional)</label>
                        <select value={comisionVendedorId} onChange={e => setComisionVendedorId(e.target.value)}
                          className="bg-arandu-dark border border-white/10 rounded-lg px-3 py-2 text-white text-sm outline-none min-w-[180px]">
                          <option value="">Todos los vendedores</option>
                          {comisionVendedoresOpciones.map(v => (
                            <option key={v.id} value={v.id}>{v.nombre}{v.tipo === "catastro" ? " (Catastro)" : v.tipo === "historico" ? " (histórico)" : ""}</option>
                          ))}
                        </select>
                      </div>
                      <button onClick={() => generarReporte("comisiones_vendedores")} disabled={genericReportLoading}
                        className="flex items-center justify-center gap-2 px-5 py-2.5 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 text-white rounded-lg text-sm font-semibold transition-colors shadow-lg shadow-emerald-500/20">
                        <Percent className="w-4 h-4" /> Generar reporte
                      </button>
                    </div>
                  )}

                  {/* Costo por Hectárea */}
                  {reporteSeleccionado === "costo_hectarea" && (
                    <div className="flex flex-wrap items-end gap-3">
                      <div className="flex flex-col gap-1">
                        <label className="text-slate-500 text-xs font-medium">Desde</label>
                        <DateInput value={costoHaDesde} onChange={e => setCostoHaDesde(e.target.value)}
                          className="bg-arandu-dark border border-white/10 rounded-lg px-3 py-2 text-white text-sm outline-none" />
                      </div>
                      <div className="flex flex-col gap-1">
                        <label className="text-slate-500 text-xs font-medium">Hasta</label>
                        <DateInput value={costoHaHasta} onChange={e => setCostoHaHasta(e.target.value)}
                          className="bg-arandu-dark border border-white/10 rounded-lg px-3 py-2 text-white text-sm outline-none" />
                      </div>
                      <div className="flex flex-col gap-1">
                        <label className="text-slate-500 text-xs font-medium">Área (chacra)</label>
                        <select value={costoHaChacra} onChange={e => setCostoHaChacra(e.target.value)}
                          className="bg-arandu-dark border border-white/10 rounded-lg px-3 py-2 text-white text-sm outline-none min-w-[200px]">
                          <option value="">Todas las áreas</option>
                          {costoHaChacras.map(n => <option key={n} value={n}>{n}</option>)}
                        </select>
                      </div>
                      <button onClick={() => generarReporte("costo_hectarea")} disabled={genericReportLoading}
                        className="flex items-center justify-center gap-2 px-5 py-2.5 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 text-white rounded-lg text-sm font-semibold transition-colors shadow-lg shadow-emerald-500/20">
                        <Layers className="w-4 h-4" /> Generar reporte
                      </button>
                    </div>
                  )}

                  {/* Gastos por Categoría */}
                  {reporteSeleccionado === "gastos_categoria" && (
                    <div className="flex flex-wrap items-end gap-3">
                      <div className="flex flex-col gap-1">
                        <label className="text-slate-500 text-xs font-medium">Desde</label>
                        <DateInput value={gastosCategoriaDesde} onChange={e => setGastosCategoriaDesde(e.target.value)}
                          className="bg-arandu-dark border border-white/10 rounded-lg px-3 py-2 text-white text-sm outline-none" />
                      </div>
                      <div className="flex flex-col gap-1">
                        <label className="text-slate-500 text-xs font-medium">Hasta</label>
                        <DateInput value={gastosCategoriaHasta} onChange={e => setGastosCategoriaHasta(e.target.value)}
                          className="bg-arandu-dark border border-white/10 rounded-lg px-3 py-2 text-white text-sm outline-none" />
                      </div>
                      <div className="flex flex-col gap-1">
                        <label className="text-slate-500 text-xs font-medium">Categoría (opcional)</label>
                        <select value={gastosCategoriaSel} onChange={e => setGastosCategoriaSel(e.target.value)}
                          className="bg-arandu-dark border border-white/10 rounded-lg px-3 py-2 text-white text-sm outline-none min-w-[200px]">
                          <option value="">Todas las categorías</option>
                          {gastosCategoriaOpciones.map(c => (
                            <option key={c.id} value={c.nombre}>{c.nombre}</option>
                          ))}
                        </select>
                      </div>
                      <button onClick={() => generarReporte("gastos_categoria")} disabled={genericReportLoading}
                        className="flex items-center justify-center gap-2 px-5 py-2.5 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 text-white rounded-lg text-sm font-semibold transition-colors shadow-lg shadow-emerald-500/20">
                        <Calendar className="w-4 h-4" /> Generar reporte
                      </button>
                    </div>
                  )}

                  {reporteSeleccionado === "retenciones" && (
                    <div className="flex flex-wrap items-end gap-3">
                      <div className="flex flex-col gap-1">
                        <label className="text-slate-500 text-xs font-medium">Desde</label>
                        <DateInput value={retencionesDesde} onChange={e => setRetencionesDesde(e.target.value)}
                          className="bg-arandu-dark border border-white/10 rounded-lg px-3 py-2 text-white text-sm outline-none" />
                      </div>
                      <div className="flex flex-col gap-1">
                        <label className="text-slate-500 text-xs font-medium">Hasta</label>
                        <DateInput value={retencionesHasta} onChange={e => setRetencionesHasta(e.target.value)}
                          className="bg-arandu-dark border border-white/10 rounded-lg px-3 py-2 text-white text-sm outline-none" />
                      </div>
                      <div className="flex flex-col gap-1">
                        <label className="text-slate-500 text-xs font-medium">Origen</label>
                        <select value={retencionesOrigen} onChange={e => setRetencionesOrigen(e.target.value)}
                          className="bg-arandu-dark border border-white/10 rounded-lg px-3 py-2 text-white text-sm outline-none">
                          <option value="todas">Compras y ventas</option>
                          <option value="compras">Solo compras (practicadas)</option>
                          <option value="ventas">Solo ventas (sufridas)</option>
                        </select>
                      </div>
                      <button onClick={() => generarReporte("retenciones")} disabled={genericReportLoading}
                        className="flex items-center justify-center gap-2 px-5 py-2.5 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 text-white rounded-lg text-sm font-semibold transition-colors shadow-lg shadow-emerald-500/20">
                        <Percent className="w-4 h-4" /> Generar reporte
                      </button>
                    </div>
                  )}

                  {/* Constructora */}
                  {String(reporteSeleccionado || "").startsWith("constructora_") && (
                    <div className="flex flex-wrap items-end gap-3">
                      <div className="flex flex-col gap-1">
                        <label className="text-slate-500 text-xs font-medium">Proyecto</label>
                        <select value={constructoraProyectoId} onChange={e => setConstructoraProyectoId(e.target.value)}
                          className="bg-arandu-dark border border-white/10 rounded-lg px-3 py-2 text-white text-sm outline-none min-w-[260px]">
                          <option value="">Seleccionar proyecto…</option>
                          {constructoraProyectos.map(p => <option key={p.id} value={p.id}>{p.numero} — {p.nombre}</option>)}
                        </select>
                      </div>
                      <button onClick={() => generarReporte(reporteSeleccionado)} disabled={genericReportLoading || !constructoraProyectoId}
                        className="flex items-center justify-center gap-2 px-5 py-2.5 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 text-white rounded-lg text-sm font-semibold transition-colors shadow-lg shadow-emerald-500/20">
                        <Building2 className="w-4 h-4" /> Generar reporte
                      </button>
                    </div>
                  )}

                  {/* Transporte (fletes) */}
                  {(reporteSeleccionado === "transporte_chofer" || reporteSeleccionado === "transporte_cliente") && (
                    <div className="flex flex-wrap items-end gap-3">
                      <div className="flex flex-col gap-1">
                        <label className="text-slate-500 text-xs font-medium">Desde</label>
                        <DateInput value={transporteDesde} onChange={e => setTransporteDesde(e.target.value)}
                          className="bg-arandu-dark border border-white/10 rounded-lg px-3 py-2 text-white text-sm outline-none" />
                      </div>
                      <div className="flex flex-col gap-1">
                        <label className="text-slate-500 text-xs font-medium">Hasta</label>
                        <DateInput value={transporteHasta} onChange={e => setTransporteHasta(e.target.value)}
                          className="bg-arandu-dark border border-white/10 rounded-lg px-3 py-2 text-white text-sm outline-none" />
                      </div>
                      {reporteSeleccionado === "transporte_cliente" && <div className="flex flex-col gap-1">
                        <label className="text-slate-500 text-xs font-medium">Cliente (opcional)</label>
                        <select value={transporteClienteId} onChange={e => setTransporteClienteId(e.target.value)}
                          className="bg-arandu-dark border border-white/10 rounded-lg px-3 py-2 text-white text-sm outline-none min-w-[180px]">
                          <option value="">Todos los clientes</option>
                          {transporteClientesOpciones.map(c => (
                            <option key={c.id} value={c.id}>{c.nombre || c.razon_social}</option>
                          ))}
                        </select>
                      </div>}
                      {reporteSeleccionado === "transporte_chofer" && <div className="flex flex-col gap-1">
                        <label className="text-slate-500 text-xs font-medium">Chofer (opcional)</label>
                        <select value={transporteChoferId} onChange={e => setTransporteChoferId(e.target.value)}
                          className="bg-arandu-dark border border-white/10 rounded-lg px-3 py-2 text-white text-sm outline-none min-w-[180px]">
                          <option value="">Todos los choferes</option>
                          {transporteChoferesOpciones.map(c => (
                            <option key={c.id} value={c.id}>{c.nombre || c.razon_social}</option>
                          ))}
                        </select>
                      </div>}
                      <button onClick={() => generarReporte(reporteSeleccionado)} disabled={genericReportLoading}
                        className="flex items-center justify-center gap-2 px-5 py-2.5 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 text-white rounded-lg text-sm font-semibold transition-colors shadow-lg shadow-emerald-500/20">
                        <Truck className="w-4 h-4" /> Generar reporte
                      </button>
                    </div>
                  )}

                  {/* Vencimiento de productos */}
                  {reporteSeleccionado === "vencimiento_productos" && (
                    <div className="flex flex-wrap items-end gap-3">
                      <div className="flex flex-col gap-1">
                        <label className="text-slate-500 text-xs font-medium">Estado</label>
                        <div className="flex gap-1">
                          {[["todos", "Todos"], ["vencido", "Vencidos"], ["por_vencer", "Por vencer"], ["ok", "OK"]].map(([val, lbl]) => (
                            <button key={val} onClick={() => setVencProdEstado(val)}
                              className={`px-3 py-2 rounded-lg text-xs font-medium border transition-colors ${vencProdEstado === val ? "bg-blue-500/20 border-blue-500/40 text-blue-300" : "bg-transparent border-white/10 text-slate-400 hover:text-white"}`}>
                              {lbl}
                            </button>
                          ))}
                        </div>
                      </div>
                      <button onClick={() => generarReporte("vencimiento_productos")} disabled={genericReportLoading}
                        className="flex items-center gap-2 px-5 py-2.5 bg-blue-500 hover:bg-blue-600 disabled:opacity-50 text-white rounded-lg text-sm font-semibold transition-colors shadow-lg shadow-blue-500/20">
                        <Clock className="w-4 h-4" /> Generar reporte
                      </button>
                    </div>
                  )}

                  {/* Volumetría de Bombas */}
                  {reporteSeleccionado === "reporte_volumetria_bombas" && (
                    <div className="flex flex-wrap items-end gap-3">
                      <div className="flex flex-col gap-1">
                        <label className="text-slate-500 text-xs font-medium">Desde</label>
                        <DateInput value={bombasDesde} onChange={e => setBombasDesde(e.target.value)}
                          style={{ colorScheme: "dark" }}
                          className="bg-arandu-dark border border-white/10 rounded-lg px-3 py-2 text-slate-300 text-sm focus:outline-none focus:border-orange-500" />
                      </div>
                      <div className="flex flex-col gap-1">
                        <label className="text-slate-500 text-xs font-medium">Hasta</label>
                        <DateInput value={bombasHasta} onChange={e => setBombasHasta(e.target.value)}
                          style={{ colorScheme: "dark" }}
                          className="bg-arandu-dark border border-white/10 rounded-lg px-3 py-2 text-slate-300 text-sm focus:outline-none focus:border-orange-500" />
                      </div>
                      {bombasPlayeros.length > 0 && (
                        <div className="flex flex-col gap-1">
                          <label className="text-slate-500 text-xs font-medium">Playero</label>
                          <select value={bombasPlayero} onChange={e => setBombasPlayero(e.target.value)}
                            style={{ colorScheme: "dark" }}
                            className="bg-arandu-dark border border-white/10 rounded-lg px-3 py-2 text-slate-300 text-sm focus:outline-none focus:border-orange-500">
                            <option value="">Todos</option>
                            {bombasPlayeros.map(p => (
                              <option key={p.id} value={p.id}>{p.name || p.email}</option>
                            ))}
                          </select>
                        </div>
                      )}
                      <button
                        onClick={async () => {
                          setBombasLoading(true);
                          setBombasData(null);
                          try {
                            const params = new URLSearchParams();
                            params.set("fecha_desde", bombasDesde);
                            params.set("fecha_hasta", bombasHasta);
                            if (activeEmpresaPropia?.slug) params.set("logo_tipo", activeEmpresaPropia.slug);
                            if (bombasPlayero) params.set("playero_id", bombasPlayero);
                            const res = await fetch(`${API}/admin/lectura-bombas/reporte-volumetria?${params}`, { headers: { Authorization: `Bearer ${token}` } });
                            if (!res.ok) { const e = await res.json(); toast.error(e.detail || "Error al generar reporte"); return; }
                            setBombasData(await res.json());
                          } catch { toast.error("Error al cargar reporte de bombas"); }
                          finally { setBombasLoading(false); }
                        }}
                        disabled={bombasLoading}
                        className="flex items-center gap-2 px-5 py-2.5 bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white rounded-lg text-sm font-semibold transition-colors shadow-lg shadow-orange-500/20">
                        {bombasLoading ? <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Fuel className="w-4 h-4" />}
                        {bombasLoading ? "Generando..." : "Generar reporte"}
                      </button>
                      {bombasData && (
                        <a
                          href={`${API}/admin/lectura-bombas/reporte-volumetria/pdf?fecha_desde=${bombasDesde}&fecha_hasta=${bombasHasta}${activeEmpresaPropia?.slug ? `&logo_tipo=${activeEmpresaPropia.slug}` : ""}${bombasPlayero ? `&playero_id=${bombasPlayero}` : ""}`}
                          download
                          onClick={e => {
                            e.preventDefault();
                            fetch(e.currentTarget.href, { headers: { Authorization: `Bearer ${token}` } })
                              .then(r => r.blob())
                              .then(b => { const a = document.createElement("a"); a.href = URL.createObjectURL(b); a.download = `volumetria-bombas-${bombasDesde}-${bombasHasta}.pdf`; a.click(); });
                          }}
                          className="flex items-center gap-2 px-4 py-2.5 border border-orange-500/40 text-orange-300 hover:bg-orange-500/10 rounded-lg text-sm font-medium transition-colors cursor-pointer">
                          <Download className="w-4 h-4" /> Exportar PDF
                        </a>
                      )}
                    </div>
                  )}

                  {/* Cuentas por cobrar / pagar — filtros propios */}
                  {["cuentas_cobrar", "cuentas_pagar"].includes(reporteSeleccionado) && (
                    <div className="space-y-3">
                      <div className="flex flex-wrap items-end gap-3">

                        {/* ── Búsqueda cliente (cobrar) ── */}
                        {reporteSeleccionado === "cuentas_cobrar" && (
                          <div className="flex flex-col gap-1 relative">
                            <label className="text-slate-500 text-xs font-medium">
                              Cliente <span className="text-slate-600 font-normal">(opcional)</span>
                            </label>
                            {cuentasEmpresaId ? (
                              /* Chip con cliente seleccionado */
                              <div className="flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 rounded-lg px-3 py-2 text-sm min-w-[220px]">
                                <span className="flex-1 truncate">{cuentasEmpresaNombre}</span>
                                <button onClick={() => { setCuentasEmpresaId(""); setCuentasEmpresaNombre(""); setCuentasEmpresaBusq(""); setCuentasData(null); }}
                                  className="text-emerald-400 hover:text-white transition-colors shrink-0">✕</button>
                              </div>
                            ) : (
                              <div className="relative">
                                <input
                                  type="text"
                                  placeholder="Buscar cliente..."
                                  value={cuentasEmpresaBusq}
                                  onChange={e => { setCuentasEmpresaBusq(e.target.value); setCuentasEmpresaOpen(true); }}
                                  onFocus={() => setCuentasEmpresaOpen(true)}
                                  onBlur={() => setTimeout(() => setCuentasEmpresaOpen(false), 150)}
                                  className="bg-arandu-dark border border-white/10 text-white rounded-lg px-3 py-2.5 text-sm outline-none min-w-[220px] w-full"
                                />
                                {cuentasEmpresaOpen && (
                                  <div className="absolute z-50 top-full mt-1 left-0 right-0 bg-arandu-dark border border-white/10 rounded-lg shadow-xl max-h-52 overflow-y-auto">
                                    {empresas
                                      .filter(e => !cuentasEmpresaBusq || e.nombre?.toLowerCase().includes(cuentasEmpresaBusq.toLowerCase()))
                                      .slice(0, 40)
                                      .map(e => (
                                        <button key={e.id} onMouseDown={() => {
                                          setCuentasEmpresaId(e.id);
                                          setCuentasEmpresaNombre(e.nombre);
                                          setCuentasEmpresaBusq("");
                                          setCuentasProveedorId("");
                                          setCuentasProveedorNombre("");
                                          setCuentasProveedorBusq("");
                                          setCuentasEmpresaOpen(false);
                                          setCuentasData(null);
                                        }}
                                          className="w-full text-left px-3 py-2 text-sm text-white hover:bg-white/5 transition-colors">
                                          {e.nombre}
                                        </button>
                                      ))}
                                    {empresas.filter(e => !cuentasEmpresaBusq || e.nombre?.toLowerCase().includes(cuentasEmpresaBusq.toLowerCase())).length === 0 && (
                                      <p className="px-3 py-2 text-xs text-slate-500">Sin resultados</p>
                                    )}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        )}

                        {/* ── Búsqueda proveedor (pagar / anticipos por cobrar) ── */}
                        {["cuentas_cobrar", "cuentas_pagar"].includes(reporteSeleccionado) && (
                          <div className="flex flex-col gap-1 relative">
                            <label className="text-slate-500 text-xs font-medium">
                              {reporteSeleccionado === "cuentas_cobrar" ? "Proveedor con anticipo" : "Proveedor"} <span className="text-slate-600 font-normal">(opcional)</span>
                            </label>
                            {cuentasProveedorId ? (
                              <div className="flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 rounded-lg px-3 py-2 text-sm min-w-[220px]">
                                <span className="flex-1 truncate">{cuentasProveedorNombre}</span>
                                <button onClick={() => { setCuentasProveedorId(""); setCuentasProveedorNombre(""); setCuentasProveedorBusq(""); setCuentasData(null); }}
                                  className="text-emerald-400 hover:text-white transition-colors shrink-0">✕</button>
                              </div>
                            ) : (
                              <div className="relative">
                                <input
                                  type="text"
                                  placeholder={reporteSeleccionado === "cuentas_cobrar" ? "Buscar proveedor con anticipo..." : "Buscar proveedor..."}
                                  value={cuentasProveedorBusq}
                                  onChange={e => { setCuentasProveedorBusq(e.target.value); setCuentasProveedorOpen(true); }}
                                  onFocus={() => setCuentasProveedorOpen(true)}
                                  onBlur={() => setTimeout(() => setCuentasProveedorOpen(false), 150)}
                                  className="bg-arandu-dark border border-white/10 text-white rounded-lg px-3 py-2.5 text-sm outline-none min-w-[220px] w-full"
                                />
                                {cuentasProveedorOpen && (
                                  <div className="absolute z-50 top-full mt-1 left-0 right-0 bg-arandu-dark border border-white/10 rounded-lg shadow-xl max-h-52 overflow-y-auto">
                                    {(reporteSeleccionado === "cuentas_cobrar" ? cuentasProveedoresCobrables : cuentasProveedores)
                                      .filter(p => !cuentasProveedorBusq || p.nombre?.toLowerCase().includes(cuentasProveedorBusq.toLowerCase()))
                                      .slice(0, 40)
                                      .map(p => (
                                        <button key={p.id} onMouseDown={() => {
                                          setCuentasProveedorId(p.id);
                                          setCuentasProveedorNombre(p.nombre);
                                          setCuentasProveedorBusq("");
                                          if (reporteSeleccionado === "cuentas_cobrar") {
                                            setCuentasEmpresaId("");
                                            setCuentasEmpresaNombre("");
                                            setCuentasEmpresaBusq("");
                                          }
                                          setCuentasProveedorOpen(false);
                                          setCuentasData(null);
                                        }}
                                          className="w-full text-left px-3 py-2 text-sm text-white hover:bg-white/5 transition-colors flex items-center justify-between gap-2">
                                          <span className="truncate">{p.nombre}</span>
                                          {reporteSeleccionado === "cuentas_cobrar" && (
                                            <span className="shrink-0 text-[10px] text-emerald-300/80 bg-emerald-500/10 border border-emerald-500/20 rounded-full px-2 py-0.5">
                                              {p.tiene_anticipo && p.tiene_prestamo ? "Anticipo/préstamo" : (p.tiene_prestamo ? "Préstamo" : "Anticipo")}
                                            </span>
                                          )}
                                        </button>
                                      ))}
                                    {(reporteSeleccionado === "cuentas_cobrar" ? cuentasProveedoresCobrables : cuentasProveedores).filter(p => !cuentasProveedorBusq || p.nombre?.toLowerCase().includes(cuentasProveedorBusq.toLowerCase())).length === 0 && (
                                      <p className="px-3 py-2 text-xs text-slate-500">Sin resultados</p>
                                    )}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        )}

                        {/* ── Multi-select cuenta contable ── */}
                        <div ref={cuentasPlanRef} className="flex flex-col gap-1 relative">
                          <label className="text-slate-500 text-xs font-medium">Cuenta contable <span className="text-slate-600 font-normal">(opcional)</span></label>
                          <button
                            onClick={() => setCuentasPlanOpen(v => !v)}
                            className="flex items-center justify-between gap-2 bg-arandu-dark border border-white/10 text-white rounded-lg px-3 py-2.5 text-sm outline-none min-w-[220px] text-left">
                            <span className={cuentasPlanIds.length === 0 ? "text-slate-500" : "text-white"}>
                              {cuentasPlanIds.length === 0
                                ? "Todas las cuentas"
                                : cuentasPlanIds.length === 1
                                  ? (cuentasPlanCuentas.find(c => c.id === cuentasPlanIds[0])?.nombre || "1 cuenta")
                                  : `${cuentasPlanIds.length} cuentas`}
                            </span>
                            <svg className={`w-4 h-4 text-slate-400 transition-transform ${cuentasPlanOpen ? "rotate-180" : ""}`} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                          </button>
                          {cuentasPlanOpen && (
                            <div className="absolute z-50 top-full mt-1 left-0 bg-arandu-dark border border-white/10 rounded-lg shadow-xl min-w-[280px] max-h-64 overflow-y-auto">
                              {/* Opción "Todas" */}
                              <button onMouseDown={() => { setCuentasPlanIds([]); setCuentasData(null); }}
                                className={`w-full text-left flex items-center gap-2 px-3 py-2 text-sm transition-colors border-b border-white/5 ${cuentasPlanIds.length === 0 ? "text-emerald-400" : "text-white hover:bg-white/5"}`}>
                                <div className={`w-4 h-4 rounded border-2 flex items-center justify-center shrink-0 ${cuentasPlanIds.length === 0 ? "bg-emerald-500 border-emerald-500" : "border-white/30"}`}>
                                  {cuentasPlanIds.length === 0 && <Check className="w-2.5 h-2.5 text-white" />}
                                </div>
                                Todas las cuentas
                              </button>
                              {cuentasPlanCuentas.map(c => {
                                const sel = cuentasPlanIds.includes(c.id);
                                return (
                                  <button key={c.id}
                                    onMouseDown={() => {
                                      setCuentasPlanIds(prev => sel ? prev.filter(x => x !== c.id) : [...prev, c.id]);
                                      setCuentasData(null);
                                    }}
                                    className={`w-full text-left flex items-center gap-2 px-3 py-2 text-sm transition-colors hover:bg-white/5 ${sel ? "text-emerald-300" : "text-white"}`}>
                                    <div className={`w-4 h-4 rounded border-2 flex items-center justify-center shrink-0 ${sel ? "bg-emerald-500 border-emerald-500" : "border-white/30"}`}>
                                      {sel && <Check className="w-2.5 h-2.5 text-white" />}
                                    </div>
                                    <span className="truncate">{c.codigo} – {c.nombre}</span>
                                  </button>
                                );
                              })}
                            </div>
                          )}
                        </div>

                        {/* Período */}
                        <div className="flex flex-col gap-1">
                          <label className="text-slate-500 text-xs font-medium">Desde</label>
                          <DateInput value={cuentasDesde} onChange={e => { setCuentasDesde(e.target.value); setCuentasData(null); }}
                            className="bg-arandu-dark border border-white/10 text-white rounded-lg px-3 py-2.5 text-sm outline-none" />
                        </div>
                        <div className="flex flex-col gap-1">
                          <label className="text-slate-500 text-xs font-medium">Hasta</label>
                          <DateInput value={cuentasHasta} onChange={e => { setCuentasHasta(e.target.value); setCuentasData(null); }}
                            className="bg-arandu-dark border border-white/10 text-white rounded-lg px-3 py-2.5 text-sm outline-none" />
                        </div>
                      </div>
                      <div className="flex flex-wrap items-center gap-3">
                        {/* Vencimiento */}
                        <div className="flex flex-col gap-1">
                          <label className="text-slate-500 text-xs font-medium">Vencimiento</label>
                          <div className="flex gap-1 flex-wrap">
                            {[
                              { id: "todos", label: "Todos" },
                              { id: "vencido", label: "Vencidos" },
                              { id: "30dias", label: "1–30 días" },
                              { id: "60dias", label: "31–60 días" },
                              { id: "mas60dias", label: "+60 días" },
                            ].map(opt => (
                              <button key={opt.id} onClick={() => { setCuentasVencimiento(opt.id); setCuentasData(null); }}
                                className={`px-3 py-1.5 rounded-lg border text-xs transition-colors ${cuentasVencimiento === opt.id ? "bg-emerald-500 text-white border-emerald-400" : "bg-white/5 text-slate-300 border-white/10 hover:bg-white/10"}`}>
                                {opt.label}
                              </button>
                            ))}
                          </div>
                        </div>
                        {/* Toggle pagadas */}
                        <div className="flex flex-col gap-1">
                          <label className="text-slate-500 text-xs font-medium">Mostrar pagadas</label>
                          <button onClick={() => { setCuentasIncluirPagadas(v => !v); setCuentasData(null); }}
                            className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border text-xs transition-colors ${cuentasIncluirPagadas ? "bg-emerald-500/20 text-emerald-300 border-emerald-500/40" : "bg-white/5 text-slate-400 border-white/10 hover:bg-white/10"}`}>
                            <div className={`w-3.5 h-3.5 rounded border flex items-center justify-center ${cuentasIncluirPagadas ? "bg-emerald-500 border-emerald-500" : "border-white/30"}`}>
                              {cuentasIncluirPagadas && <Check className="w-2.5 h-2.5 text-white" />}
                            </div>
                            {cuentasIncluirPagadas ? "Sí" : "No"}
                          </button>
                        </div>
                        {/* Tipo de factura — solo cuentas_cobrar, con permiso y módulo */}
                        {reporteSeleccionado === "cuentas_cobrar" && hasModule?.("tipos_factura") && hasPermission?.("reportes.filtrar_tipo_factura") && tiposFacList.length > 0 && (
                          <div className="flex flex-col gap-1">
                            <label className="text-slate-500 text-xs font-medium">Tipo de factura</label>
                            <select
                              value={cuentasTipoFacId}
                              onChange={e => {
                                const sel = tiposFacList.find(t => t.id === e.target.value);
                                setCuentasTipoFacId(e.target.value);
                                setCuentasTipoFacNombre(sel?.nombre || "");
                                setCuentasData(null);
                              }}
                              className="bg-arandu-dark border border-white/10 text-white rounded-lg px-3 py-1.5 text-xs outline-none"
                            >
                              <option value="">Todos los tipos</option>
                              {tiposFacList.map(t => (
                                <option key={t.id} value={t.id}>{t.nombre}</option>
                              ))}
                            </select>
                          </div>
                        )}
                        <button onClick={buscarCuentas} disabled={cuentasLoading}
                          className="flex items-center gap-2 px-5 py-2.5 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg text-sm font-semibold transition-colors shadow-lg shadow-emerald-500/20 mt-auto">
                          <Search className="w-4 h-4" />{cuentasLoading ? "Buscando..." : "Buscar"}
                        </button>
                        {cuentasData && (
                          <div className="flex items-center gap-2 mt-auto">
                            <button onClick={handlePrintCuentas}
                              className="flex items-center gap-2 px-4 py-2.5 bg-slate-700/50 hover:bg-slate-700 border border-white/10 text-slate-300 rounded-lg text-sm font-medium transition-colors">
                              <Printer className="w-4 h-4" /> Imprimir
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Estado de Cuenta — filtros propios */}
                  {reporteSeleccionado === "estado_cuenta" && (
                    <div className="space-y-3">
                      <div className="flex flex-wrap items-end gap-3">

                        {/* Tipo: cobrar / pagar */}
                        <div className="flex flex-col gap-1">
                          <label className="text-slate-500 text-xs font-medium">Tipo</label>
                          <div className="flex rounded-lg overflow-hidden border border-white/10">
                            {[["cobrar", "Por cobrar"], ["pagar", "Por pagar"]].map(([t, label]) => (
                              <button key={t} onClick={() => { setEcTipo(t); setEcEntidadId(""); setEcEntidadNombre(""); setEcEntidadBusq(""); setEcData(null); }}
                                className={`px-4 py-2.5 text-sm font-medium transition-colors ${ecTipo === t ? "bg-emerald-500 text-white" : "bg-arandu-dark text-slate-400 hover:text-white"}`}>
                                {label}
                              </button>
                            ))}
                          </div>
                        </div>

                          {/* Entidad search */}
                        <div className="flex flex-col gap-1 relative">
                          <label className="text-slate-500 text-xs font-medium">
                            {ecTipo === "cobrar" ? "Cliente / proveedor" : "Proveedor"} <span className="text-slate-600">(opcional)</span>
                          </label>
                          {ecEntidadId ? (
                            <div className={`flex items-center gap-2 rounded-lg px-3 py-2 text-sm min-w-[220px] border ${ecEntidadId === EC_TODOS ? "bg-blue-500/10 border-blue-500/30 text-blue-300" : "bg-emerald-500/10 border-emerald-500/30 text-emerald-300"}`}>
                              <span className="flex-1 truncate">{ecEntidadNombre}</span>
                              <button onClick={() => { setEcEntidadId(""); setEcEntidadNombre(""); setEcEntidadBusq(""); setEcData(null); }}
                                className={`hover:text-white transition-colors shrink-0 ${ecEntidadId === EC_TODOS ? "text-blue-400" : "text-emerald-400"}`}>✕</button>
                            </div>
                          ) : (
                            <div className="relative">
                              <input
                                type="text"
                                placeholder={`Todos, o buscar ${ecTipo === "cobrar" ? "cliente o proveedor" : "proveedor"}...`}
                                value={ecEntidadBusq}
                                onChange={e => { setEcEntidadBusq(e.target.value); setEcEntidadOpen(true); }}
                                onFocus={() => setEcEntidadOpen(true)}
                                onBlur={() => setTimeout(() => setEcEntidadOpen(false), 150)}
                                className="bg-arandu-dark border border-white/10 text-white rounded-lg px-3 py-2.5 text-sm outline-none min-w-[220px] w-full"
                              />
                              {ecEntidadOpen && (
                                <div className="absolute z-50 top-full mt-1 left-0 right-0 bg-arandu-dark border border-white/10 rounded-lg shadow-xl max-h-52 overflow-y-auto">
                                  {!ecEntidadBusq && (
                                    <button onMouseDown={() => { setEcEntidadId(EC_TODOS); setEcEntidadNombre("Todos"); setEcEntidadBusq(""); setEcEntidadOpen(false); setEcData(null); }}
                                      className="w-full text-left px-3 py-2 text-sm text-blue-300 hover:bg-white/5 transition-colors flex items-center gap-2 border-b border-white/5">
                                      <span className="font-medium">Todos</span>
                                      <span className="text-[10px] text-slate-500">{ecTipo === "cobrar" ? "todos los clientes y proveedores" : "todos los proveedores"}</span>
                                    </button>
                                  )}
                                  {estadoCuentaEntidades
                                    .filter(e => !ecEntidadBusq || e.nombre?.toLowerCase().includes(ecEntidadBusq.toLowerCase()))
                                    .slice(0, 40)
                                    .map(e => (
                                      <button key={e.id} onMouseDown={() => { setEcEntidadId(e.id); setEcEntidadNombre(e.nombre); setEcEntidadBusq(""); setEcEntidadOpen(false); setEcData(null); }}
                                        className="w-full text-left px-3 py-2 text-sm text-white hover:bg-white/5 transition-colors flex items-center justify-between gap-2">
                                        <span className="truncate">{e.nombre}</span>
                                        {ecTipo === "cobrar" && (
                                          <span className="shrink-0 text-[10px] text-emerald-300/80 bg-emerald-500/10 border border-emerald-500/20 rounded-full px-2 py-0.5">
                                            {e._tipoEstadoCuenta}
                                          </span>
                                        )}
                                      </button>
                                    ))}
                                  {estadoCuentaEntidades.filter(e => !ecEntidadBusq || e.nombre?.toLowerCase().includes(ecEntidadBusq.toLowerCase())).length === 0 && (
                                    <p className="px-3 py-2 text-xs text-slate-500">Sin resultados</p>
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                        </div>

                        {/* Incluir pagadas */}
                        <div className="flex flex-col gap-1">
                          <label className="text-slate-500 text-xs font-medium">Mostrar pagadas</label>
                          <button onClick={() => { setEcIncluirPagadas(v => !v); setEcData(null); }}
                            className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border text-xs transition-colors ${ecIncluirPagadas ? "bg-emerald-500/20 text-emerald-300 border-emerald-500/40" : "bg-white/5 text-slate-400 border-white/10 hover:bg-white/10"}`}>
                            <div className={`w-3.5 h-3.5 rounded border flex items-center justify-center ${ecIncluirPagadas ? "bg-emerald-500 border-emerald-500" : "border-white/30"}`}>
                              {ecIncluirPagadas && <Check className="w-2.5 h-2.5 text-white" />}
                            </div>
                            {ecIncluirPagadas ? "Sí" : "No"}
                          </button>
                        </div>

                        <button onClick={buscarEstadoCuenta} disabled={ecLoading}
                          className="flex items-center gap-2 px-5 py-2.5 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg text-sm font-semibold transition-colors shadow-lg shadow-emerald-500/20 mt-auto">
                          <Search className="w-4 h-4" />{ecLoading ? "Buscando..." : "Buscar"}
                        </button>
                        {ecData && (
                          <button onClick={handlePrintEC}
                            className="flex items-center gap-2 px-4 py-2.5 bg-slate-700/50 hover:bg-slate-700 border border-white/10 text-slate-300 rounded-lg text-sm font-medium transition-colors mt-auto">
                            <Printer className="w-4 h-4" /> Imprimir
                          </button>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Inventario técnico */}
                  {reporteSeleccionado === "inventario_tecnico" && (
                    <div>
                      <p className="text-slate-500 text-xs mb-3">Mostrando clientes de {activeEmpresaPropia?.nombre || "la empresa activa"}. Sin seleccionar clientes genera el reporte completo.</p>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 mb-4 max-h-52 overflow-y-auto">
                        {empresas.map(emp => (
                          <button key={emp.id} onClick={() => toggleEmpresa(emp.id)}
                            className={`flex items-center gap-2 p-2.5 rounded-lg text-left text-sm transition-all ${selectedEmpresas.includes(emp.id) ? "bg-amber-500/10 border border-amber-500/30 text-amber-400" : "bg-arandu-dark border border-white/5 text-slate-400 hover:border-white/20"}`}>
                            <div className={`w-4 h-4 rounded border-2 flex items-center justify-center shrink-0 transition-all ${selectedEmpresas.includes(emp.id) ? "bg-amber-500 border-amber-500" : "border-slate-600"}`}>
                              {selectedEmpresas.includes(emp.id) && <Check className="w-2.5 h-2.5 text-white" />}
                            </div>
                            <span className="truncate text-xs">{emp.nombre}</span>
                          </button>
                        ))}
                      </div>
                      <div className="flex flex-wrap items-end gap-3 pt-3 border-t border-white/10">
                        <div>
                          <label className="text-slate-500 text-xs mb-1 block font-medium">Ordenar por</label>
                          <select value={sortBy} onChange={e => setSortBy(e.target.value)} className="bg-arandu-dark border border-white/10 text-white rounded-lg px-3 py-2 text-sm">
                            <option value="nombre">Nombre</option>
                            <option value="fecha">Fecha</option>
                            <option value="ip">IP</option>
                            <option value="categoria">Categoría</option>
                            <option value="empresa">Cliente</option>
                            <option value="ip_agrupado">NVR/DVR agrupado</option>
                          </select>
                        </div>
                        <div className="flex gap-2 ml-auto">
                          <Button onClick={() => fetchReportData(false)} className="bg-amber-600 hover:bg-amber-700 text-white">
                            <Eye className="w-4 h-4 mr-2" /> Generar reporte
                          </Button>
                          {isAdmin && (
                            <Button onClick={() => fetchReportData(true)} className="bg-slate-700 hover:bg-slate-600 text-white">
                              <Lock className="w-4 h-4 mr-2" /> Con credenciales
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* IPs Libres */}
                  {reporteSeleccionado === "ips_libres" && (
                    <div className="space-y-4">
                      {/* Fila 1: empresa + rango */}
                      <div className="flex flex-wrap items-end gap-3">
                        {/* Selector empresa */}
                        <div className="flex flex-col gap-1 relative">
                          <label className="text-slate-500 text-xs font-medium">
                            Cliente <span className="text-slate-600 font-normal">(opcional — vacío = todos)</span>
                          </label>
                          {ipsLibresEmpresaId ? (
                            <div className="flex items-center gap-2 bg-amber-500/10 border border-amber-500/30 text-amber-300 rounded-lg px-3 py-2 text-sm min-w-[220px]">
                              <span className="flex-1 truncate">{ipsLibresEmpresaNombre}</span>
                              <button onClick={() => { setIpsLibresEmpresaId(""); setIpsLibresEmpresaNombre(""); setIpsLibresEmpresaBusq(""); setIpsLibresData(null); }}
                                className="text-amber-400 hover:text-white transition-colors shrink-0">✕</button>
                            </div>
                          ) : (
                            <div className="relative">
                              <input
                                type="text"
                                placeholder="Buscar cliente..."
                                value={ipsLibresEmpresaBusq}
                                onChange={e => { setIpsLibresEmpresaBusq(e.target.value); setIpsLibresEmpresaOpen(true); }}
                                onFocus={() => setIpsLibresEmpresaOpen(true)}
                                onBlur={() => setTimeout(() => setIpsLibresEmpresaOpen(false), 150)}
                                className="bg-arandu-dark border border-white/10 text-white rounded-lg px-3 py-2.5 text-sm outline-none min-w-[220px] w-full"
                              />
                              {ipsLibresEmpresaOpen && (
                                <div className="absolute z-20 top-full mt-1 w-full bg-arandu-dark-light border border-white/10 rounded-lg shadow-xl max-h-48 overflow-y-auto">
                                  {empresas
                                    .filter(e => !ipsLibresEmpresaBusq || e.nombre.toLowerCase().includes(ipsLibresEmpresaBusq.toLowerCase()))
                                    .map(e => (
                                      <button key={e.id} onMouseDown={() => { setIpsLibresEmpresaId(e.id); setIpsLibresEmpresaNombre(e.nombre); setIpsLibresEmpresaBusq(""); setIpsLibresEmpresaOpen(false); setIpsLibresData(null); }}
                                        className="w-full text-left px-3 py-2 text-sm text-slate-300 hover:bg-white/5 hover:text-white transition-colors">
                                        {e.nombre}
                                      </button>
                                    ))}
                                </div>
                              )}
                            </div>
                          )}
                        </div>

                        {/* Tipo de IP */}
                        <div className="flex flex-col gap-1">
                          <label className="text-slate-500 text-xs font-medium">Tipo de IP</label>
                          <div className="flex gap-1">
                            {[["local", "Privada (local)"], ["publica", "Pública"]].map(([val, lbl]) => (
                              <button key={val} onClick={() => { setIpsLibresTipo(val); setIpsLibresData(null); }}
                                className={`px-3 py-2 rounded-lg text-xs font-medium border transition-colors ${ipsLibresTipo === val ? "bg-amber-500/20 border-amber-500/40 text-amber-300" : "bg-transparent border-white/10 text-slate-400 hover:text-white"}`}>
                                {lbl}
                              </button>
                            ))}
                          </div>
                        </div>

                        {/* IP Desde */}
                        <div className="flex flex-col gap-1">
                          <label className="text-slate-500 text-xs font-medium">IP Desde</label>
                          <input
                            type="text"
                            placeholder="192.168.0.1"
                            value={ipsLibresDesde}
                            onChange={e => { setIpsLibresDesde(e.target.value); setIpsLibresData(null); }}
                            className="bg-arandu-dark border border-white/10 text-white rounded-lg px-3 py-2.5 text-sm font-mono outline-none w-40 focus:border-amber-500/40 transition-colors"
                          />
                        </div>

                        {/* IP Hasta */}
                        <div className="flex flex-col gap-1">
                          <label className="text-slate-500 text-xs font-medium">IP Hasta</label>
                          <input
                            type="text"
                            placeholder="192.168.0.254"
                            value={ipsLibresHasta}
                            onChange={e => { setIpsLibresHasta(e.target.value); setIpsLibresData(null); }}
                            className="bg-arandu-dark border border-white/10 text-white rounded-lg px-3 py-2.5 text-sm font-mono outline-none w-40 focus:border-amber-500/40 transition-colors"
                          />
                        </div>

                        {/* Botón generar */}
                        <button
                          onClick={async () => {
                            setIpsLibresLoading(true);
                            setIpsLibresData(null);
                            try {
                              const params = new URLSearchParams({
                                ip_desde: ipsLibresDesde.trim(),
                                ip_hasta: ipsLibresHasta.trim(),
                                tipo_ip: ipsLibresTipo,
                              });
                              if (ipsLibresEmpresaId) params.set("empresa_id", ipsLibresEmpresaId);
                              if (activeEmpresaPropia?.slug) params.set("logo_tipo", activeEmpresaPropia.slug);
                              const res = await fetch(`${API}/admin/inventario/ips-libres?${params}`, { headers: { Authorization: `Bearer ${token}` } });
                              if (!res.ok) { const e = await res.json(); toast.error(e.detail || "Error al generar reporte"); return; }
                              setIpsLibresData(await res.json());
                            } catch { toast.error("Error al cargar reporte de IPs"); }
                            finally { setIpsLibresLoading(false); }
                          }}
                          disabled={ipsLibresLoading || !ipsLibresDesde || !ipsLibresHasta}
                          className="flex items-center gap-2 px-5 py-2.5 bg-amber-600 hover:bg-amber-500 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg text-sm font-semibold transition-colors shadow-lg shadow-amber-600/20">
                          {ipsLibresLoading ? <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Network className="w-4 h-4" />}
                          {ipsLibresLoading ? "Consultando..." : "Consultar"}
                        </button>

                        {ipsLibresData && (
                          <button
                            onClick={() => imprimirIpsLibres(ipsLibresData)}
                            className="flex items-center gap-2 px-4 py-2.5 border border-amber-500/40 text-amber-300 hover:bg-amber-500/10 rounded-lg text-sm font-medium transition-colors">
                            <Printer className="w-4 h-4" /> Imprimir / PDF
                          </button>
                        )}
                      </div>

                      {/* Preview inline */}
                      {ipsLibresData && (
                        <div className="mt-2 space-y-4">
                          {/* Stats */}
                          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                            {[
                              { label: "Total en rango", value: ipsLibresData.total_rango.toLocaleString(), color: "text-slate-300", bg: "bg-white/5 border-white/10" },
                              { label: "IPs en uso", value: ipsLibresData.total_usadas.toLocaleString(), color: "text-orange-400", bg: "bg-orange-500/5 border-orange-500/20" },
                              { label: "IPs libres", value: ipsLibresData.total_libres.toLocaleString(), color: "text-emerald-400", bg: "bg-emerald-500/5 border-emerald-500/20" },
                              { label: "Disponibilidad",
                                value: ipsLibresData.total_rango > 0
                                  ? `${Math.round((ipsLibresData.total_libres / ipsLibresData.total_rango) * 100)}%`
                                  : "0%",
                                color: "text-blue-400", bg: "bg-blue-500/5 border-blue-500/20" },
                            ].map(s => (
                              <div key={s.label} className={`rounded-xl border px-4 py-3 ${s.bg}`}>
                                <div className="text-xs text-slate-500 mb-1">{s.label}</div>
                                <div className={`text-2xl font-bold ${s.color}`}>{s.value}</div>
                              </div>
                            ))}
                          </div>

                          {/* IPs libres */}
                          {ipsLibresData.libres.length > 0 && (
                            <div>
                              <div className="text-xs font-semibold text-emerald-400 mb-2 flex items-center gap-1.5">
                                <div className="w-2 h-2 rounded-full bg-emerald-400" />
                                IPs Libres ({ipsLibresData.total_libres})
                              </div>
                              <div className="flex flex-wrap gap-1.5 max-h-48 overflow-y-auto p-3 bg-emerald-500/5 border border-emerald-500/20 rounded-xl">
                                {ipsLibresData.libres.map(ip => (
                                  <span key={ip} className="font-mono text-xs text-emerald-300 bg-emerald-500/10 border border-emerald-500/20 rounded px-2 py-0.5">{ip}</span>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* IPs en uso */}
                          {ipsLibresData.usadas.length > 0 && (
                            <div>
                              <div className="text-xs font-semibold text-orange-400 mb-2 flex items-center gap-1.5">
                                <div className="w-2 h-2 rounded-full bg-orange-400" />
                                IPs en Uso ({ipsLibresData.total_usadas})
                              </div>
                              <div className="overflow-x-auto rounded-xl border border-orange-500/20">
                                <table className="w-full text-xs">
                                  <thead>
                                    <tr className="bg-orange-500/10 border-b border-orange-500/20">
                                      <th className="text-left px-3 py-2 text-orange-300 font-semibold">IP</th>
                                      <th className="text-left px-3 py-2 text-orange-300 font-semibold">Dispositivo</th>
                                      <th className="text-left px-3 py-2 text-orange-300 font-semibold">Categoría</th>
                                      <th className="text-left px-3 py-2 text-orange-300 font-semibold">Subtipo</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {ipsLibresData.usadas.map((u, i) => (
                                      <tr key={u.ip} className={i % 2 === 0 ? "bg-transparent" : "bg-white/3"}>
                                        <td className="px-3 py-1.5 font-mono text-orange-300 font-medium">{u.ip}</td>
                                        <td className="px-3 py-1.5 text-slate-300">{u.nombre}</td>
                                        <td className="px-3 py-1.5 text-slate-400">{u.categoria || "-"}</td>
                                        <td className="px-3 py-1.5 text-slate-400">{u.subtipo || "-"}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          )}

                          {ipsLibresData.libres.length === 0 && ipsLibresData.usadas.length === 0 && (
                            <p className="text-center text-slate-500 text-sm py-6">No se encontraron activos con IPs en ese rango.</p>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Caja / Banco — filtros propios con rango de fechas */}
                  {reporteSeleccionado === "caja_banco" && (
                    <div className="flex flex-wrap items-end gap-3">
                      <div className="flex flex-col gap-1">
                        <label className="text-slate-500 text-xs font-medium">Desde</label>
                        <DateInput value={reportCajaDesde} onChange={e => setReportCajaDesde(e.target.value)}
                          className="bg-arandu-dark border border-white/10 text-white rounded-lg px-3 py-2.5 text-sm focus:border-emerald-500/40 outline-none transition-colors" />
                      </div>
                      <div className="flex flex-col gap-1">
                        <label className="text-slate-500 text-xs font-medium">Hasta</label>
                        <DateInput value={reportCajaHasta} onChange={e => setReportCajaHasta(e.target.value)}
                          className="bg-arandu-dark border border-white/10 text-white rounded-lg px-3 py-2.5 text-sm focus:border-emerald-500/40 outline-none transition-colors" />
                      </div>
                      <button onClick={handleGenerar} disabled={genericReportLoading || !reportCajaDesde || !reportCajaHasta}
                        className="flex items-center gap-2 px-5 py-2.5 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg text-sm font-semibold transition-colors shadow-lg shadow-emerald-500/20">
                        <Printer className="w-4 h-4" /> Elegir cuenta y generar
                      </button>
                    </div>
                  )}

                  {/* Movimientos — extracto tipo banco: mes rápido, rango personalizado, filtros por tipo */}
                  {reporteSeleccionado === "movimientos" && (
                    <div className="flex flex-wrap items-end gap-3">
                      <div className="flex flex-col gap-1">
                        <label className="text-slate-500 text-xs font-medium">Mes rápido</label>
                        <input type="month" value={reportMovMes}
                          onChange={e => {
                            const mes = e.target.value;
                            if (!mes) return;
                            setReportMovMes(mes);
                            const finMes = endOfMonthDate(mes);
                            setReportMovDesde(`${mes}-01`);
                            setReportMovHasta(finMes > hoyIso ? hoyIso : finMes);
                          }}
                          className="bg-arandu-dark border border-white/10 text-white rounded-lg px-3 py-2.5 text-sm focus:border-emerald-500/40 outline-none transition-colors" />
                      </div>
                      <div className="flex flex-col gap-1">
                        <label className="text-slate-500 text-xs font-medium">Desde</label>
                        <DateInput value={reportMovDesde} onChange={e => setReportMovDesde(e.target.value)}
                          className="bg-arandu-dark border border-white/10 text-white rounded-lg px-3 py-2.5 text-sm focus:border-emerald-500/40 outline-none transition-colors" />
                      </div>
                      <div className="flex flex-col gap-1">
                        <label className="text-slate-500 text-xs font-medium">Hasta</label>
                        <DateInput value={reportMovHasta} onChange={e => setReportMovHasta(e.target.value)}
                          className="bg-arandu-dark border border-white/10 text-white rounded-lg px-3 py-2.5 text-sm focus:border-emerald-500/40 outline-none transition-colors" />
                      </div>
                      <button onClick={handleGenerar} disabled={genericReportLoading || !reportMovDesde || !reportMovHasta}
                        className="flex items-center gap-2 px-5 py-2.5 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg text-sm font-semibold transition-colors shadow-lg shadow-emerald-500/20">
                        <Printer className="w-4 h-4" /> Elegir cuenta y generar
                      </button>
                      <span className="text-slate-500 text-xs max-w-[220px]">
                        Elegí un mes (el saldo inicial arrastra automáticamente el saldo final del mes anterior) o un rango personalizado con Desde/Hasta.
                      </span>
                    </div>
                  )}

                  {/* Préstamos — filtros propios */}
                  {reporteSeleccionado === "prestamos" && (
                    <div className="space-y-4">
                      <div className="flex flex-wrap items-end gap-3">
                        <div className="flex flex-col gap-1">
                          <label className="text-slate-500 text-xs font-medium">Desde</label>
                          <DateInput value={prestReporteDesde} onChange={e => setPrestReporteDesde(e.target.value)}
                            className="bg-arandu-dark border border-white/10 text-white rounded-lg px-3 py-2.5 text-sm focus:border-emerald-500/40 outline-none transition-colors" />
                        </div>
                        <div className="flex flex-col gap-1">
                          <label className="text-slate-500 text-xs font-medium">Hasta</label>
                          <DateInput value={prestReporteHasta} onChange={e => setPrestReporteHasta(e.target.value)}
                            className="bg-arandu-dark border border-white/10 text-white rounded-lg px-3 py-2.5 text-sm focus:border-emerald-500/40 outline-none transition-colors" />
                        </div>
                        <div className="flex flex-col gap-1">
                          <label className="text-slate-500 text-xs font-medium">Tipo</label>
                          <div className="flex gap-1 flex-wrap">
                            {[["todos","Todos"],["bancario","Bancario"],["funcionario","Funcionario"],["proveedor","Proveedor"]].map(([id, label]) => (
                              <button key={id} onClick={() => setPrestReporteTipo(id)}
                                className={`px-3 py-2 rounded-lg border text-sm transition-colors ${prestReporteTipo === id ? "bg-emerald-500 text-white border-emerald-400" : "bg-white/5 text-slate-300 border-white/10 hover:bg-white/10"}`}>
                                {label}
                              </button>
                            ))}
                          </div>
                        </div>
                        <div className="flex flex-col gap-1">
                          <label className="text-slate-500 text-xs font-medium">Estado</label>
                          <select value={prestReporteEstado} onChange={e => setPrestReporteEstado(e.target.value)}
                            className="bg-arandu-dark border border-white/10 text-white rounded-lg px-3 py-2.5 text-sm focus:border-emerald-500/40 outline-none transition-colors">
                            <option value="todos">Todos</option>
                            <option value="activo">Activos</option>
                            <option value="finalizado">Finalizados</option>
                            <option value="cancelado">Cancelados</option>
                          </select>
                        </div>
                        <div className="flex flex-col gap-1">
                          <label className="text-slate-500 text-xs font-medium">Moneda</label>
                          <div className="flex gap-1">
                            {[{ id: "PYG", label: "₲ Gs." }, { id: "USD", label: "$ USD" }, { id: "BRL", label: "R$ BRL" }, { id: "AMBOS", label: "Todos" }].map(opt => (
                              <button key={opt.id} onClick={() => setReportCurrency(opt.id)}
                                className={`px-3 py-2 rounded-lg border text-sm transition-colors ${reportCurrency === opt.id ? "bg-emerald-500 text-white border-emerald-400" : "bg-white/5 text-slate-300 border-white/10 hover:bg-white/10"}`}>
                                {opt.label}
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 items-end">
                        <div className="flex flex-col gap-1 lg:col-span-1 relative">
                          <label className="text-slate-500 text-xs font-medium">Entidad específica</label>
                          {prestReporteEntidadId ? (
                            <div className="flex items-center gap-2 bg-arandu-dark border border-white/10 text-white rounded-lg px-3 py-2.5 text-sm">
                              <span className="flex-1 truncate">{prestReporteEntidadNombre}</span>
                              <button onClick={() => { setPrestReporteEntidadId(""); setPrestReporteEntidadNombre(""); }} className="text-slate-400 hover:text-white">
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                          ) : (
                            <div className="relative">
                              <input value={prestReporteEntidadBusq}
                                onFocus={() => setPrestReporteEntidadOpen(true)}
                                onChange={e => { setPrestReporteEntidadBusq(e.target.value); setPrestReporteEntidadOpen(true); }}
                                placeholder={prestReporteTipo === "funcionario" ? "Buscar funcionario..." : prestReporteTipo === "bancario" ? "Buscar banco/financiera..." : prestReporteTipo === "proveedor" ? "Buscar proveedor..." : "Buscar entidad..."}
                                className="w-full bg-arandu-dark border border-white/10 text-white rounded-lg px-3 py-2.5 text-sm focus:border-emerald-500/40 outline-none transition-colors" />
                              {prestReporteEntidadOpen && (
                                <div className="absolute z-40 mt-1 w-full max-h-56 overflow-y-auto bg-arandu-dark-light border border-white/10 rounded-lg shadow-xl">
                                  {prestReporteEntidades
                                    .filter(e => !prestReporteEntidadBusq || `${e.nombre || ""} ${e.apellido || ""} ${e.ruc || ""}`.toLowerCase().includes(prestReporteEntidadBusq.toLowerCase()))
                                    .slice(0, 40)
                                    .map(e => {
                                      const nombre = `${e.nombre || ""}${e.apellido ? ` ${e.apellido}` : ""}`.trim();
                                      return (
                                        <button key={e.id} onMouseDown={() => { setPrestReporteEntidadId(e.id); setPrestReporteEntidadNombre(nombre); setPrestReporteEntidadOpen(false); }}
                                          className="w-full px-3 py-2 text-left text-sm text-slate-200 hover:bg-white/5 flex justify-between gap-2">
                                          <span className="truncate">{nombre}</span>
                                          {e.ruc && <span className="text-slate-500 text-xs shrink-0">{e.ruc}</span>}
                                        </button>
                                      );
                                    })}
                                  {prestReporteEntidades.filter(e => !prestReporteEntidadBusq || `${e.nombre || ""} ${e.apellido || ""} ${e.ruc || ""}`.toLowerCase().includes(prestReporteEntidadBusq.toLowerCase())).length === 0 && (
                                    <div className="px-3 py-2 text-slate-500 text-sm">Sin resultados</div>
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                        <div className="flex flex-col gap-1">
                          <label className="text-slate-500 text-xs font-medium">Alias / referencia / destino</label>
                          <input value={prestReporteBuscar} onChange={e => setPrestReporteBuscar(e.target.value)}
                            placeholder="Ej: operación, alias, contrato..."
                            className="bg-arandu-dark border border-white/10 text-white rounded-lg px-3 py-2.5 text-sm focus:border-emerald-500/40 outline-none transition-colors" />
                        </div>
                        <div className="flex flex-wrap gap-2 justify-start lg:justify-end">
                          <button onClick={() => {
                            setPrestReporteTipo("todos"); setPrestReporteEstado("todos"); setPrestReporteEntidadId(""); setPrestReporteEntidadNombre(""); setPrestReporteEntidadBusq(""); setPrestReporteBuscar("");
                          }} className="px-3 py-2 rounded-lg bg-white/5 hover:bg-white/10 text-slate-300 border border-white/10 text-sm">
                            Limpiar
                          </button>
                          <button onClick={handleGenerar} disabled={genericReportLoading || !prestReporteDesde || !prestReporteHasta}
                            className="flex items-center gap-2 px-5 py-2.5 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg text-sm font-semibold transition-colors shadow-lg shadow-emerald-500/20">
                            <Printer className="w-4 h-4" /> Generar reporte
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Reportes financieros — período + moneda (todos salvo los que tienen filtro propio) */}
                  {reporteSeleccionado === "catastros" && (
                    <div className="flex flex-wrap items-end gap-3">
                      <div className="flex flex-col gap-1">
                        <label className="text-slate-500 text-xs font-medium">Tipo de catastro</label>
                        <div className="flex gap-1">
                          {[
                            { id: "todos", label: "Todos" },
                            { id: "clientes", label: "Clientes" },
                            { id: "proveedores", label: "Proveedores" },
                            { id: "empleados", label: "Empleados" },
                          ].map(opt => (
                            <button key={opt.id} onClick={() => setCatastroTipo(opt.id)}
                              className={`px-3 py-2 rounded-lg border text-sm transition-colors ${catastroTipo === opt.id ? "bg-emerald-500 text-white border-emerald-400" : "bg-white/5 text-slate-300 border-white/10 hover:bg-white/10"}`}>
                              {opt.label}
                            </button>
                          ))}
                        </div>
                      </div>
                      <button onClick={handleGenerar} disabled={genericReportLoading}
                        className="flex items-center gap-2 px-5 py-2.5 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg text-sm font-semibold transition-colors shadow-lg shadow-emerald-500/20 ml-auto">
                        <Printer className="w-4 h-4" /> Generar reporte
                      </button>
                    </div>
                  )}

                  {reporteSeleccionado === "balancete_obra" && (
                    <div className="space-y-3">
                      <div className="flex flex-wrap items-end gap-3">
                        <div className="flex flex-col gap-1">
                          <label className="text-slate-500 text-xs font-medium">Cliente</label>
                          <select value={balObraClienteId} onChange={e => setBalObraClienteId(e.target.value)}
                            className="bg-arandu-dark border border-white/10 text-white rounded-lg px-3 py-2.5 text-sm focus:border-emerald-500/40 outline-none transition-colors min-w-[200px]">
                            <option value="">Todos los clientes</option>
                            {clientesBalObra.map(c => (
                              <option key={c.id} value={c.id}>{c.nombre || c.razon_social}</option>
                            ))}
                          </select>
                        </div>
                        <div className="flex flex-col gap-1">
                          <label className="text-slate-500 text-xs font-medium">Desde</label>
                          <DateInput value={balObraDesde} onChange={e => setBalObraDesde(e.target.value)}
                            className="bg-arandu-dark border border-white/10 text-white rounded-lg px-3 py-2.5 text-sm focus:border-emerald-500/40 outline-none transition-colors" />
                        </div>
                        <div className="flex flex-col gap-1">
                          <label className="text-slate-500 text-xs font-medium">Hasta</label>
                          <DateInput value={balObraHasta} onChange={e => setBalObraHasta(e.target.value)}
                            className="bg-arandu-dark border border-white/10 text-white rounded-lg px-3 py-2.5 text-sm focus:border-emerald-500/40 outline-none transition-colors" />
                        </div>
                        <button onClick={() => { setBalObraClienteId(""); setBalObraTipoFacturaIds([]); setBalObraDesde(""); setBalObraHasta(""); }}
                          className="px-3 py-2.5 rounded-lg bg-white/5 hover:bg-white/10 text-slate-300 border border-white/10 text-sm">
                          Limpiar filtros
                        </button>
                        <button onClick={handleGenerar} disabled={genericReportLoading}
                          className="flex items-center gap-2 px-5 py-2.5 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg text-sm font-semibold transition-colors shadow-lg shadow-emerald-500/20 ml-auto">
                          <Printer className="w-4 h-4" /> Generar reporte
                        </button>
                      </div>
                      <div className="flex flex-col gap-1.5">
                        <label className="text-slate-500 text-xs font-medium">Obra / Tipo de Factura (dejá todas sin marcar para incluirlas todas)</label>
                        <div className="flex flex-wrap gap-1.5">
                          {obrasBalObra.length === 0 && (
                            <span className="text-slate-600 text-xs">No hay Tipos de Factura configurados para esta empresa. Se pueden crear en Ventas → Tipos de Factura.</span>
                          )}
                          {obrasBalObra.map(o => {
                            const activa = balObraTipoFacturaIds.includes(o.id);
                            return (
                              <button key={o.id}
                                onClick={() => setBalObraTipoFacturaIds(prev => activa ? prev.filter(id => id !== o.id) : [...prev, o.id])}
                                className={`px-2.5 py-1.5 rounded-lg border text-xs transition-colors flex items-center gap-1.5 ${activa ? "border-emerald-400 bg-emerald-500/15 text-emerald-200" : "border-white/10 bg-white/5 text-slate-300 hover:bg-white/10"}`}>
                                <span className="w-2.5 h-2.5 rounded-full" style={{ background: o.color || "#6366f1" }} />
                                {o.nombre}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  )}

                  {reporteSeleccionado === "garantias" && (
                    <div className="flex flex-wrap items-end gap-3">
                      <div className="flex flex-col gap-1">
                        <label className="text-slate-500 text-xs font-medium">Tipo</label>
                        <div className="flex gap-1">
                          {[
                            { id: "todas", label: "Todas" },
                            { id: "recibidas", label: "Recibidas" },
                            { id: "concedidas", label: "Concedidas" },
                          ].map(opt => (
                            <button key={opt.id} onClick={() => setGarantiasTipo(opt.id)}
                              className={`px-3 py-2 rounded-lg border text-sm transition-colors ${garantiasTipo === opt.id ? "bg-emerald-500 text-white border-emerald-400" : "bg-white/5 text-slate-300 border-white/10 hover:bg-white/10"}`}>
                              {opt.label}
                            </button>
                          ))}
                        </div>
                      </div>
                      <div className="flex flex-col gap-1">
                        <label className="text-slate-500 text-xs font-medium">Estado del documento</label>
                        <select value={garantiasEstadoDoc} onChange={e => setGarantiasEstadoDoc(e.target.value)}
                          className="bg-arandu-dark border border-white/10 text-white rounded-lg px-3 py-2.5 text-sm focus:border-emerald-500/40 outline-none transition-colors">
                          <option value="">Todos</option>
                          <option value="vigente">Vigente</option>
                          <option value="cobrado">Cobrado</option>
                          <option value="cancelado">Cancelado</option>
                          <option value="anulado">Anulado</option>
                          <option value="destruido">Destruido</option>
                        </select>
                      </div>
                      <div className="flex flex-col gap-1">
                        <label className="text-slate-500 text-xs font-medium">Desde</label>
                        <DateInput value={garantiasDesde} onChange={e => setGarantiasDesde(e.target.value)}
                          className="bg-arandu-dark border border-white/10 text-white rounded-lg px-3 py-2.5 text-sm focus:border-emerald-500/40 outline-none transition-colors" />
                      </div>
                      <div className="flex flex-col gap-1">
                        <label className="text-slate-500 text-xs font-medium">Hasta</label>
                        <DateInput value={garantiasHasta} onChange={e => setGarantiasHasta(e.target.value)}
                          className="bg-arandu-dark border border-white/10 text-white rounded-lg px-3 py-2.5 text-sm focus:border-emerald-500/40 outline-none transition-colors" />
                      </div>
                      <button onClick={() => { setGarantiasTipo("todas"); setGarantiasEstadoDoc(""); setGarantiasDesde(""); setGarantiasHasta(""); }}
                        className="px-3 py-2.5 rounded-lg bg-white/5 hover:bg-white/10 text-slate-300 border border-white/10 text-sm">
                        Limpiar filtros
                      </button>
                      <button onClick={handleGenerar} disabled={genericReportLoading}
                        className="flex items-center gap-2 px-5 py-2.5 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg text-sm font-semibold transition-colors shadow-lg shadow-emerald-500/20 ml-auto">
                        <Printer className="w-4 h-4" /> Generar reporte
                      </button>
                    </div>
                  )}

                  {reporteSeleccionado === "loteadora_precificacion" && (
                    <div className="flex flex-wrap items-end gap-3">
                      <div className="flex flex-col gap-1 min-w-[220px]">
                        <label className="text-slate-500 text-xs font-medium">Loteamiento</label>
                        <select value={loteadoraPrecifLoteamientoId} onChange={e => setLoteadoraPrecifLoteamientoId(e.target.value)}
                          className="bg-arandu-dark border border-white/10 text-white rounded-lg px-3 py-2.5 text-sm focus:border-emerald-500/40 outline-none transition-colors">
                          <option value="">Seleccioná un loteamiento...</option>
                          {loteamientosLoteadora.map(l => <option key={l.id} value={l.id}>{l.nombre}</option>)}
                        </select>
                      </div>
                      <div className="flex flex-col gap-1">
                        <label className="text-slate-500 text-xs font-medium">N° de cuotas (para calcular la cuota)</label>
                        <input type="number" min="1" value={loteadoraPrecifNCuotas} onChange={e => setLoteadoraPrecifNCuotas(e.target.value)}
                          className="bg-arandu-dark border border-white/10 text-white rounded-lg px-3 py-2.5 text-sm w-32 focus:border-emerald-500/40 outline-none transition-colors" />
                      </div>
                      <button onClick={handleGenerar} disabled={genericReportLoading || !loteadoraPrecifLoteamientoId}
                        className="flex items-center gap-2 px-5 py-2.5 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg text-sm font-semibold transition-colors shadow-lg shadow-emerald-500/20 ml-auto">
                        <Printer className="w-4 h-4" /> Generar reporte
                      </button>
                    </div>
                  )}

                  {isFinanciero && !["caja_banco", "movimientos", "cuentas_cobrar", "cuentas_pagar", "estado_cuenta", "balance", "flow_facturaciones", "flow_financieros", "prestamos", "catastros", "balancete_obra", "garantias", "loteadora_precificacion"].includes(reporteSeleccionado) && (
                    <div className="space-y-4">
                      {/* Período */}
                      <div className="flex flex-wrap items-end gap-3">
                        <div className="flex flex-col gap-1">
                          <label className="text-slate-500 text-xs font-medium">Período</label>
                          <div className="flex items-center gap-1 bg-arandu-dark border border-white/10 rounded-lg px-2 py-1.5">
                            <button onClick={() => updateReportMonth(addMonths(reportMonth, -1))} className="w-7 h-7 rounded-md bg-white/5 hover:bg-white/10 text-slate-300 flex items-center justify-center">
                              <ChevronLeft className="w-4 h-4" />
                            </button>
                            <select value={reportMonthIndex} onChange={e => updateReportMonth(setMonthPart(reportMonth, Number(e.target.value)))}
                              className="bg-transparent text-white text-sm outline-none px-1">
                              {MESES.map((m, idx) => <option key={m} value={idx}>{m}</option>)}
                            </select>
                            <select value={reportYear} onChange={e => { setReportYear(e.target.value); setReportMonth(`${e.target.value}-${reportMonth.slice(5, 7)}`); }}
                              className="bg-transparent text-white text-sm outline-none px-1">
                              {reportYears.map(y => <option key={y} value={y}>{y}</option>)}
                            </select>
                            <button onClick={() => updateReportMonth(addMonths(reportMonth, 1))} className="w-7 h-7 rounded-md bg-white/5 hover:bg-white/10 text-slate-300 flex items-center justify-center">
                              <ChevronRight className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                        {!["balance_anual", "proveedores", "iva", "balance_gerencial"].includes(reporteSeleccionado) && (
                          <div className="flex flex-col gap-1">
                            <label className="text-slate-500 text-xs font-medium">Moneda</label>
                            <div className="flex gap-1">
                              {[{ id: "PYG", label: "₲ Gs." }, { id: "USD", label: "$ USD" }, { id: "BRL", label: "R$ BRL" }, { id: "AMBOS", label: "Todos" }].map(opt => (
                                <button key={opt.id} onClick={() => setReportCurrency(opt.id)}
                                  className={`px-3 py-2 rounded-lg border text-sm transition-colors ${reportCurrency === opt.id ? "bg-emerald-500 text-white border-emerald-400" : "bg-white/5 text-slate-300 border-white/10 hover:bg-white/10"}`}>
                                  {opt.label}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                        <button onClick={() => updateReportMonth(mesActual())}
                          className="px-3 py-2 rounded-lg bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-300 border border-emerald-500/25 text-sm">
                          Mes actual
                        </button>
                        <button onClick={handleGenerar} disabled={genericReportLoading}
                          className="flex items-center gap-2 px-5 py-2.5 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg text-sm font-semibold transition-colors shadow-lg shadow-emerald-500/20 ml-auto">
                          <Printer className="w-4 h-4" /> Generar reporte
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {/* ── Resultados inline (solo Kardex) ────────────────────── */}
                {reporteSeleccionado === "kardex" && kardexData && (() => {
                  const p = kardexData.producto;
                  const movs = kardexData.movimientos || [];
                  const res = kardexData.resumen || {};
                  const unidad = p.unidad || "unidad";
                  const fmt = n => Number(n || 0).toLocaleString("es-PY");
                  return (
                    <div className="space-y-4">
                      {/* Producto header */}
                      <div className="flex items-center gap-4 p-3 bg-arandu-dark-light rounded-xl border border-white/5">
                        <div className="flex-1 min-w-0">
                          <p className="text-white font-semibold text-sm">{p.nombre}</p>
                          <p className="text-slate-400 text-xs mt-0.5">{p.sku && <span className="text-blue-400 font-mono mr-2">{p.sku}</span>}{p.categoria}{p.marca ? ` • ${p.marca}` : ""}{p.deposito ? ` • ${p.deposito}` : ""}</p>
                          {kardexData.es_servicio && (
                            <p className="text-amber-400/80 text-xs mt-1">Este producto es un Servicio — no maneja stock, se muestran sus ventas y devoluciones como movimientos.</p>
                          )}
                        </div>
                        <div className="text-right flex-shrink-0">
                          <p className="text-xs text-slate-500 uppercase tracking-wide">Stock actual</p>
                          <p className="text-2xl font-bold text-blue-400">{fmt(res.stock_actual)}</p>
                          <p className="text-xs text-slate-500">{unidad}</p>
                        </div>
                      </div>
                      {/* Resumen */}
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                        <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-lg p-3 text-center">
                          <p className="text-xs text-emerald-400/70 uppercase tracking-wide">Total Entradas</p>
                          <p className="text-xl font-bold text-emerald-400">{fmt(res.total_entradas)}</p>
                          <p className="text-xs text-emerald-400/60">{unidad}</p>
                        </div>
                        <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 text-center">
                          <p className="text-xs text-red-400/70 uppercase tracking-wide">Total Salidas</p>
                          <p className="text-xl font-bold text-red-400">{fmt(res.total_salidas)}</p>
                          <p className="text-xs text-red-400/60">{unidad}</p>
                        </div>
                        <div className="bg-white/5 border border-white/10 rounded-lg p-3 text-center">
                          <p className="text-xs text-slate-400 uppercase tracking-wide">Diferencia</p>
                          <p className="text-xl font-bold text-white">{fmt(res.diferencia)}</p>
                          <p className="text-xs text-slate-500">{unidad}</p>
                        </div>
                        <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-3 text-center">
                          <p className="text-xs text-blue-400/70 uppercase tracking-wide">Stock Actual</p>
                          <p className="text-xl font-bold text-blue-400">{fmt(res.stock_actual)}</p>
                          <p className="text-xs text-blue-400/60">{unidad}</p>
                        </div>
                      </div>
                      {/* Tabla */}
                      <div>
                        <p className="text-xs text-slate-500 uppercase tracking-wide font-semibold mb-2">Historial de movimientos</p>
                        {movs.length === 0 ? (
                          <p className="text-slate-500 text-sm text-center py-6">Sin movimientos en el período seleccionado</p>
                        ) : (
                          <div className="overflow-x-auto rounded-xl border border-white/5">
                            <table className="w-full text-xs min-w-[800px]">
                              <thead>
                                <tr className="bg-slate-800/80">
                                  <th className="px-3 py-2 text-left text-slate-400 font-medium">Fecha</th>
                                  <th className="px-3 py-2 text-left text-slate-400 font-medium">Tipo</th>
                                  <th className="px-3 py-2 text-left text-slate-400 font-medium">Documento</th>
                                  <th className="px-3 py-2 text-left text-slate-400 font-medium">Descripción</th>
                                  <th className="px-3 py-2 text-right text-emerald-400 font-medium">Entrada</th>
                                  <th className="px-3 py-2 text-right text-red-400 font-medium">Salida</th>
                                  <th className="px-3 py-2 text-right text-blue-400 font-medium">Saldo</th>
                                  <th className="px-3 py-2 text-right text-amber-400 font-medium">Precio Unit.</th>
                                  {(isAdmin || hasPermission?.("inventario_productos.ver_costo_compra")) && <th className="px-3 py-2 text-right text-orange-400 font-medium">Costo Compra</th>}
                                  {(isAdmin || hasPermission?.("inventario_productos.ver_costo_compra")) && <th className="px-3 py-2 text-right text-emerald-300 font-medium">Margen</th>}
                                  <th className="px-3 py-2 text-left text-slate-400 font-medium">Usuario</th>
                                </tr>
                              </thead>
                              <tbody>
                                {movs.map((m, i) => {
                                  const cfg = TIPO_CONFIG[m.tipo_clase] || TIPO_CONFIG.otro;
                                  const esEliminado = m.eliminado === true;
                                  return (
                                    <tr key={m.id || i} className={`${esEliminado ? "opacity-50 bg-red-900/10" : (i % 2 === 0 ? "bg-arandu-dark" : "bg-arandu-dark-light")}`}>
                                      <td className="px-3 py-2 text-slate-400 whitespace-nowrap">{m.fecha || ""}</td>
                                      <td className="px-3 py-2">
                                        <div className="flex items-center gap-1.5 flex-wrap">
                                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-white text-xs font-medium whitespace-nowrap"
                                            style={{ backgroundColor: cfg.bg + "33", color: cfg.bg, border: `1px solid ${cfg.bg}55` }}>
                                            {m.tipo_label || cfg.label}
                                          </span>
                                          {esEliminado && (
                                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-bold bg-red-500/20 text-red-400 border border-red-500/30 whitespace-nowrap">
                                              ANULADO
                                            </span>
                                          )}
                                        </div>
                                      </td>
                                      <td className="px-3 py-2 text-white font-mono font-semibold whitespace-nowrap">{m.documento || "-"}</td>
                                      <td className="px-3 py-2 text-slate-400 max-w-[160px] truncate">{m.notas || ""}</td>
                                      <td className="px-3 py-2 text-right font-semibold text-emerald-400">{m.entrada > 0 ? fmt(m.entrada) : <span className="text-slate-600">—</span>}</td>
                                      <td className="px-3 py-2 text-right font-semibold text-red-400">{m.salida > 0 ? fmt(m.salida) : <span className="text-slate-600">—</span>}</td>
                                      <td className="px-3 py-2 text-right font-bold text-blue-300 whitespace-nowrap">{fmt(m.saldo)}</td>
                                      <td className="px-3 py-2 text-right text-amber-300 whitespace-nowrap">{m.precio_unitario ? fmtPYG(m.precio_unitario) : <span className="text-slate-600">—</span>}</td>
                                      {(isAdmin || hasPermission?.("inventario_productos.ver_costo_compra")) && (
                                        <td className="px-3 py-2 text-right text-orange-300 whitespace-nowrap">{m.costo_compra ? fmtPYG(m.costo_compra) : <span className="text-slate-600">—</span>}</td>
                                      )}
                                      {(isAdmin || hasPermission?.("inventario_productos.ver_costo_compra")) && (
                                        <td className={`px-3 py-2 text-right font-semibold whitespace-nowrap ${m.margen != null ? (m.margen >= 0 ? "text-emerald-400" : "text-red-400") : "text-slate-600"}`}>
                                          {m.margen != null ? fmtPYG(m.margen) : "—"}
                                        </td>
                                      )}
                                      <td className="px-3 py-2 text-slate-500 truncate max-w-[100px]">{m.usuario_nombre || ""}</td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                      <p className="text-xs text-slate-600 text-center">
                        ⓘ {movs.length} movimiento{movs.length !== 1 ? "s" : ""} en el período.
                      </p>
                    </div>
                  );
                })()}

                {/* ── Resultados Cuentas por Cobrar / Pagar ──────────────── */}
                {["cuentas_cobrar", "cuentas_pagar"].includes(reporteSeleccionado) && cuentasData && (() => {
                  const rows = cuentasData.rows || [];
                  const resumenByCur = cuentasData.resumen || {};   // { PYG: {...}, USD: {...} }
                  const esCobrar = reporteSeleccionado === "cuentas_cobrar";
                  const hoyStr = new Date().toISOString().slice(0,10);

                  // Formateo por moneda
                  const fmtNum = n => Number(n || 0).toLocaleString("es-PY");
                  const fmtMoney = (moneda, n) => {
                    if (moneda === "USD") return `$ ${Number(n||0).toLocaleString("es-PY", {minimumFractionDigits:2, maximumFractionDigits:2})}`;
                    return `Gs. ${fmtNum(n)}`;
                  };

                  const estadoStyle = {
                    pendiente: "bg-amber-500/15 text-amber-300 border-amber-500/30",
                    pagado:    "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
                    parcial:   "bg-blue-500/15 text-blue-300 border-blue-500/30",
                    anulado:   "bg-slate-500/15 text-slate-400 border-slate-500/30",
                    vencido:   "bg-red-500/15 text-red-300 border-red-500/30",
                  };
                  const estadoLabel = { pendiente: "Pendiente", pagado: "Pagado", parcial: "Parcial", anulado: "Anulado", vencido: "Vencido" };

                  // Agrupar filas por moneda para renderizar secciones separadas
                  const currencies = [...new Set(rows.map(r => r.moneda || "PYG"))].sort();

                  const toggleExpand = (docId) => setCuentasExpanded(prev => ({...prev, [docId]: !prev[docId]}));

                  const totalDocs = rows.length;

                  return (
                    <div className="space-y-6 mt-4" id="cuentas-print">
                      {/* Encabezado */}
                      <div className="flex items-center justify-between">
                        <h3 className="text-white font-semibold text-base">
                          {REPORT_META[reporteSeleccionado]?.title} — {cuentasDesde} al {cuentasHasta}
                        </h3>
                        <span className="text-slate-400 text-xs">{totalDocs} documento{totalDocs !== 1 ? "s" : ""}{currencies.length > 1 ? ` · ${currencies.join(" + ")}` : ""}</span>
                      </div>

                      {rows.length === 0 && (
                        <p className="text-slate-500 text-sm text-center py-8">Sin documentos para los filtros seleccionados</p>
                      )}

                      {/* Una sección por moneda */}
                      {currencies.map(moneda => {
                        const rowsMon = rows.filter(r => (r.moneda || "PYG") === moneda);
                        const res = resumenByCur[moneda] || {};
                        const fmt = n => fmtMoney(moneda, n);
                        const currencyLabel = moneda === "USD" ? "Dólares (USD)" : moneda === "PYG" ? "Guaraníes (PYG)" : moneda;

                        return (
                          <div key={moneda} className="space-y-3">
                            {/* Encabezado de moneda (solo si hay más de una) */}
                            {currencies.length > 1 && (
                              <div className="flex items-center gap-2">
                                <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${moneda === "USD" ? "bg-emerald-500/15 text-emerald-300 border border-emerald-500/30" : "bg-blue-500/15 text-blue-300 border border-blue-500/30"}`}>
                                  {currencyLabel}
                                </span>
                                <div className="flex-1 h-px bg-white/10" />
                              </div>
                            )}

                            {/* Tabla de documentos */}
                            <div className="overflow-x-auto rounded-xl border border-white/10">
                              <table className="w-full text-xs">
                                <thead>
                                  <tr className="bg-arandu-dark-light border-b border-white/10">
                                    <th className="px-2 py-2.5 w-7" />
                                    {["Documento","Fecha","Vencimiento",esCobrar?"Cliente":"Proveedor","Descripción","Ítems","Total doc.","Pagado","Saldo pend.","Estado"].map(col => (
                                      <th key={col} className="px-3 py-2.5 text-left text-[10px] font-bold tracking-wider text-slate-400 uppercase whitespace-nowrap">{col}</th>
                                    ))}
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-white/5">
                                  {rowsMon.map((r) => {
                                    const fvenc = r.fecha_vencimiento;
                                    const vencida = fvenc && fvenc < hoyStr && r.estado !== "pagado";
                                    const expanded = !!cuentasExpanded[r.doc_id];
                                    // Compras a cuotas: mostrar el botón de expandir aunque
                                    // haya una sola cuota, para ver su vencimiento detallado.
                                    const hasItems = r.n_items > 1 || r.es_cuotas;
                                    return (
                                      <>
                                        {/* Fila principal del documento */}
                                        <tr key={r.doc_id} className={`transition-colors ${expanded ? "bg-arandu-dark-light" : "hover:bg-white/3"}`}>
                                          {/* Expand toggle */}
                                          <td className="px-2 py-2.5 text-center">
                                            {hasItems && (
                                              <button onClick={() => toggleExpand(r.doc_id)}
                                                className="text-slate-500 hover:text-white transition-colors">
                                                <svg className={`w-3.5 h-3.5 transition-transform ${expanded ? "rotate-90" : ""}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                                </svg>
                                              </button>
                                            )}
                                          </td>
                                          <td className="px-3 py-2.5 font-mono text-blue-300 whitespace-nowrap font-semibold">{r.numero}</td>
                                          <td className="px-3 py-2.5 text-slate-300 whitespace-nowrap">{r.fecha_emision || "—"}</td>
                                          <td className={`px-3 py-2.5 whitespace-nowrap font-medium ${vencida ? "text-red-400" : "text-slate-300"}`}>{fvenc || "—"}</td>
                                          <td className="px-3 py-2.5 text-slate-300 max-w-[160px] truncate">{r.razon_social}</td>
                                          <td className="px-3 py-2.5 text-slate-400 max-w-[180px] truncate">{r.descripcion}</td>
                                          <td className="px-3 py-2.5 text-center">
                                            <span className="text-slate-500 text-[10px] bg-white/5 px-1.5 py-0.5 rounded">{r.n_items}</span>
                                          </td>
                                          <td className="px-3 py-2.5 text-right text-white font-semibold whitespace-nowrap">{fmt(r.total_documento)}</td>
                                          <td className="px-3 py-2.5 text-right text-emerald-400 whitespace-nowrap">{fmt(r.monto_pagado)}</td>
                                          <td className={`px-3 py-2.5 text-right font-bold whitespace-nowrap ${r.saldo_pendiente > 0 ? "text-amber-300" : "text-emerald-400"}`}>{fmt(r.saldo_pendiente)}</td>
                                          <td className="px-3 py-2.5">
                                            <span className={`text-[10px] px-2 py-0.5 rounded-full border font-medium whitespace-nowrap ${estadoStyle[r.estado] || estadoStyle.pendiente}`}>
                                              {estadoLabel[r.estado] || r.estado}
                                            </span>
                                          </td>
                                        </tr>
                                        {/* Sub-filas de ítems (expandibles) — si el ítem trae fecha_vencimiento
                                            (cuotas de compras a crédito en cuotas o de préstamos), se muestra
                                            ahí mismo en vez de en la columna "Vencimiento" (esa es la del documento
                                            completo / próxima cuota impaga, no la de cada cuota individual) */}
                                        {expanded && (r.items || []).map((item, ii) => (
                                          <tr key={`${r.doc_id}-item-${ii}`} className="bg-white/2 border-l-2 border-blue-500/30">
                                            <td className="px-2 py-1.5" />
                                            <td colSpan={3} className="px-3 py-1.5 pl-6 text-slate-400 italic">{item.descripcion}</td>
                                            <td className={`px-3 py-1.5 whitespace-nowrap ${item.fecha_vencimiento && item.estado === "vencido" ? "text-red-400" : "text-slate-500"}`}>
                                              {item.fecha_vencimiento || ""}
                                            </td>
                                            <td className="px-3 py-1.5 text-slate-500 text-right">{fmtNum(item.cantidad)} u.</td>
                                            <td className="px-3 py-1.5 text-right text-slate-400 whitespace-nowrap">{fmt(item.precio_unitario)}</td>
                                            <td colSpan={3} className="px-3 py-1.5 text-right text-slate-300 whitespace-nowrap font-medium">{fmt(item.subtotal)}</td>
                                            <td />
                                          </tr>
                                        ))}
                                      </>
                                    );
                                  })}
                                </tbody>
                                <tfoot>
                                  <tr className="bg-arandu-dark border-t-2 border-white/10">
                                    <td colSpan={6} className="px-3 py-3 text-xs font-bold text-slate-400 uppercase tracking-wide">Totales {currencyLabel} ({res.n_documentos || 0} doc.)</td>
                                    <td className="px-3 py-3 text-right font-bold text-white whitespace-nowrap">{fmt(res.total_facturado)}</td>
                                    <td className="px-3 py-3 text-right font-bold text-emerald-400 whitespace-nowrap">{fmt(res.total_cobrado)}</td>
                                    <td className="px-3 py-3 text-right font-bold text-amber-300 whitespace-nowrap">{fmt(res.total_pendiente)}</td>
                                    <td />
                                  </tr>
                                </tfoot>
                              </table>
                            </div>

                            {/* Resúmenes de esta moneda */}
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                              <div className="bg-arandu-dark-light border border-white/10 rounded-xl p-4">
                                <p className="text-[10px] font-bold tracking-wider uppercase text-emerald-400 mb-3">Resumen {currencyLabel}</p>
                                <div className="grid grid-cols-2 gap-3">
                                  {[
                                    ["Total facturado", fmt(res.total_facturado), "text-white"],
                                    [esCobrar ? "Cobrado" : "Pagado", fmt(res.total_cobrado), "text-emerald-400"],
                                    ["Pendiente", fmt(res.total_pendiente), "text-amber-300"],
                                    ["Documentos", res.n_documentos || 0, "text-white"],
                                    ["Vencidos", fmt(res.vencidos), "text-red-400"],
                                    ["Por vencer", fmt((res.por_vencer_30||0)+(res.por_vencer_60||0)+(res.mas_60||0)), "text-blue-300"],
                                  ].map(([label, val, cls]) => (
                                    <div key={label}>
                                      <p className="text-[10px] text-slate-500 uppercase">{label}</p>
                                      <p className={`font-bold text-sm ${cls}`}>{val}</p>
                                    </div>
                                  ))}
                                </div>
                              </div>
                              <div className="bg-arandu-dark-light border border-white/10 rounded-xl p-4">
                                <p className="text-[10px] font-bold tracking-wider uppercase text-blue-400 mb-3">Por vencimiento</p>
                                <div className="space-y-2">
                                  {[
                                    ["Vencidos (hasta hoy)", res.vencidos, "text-red-400"],
                                    ["Por vencer (1–30 días)", res.por_vencer_30, "text-amber-300"],
                                    ["Por vencer (31–60 días)", res.por_vencer_60, "text-blue-300"],
                                    ["Por vencer (+60 días)", res.mas_60, "text-slate-300"],
                                  ].map(([label, val, cls]) => (
                                    <div key={label} className="flex justify-between items-center py-1 border-b border-white/5">
                                      <span className="text-slate-400 text-xs">{label}</span>
                                      <span className={`font-semibold text-sm ${cls}`}>{fmt(val || 0)}</span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                              <div className="bg-arandu-dark-light border border-white/10 rounded-xl p-4">
                                <p className="text-[10px] font-bold tracking-wider uppercase text-slate-400 mb-3">Por estado</p>
                                <div className="space-y-2">
                                  {Object.entries(res.por_estado || {}).filter(([,v])=>v>0).map(([est, val]) => (
                                    <div key={est} className="flex justify-between items-center py-1 border-b border-white/5">
                                      <span className={`text-xs px-2 py-0.5 rounded-full border font-medium ${estadoStyle[est] || "text-slate-400"}`}>{estadoLabel[est] || est}</span>
                                      <span className="text-white font-semibold text-sm">{fmt(val)}</span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}

              {/* ── Resultados Estado de Cuenta ──────────────────────── */}
              {reporteSeleccionado === "estado_cuenta" && ecData && (() => {
                const cuentas = ecData.cuentas || [];
                const totales = ecData.totales || {};
                const esCobrar = ecData.tipo === "cobrar";
                const currencies = [...new Set(cuentas.map(c => c.moneda || "PYG"))].sort();

                const fmtMoney = (moneda, n) => {
                  if (moneda === "USD") return `$ ${Number(n||0).toLocaleString("es-PY", {minimumFractionDigits:2, maximumFractionDigits:2})}`;
                  if (moneda === "BRL") return `R$ ${Number(n||0).toLocaleString("es-PY", {minimumFractionDigits:2, maximumFractionDigits:2})}`;
                  return `Gs. ${Number(n||0).toLocaleString("es-PY")}`;
                };

                const totalCuentas = Object.values(totales).reduce((s, t) => s + (t.n_cuentas || 0), 0);

                return (
                  <div className="space-y-6 mt-4">
                    {/* Encabezado */}
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-white font-semibold text-base">Estado de Cuenta — {ecData.entidad_nombre || ecEntidadNombre}</h3>
                        <p className="text-slate-500 text-xs mt-0.5">{esCobrar ? "Cuentas por cobrar" : "Cuentas por pagar"}</p>
                      </div>
                      <span className="text-slate-400 text-xs">{totalCuentas} cuenta{totalCuentas !== 1 ? "s" : ""}</span>
                    </div>

                    {cuentas.length === 0 && (
                      <p className="text-slate-500 text-sm text-center py-8">Sin cuentas para los filtros seleccionados</p>
                    )}

                    {currencies.map(moneda => {
                      const cuentasMon = cuentas.filter(c => (c.moneda || "PYG") === moneda);
                      const tot = totales[moneda] || {};
                      const fmt = n => fmtMoney(moneda, n);
                      const saldoLabel = esCobrar ? "Saldo Deudor" : "Saldo Acreedor";

                      return (
                        <div key={moneda} className="space-y-3">
                          {currencies.length > 1 && (
                            <div className="flex items-center gap-2">
                              <span className={`text-xs font-bold px-2.5 py-1 rounded-full border ${moneda === "USD" ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" : moneda === "BRL" ? "bg-orange-500/15 text-orange-300 border-orange-500/30" : "bg-blue-500/15 text-blue-300 border-blue-500/30"}`}>
                                {moneda === "USD" ? "Dólares (USD)" : moneda === "BRL" ? "Reales (BRL)" : "Guaraníes (PYG)"}
                              </span>
                              <div className="flex-1 h-px bg-white/10" />
                            </div>
                          )}

                          <div className="overflow-x-auto rounded-xl border border-white/10">
                            <table className="w-full text-xs">
                              <thead>
                                <tr className="bg-arandu-dark-light border-b border-white/10">
                                  <th className="px-4 py-2.5 text-left text-[10px] font-bold tracking-wider text-slate-400 uppercase">{esCobrar ? "Cliente" : "Proveedor"}</th>
                                  <th className="px-4 py-2.5 text-left text-[10px] font-bold tracking-wider text-slate-400 uppercase">Cuenta</th>
                                  <th className="px-4 py-2.5 text-center text-[10px] font-bold tracking-wider text-slate-400 uppercase w-16">Mon.</th>
                                  <th className="px-4 py-2.5 text-right text-[10px] font-bold tracking-wider text-slate-400 uppercase whitespace-nowrap">Valor Total</th>
                                  <th className="px-4 py-2.5 text-right text-[10px] font-bold tracking-wider text-slate-400 uppercase whitespace-nowrap">{saldoLabel}</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-white/5">
                                {cuentasMon.map((c, i) => (
                                  <tr key={`${c.entidad_id || "sin"}-${c.cuenta_id || i}`} className="hover:bg-white/3 transition-colors">
                                    <td className="px-4 py-3 text-slate-200 font-medium">{c.entidad_nombre || "-"}</td>
                                    <td className="px-4 py-3 text-slate-400">{c.cuenta_nombre}</td>
                                    <td className="px-4 py-3 text-center text-slate-400 font-mono text-[10px]">{c.moneda}</td>
                                    <td className="px-4 py-3 text-right text-white font-semibold whitespace-nowrap">{fmt(c.total_valor)}</td>
                                    <td className={`px-4 py-3 text-right font-bold whitespace-nowrap ${Number(c.saldo) > 0 ? "text-amber-300" : "text-emerald-400"}`}>{fmt(c.saldo)}</td>
                                  </tr>
                                ))}
                              </tbody>
                              <tfoot>
                                <tr className="bg-arandu-dark border-t-2 border-white/10">
                                  <td colSpan={3} className="px-4 py-3 text-xs font-bold text-slate-400 uppercase tracking-wide">
                                    Total General ({moneda})
                                  </td>
                                  <td className="px-4 py-3 text-right font-bold text-white whitespace-nowrap">{fmt(tot.total_valor)}</td>
                                  <td className={`px-4 py-3 text-right font-bold whitespace-nowrap ${Number(tot.saldo) > 0 ? "text-amber-300" : "text-emerald-400"}`}>
                                    {fmt(tot.saldo)}
                                  </td>
                                </tr>
                              </tfoot>
                            </table>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}

              {/* ── Resultados Volumetría de Bombas ──────────────────── */}
              {reporteSeleccionado === "reporte_volumetria_bombas" && bombasData && (() => {
                const fmtN = (v, d = 2) => v != null ? Number(v).toLocaleString("es-PY", { minimumFractionDigits: d, maximumFractionDigits: d }) : "-";
                const fmtG = (v) => v != null ? `₲ ${Math.round(Number(v)).toLocaleString("es-PY")}` : "-";
                const filas = bombasData.filas || [];

                // Agrupar por bomba para subtotales
                const porBomba = {};
                filas.forEach(f => {
                  if (!porBomba[f.bomba_id]) porBomba[f.bomba_id] = { filas: [], litros: 0, gs: 0 };
                  porBomba[f.bomba_id].filas.push(f);
                  porBomba[f.bomba_id].litros += Number(f.total_expedido || 0);
                  porBomba[f.bomba_id].gs += Number(f.total_gs || 0);
                });

                return (
                  <div className="space-y-4 mt-2">
                    {/* Resumen */}
                    <div className="grid grid-cols-3 gap-3">
                      <div className="bg-orange-500/10 border border-orange-500/20 rounded-xl p-4 text-center">
                        <p className="text-[11px] text-orange-400/70 uppercase tracking-wide mb-1">Total litros</p>
                        <p className="text-white font-bold text-lg">{fmtN(bombasData.total_litros)}</p>
                      </div>
                      <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-4 text-center">
                        <p className="text-[11px] text-emerald-400/70 uppercase tracking-wide mb-1">Total facturado</p>
                        <p className="text-white font-bold text-lg">{fmtG(bombasData.total_gs)}</p>
                      </div>
                      <div className="bg-white/5 border border-white/10 rounded-xl p-4 text-center">
                        <p className="text-[11px] text-slate-400 uppercase tracking-wide mb-1">Período</p>
                        <p className="text-white font-medium text-sm">{bombasData.periodo}</p>
                      </div>
                    </div>

                    {/* Tabla por bomba */}
                    {Object.entries(porBomba).map(([bombaId, { filas: bFilas, litros, gs }]) => (
                      <div key={bombaId} className="bg-arandu-dark-light border border-white/5 rounded-xl overflow-hidden">
                        <div className="px-4 py-3 border-b border-white/5 flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Fuel className="w-4 h-4 text-orange-400" />
                            <span className="text-white font-medium font-body">{bombaId}</span>
                          </div>
                          <div className="flex items-center gap-4 text-xs">
                            <span className="text-slate-400">{fmtN(litros)} L</span>
                            <span className="text-emerald-400 font-medium">{fmtG(gs)}</span>
                          </div>
                        </div>
                        <table className="w-full text-xs">
                          <thead>
                            <tr className="border-b border-white/5">
                              {["Fecha", "Turno", "Pico", "Combustible", "Precio/L", "Lect. Ini.", "Lect. Fin.", "Total L", "Total (₲)"].map(h => (
                                <th key={h} className="px-3 py-2 text-left text-[10px] text-slate-500 font-medium">{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {bFilas.map((f, idx) => (
                              <tr key={idx} className={`border-b border-white/3 ${idx % 2 === 0 ? "" : "bg-white/1"}`}>
                                <td className="px-3 py-1.5 text-slate-400">{f.fecha}</td>
                                <td className="px-3 py-1.5 text-slate-500">{f.turno || "-"}</td>
                                <td className="px-3 py-1.5 text-center text-slate-400">{f.nro_pico}</td>
                                <td className="px-3 py-1.5 text-cyan-400">{f.tipo_combustible}</td>
                                <td className="px-3 py-1.5 text-right text-slate-300 font-mono">{fmtG(f.precio_litro)}</td>
                                <td className="px-3 py-1.5 text-right font-mono text-slate-400">{fmtN(f.lectura_inicial)}</td>
                                <td className="px-3 py-1.5 text-right font-mono text-slate-400">{fmtN(f.lectura_final)}</td>
                                <td className="px-3 py-1.5 text-right font-mono text-white font-medium">{fmtN(f.total_expedido)}</td>
                                <td className="px-3 py-1.5 text-right font-mono text-emerald-400">{fmtG(f.total_gs)}</td>
                              </tr>
                            ))}
                          </tbody>
                          <tfoot>
                            <tr className="border-t border-white/10 bg-white/3">
                              <td colSpan={7} className="px-3 py-2 text-right text-[10px] text-slate-500 font-medium">Subtotal {bombaId}:</td>
                              <td className="px-3 py-2 text-right font-bold text-white font-mono text-xs">{fmtN(litros)}</td>
                              <td className="px-3 py-2 text-right font-bold text-emerald-400 font-mono text-xs">{fmtG(gs)}</td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    ))}

                    {/* Total general */}
                    <div className="flex justify-end gap-6 px-4 py-3 bg-white/3 border border-white/10 rounded-xl">
                      <span className="text-slate-400 text-sm">Total general:</span>
                      <span className="text-white font-bold">{fmtN(bombasData.total_litros)} L</span>
                      <span className="text-emerald-400 font-bold">{fmtG(bombasData.total_gs)}</span>
                    </div>
                  </div>
                );
              })()}

              </>)}
            </div>
          </div>
        );
      })()}


      {/* ══════════════════ TAB: Alertas ══════════════════ */}
      {tab === "alertas" && hasPermission?.("alertas.ver") && (
        <div className="mb-6 bg-arandu-dark-light border border-pink-500/20 rounded-xl p-4">
          <h3 className="text-pink-300 font-semibold text-sm mb-3 flex items-center gap-2"><Bell className="w-4 h-4" /> Cumpleaños (valores por defecto)</h3>
          <p className="text-slate-500 text-xs mb-3">Rojo = día del cumpleaños. Amarillo = días antes. Valores globales; en Clientes podés definir umbrales distintos por cliente (dejá vacío para usar estos).</p>
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <label className="text-slate-400 text-xs block mb-1">Aviso amarillo (días antes)</label>
              <Input type="number" min={0} value={cumpleanosConfig.notificar_dias_amarillo ?? 10}
                onChange={e => setCumpleanosConfig(c => ({ ...c, notificar_dias_amarillo: parseInt(e.target.value, 10) || 0 }))}
                className="bg-arandu-dark border-white/10 text-white w-28" />
            </div>
            <div>
              <label className="text-slate-400 text-xs block mb-1">Urgente rojo (días: 0 = hoy)</label>
              <Input type="number" min={0} value={cumpleanosConfig.notificar_dias_urgente ?? 0}
                onChange={e => setCumpleanosConfig(c => ({ ...c, notificar_dias_urgente: parseInt(e.target.value, 10) || 0 }))}
                className="bg-arandu-dark border-white/10 text-white w-28" />
            </div>
            <Button onClick={saveCumpleanosConfig} className="bg-pink-600 hover:bg-pink-700 text-white text-sm">Guardar y sincronizar</Button>
          </div>
        </div>
      )}

      {tab === "alertas" && (
        <div className="space-y-4">
          <div className="flex flex-col lg:flex-row gap-3 mb-4">
            <ChipSearch
              value={alertSearch}
              onChange={setAlertSearch}
              chips={alertSearchChips}
              onChipsChange={setAlertSearchChips}
              placeholder="Buscar por cliente, alerta, tipo, fecha, estado... (Enter para agregar filtro)"
            />
            <Button onClick={openNewAlert} className="bg-red-600 hover:bg-red-700 text-white lg:ml-auto" data-testid="new-alert-btn"><Plus className="w-4 h-4 mr-2" /> Nueva Alerta</Button>
          </div>
          {filteredAlertas.length === 0 ? (
            <div className="text-center py-12 bg-arandu-dark-light rounded-xl border border-white/5">
              <Bell className="w-12 h-12 text-slate-600 mx-auto mb-3" /><p className="text-slate-400">No hay alertas</p>
            </div>
          ) : filteredAlertas.map((alerta, idx) => {
            const isR = alerta.estado === "resuelta";
            const venc = alerta.fecha_vencimiento?.split("T")[0];
            const dias = alerta.dias_restantes ?? Math.floor((new Date(venc) - new Date()) / 86400000);
            return (
              <motion.div key={alerta.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                className={`bg-arandu-dark-light border rounded-xl p-4 ${isR ? "border-white/5 opacity-60" : dias < 0 ? "border-red-500/30" : dias <= 30 ? "border-amber-500/20" : "border-white/5"}`} data-testid={`alert-item-${idx}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3 flex-1">
                    <button onClick={() => toggleAlertEstado(alerta)} className={`w-6 h-6 rounded-md border-2 flex items-center justify-center shrink-0 ${isR ? "bg-emerald-500 border-emerald-500" : "border-slate-500 hover:border-amber-400"}`}>
                      {isR && <Check className="w-4 h-4 text-white" />}
                    </button>
                    <div className="flex-1">
                      <div className="flex flex-wrap items-center gap-2 mb-1">
                        <h3 className={`font-semibold text-sm ${isR ? "text-slate-500 line-through" : "text-white"}`}>{alerta.nombre}</h3>
                        <span className={`text-xs px-2 py-0.5 rounded border ${alerta.tipo === "cumpleanos" ? "text-pink-400 bg-pink-500/10 border-pink-500/20" : alerta.tipo === "dominio" ? "text-blue-400 bg-blue-500/10 border-blue-500/20" : alerta.tipo === "nic" ? "text-purple-400 bg-purple-500/10 border-purple-500/20" : "text-slate-400 bg-slate-500/10 border-slate-500/20"}`}>{alerta.tipo}{alerta.auto_generada ? " · auto" : ""}</span>
                        <span className="text-slate-500 text-xs">{alerta.empresa_nombre}</span>
                      </div>
                      {alerta.descripcion && <p className="text-slate-400 text-xs">{alerta.descripcion}</p>}
                      <div className="flex items-center gap-3 mt-1.5 text-xs text-slate-500">
                        <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {venc}</span>
                        <span className="flex items-center gap-1"><Bell className="w-3 h-3" /> {alerta.notificar_dias}d antes</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {!isR && <span className={`text-xs font-bold px-2 py-1 rounded border ${getDaysColor(dias, alerta.color_alerta)}`}>{dias < 0 ? "Vencido" : dias === 0 ? "Hoy" : `${dias}d`}</span>}
                    {!alerta.auto_generada && <button onClick={() => openEditAlert(alerta)} className="p-1 text-slate-400 hover:text-amber-400"><Edit className="w-3.5 h-3.5" /></button>}
                    <button onClick={() => deleteAlert(alerta.id)} className="p-1 text-slate-400 hover:text-red-400"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      )}

      {/* ══════════════════ MODAL: Alerta ══════════════════ */}
      <AnimatePresence>
        {showAlertForm && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
            <motion.div initial={{ scale: 0.9 }} animate={{ scale: 1 }} className="bg-arandu-dark-light border border-white/10 rounded-xl w-full max-w-lg">
              <div className="border-b border-white/10 p-4 flex justify-between items-center">
                <h2 className="font-heading text-lg font-bold text-white">{editingAlert ? "Editar" : "Nueva"} Alerta</h2>
                <Button onClick={() => setShowAlertForm(false)} variant="ghost" className="text-slate-400"><X className="w-4 h-4" /></Button>
              </div>
              <div className="p-4 space-y-3">
                <div><label className="text-slate-400 text-xs mb-1 block">Cliente *</label>
                  <select value={alertForm.empresa_id} onChange={(e) => setAlertForm({...alertForm, empresa_id: e.target.value, activo_id: ""})} className="w-full bg-arandu-dark border border-white/10 text-white rounded-lg px-3 py-2 text-sm" data-testid="alert-empresa"><option value="">Seleccionar cliente...</option>{empresas.map(e => <option key={e.id} value={e.id}>{e.nombre}</option>)}</select></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><label className="text-slate-400 text-xs mb-1 block">Tipo</label>
                    <select value={alertForm.tipo} onChange={(e) => setAlertForm({...alertForm, tipo: e.target.value})} className="w-full bg-arandu-dark border border-white/10 text-white rounded-lg px-3 py-2 text-sm" data-testid="alert-tipo"><option value="dominio">Dominio</option><option value="nic">NIC</option><option value="licencia">Licencia</option><option value="cumpleanos">Cumpleaños</option><option value="personalizado">Personalizado</option></select></div>
                  <div><label className="text-slate-400 text-xs mb-1 block">Notificar dias antes (amarillo)</label><Input type="number" value={alertForm.notificar_dias} onChange={(e) => setAlertForm({...alertForm, notificar_dias: parseInt(e.target.value) || 30})} className="bg-arandu-dark border-white/10 text-white" /></div>
                </div>
                {alertForm.tipo === "cumpleanos" && (
                  <div><label className="text-slate-400 text-xs mb-1 block">Urgente rojo (días: 0 = hoy)</label>
                    <Input type="number" min={0} value={alertForm.notificar_dias_urgente ?? 0}
                      onChange={(e) => setAlertForm({ ...alertForm, notificar_dias_urgente: parseInt(e.target.value, 10) || 0 })}
                      className="bg-arandu-dark border-white/10 text-white" /></div>
                )}
                <div><label className="text-slate-400 text-xs mb-1 block">Nombre *</label><Input value={alertForm.nombre} onChange={(e) => setAlertForm({...alertForm, nombre: e.target.value})} className="bg-arandu-dark border-white/10 text-white" placeholder="Dominio empresa.com.py" data-testid="alert-nombre" /></div>
                <div><label className="text-slate-400 text-xs mb-1 block">Vencimiento *</label><DateInput value={alertForm.fecha_vencimiento} onChange={(e) => setAlertForm({...alertForm, fecha_vencimiento: e.target.value})} className="bg-arandu-dark border-white/10 text-white" data-testid="alert-fecha" /></div>
                <div><label className="text-slate-400 text-xs mb-1 block">Activo (opcional)</label>
                  <select value={alertForm.activo_id} onChange={(e) => setAlertForm({...alertForm, activo_id: e.target.value})} className="w-full bg-arandu-dark border border-white/10 text-white rounded-lg px-3 py-2 text-sm"><option value="">Ninguno</option>{activosDeEmpresaActiva.filter(a => !alertForm.empresa_id || a.empresa_id === alertForm.empresa_id).map(a => <option key={a.id} value={a.id}>{a.nombre}</option>)}</select></div>
                <div><label className="text-slate-400 text-xs mb-1 block">Descripcion</label><textarea value={alertForm.descripcion} onChange={(e) => setAlertForm({...alertForm, descripcion: e.target.value})} className="w-full bg-arandu-dark border border-white/10 text-white rounded-lg px-3 py-2 text-sm min-h-[50px]" /></div>
                <div className="flex gap-2 justify-end pt-2">
                  <Button onClick={() => setShowAlertForm(false)} variant="ghost" className="text-slate-400">Cancelar</Button>
                  <Button onClick={saveAlert} className="bg-red-600 hover:bg-red-700 text-white" data-testid="save-alert-btn"><Save className="w-4 h-4 mr-2" /> Guardar</Button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showCajaPicker && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
            <motion.div initial={{ scale: 0.9 }} animate={{ scale: 1 }}
              className="bg-arandu-dark-light border border-white/10 rounded-xl w-full max-w-md p-5">
              <h3 className="text-white font-bold mb-2">Elegir cuenta bancaria</h3>
              <p className="text-slate-500 text-xs mb-3">{reportCajaDesde} → {reportCajaHasta}</p>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {cajaCuentasPicker.map(c => (
                  <button key={c.id} type="button" onClick={() => ejecutarReporteCajaBanco(c.id)}
                    className="w-full text-left px-4 py-3 rounded-lg border border-white/10 hover:border-cyan-500/40 hover:bg-cyan-500/10 transition-all">
                    <p className="text-white text-sm font-medium">{c.nombre}</p>
                    <p className="text-slate-500 text-xs">{c.moneda || "PYG"} · {c.banco || c.tipo || "Cuenta"}</p>
                  </button>
                ))}
              </div>
              <Button onClick={() => setShowCajaPicker(false)} variant="ghost" className="w-full mt-4 text-slate-400">Cancelar</Button>
            </motion.div>
          </motion.div>
        )}

        {showMovPicker && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
            <motion.div initial={{ scale: 0.9 }} animate={{ scale: 1 }}
              className="bg-arandu-dark-light border border-white/10 rounded-xl w-full max-w-md p-5">
              <h3 className="text-white font-bold mb-2">Elegir cuenta bancaria</h3>
              <p className="text-slate-500 text-xs mb-3">{reportMovDesde} → {reportMovHasta}</p>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {movCuentasPicker.map(c => (
                  <button key={c.id} type="button" onClick={() => ejecutarReporteMovimientos(c.id)}
                    className="w-full text-left px-4 py-3 rounded-lg border border-white/10 hover:border-cyan-500/40 hover:bg-cyan-500/10 transition-all">
                    <p className="text-white text-sm font-medium">{c.nombre}</p>
                    <p className="text-slate-500 text-xs">{c.moneda || "PYG"} · {c.banco || c.tipo || "Cuenta"}</p>
                  </button>
                ))}
              </div>
              <Button onClick={() => setShowMovPicker(false)} variant="ghost" className="w-full mt-4 text-slate-400">Cancelar</Button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ReportesPage;
